// Architecture boundary reference: docs/eeg/EEGVIEWER_MODULE_BOUNDARIES.md
import { Trans, useTranslation } from "react-i18next";
import React, {
  useState,
  useEffect,
  useId,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  useTransition,
  Suspense,
  lazy,
} from "react";
import { useAppDialog } from "@shared/context/AppDialogContext";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Badge, Spinner } from "react-bootstrap";
import apiClient from "@shared/services/apiClient";
import type { AxiosError } from "@shared/services/apiClient";
import { resolveRecordingExamId } from "./recordingTableModel";
import logger from "@shared/utils/logger";
import { useOptionalEegImportTasks } from "@features/Patient/context/EegImportTaskContext";
const AnnotationActionCatalog = lazy(() => import("./AnnotationActionCatalog"));
import {
  getPatientBirthYearLabel,
  getPatientDisplayName,
  getPatientRecordLabel,
  isMaskedPatient,
} from "@shared/utils/patientPrivacy";
import timescaleAPI, {
  type BackgroundTrendData,
} from "@shared/services/timescaleApiClient";
import {
  getEegPlaybackManifest,
  getEegPlaybackTiles,
  postEegClinicalExport,
  postEegPlaybackPrefetch,
  prewarmEegPlaybackAccessToken,
  type EegClinicalExportRequest,
  type EegPlaybackManifest,
} from "@features/EEG/services/eegPlaybackApi";
import { useStudy } from "@shared/context/StudyContext";
import { usePerspectiveState } from "@shared/context/PerspectiveStateContext";
import { useMontages } from "@shared/context/MontageContext";
import { useUserPreferences } from "@shared/context/UserPreferencesContext";
import { useSettingsSafe } from "@shared/contexts/SettingsContext";
import { useAuth } from "@shared/context/AuthContext";
import { useFeatureFlags } from "@shared/hooks/useFeatureFlags";
import { usePerspectives } from "@features/Perspective/hooks/usePerspectives";
import {
  fetchEventCatalog,
  fetchEventSets,
  fetchTrendTemplates,
} from "@features/EEGConfig/services/eegConfigService";
import type { ChannelPair as EditableChannelPair } from "@features/Montage/components/EditChannelPairModal";
const EditChannelPairModal = lazy(
  () => import("@features/Montage/components/EditChannelPairModal"),
);
import {
  fetchMontageByIdWithMeta,
  updateMontage,
  createMontageVariant,
} from "@features/Montage/services/MontageService";
import {
  ensureEegChannelCatalogLoaded,
  getEegChannelCodesFromCatalog,
} from "@shared/services/eegChannelCatalog";
import {
  getEegStartupTelemetry,
  markEegStartup,
  measureEegStartupBlock,
  subscribeEegStartupMilestone,
} from "../utils/eegStartupTelemetry";
import {
  configureEegStartupSchedulerGate,
  scheduleEegStartupTask,
} from "../utils/eegStartupScheduler";
import { DOCK_PANEL_PLAYBACK_REFRESH_PANELS } from "../utils/eegDockPanelRefreshScheduler";
import {
  EEG_STANDARD_WAVEFORM_COLOR_DEFAULTS,
  readCachedWaveformColors,
  writeCachedWaveformColors,
  type EegWaveformColorEntry,
} from "../utils/eegWaveformColorDefaults";
import {
  clampTimelineTimeToRecording,
  isTimelineTimeWithinRecording,
} from "../utils/eegTimelineBounds";
import usePlaybackPerf from "../hooks/usePlaybackPerf";
import usePlaybackClock, {
  type PlaybackClockSchedulerSample,
  type PlaybackClockTimingSample,
  type PlaybackClockUiPublishRequest,
} from "../hooks/usePlaybackClock";
import useRendererMode from "../hooks/useRendererMode";
import {
  useEEGViewerDockLayout,
  DEFAULT_DOCK_LAYOUT,
  CLINICAL_DOCK_MIN_WIDTH_PX,
  CLINICAL_DOCK_MAX_WIDTH_PX,
  RIGHT_DOCK_MIN_WIDTH_PX,
  RIGHT_DOCK_MAX_WIDTH_PX,
  DEFAULT_DOCK_PANEL_VISIBILITY,
  DEFAULT_DOCK_PANEL_ORDER,
  DOCK_BOTTOM_COLLAPSED_STORAGE_KEY,
  DOCK_RIGHT_COLLAPSED_STORAGE_KEY,
  CLINICAL_DOCK_WIDTH_STORAGE_KEY,
  readDockCollapsedPreferenceOverride,
  readDockCollapsedPreference,
  readDockDimensionPreferenceOverride,
  type OutputToolbarSectionKey,
} from "../hooks/useEEGViewerDockLayout";
import { useEEGFloatingToolbar } from "../hooks/useEEGFloatingToolbar";
import { useEEGAnnotationPanel } from "../hooks/useEEGAnnotationPanel";
import { useEEGConsensusReview } from "../hooks/useEEGConsensusReview";
import { useEEGPausedInspectHold } from "../hooks/useEEGPausedInspectHold";
import { useEEGViewerSeek } from "../hooks/useEEGViewerSeek";
import {
  useEEGTraceChannelSelection,
  type EEGTraceGainEstimateSource,
} from "../hooks/useEEGTraceChannelSelection";
import { useEEGViewerMontageDraft } from "../hooks/useEEGViewerMontageDraft";
import { useEEGPlaybackTick } from "../hooks/useEEGPlaybackTick";
import { useDockPanelPlaybackRefresh } from "../hooks/useDockPanelPlaybackRefresh";
import type { EEGViewerPlaybackTickRefs } from "../components/eegViewerPlaybackTick";
import { useEEGScreenCalibration } from "../hooks/useEEGScreenCalibration";
import { useEEGDisplayProfile } from "../hooks/useEEGDisplayProfile";
import {
  useEEGDisplayProfileSession,
  type ViewerConfiguration,
} from "../hooks/useEEGDisplayProfileSession";
import { useEEGPlaybackSchedulerTelemetry } from "../hooks/useEEGPlaybackSchedulerTelemetry";
import {
  createDisplayProfileConfigExtractors,
  DEFAULT_ALLOW_TRACE_OVERFLOW,
  DEFAULT_DUAL_MONTAGE_PREFERENCE,
  DEFAULT_REFIT_ON_CHANNEL_CHANGE,
} from "../utils/eegViewerDisplayProfileConfigExtractors";
import DualMontageReviewPanel from "./DualMontageReviewPanel";
import DualMontageReviewFloatingPanel from "./DualMontageReviewFloatingPanel";
import type { DualMontageChannelSeries } from "../utils/dualMontageReviewUtils";
import {
  FALLBACK_DISPLAY_PROFILE_POLICY,
  RENDER_THEME_MAP,
  type DisplayProfileBackgroundTheme,
  type DisplayProfileConfig,
  type DisplayProfileGridDensity,
} from "../types/eegViewerDisplayProfileTypes";
import {
  eegVisualToWorkspaceDensity,
  parsePortalWorkspaceDensity,
  portalThemeToEegPaperTheme,
  resolveEegWorkspaceTheme,
  rowHeightModeToGridDensity,
  workspaceDensityToEegVisual,
} from "../utils/eegWorkspaceTheme";
import {
  getNotchFlagsForMode,
  getNotchModeFromFlags,
  getNotchModeLabel,
  inferNotchModeFromRecordingMetadata,
  mergeNotchFlags,
  normalizeNotchFlags,
  type NotchMode,
} from "../utils/eegNotchFilter";
import {
  CLINICAL_HIGH_PASS_HZ,
  CLINICAL_LOW_PASS_HZ,
  collectPresetFilterHz,
} from "../utils/eegFilterFrequencyOptions";
import EegFilterFrequencySelect from "./EegFilterFrequencySelect";
import {
  defaultMontagePairLabel,
  normalizeMontageDraftPair,
  type ViewerMontageDraftPair,
} from "../utils/eegViewerMontageDraft";
import {
  normalizeChannelPairKey,
  normalizeHexColor,
} from "../types/eegViewerTraceTypes";
import {
  useEEGTileCache,
  cancelPrefetchRequests,
  getPrefetchDebugSnapshot,
  getStaleTileDropCount,
  getTilePrefetchQueueDepth,
  prefetchWindowsBackground,
  recordStaleTileDrop,
  waitForInFlightPrefetchTile,
} from "../hooks/useEEGTileCache";
import type { EEGTileTransportMode } from "../services/EEGSegmentCache";
import type { TileCacheIdentity } from "../utils/tileCacheIdentity";
import {
  buildTileCacheKey,
  resolveTileCacheIdentity,
} from "../utils/tileCacheIdentity";
import { alignTileWindowToBackend } from "../utils/tileWindowAlignment";
import { createDecodedTileLruCache } from "../utils/eegDecodedTileCache";
import {
  intersectChannelLabelLists,
  resolveSelectionMeasuredChannels,
  SEGMENT_SELECTION_DRAG_PX,
  shouldUseRectangularSegmentSelection,
} from "../utils/segmentSelectionUtils";
const EEGSegmentInspector = lazy(() => import("./EEGSegmentInspector"));
import type {
  EEGSelection,
  EEGSelectionDraft,
  EEGSelectionMetrics,
  EEGSelectionSimilarEvent,
  EEGSelectionSpectrogramCell,
  EEGSelectionSpectrumBin,
} from "./eegSegmentSelectionTypes";
import {
  getPausedHighSpeedInspectGraceMs,
  shouldCreatePausedPlayTransportHold,
} from "../utils/pausedHighSpeedInspect";
import {
  useKeyboardShortcuts,
  type KeyboardShortcut,
} from "../hooks/useKeyboardShortcuts";
import ChangeSummaryBanner, {
  ConfigChange,
} from "@features/Perspective/components/ChangeSummaryBanner";
import LockToggle from "@features/Perspective/components/LockToggle";
import SignalStateStrip from "@features/Perspective/components/SignalStateStrip";
import MontageEventBus, {
  type MontageAppliedEvent,
  isMontageAppliedEvent,
} from "@features/Montage/services/MontageEventBus";
import EEGWaveformRenderer, {
  EEGMontageChannel,
  RenderConfig,
  EEGChannel,
  calculateHorizontalMmPerSecond,
} from "../services/EEGWaveformRenderer";
// SpectralAnalysisPanel and ComparisonView are only shown on user demand
// (toggle buttons / dock panel), so lazy-load them to cut initial JS parse
// time by ~300–500 ms on first page load. VideoPanel is loaded behind the
// EEGVideoSyncPanel layer boundary instead (see below).
const WebSocketStreaming = lazy(() =>
  import("../../../components/WebSocketStreaming").then((m) => ({
    default: m.WebSocketStreaming,
  })),
);
import type {
  ExternalEventMarker,
  PlaybackClockSnapshot,
  PlaybackClockUpdate,
  PlaybackTransportController,
} from "@features/VideoEEG/types/video.types";
import { useStudyVideoPlaybackWarmup } from "@features/VideoEEG/hooks/useStudyVideoPlaybackWarmup";
import { useVideoPopout } from "@features/VideoEEG/hooks/useVideoPopout";
import {
  ArrowDown,
  ArrowUp,
  Menu,
} from "lucide-react";
import type { TimelineAnnotation } from "./AnnotationTimeline";
import EEGPlaybackCore from "./EEGPlaybackCore";
import MobileReadOnlyBanner from "./MobileReadOnlyBanner";
const EEGViewerDeferredChrome = lazy(
  () => import("./EEGViewerDeferredChrome"),
);
import {
  EEGViewerRuntimeProvider,
  type EEGViewerRuntimeContextValue,
} from "./EEGViewerRuntimeContext";
import {
  mergeReviewedRanges,
  readReviewedRanges,
  writeReviewedRanges,
  type ReviewedTimeRange,
} from "./eegReviewProgressUtils";
import {
  isNavigationTimelineSlidingWindow,
  readMacroOverviewCollapsed,
  readMacroOverviewCustomRange,
  readMacroOverviewZoomMode,
  readNavigationTimeTicksPreference,
  writeMacroOverviewCollapsed,
  writeNavigationTimeTicksPreference,
  type MacroOverviewZoomMode,
  type NavigationTimelineVisibleRange,
} from "./eegNavigationTimelineUtils";
import {
  buildEEGViewerLiveStatus,
  resolveEEGViewerWaveformTextOverlays,
} from "./eegViewerLiveStatusUtils";
import { openEegDetachedWindow } from "./eegDetachedWindowUtils";
import {
  ACTIVE_DOCK_PANEL_ORDER,
  ANALYSIS_DOCK_RAIL_PANEL_KEYS,
  DOCK_PANEL_LABELS,
  DOCK_PANEL_RAIL_MODIFIER,
  getDockPanelLabel,
  getRightDockRailLabel,
  isActiveDockPanelKey,
  normalizeDockPanelOrder,
  type DockPanelKey,
} from "./dockPanelLabels";
import { NAV_TIMELINE_COMPACT_HEIGHT_PX } from "./eegNavigationTimelineUtils";
import {
  readEEGViewerSessionReviewConfig,
  writeEEGViewerSessionReviewConfig,
} from "./eegViewerSessionReviewConfig";
import type { WaveformAnnotationMarkerItem } from "./WaveformAnnotationMarkers";
const KeyboardShortcutsModal = lazy(() => import("./KeyboardShortcutsModal"));
const EEGPatientContextBar = lazy(() => import("./EEGPatientContextBar"));
const MacroOverviewZoomSelect = lazy(() => import("./MacroOverviewZoomSelect"));
import ToolbarOverflowRow from "./ToolbarOverflowRow";
import type { SpectrogramConfig } from "./SpectrogramPanel";
import EEGPlayableShell from "./EEGPlayableShell";
import {
  createEmptyLiveTopographyPreview,
  type LiveTopographyPreviewModel,
} from "./eegViewerLiveTopography";
import {
  buildLiveTopographyPreviewFromChannels,
  resolveLiveScalpElectrodeToChannelLabels,
} from "./liveScalpLocalizeHelpers";
import {
  buildSelectionBandPower,
  buildSelectionSpectrogram,
  buildSelectionSpectrum,
  estimateSelectionDominantFrequency,
} from "./eegViewerSelectionAnalysis";
import {
  PlaybackControlButton,
  PlaybackControlCluster,
} from "./EEGPlaybackControls";
const ComparisonView = lazy(() =>
  import("./ComparisonView").then((m) => ({ default: m.ComparisonView })),
);
import type { AnnotationListItem } from "./AnnotationListView";
import { formatAnnotationListLabel, getAnnotationTypeDisplayName } from "../utils/annotationListLabel";
const EegClipScissorsModal = lazy(() => import("./EegClipScissorsModal"));
const ConfigSettingsPanel = lazy(() => import("./ConfigSettingsPanel"));
const ConfigSettingsPanelContent = lazy(
  () => import("./ConfigSettingsPanelContent"),
);
import type { DetectedSpike, SeizureEvent } from "../types/detectionTypes";
import type { ClinicalReport } from "./ReportBuilder";
import BadChannelManagerPanel from "./customization/BadChannelManagerPanel";
import FilterOverrideEditorPanel from "./customization/FilterOverrideEditorPanel";
const ChannelManagerPanel = lazy(() => import("./ChannelManagerPanel"));
const EegDetachedPanelWindow = lazy(() => import("./EegDetachedPanelWindow"));
import {
  createEegSyncChannel,
  throttleToAnimationFrame,
  isEegSyncMessage,
  type EegSyncMessage,
} from "../services/eegSyncChannel";
import { completeAiTriageCloseDraft } from "../services/aiTriageCloseDraft";
import {
  validateRapidEventDraft,
  type RapidEventType,
} from "../utils/rapidEventValidation";
import {
  applyChannelOffsetChange,
  clampDisplayProfileNumber,
  snapDisplayProfileNumber,
  normalizeChannelOffsets,
  resetAllChannelOffsets,
  resetChannelOffset,
  sanitizeChannelPositionOffsets,
} from "../utils/displayProfileVisualState";
import {
  DEFAULT_CALIBRATION_BAR_PX,
  DEFAULT_SCREEN_DPI,
  SCREEN_CALIBRATION_ENV_LOCAL_KEY,
  SCREEN_CALIBRATION_ENV_SESSION_KEY,
  SCREEN_CALIBRATION_LOCAL_KEY,
  SCREEN_CALIBRATION_SESSION_KEY,
  calculateCalibrationScale,
  defaultScreenCalibrationState,
  getCurrentCalibrationEnvironmentSnapshot,
  hasCalibrationEnvironmentChanged,
  parseScreenCalibrationEnvironmentSession,
  parseScreenCalibrationSession,
  resolveEffectiveScreenDpi,
  safeDetectScreenDpi,
  toPixelsPerMm,
  type ScreenCalibrationEnvironmentSnapshot,
  type ScreenCalibrationSessionState,
} from "../utils/screenCalibration";
import {
  canvasXToPagePlayheadOffsetSeconds,
  clampPagePlayheadOffsetSeconds,
  isPointerNearPagePlayhead,
  PAGE_PLAYHEAD_HIT_SLOP_PX,
  resolvePagePlayheadAbsoluteTime,
  viewportStartForPlayheadAbsoluteTime,
} from "../utils/pagePlayhead";
import type {
  AnnotationDictionary,
  BadChannelMark,
  DualMontagePreference,
  FilterOverride,
  ReviewTemplate,
  TrendProfile,
} from "./customization/customizationTypes";
export type { EEGViewerTileContextExpectation } from "./eegViewerPlaybackHelpers";
export {
  shouldKeepEEGViewerProtectedUltraRapidRunway,
  resolveEEGViewerProtectedUltraRapidRunwayWindowCount,
  isAbortLikeEegTileRequestError,
  shouldRetryEegTileRequest,
  isEegArtifactRepairConflict,
  shouldDropStaleEegDataQuery,
  shouldAutoTransitionStudyToReviewed,
  isEEGViewerTilePayloadStale,
  doesEEGViewerTilePlaybackContextMatch,
  resolveEEGViewerOutOfOrderBufferDiscard,
  shouldDiscardEEGViewerBackwardPendingBuffer,
  shouldDiscardEEGViewerFarFuturePendingBuffer,
  shouldHoldEEGViewerPlaybackClockAtRenderableEdge,
  resolveEEGViewerDataGapLeapDecision,
  shouldRecoverEEGViewerStalledPlaybackWindow,
  resolveEEGViewerMonotonicPlaybackDisplayTime,
  shouldDiscardEEGViewerPausedPlayHoldBuffer,
  shouldDiscardEEGViewerFuturePendingBufferOnPause,
  shouldResetEEGViewerPausedPlayQueryWindow,
  resolveEEGViewerMaxPlaybackTime,
  resolveEEGViewerAdvanceBoundaryWindowStart,
  shouldResetEEGViewerWindowStartOnSeek,
  shouldDeferEEGViewerPausedSeekUntilBufferLoad,
  shouldExitEEGViewerPausedPlayHoldOnManualSeek,
  resolveEEGViewerRequestedTime,
  resolveEEGViewerTileLogicalDuration,
  resolveEEGViewerTileSampleRate,
  resolveEEGViewerHighSpeedPlaySeedWindowStarts,
  resolveEEGViewerPausedHighSpeedPlaySeedWindowCount,
  shouldLoadEEGViewerPlaybackBootstrapBuffer,
  resolveEEGViewerPausedFrameTime,
  resolveEEGViewerLastPaintedPlaybackTime,
  resolveEEGViewerPlaybackRenderIntervalMs,
  resolveEEGViewerPlaybackBlitSafeCeilingMs,
  resolveEEGViewerPlaybackSchedulerIntervalMs,
  resolveEEGViewerAdaptivePlaybackRenderIntervalMs,
  shouldSuspendEEGViewerLiveTopographySampling,
  shouldSuspendEEGViewerAnnotationTimeline,
  shouldSuspendEEGViewerClinicalDockRendering,
  shouldFetchEEGViewerAnnotations,
  shouldComputeEEGViewerTrendInsights,
  resolveEEGViewerPlaybackPrefetchPlan,
  shouldScheduleEEGViewerPlaybackBackgroundPrefetch,
  shouldScheduleEEGViewerPlaybackWindowTransition,
  resolveEEGViewerEventNavigationState,
} from "./eegViewerPlaybackHelpers";
import {
  resolveEEGViewerProtectedUltraRapidRunwayWindowCount,
  isAbortLikeEegTileRequestError,
  shouldRetryEegTileRequest,
  isEegArtifactRepairConflict,
  shouldDropStaleEegDataQuery,
  shouldAutoTransitionStudyToReviewed,
  isEEGViewerTilePayloadStale,
  resolveEEGViewerOutOfOrderBufferDiscard,
  shouldDiscardEEGViewerBackwardPendingBuffer,
  shouldDiscardEEGViewerFarFuturePendingBuffer,
  shouldHoldEEGViewerPlaybackClockAtRenderableEdge,
  resolveEEGViewerDataGapLeapDecision,
  shouldRecoverEEGViewerStalledPlaybackWindow,
  resolveEEGViewerMonotonicPlaybackDisplayTime,
  shouldDiscardEEGViewerPausedPlayHoldBuffer,
  shouldDiscardEEGViewerFuturePendingBufferOnPause,
  shouldResetEEGViewerPausedPlayQueryWindow,
  resolveEEGViewerMaxPlaybackTime,
  resolveEEGViewerAdvanceBoundaryWindowStart,
  shouldResetEEGViewerWindowStartOnSeek,
  shouldDeferEEGViewerPausedSeekUntilBufferLoad,
  shouldExitEEGViewerPausedPlayHoldOnManualSeek,
  resolveEEGViewerRequestedTime,
  resolveEEGViewerHighSpeedPlaySeedWindowStarts,
  resolveEEGViewerPausedHighSpeedPlaySeedWindowCount,
  shouldLoadEEGViewerPlaybackBootstrapBuffer,
  resolveEEGViewerPausedFrameTime,
  resolveEEGViewerLastPaintedPlaybackTime,
  resolveEEGViewerAdaptivePlaybackRenderIntervalMs,
  shouldSuspendEEGViewerLiveTopographySampling,
  shouldSuspendEEGViewerAnnotationTimeline,
  shouldSuspendEEGViewerClinicalDockRendering,
  shouldFetchEEGViewerAnnotations,
  shouldComputeEEGViewerTrendInsights,
  resolveEEGViewerPlaybackPrefetchPlan,
  shouldScheduleEEGViewerPlaybackBackgroundPrefetch,
  shouldScheduleEEGViewerPlaybackWindowTransition,
  resolveEEGViewerEventNavigationState,
  shouldApplyVideoSlaveSeekToEeg,
  shouldApplyVideoSlaveSpeedToEeg,
} from "./eegViewerPlaybackHelpers";

export type {
  AnnotationTypeCatalogEntry,
  WaveformDisplayMode,
  RowHeightMode,
} from "./eegViewerDisplayHelpers";
export {
  resolveEEGViewerAnnotationDisplayColor,
  resolveEffectiveViewerMontage,
  isPassthroughViewerMontage,
  montagePairsMatchRecordingChannels,
  buildPassthroughMontagePairs,
  resolveRecordingChannelName,
  ROW_HEIGHT_CHANNEL_SPACING,
  clampNumber,
  getChannelSpacingForRowHeightMode,
  getRowHeightModeFromChannelSpacing,
  getVisibleChannelRefitKey,
  shouldTriggerRefitOnVisibleChannelChange,
  calculateAutoFitSensitivityForWindow,
  isEEGViewerTypingTarget,
  isEEGViewerShortcutFocusWithinViewer,
} from "./eegViewerDisplayHelpers";
import {
  type AnnotationTypeCatalogEntry,
  type WaveformDisplayMode,
  type RowHeightMode,
  resolveEEGViewerAnnotationDisplayColor,
  resolveEffectiveViewerMontage,
  isPassthroughViewerMontage,
  montagePairsMatchRecordingChannels,
  buildPassthroughMontagePairs,
  resolveRecordingChannelName,
  ROW_HEIGHT_CHANNEL_SPACING,
  clampNumber,
  getChannelSpacingForRowHeightMode,
  getRowHeightModeFromChannelSpacing,
  getVisibleChannelRefitKey,
  shouldTriggerRefitOnVisibleChannelChange,
  calculateAutoFitSensitivityForWindow,
  isEEGViewerShortcutFocusWithinViewer,
} from "./eegViewerDisplayHelpers";
import {
  LIFELINES_T4A_DEFAULT_PROFILE,
  buildAliasMaps,
  buildAliasMapsFromSnapshot,
  getChannelMapProfileById,
  type ChannelMapAliasMaps,
} from "../utils/channelMaps/lifelinesT4aDefault";

import {
  EEGWaveformAutoFitControl,
  EEGWaveformLayoutControls,
  EEGWaveformVisibilityControls,
} from "./EEGWaveformToolbarControls";

export {
  EEGWaveformLayoutControls,
  EEGWaveformVisibilityControls,
} from "./EEGWaveformToolbarControls";

import "./EEGViewer.css";
import "../styles/eeg-workspace-theme.css";
import "./customization/customizationPanels.css";
import "./EEGViewer.toolbar-overrides.css";

// Sprint 4A layer split: dock-panel analysis surfaces, canvas overlays, and
// the video-sync panel each live behind their own lazy chunk so none of them
// are pulled into the first-paint bundle for the EEG viewer.
const EEGVideoSyncPanel = lazy(() => import("./EEGVideoSyncPanel"));
const EEGClinicalOverlays = lazy(() => import("./EEGClinicalOverlays"));
const EEGAdvancedDockPanel = lazy(() =>
  import("./EEGAdvancedPanels").then((m) => ({ default: m.EEGAdvancedDockPanel })),
);
const EEGDiagnosticsPanel = lazy(() => import("./EEGDiagnosticsPanel"));

interface EEGViewerProps {
  studyId: string;
  patientId: string;
  recordingId?: string;
}

interface EEGConfiguration {
  config_id?: string; // optional — undefined signals "not yet persisted" (no DB row exists yet)
  config_name: string;
  montage_id: string;
  sensitivity_value: number;
  time_scale: number;
  high_pass_filter: number;
  low_pass_filter: number;
  notch_filter_60hz: boolean;
  notch_filter_50hz?: boolean; // EEG-CTRL-003: 50 Hz mains notch
  remove_dc_offset?: boolean; // EEG-CTRL-020: DC offset correction
  polarity?: "negative-up" | "positive-up"; // EEG-CTRL-001: waveform polarity
  channel_color_mode?: "uniform" | "alternating" | "by-type"; // EEG-CTRL-021
  reference_type: string;
  playback_speed: number;
  is_default: boolean;
}

interface EEGMontage {
  montage_id: string;
  montage_name: string;
  montage_type: string;
  description?: string;
  is_default?: boolean;
  is_shared?: boolean;
  updated_at?: string;
  lock_version?: string | number;
  version?: number;
  channel_pairs: Array<{
    positive: string;
    negative?: string;
    label?: string;
    color?: string;
  }>;
}

interface EEGPreset {
  preset_id: string;
  preset_name: string;
  preset_category: string;
  config_settings: Record<string, unknown>;
}

type ClinicianExportFormat = EegClinicalExportRequest["format"] | "pdf";
type WaveformPrintColorMode = "color" | "bw";
type WaveformPrintOrientation = "portrait" | "landscape";
type WaveformPrintPageSize = "A4" | "Letter" | "Legal";

interface Annotation {
  annotation_id?: string;
  study_id?: string;
  user_id?: string;
  recording_id?: string | null;
  annotation_type: string;
  annotation_type_id?: string | null;
  label: string;
  description?: string | null;
  notes?: string | null;
  start_time: number;
  end_time?: number;
  duration?: number | null;
  channel_scope?: "all" | "channel_set";
  channels_affected: string[];
  color?: string;
  confidence_level?: number | null;
  is_reviewed?: boolean;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  user?: {
    user_id?: string;
    user_name?: string;
    first_name?: string;
    last_name?: string;
  } | null;
  reviewer?: {
    user_id?: string;
    user_name?: string;
    first_name?: string;
    last_name?: string;
  } | null;
}

interface InterventionResponse {
  response_id: string;
  observed_at_seconds: number;
  response_window_seconds?: number | null;
  observed_effect?: string | null;
  severity_delta?: number | null;
  notes?: string | null;
}

interface InterventionEvent {
  intervention_id: string;
  study_id: string;
  recording_id?: string | null;
  event_time_seconds: number;
  event_type: string;
  medication_name?: string | null;
  medication_dose?: string | null;
  medication_route?: string | null;
  procedure_name?: string | null;
  reason?: string | null;
  expected_effect?: string | null;
  source?: string | null;
  linked_annotation_id?: string | null;
  linked_annotation?: {
    annotation_id: string;
    annotation_type: string;
    label: string;
    start_time: number;
    description?: string | null;
  } | null;
  responses?: InterventionResponse[];
}

interface AnnotationNoteComposerState {
  title: string;
  placeholder: string;
  defaultValue: string;
  requireText?: boolean;
  onSubmit: (note: string) => Promise<void> | void;
  onCancel?: () => void;
}

export type { ConsensusResolutionMethod } from "./eegViewerChromeShared";
import type { ConsensusResolutionMethod } from "./eegViewerChromeShared";

interface ConsensusReviewerAnnotation {
  reviewerId: string;
  reviewerName: string;
  reviewerRole: string;
  annotationType: string;
  label: string;
  confidence: number;
}

interface ConsensusDisagreement {
  annotationId: string;
  startTime: number;
  endTime?: number;
  reviewers: ConsensusReviewerAnnotation[];
  resolutionMethod?: ConsensusResolutionMethod;
  resolvedBy?: string;
  resolvedAt?: string;
  finalDecision?: string;
}

interface ConsensusAutoSummary {
  studyId: string;
  kappaType: "cohen" | "fleiss" | string;
  reviewerCount: number;
  kappa: number;
  interpretation: string;
  agreementPercentage: number;
  totalAnnotations: number;
  agreedAnnotations: number;
  disagreedAnnotations: number;
}

interface ConsensusReportSummary {
  studyId: string;
  totalAnnotations: number;
  consensusReached: number;
  consensusPercentage: number;
  disagreements: ConsensusDisagreement[];
  kappaScore: number;
  recommendations: string[];
}

interface ConsensusDisagreementSummary {
  studyId: string;
  totalDisagreements: number;
  disagreements: ConsensusDisagreement[];
  unresolvedCount: number;
}

interface ConsensusReviewerSearchResult {
  user_id: string;
  user_name?: string;
  full_name?: string;
  email?: string;
  role?: string;
}

interface ConsensusReviewerAssignmentSummary {
  assignment_id: string;
  study_id: string;
  user_id: string;
  user_name?: string;
  full_name?: string;
  email?: string;
  role?: string;
  is_blind: boolean;
  is_completed: boolean;
  assigned_at: string;
  completed_at?: string | null;
}

interface ConsensusReviewerAssignmentResponse {
  studyId: string;
  reviewerCount: number;
  blindReview: boolean;
  completedCount: number;
  assignments: ConsensusReviewerAssignmentSummary[];
}

type TemplateStructuredFieldType = "text" | "number" | "select" | "boolean";

interface TemplateStructuredFieldDefinition {
  key: string;
  label: string;
  type: TemplateStructuredFieldType;
  required: boolean;
  options: string[];
  placeholder?: string;
  default_value?: string;
}

interface ParsedTemplateDefinition {
  annotationType: string;
  annotationLabel: string;
  annotationColor: string;
  defaultDurationSeconds: number | null;
  requiredFields: string[];
  structuredFields: TemplateStructuredFieldDefinition[];
}

interface ReviewTemplateUsageRow {
  template_id: string | null;
  template_name: string;
  template_category: string | null;
  usage_count: number;
  unique_user_count: number;
  last_used_at: string;
}

interface ReviewTemplateUsageSummary {
  days: number;
  total_template_uses: number;
  templates: ReviewTemplateUsageRow[];
}

type RapidEventDraft = {
  eventType: RapidEventType;
  medicationName: string;
  medicationDose: string;
  medicationRoute: string;
  procedureName: string;
  reason: string;
  expectedEffect: string;
  followUpWindowSeconds: string;
  anchorTimeSeconds: number | null;
  selectedChannels: string[];
};

type AnnotationPanelTab =
  | "annotate"
  | "review"
  | "channels"
  | "trend_event";

const ANNOTATION_PANEL_TABS: AnnotationPanelTab[] = [
  "annotate",
  "review",
  "channels",
  "trend_event",
];

type TrendMarkerType =
  | "threshold_crossing"
  | "abrupt_power_shift"
  | "asymmetry_spike";

type TrendMarker = {
  id: string;
  timeSeconds: number;
  timestamp: string;
  type: TrendMarkerType;
  metric: "delta_power" | "total_power" | "alpha_power" | "asymmetry_pct";
  label: string;
  severity: "info" | "warning" | "critical";
  value: number;
  threshold?: number;
  deltaPercent?: number;
};

type SmartSuggestionSource = "trend_marker";

type SmartAnnotationSuggestion = {
  id: string;
  source: SmartSuggestionSource;
  annotationType: string;
  label: string;
  color: string;
  startTime: number;
  defaultDurationSeconds: number;
  confidence: number;
  explanation: string;
  channels: string[];
  markerType: TrendMarkerType;
  metric: TrendMarker["metric"];
};

type SmartAnnotationSuggestionDraft = {
  label: string;
  startTime: string;
  durationSeconds: string;
  notes: string;
};

type TrendPoint = {
  id: string;
  timestamp: string;
  timeSeconds: number;
  deltaPower: number | null;
  totalPower: number | null;
  alphaPower: number | null;
  asymmetryPct: number | null;
};

type EventTrendCorrelationSummary = {
  interventionId: string;
  interventionLabel: string;
  eventTimeSeconds: number;
  lookbackSeconds: number;
  lookaheadSeconds: number;
  preWindow: {
    points: number;
    avgDeltaPower: number | null;
    avgTotalPower: number | null;
    avgAsymmetryPct: number | null;
  };
  postWindow: {
    points: number;
    avgDeltaPower: number | null;
    avgTotalPower: number | null;
    avgAsymmetryPct: number | null;
  };
  delta: {
    deltaPower: number | null;
    totalPower: number | null;
    asymmetryPct: number | null;
  };
  markerCounts: {
    pre: number;
    post: number;
    total: number;
  };
};

type WaveformColorEntry = {
  color_id: string;
  channel_pair: string;
  color_hex: string;
  color_name?: string | null;
  is_system_default: boolean;
  display_order?: number | null;
  montage_id?: string | null;
};

const CLINICAL_GRID_MODE_STORAGE_KEY = "eeg-clinical-grid-mode";
const SHOW_GRID_STORAGE_KEY = "eeg-waveform-grid-visible";
const COMPARISON_VIEW_STORAGE_KEY = "eeg-comparison-view-open";
const PLAYBACK_CONTROLS_LOCK_ON_PLAYBACK_STORAGE_KEY =
  "eeg-playback-controls-lock-on-playback";

const TRACE_BAD_CHANNEL_COLOR = "#ef4444";
const TRACE_FILTER_OVERRIDE_COLOR = "#f59e0b";

const RAPID_EVENT_LABELS: Record<RapidEventType, string> = {
  seizure_onset: "Seizure Onset",
  seizure_offset: "Seizure Offset",
  medication_administered: "Medication",
  procedure_marker: "Procedure",
  clinical_note: "Clinical Note",
};

const RAPID_EVENT_COLORS: Record<RapidEventType, string> = {
  seizure_onset: "#ef4444",
  seizure_offset: "#22c55e",
  medication_administered: "#3b82f6",
  procedure_marker: "#8b5cf6",
  clinical_note: "#f59e0b",
};

// Annotation types must be DB-driven only. If the API returns empty or errors,
// the catalog stays empty and the UI shows a clear message that types aren't
// available rather than silently falling back to hardcoded types.
const FALLBACK_VIEWER_ANNOTATION_CATALOG: AnnotationTypeCatalogEntry[] = [];

export type { AnnotationCategoryFilter } from "./eegViewerChromeShared";
import type { AnnotationCategoryFilter } from "./eegViewerChromeShared";
type AnnotationCategoryBucket = Exclude<AnnotationCategoryFilter, "all">;

const DEFAULT_ANNOTATION_CATEGORY_FILTER: AnnotationCategoryFilter = "all";

const TECHNICALLY_IMPORTED_ANNOTATION_COLOR = "#64748b";
const FOCUSED_CHANNEL_SPACING_GAP_UNITS = 0.7;

function isAnnotationCategoryFilter(
  value: unknown,
): value is AnnotationCategoryFilter {
  return (
    value === "all" || value === "clinical" || value === "technical_imported"
  );
}

function normalizeAnnotationTypeCode(value?: string | null): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function normalizeAnnotationCatalogLink(value?: string | null): string {
  return normalizeAnnotationTypeCode(value).replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function isTechnicalImportedAnnotationTypeCode(value?: string | null): boolean {
  const normalized = normalizeAnnotationTypeCode(value);
  return (
    normalized === "edf_import" ||
    normalized.startsWith("edf_") ||
    normalized.startsWith("technical_") ||
    normalized.includes("import")
  );
}

function toRapidEventType(value: string): RapidEventType | null {
  if (
    value === "seizure_onset" ||
    value === "seizure_offset" ||
    value === "medication_administered" ||
    value === "procedure_marker" ||
    value === "clinical_note"
  ) {
    return value;
  }
  return null;
}

function buildInterventionLabel(event: InterventionEvent): string {
  const rapidEventType = toRapidEventType(event.event_type);
  const baseLabel = rapidEventType
    ? RAPID_EVENT_LABELS[rapidEventType]
    : event.event_type;
  if (event.event_type === "medication_administered" && event.medication_name) {
    return `${baseLabel}: ${event.medication_name}`;
  }
  if (event.event_type === "procedure_marker" && event.procedure_name) {
    return `${baseLabel}: ${event.procedure_name}`;
  }
  if (event.reason) {
    return `${baseLabel}: ${event.reason}`;
  }
  return baseLabel;
}

function withAlphaHex(baseColor: string, alphaHex: string): string {
  if (!baseColor.startsWith("#") || baseColor.length !== 7) {
    return baseColor;
  }
  return `${baseColor}${alphaHex}`;
}

function parseTileRangeHeader(
  rangeHeader: string | null | undefined,
  fallbackStartSec: number,
  fallbackEndSec: number,
): { startSec: number; endSec: number } {
  const match = String(rangeHeader || "").match(
    /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/,
  );
  if (!match) {
    return {
      startSec: fallbackStartSec,
      endSec: fallbackEndSec,
    };
  }
  return {
    startSec: Number(match[1]) / 1000,
    endSec: Number(match[2]) / 1000,
  };
}

function toUserFacingApiError(error: unknown): {
  status: number | null;
  message: string;
} {
  const axiosError = error as AxiosError<{ error?: string; message?: string }>;
  const status = axiosError?.response?.status ?? null;
  const message =
    axiosError?.response?.data?.error ||
    axiosError?.response?.data?.message ||
    axiosError?.message ||
    "Request failed.";
  return { status, message };
}

function parseTemplateFieldList(
  value: unknown,
  fallback: string[] = [],
): string[] {
  if (!Array.isArray(value)) return fallback;
  return value
    .filter((entry): entry is string => typeof entry === "string")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function parseTemplateStructuredFields(
  value: unknown,
): TemplateStructuredFieldDefinition[] {
  if (!Array.isArray(value)) return [];
  const fields: TemplateStructuredFieldDefinition[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object") continue;
    const raw = entry as Record<string, unknown>;
    if (typeof raw.key !== "string" || !raw.key.trim()) continue;

    const key = raw.key.trim();
    const label =
      typeof raw.label === "string" && raw.label.trim()
        ? raw.label.trim()
        : key;
    const type =
      raw.type === "number" || raw.type === "select" || raw.type === "boolean"
        ? raw.type
        : "text";
    const options = parseTemplateFieldList(raw.options);

    const parsed: TemplateStructuredFieldDefinition = {
      key,
      label,
      type,
      required: Boolean(raw.required),
      options,
    };
    if (typeof raw.placeholder === "string" && raw.placeholder.trim()) {
      parsed.placeholder = raw.placeholder.trim();
    }
    if (raw.default_value !== undefined && raw.default_value !== null) {
      parsed.default_value = String(raw.default_value);
    }
    fields.push(parsed);
  }
  return fields;
}

function parseReviewTemplateDefinition(
  template: ReviewTemplate,
): ParsedTemplateDefinition {
  const config =
    template.config_json && typeof template.config_json === "object"
      ? (template.config_json as Record<string, unknown>)
      : {};

  const annotationType =
    typeof config.annotation_type === "string" && config.annotation_type.trim()
      ? config.annotation_type.trim()
      : "template";
  const annotationLabel =
    typeof config.annotation_label === "string" &&
    config.annotation_label.trim()
      ? config.annotation_label.trim()
      : template.name;
  const annotationColor =
    normalizeHexColor(config.annotation_color) ||
    normalizeHexColor(config.color_hex) ||
    "#5b8def";
  const defaultDurationSeconds =
    typeof config.duration_seconds === "number" &&
    Number.isFinite(config.duration_seconds) &&
    config.duration_seconds > 0
      ? config.duration_seconds
      : null;

  const requiredFieldsByType =
    config.required_fields_by_type &&
    typeof config.required_fields_by_type === "object"
      ? (config.required_fields_by_type as Record<string, unknown>)
      : null;
  const structuredFieldsByType =
    config.structured_fields_by_type &&
    typeof config.structured_fields_by_type === "object"
      ? (config.structured_fields_by_type as Record<string, unknown>)
      : null;

  const requiredFields = parseTemplateFieldList(
    requiredFieldsByType?.[annotationType] ?? config.required_fields,
  );
  const structuredFields = parseTemplateStructuredFields(
    structuredFieldsByType?.[annotationType] ?? config.structured_fields,
  );

  return {
    annotationType,
    annotationLabel,
    annotationColor,
    defaultDurationSeconds,
    requiredFields,
    structuredFields,
  };
}

function deriveTrendSuggestionConfidence(marker: TrendMarker): number {
  const baseBySeverity =
    marker.severity === "critical"
      ? 0.9
      : marker.severity === "warning"
        ? 0.75
        : 0.6;
  if (
    typeof marker.deltaPercent === "number" &&
    Number.isFinite(marker.deltaPercent)
  ) {
    const bump = Math.min(0.08, marker.deltaPercent / 1000);
    return Math.max(0.05, Math.min(0.99, baseBySeverity + bump));
  }
  return Math.max(0.05, Math.min(0.99, baseBySeverity));
}

function buildTrendSuggestionExplanation(marker: TrendMarker): string {
  const thresholdText =
    typeof marker.threshold === "number" && Number.isFinite(marker.threshold)
      ? `Threshold ${marker.threshold.toFixed(2)}`
      : "Threshold not configured";
  const deltaText =
    typeof marker.deltaPercent === "number" &&
    Number.isFinite(marker.deltaPercent)
      ? `, delta ${marker.deltaPercent.toFixed(1)}%`
      : "";
  return `${marker.label}. ${thresholdText}, observed ${marker.value.toFixed(2)}${deltaText}.`;
}

type DecodedArrowTile = {
  channels: string[];
  /**
   * Column-major channel data: columnArrays[channelIdx] is a Float32Array of
   * all samples for that channel. Received as zero-copy Transferables from the
   * decode worker — no structured-clone or transpose overhead.
   */
  columnArrays: Float32Array[];
  preparedChannels?: EEGChannel[];
  /** Total number of samples per channel (= columnArrays[0]?.length). */
  numSamples: number;
  sampling_rate: number;
  duration: number;
  observedMedianPeakUv?: number;
  /** True per-channel min values per bin — from pyramid parquet _min columns (level > 0 only) */
  envelopeMin?: Float32Array[];
  /** True per-channel max values per bin — from pyramid parquet _max columns (level > 0 only) */
  envelopeMax?: Float32Array[];
  /**
   * Actual data extent in seconds, derived from Arrow time-column span
   * (times[last] - times[0]).  Only set when tile normalization fires — i.e.
   * when decodedDuration differs significantly from the requested window size
   * (e.g. L1 tiles returning 300 s of real data inside a 600 s window).
   * Use this instead of `duration` for buffer-end tracking so the render
   * clamp never advances past the real data boundary → no blank canvas.
   */
  actualDataDuration?: number;
  requestContext?: {
    recordingId: string;
    transportMode: EEGTileTransportMode;
    speed: number;
    timeScale: number;
    windowStart: number;
    windowEnd: number;
    actualWindowStart?: number;
    actualWindowEnd?: number;
    requestedLevel: number;
    actualLevel: number;
  };
  transferMetrics?: {
    size: number;
    time: number;
    level: number;
    format: string;
  };
  decodeTelemetry?: {
    startedAtMs: number;
    readyAtMs: number;
    waitMs: number;
    source: SharedDecodedTileSource;
    requestedLevel: number;
    actualLevel: number;
    startTimeSeconds: number;
    endTimeSeconds: number;
  };
};

// Default configuration constant - defined outside component to prevent recreation on every render
// config_id is intentionally absent — undefined signals "not yet persisted" (no magic sentinel string)
const DEFAULT_EEG_CONFIG: EEGConfiguration = {
  config_name: "Default Configuration",
  montage_id: "",
  sensitivity_value: 7,
  time_scale: 10,
  high_pass_filter: 0.5,
  low_pass_filter: 70,
  notch_filter_60hz: false,
  notch_filter_50hz: false,
  remove_dc_offset: true,
  polarity: "negative-up",
  channel_color_mode: "by-type",
  reference_type: "average",
  playback_speed: 1,
  is_default: true,
};

// Empty array constant to prevent recreation
const EMPTY_ANNOTATIONS: Annotation[] = [];
const EMPTY_CONSENSUS_DISAGREEMENTS: ConsensusDisagreement[] = [];
export { EMPTY_CONSENSUS_RECOMMENDATIONS } from "./eegViewerChromeShared";
import { EMPTY_CONSENSUS_RECOMMENDATIONS } from "./eegViewerChromeShared";
const EMPTY_CHANNEL_NAMES: string[] = [];
const EMPTY_TREND_MARKERS: TrendMarker[] = [];
const EMPTY_SMART_ANNOTATION_SUGGESTIONS: SmartAnnotationSuggestion[] = [];
const VIEWER_SENSITIVITY_STEPS = [2, 3, 5, 7, 10, 15, 20, 30, 50, 70, 100];
const VIEWER_TIME_SCALE_STEPS = [5, 10, 15, 20, 30];
const PLAYBACK_SPEED_SLIDER_STEPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8];

/**
 * Returns the recommended sensitivity (μV/mm) for rapid/ultra-rapid review speeds.
 * Higher μV/mm = less gain = only large-amplitude events visible = better for fast scanning.
 *   ≥ 100× → 20 μV/mm (ultra-rapid: seizures and large artefacts only)
 *    ≥ 30× → 15 μV/mm (rapid scan: moderate compression)
 *     else → null (no auto-adjustment; standard review speed)
 */
/**
 * Auto-sensitivity for rapid-review speeds.
 * When the observed p95 peak amplitude is known (from loaded EEG data), the
 * returned value is calibrated so the tallest channel fills roughly 60% of
 * its lane height.  Snaps to the nearest VIEWER_SENSITIVITY_STEPS value.
 * Falls back to conservative hard-coded defaults when no amplitude data has
 * been collected yet.
 */
/**
 * Returns the auto time-scale (sec/page) for a given playback speed,
 * or null if the speed is below the rapid-review threshold (16×).
 * Rapid review now stays within a 15-20 s/page overview profile so 150×
 * playback remains readable without widening the active viewport to 30 s/page.
 *   16–29×  → 15 s/page
 *   ≥30×    → 20 s/page
 */
function getAutoTimeScaleForSpeed(speed: number): number | null {
  if (speed < 16) return null;
  if (speed >= 30) return 20;
  return 15;
}

function getAutoSensitivityForSpeed(
  speed: number,
  observedPeakUv?: number | null,
  prerapidSensitivity?: number | null,
): number | null {
  if (speed < 16) return null;

  const steps = [2, 3, 5, 7, 10, 15, 20, 30, 50, 70, 100];
  const snapUp = (ideal: number) =>
    steps.find((s) => s >= ideal) ?? steps[steps.length - 1];

  // The observedPeakUv path is intentionally disabled: pyramid mean-downsampling
  // at levels 2–4 attenuates the median signal amplitude by up to 60%, making
  // amplitude-based calibration unreliable across resolution levels. Instead we
  // preserve the pre-rapid sensitivity so the clinician sees identical waveform
  // heights in rapid-review as in standard review — only the time scale widens.

  // Priority 2: pre-rapid sensitivity — preserve the user's calibrated value
  // so waveform amplitude appears identical to standard review. Only cap at
  // 30 μV/mm to avoid inheriting an already-coarsened rapid-mode setting.
  if (
    prerapidSensitivity &&
    prerapidSensitivity > 0 &&
    prerapidSensitivity <= 30
  ) {
    return prerapidSensitivity;
  }

  // Hard-coded last-resort: 7 μV/mm is the default clinical EEG sensitivity.
  return 7;
}

const CHANNEL_SCROLL_HEIGHT_PX = 28;
const CALIBRATION_TOLERANCE_PERCENT = 5;

type EventIndexItem = {
  annotation_id: string;
  start_time: number;
  annotation_type?: string | null;
  color?: string | null;
};

export type { ChannelRegionKey } from "./eegViewerChromeShared";
export { CHANNEL_REGION_OPTIONS } from "./eegViewerChromeShared";

function getFinePanStepSeconds(timeScaleSeconds: number): number {
  return Math.max(1, Math.round((timeScaleSeconds || 10) / 10));
}

function formatViewerClockTime(totalSeconds: number): string {
  const clamped = Math.max(0, Number.isFinite(totalSeconds) ? totalSeconds : 0);
  const hours = Math.floor(clamped / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const seconds = Math.floor(clamped % 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
    seconds,
  ).padStart(2, "0")}`;
}

function formatCompactAnnotationDuration(startSeconds: number, endSeconds: number): string {
  const duration = Math.max(0, endSeconds - startSeconds);
  if (!Number.isFinite(duration) || duration <= 0) {
    return "0s";
  }
  if (duration < 1) {
    return `${Math.round(duration * 1000)}ms`;
  }
  if (duration < 60) {
    return `${duration.toFixed(duration < 10 ? 1 : 0)}s`;
  }
  const minutes = Math.floor(duration / 60);
  const seconds = Math.round(duration % 60);
  return `${minutes}m ${seconds}s`;
}

function readPlaybackControlsLockOnPlaybackPreference(): boolean {
  try {
    const stored = localStorage.getItem(
      PLAYBACK_CONTROLS_LOCK_ON_PLAYBACK_STORAGE_KEY,
    );
    if (stored === null) return true;
    return stored === "true";
  } catch {
    return true;
  }
}

function resolveEEGViewerToolbarControlsVisible(input: {
  toolbarAutoHide: boolean;
  workspaceControlsPinned: boolean;
  workspaceControlsPeekOpen: boolean;
  hasBlockingModalOpen: boolean;
}): boolean {
  return (
    !input.toolbarAutoHide ||
    input.workspaceControlsPinned ||
    input.workspaceControlsPeekOpen ||
    input.hasBlockingModalOpen
  );
}

function parseViewerClockTime(input: string): number | null {
  const raw = input.trim();
  if (!raw) return null;

  if (/^\d+(\.\d+)?$/.test(raw)) {
    const seconds = Number(raw);
    return Number.isFinite(seconds) ? Math.max(0, seconds) : null;
  }

  const parts = raw.split(":").map((part) => part.trim());
  if (parts.some((part) => part.length === 0 || !/^\d+(\.\d+)?$/.test(part))) {
    return null;
  }

  if (parts.length === 2) {
    const minutes = Number(parts[0]);
    const seconds = Number(parts[1]);
    if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
    return Math.max(0, minutes * 60 + seconds);
  }

  if (parts.length === 3) {
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    const seconds = Number(parts[2]);
    if (
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes) ||
      !Number.isFinite(seconds)
    ) {
      return null;
    }
    return Math.max(0, hours * 3600 + minutes * 60 + seconds);
  }

  return null;
}

function getNearestPlaybackSpeedStep(value: number): number {
  if (!Number.isFinite(value)) return PLAYBACK_SPEED_SLIDER_STEPS[0];
  return PLAYBACK_SPEED_SLIDER_STEPS.reduce((closest, step) =>
    Math.abs(step - value) < Math.abs(closest - value) ? step : closest,
  );
}

function normalizeSensitivityValue(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_EEG_CONFIG.sensitivity_value;
  if (value <= 1) return 2;
  if (VIEWER_SENSITIVITY_STEPS.includes(value)) return value;
  return VIEWER_SENSITIVITY_STEPS.reduce((closest, candidate) =>
    Math.abs(candidate - value) < Math.abs(closest - value)
      ? candidate
      : closest,
  );
}

function normalizeViewerConfig(config: EEGConfiguration): EEGConfiguration {
  const polarity =
    config.polarity === "positive-up" ? "positive-up" : "negative-up";
  const channelColorMode =
    config.channel_color_mode === "alternating" ||
    config.channel_color_mode === "by-type"
      ? config.channel_color_mode
      : "uniform";
  const notchFlags = normalizeNotchFlags(
    config.notch_filter_60hz,
    config.notch_filter_50hz,
  );

  return {
    ...config,
    sensitivity_value: normalizeSensitivityValue(config.sensitivity_value),
    ...notchFlags,
    remove_dc_offset: config.remove_dc_offset ?? false,
    polarity,
    channel_color_mode: channelColorMode,
  };
}

// ─── Module-level tile-worker singleton ──────────────────────────────────────
// The Worker is created once for the browser tab and shared across all
// EEGViewer mounts.  This prevents React StrictMode's unmount-remount cycle
// from terminating an in-flight decode (which would surface as
// "EEG tile worker terminated" → queryFn throws → viewer stuck on loading).
// The pending-request map lives here too so responses are routed correctly
// even if the component remounts between postMessage and onmessage.
type TileWorkerPending = {
  resolve: (value: DecodedArrowTile) => void;
  reject: (reason?: unknown) => void;
};

type SharedDecodedTileSource =
  | "decoded-cache"
  | "decoded-inflight"
  | "decoded-worker";

let _sharedTileWorker: Worker | null = null;
let _sharedTileWorkerReqId = 0;
const _sharedTileWorkerPending = new Map<string, TileWorkerPending>();
const _sharedDecodedTileCache = createDecodedTileLruCache<DecodedArrowTile>(4);
const _sharedDecodedTileInFlight = new Map<string, Promise<DecodedArrowTile>>();

function normalizeDecodedTileSpeedBucket(speed: number): number {
  const normalized = Math.trunc(Math.abs(Number(speed)));
  if (!Number.isFinite(normalized) || normalized < 1) {
    return 1;
  }
  return normalized;
}

function getDecodedTileCacheKey(
  recordingId: string,
  startMs: number,
  endMs: number,
  level: number,
  speed: number,
  transportMode: EEGTileTransportMode,
  identity?: TileCacheIdentity | null,
): string {
  return buildTileCacheKey({
    recordingId,
    startMs,
    endMs,
    level,
    speedBucket: normalizeDecodedTileSpeedBucket(speed),
    transportMode: transportMode === "play" ? "play" : "inspect",
    identity,
  });
}

function cacheDecodedTile(key: string, tile: DecodedArrowTile): void {
  _sharedDecodedTileCache.set(key, tile);
}

function getCachedDecodedTile(key: string): DecodedArrowTile | null {
  return _sharedDecodedTileCache.get(key);
}

function clearDecodedTilesForRecording(recordingId: string): void {
  _sharedDecodedTileCache.clearRecording(recordingId);
  const prefix = `${recordingId}_`;
  for (const key of Array.from(_sharedDecodedTileInFlight.keys())) {
    if (key.startsWith(prefix)) {
      _sharedDecodedTileInFlight.delete(key);
    }
  }
}

function getSharedTileWorker(): Worker {
  if (_sharedTileWorker) return _sharedTileWorker;
  const worker = new Worker(
    new URL("../workers/eegTileWorker.ts", import.meta.url),
    { type: "module" },
  );
  worker.onmessage = (event: MessageEvent<any>) => {
    const msg = event.data;
    if (!msg?.id) return;
    const pending = _sharedTileWorkerPending.get(msg.id);
    if (!pending) return;
    _sharedTileWorkerPending.delete(msg.id);
    if (msg.ok) {
      pending.resolve({
        channels: msg.data.channels || [],
        columnArrays: msg.data.columnArrays || [],
        preparedChannels: msg.data.preparedChannels || [],
        numSamples: Number(msg.data.numSamples || 0),
        sampling_rate: Number(msg.data.samplingRate || 0),
        duration: Number(msg.data.duration || 0),
        observedMedianPeakUv:
          typeof msg.data.observedMedianPeakUv === "number"
            ? Number(msg.data.observedMedianPeakUv)
            : undefined,
        envelopeMin: msg.data.envelopeMin,
        envelopeMax: msg.data.envelopeMax,
      });
    } else {
      pending.reject(new Error(msg.error || "Worker decode failed"));
    }
  };
  worker.onerror = (event) => {
    const err = event?.message || "EEG tile worker error";
    _sharedTileWorkerPending.forEach((p) => p.reject(new Error(err)));
    _sharedTileWorkerPending.clear();
    // Allow recreation on next call
    _sharedTileWorker = null;
  };
  _sharedTileWorker = worker;
  return worker;
}

function decodeArrowTileShared(buffer: ArrayBuffer): Promise<DecodedArrowTile> {
  if (typeof Worker === "undefined") {
    return Promise.reject(new Error("EEG tile worker unavailable"));
  }
  const worker = getSharedTileWorker();
  const reqId = `tile-${Date.now()}-${_sharedTileWorkerReqId++}`;
  return new Promise<DecodedArrowTile>((resolve, reject) => {
    _sharedTileWorkerPending.set(reqId, { resolve, reject });
    worker.postMessage(
      { id: reqId, type: "decode-arrow", payload: { buffer } },
      [buffer],
    );
  });
}

async function decodeArrowTileSharedCached({
  recordingId,
  startMs,
  endMs,
  level,
  speed,
  transportMode,
  buffer,
  identity,
}: {
  recordingId: string;
  startMs: number;
  endMs: number;
  level: number;
  speed: number;
  transportMode: EEGTileTransportMode;
  buffer: ArrayBuffer;
  identity?: TileCacheIdentity | null;
}): Promise<{
  tile: DecodedArrowTile;
  source: SharedDecodedTileSource;
}> {
  const cacheKey = getDecodedTileCacheKey(
    recordingId,
    startMs,
    endMs,
    level,
    speed,
    transportMode,
    identity,
  );
  const cached = getCachedDecodedTile(cacheKey);
  if (cached) {
    return { tile: cached, source: "decoded-cache" };
  }

  const inFlight = _sharedDecodedTileInFlight.get(cacheKey);
  if (inFlight) {
    return { tile: await inFlight, source: "decoded-inflight" };
  }

  const decodePromise = decodeArrowTileShared(buffer)
    .then((tile) => {
      cacheDecodedTile(cacheKey, tile);
      return tile;
    })
    .finally(() => {
      _sharedDecodedTileInFlight.delete(cacheKey);
    });
  _sharedDecodedTileInFlight.set(cacheKey, decodePromise);

  return { tile: await decodePromise, source: "decoded-worker" };
}

type PlaybackPerfRecordValue = string | number | boolean | null;
type PlaybackPerfRecordPayload = Record<string, PlaybackPerfRecordValue>;
type PostCallbackActivitySample = {
  source: "uiPublishCommit" | "sideQuerySync" | "longTask";
  startedAtMs: number;
  completedAtMs: number;
  detailLabel: string | null;
  detailValue: number | null;
};

const POST_CALLBACK_ACTIVITY_LIMIT = 64;
const HIGH_SPEED_SIDE_QUERY_SYNC_INTERVAL_MS = 2500;
const ULTRA_HIGH_SPEED_SIDE_QUERY_SYNC_INTERVAL_MS = 5000;
const MAX_HIGH_SPEED_SIDE_QUERY_SYNC_INTERVAL_MS = 8000;

function emitPlaybackPerfEvent(
  kind: string,
  payload: PlaybackPerfRecordPayload,
): void {
  if (typeof window === "undefined") return;
  const recorder = (window as any).__eegPlaybackPerfRecord;
  if (typeof recorder === "function") {
    recorder(kind, payload);
  }
}
// ─────────────────────────────────────────────────────────────────────────────

export const EEGViewer: React.FC<EEGViewerProps> = ({
  studyId,
  patientId,
  recordingId,
}) => {
  const dialog = useAppDialog();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setStudyData, clearStudyData, studyData } = useStudy();
  const { preferences } = useUserPreferences();
  const { settings } = useSettingsSafe();
  const { user } = useAuth();
  const { t } = useTranslation("features");
  useEffect(() => {
    markEegStartup("viewerComponentMounted");
    configureEegStartupSchedulerGate({
      isPlayable: () =>
        getEegStartupTelemetry().milestones.playbackControlsInteractive != null,
      subscribePlayable: (listener) =>
        subscribeEegStartupMilestone((milestone) => {
          if (milestone === "playbackControlsInteractive") {
            listener();
          }
        }),
    });
  }, []);
  const [viewerDeferredChromeReady, setViewerDeferredChromeReady] =
    useState(false);
  const startupSecondaryQueriesEnabled = viewerDeferredChromeReady;
  const toolbarUserId = user?.id || "anonymous";
  const annotationAudienceSessionStorageKey = `eeg-annotation-audience-filter:${toolbarUserId}`;
  const { checkFeaturesAvailable } = useFeatureFlags();
  const isLocalhostRuntime =
    typeof window !== "undefined" &&
    /(^|\.)localhost$/i.test(window.location.hostname || "");
  const allowTimescaleOnLocalhost =
    import.meta.env.VITE_ENABLE_TIMESCALE_ON_LOCALHOST === "true";
  const timescaleRuntimeEnabled =
    !isLocalhostRuntime || allowTimescaleOnLocalhost;
  const [timescaleFeatureUnavailable, setTimescaleFeatureUnavailable] =
    useState(false);
  const { data: timescaleTrendAccess } = useQuery({
    queryKey: ["feature-availability", "ICU_EMU_MONITORING"],
    queryFn: async ({ signal }) => {
      const result = await checkFeaturesAvailable(["ICU_EMU_MONITORING"]);
      return result["ICU_EMU_MONITORING"] ?? { available: false };
    },
    enabled: startupSecondaryQueriesEnabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const canQueryTimescaleTrends =
    Boolean(timescaleTrendAccess?.available) &&
    timescaleRuntimeEnabled &&
    !timescaleFeatureUnavailable;

  const {
    state: perspectiveState,
    applyPerspective,
    applyDraftConfig,
    discardDraft,
    toggleMontageLock,
    toggleFiltersLock,
    setClinicalMode,
  } = usePerspectiveState();
  useEffect(() => {
    setTimescaleFeatureUnavailable(false);
  }, [studyId]);
  const { montages, refreshMontages } = useMontages();
  const { perspectives } = usePerspectives();
  const [rendererPreference, setRendererPreference] = useState<
    "gpu-preferred" | "cpu-only"
  >("gpu-preferred");
  const rendererMode = useRendererMode(rendererPreference);
  const viewerRootRef = useRef<HTMLDivElement>(null);
  const annotationPanelRef = useRef<HTMLDivElement>(null);
  const annotationPanelTabRefs = useRef<
    Record<AnnotationPanelTab, HTMLButtonElement | null>
  >({
    annotate: null,
    review: null,
    channels: null,
    trend_event: null,
  });
  // Stores the user's sensitivity setting before auto-scale is applied at high speeds
  // so it can be restored when the user lowers speed back below the threshold.
  const prerapidSensitivityRef = useRef<number | null>(null);
  // Stores the user's time_scale before rapid-mode auto-changes it.
  const prerapidTimeScaleRef = useRef<number | null>(null);
  // Rolling p95 peak amplitude (μV) observed from loaded channel data.
  // Updated each time a buffer is loaded; used to calibrate auto-sensitivity.
  const observedPeakUvRef = useRef<number | null>(null);
  // Synchronous sensitivity mirror — updated at the same time as the renderer
  // so the toolbar <select> never shows a stale value between React commits.
  const pendingSensitivityRef = useRef<number | null>(null);
  const channelToolsSectionRef = useRef<HTMLDivElement | null>(null);
  const configCalibrationSectionRef = useRef<HTMLDivElement | null>(null);
  const annotationItemRefsMap = useRef<
    Map<string | number, HTMLDivElement | null>
  >(new Map());
  const lastScrolledAnnotationIdRef = useRef<string | number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const markIfPainted = () => {
      const { width, height } = canvas.getBoundingClientRect();
      if (width > 0 && height > 0) {
        markEegStartup("firstCanvasPaint");
        markEegStartup("firstCanvasRender");
        // Explicit Lighthouse sprint mark for first nonblank canvas geometry.
        if (
          typeof performance !== "undefined" &&
          typeof performance.mark === "function"
        ) {
          try {
            performance.mark("firstCanvasRender");
            performance.mark("eeg-startup:firstCanvasRender");
          } catch {
            /* ignore duplicate mark races */
          }
        }
        return true;
      }
      return false;
    };

    if (markIfPainted()) return undefined;

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      if (markIfPainted()) {
        observer.disconnect();
      }
    });
    observer.observe(canvas);
    return () => {
      observer.disconnect();
    };
  }, [studyId, recordingId]);
  const navigationTimelineRef = useRef<HTMLDivElement>(null);
  /** Tracks canvas pixel height from ResizeObserver — avoids clientHeight reads during render. */
  const canvasHeightRef = useRef<number>(0);
  const rendererRef = useRef<EEGWaveformRenderer | null>(null);
  // Ref-mirrors for values whose canonical declarations sit much later in
  // this component (behind the huge `eegData` useQuery / `usePlaybackClock`
  // blocks) but that useEEGTraceChannelSelection's estimateChannelAutoScaleGain
  // needs read access to. Declaring the refs here — before that hook is
  // called — avoids a forward-reference ordering problem while keeping the
  // hook itself free of any import from EEGViewer. Both are kept in sync
  // (see the assignment right after the `eegData` query, and the existing
  // currentTimeRef sync effect near usePlaybackClock).
  const eegDataRef = useRef<EEGTraceGainEstimateSource | null>(null);
  const currentTimeRef = useRef<number>(0);
  const [eegSelection, setEegSelection] = useState<EEGSelection | null>(null);
  const [segmentInspectorCollapsed, setSegmentInspectorCollapsed] =
    useState(true);
  const sharedSegmentRestoreAttemptedRef = useRef(false);
  const [eegSelectionDraft, setEegSelectionDraft] =
    useState<EEGSelectionDraft | null>(null);
  const eegSelectionDragRef = useRef<{
    active: boolean;
    pointerId: number | null;
    startClientX: number;
    startClientY: number;
    hasDragged: boolean;
    channelLabels: string[];
    rectangular: boolean;
  }>({
    active: false,
    pointerId: null,
    startClientX: 0,
    startClientY: 0,
    hasDragged: false,
    channelLabels: [],
    rectangular: false,
  });
  const lastPrefetchAtRef = useRef<number>(0);
  const lastPrefetchKeyRef = useRef<string>("");
  /**
   * Recording IDs for which we have already bulk-cleared stale IDB tiles this
   * session.  The first suspicious tile triggers a full clearRecording(); the
   * guard prevents repeating the purge on every subsequent treadmill step.
   */
  const purgedRecordingsRef = useRef<Set<string>>(new Set());

  /**
   * Sync the renderer's canvas reference with the current DOM element.
   * React may replace the <canvas> element during reconciliation (state changes
   * cause the parent subtree to re-mount), leaving the renderer drawing on a
   * detached node.  This must be called before every renderer operation.
   */
  const syncRendererCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas && rendererRef.current) {
      rendererRef.current.setCanvas(canvas);
    }
  }, []);

  const [currentConfig, setCurrentConfig] = useState<EEGConfiguration | null>(
    () =>
      normalizeViewerConfig({
        ...DEFAULT_EEG_CONFIG,
        sensitivity_value: preferences.eegDefaults.sensitivity,
        time_scale: preferences.eegDefaults.timeScale,
        high_pass_filter: preferences.eegDefaults.filters.highPass,
        low_pass_filter: preferences.eegDefaults.filters.lowPass,
        notch_filter_60hz: preferences.eegDefaults.filters.notch,
      }),
  );
  const [loadedRecordingChannelNames, setLoadedRecordingChannelNames] =
    useState<string[]>([]);
  const [
    manualNotchOverrideByRecordingId,
    setManualNotchOverrideByRecordingId,
  ] = useState<Record<string, boolean>>({});
  const [
    autoNotchDefaultModeByRecordingId,
    setAutoNotchDefaultModeByRecordingId,
  ] = useState<Record<string, NotchMode>>({});
  const [dataWindowStart, setDataWindowStart] = useState(0); // For buffering data
  const [isPlaying, setIsPlaying] = useState(false);
  const [rapidReviewPlaybackActive, setRapidReviewPlaybackActive] =
    useState(false);
  const isPlaybackRunning = isPlaying || rapidReviewPlaybackActive;
  const [showExtendedPlaybackSpeeds, setShowExtendedPlaybackSpeeds] =
    useState(false);
  const [collapsePlaybackSpeedOptions, setCollapsePlaybackSpeedOptions] =
    useState(false);
  const buildDisplayProfileConfigRef = useRef<
    () => DisplayProfileConfig | null
  >(() => null);

  const {
    selectedDisplayProfileId,
    setSelectedDisplayProfileId,
    newDisplayProfileName,
    setNewDisplayProfileName,
    displayProfileStatus,
    setDisplayProfileStatus,
    displayProfileError,
    setDisplayProfileError,
    displayProfileConflict,
    setDisplayProfileConflict,
    isFetchingDisplayProfiles,
    refetchDisplayProfiles,
    displayProfilesResponse,
    displayProfilePolicy,
    displayProfiles,
    selectedDisplayProfile,
    playbackSpeedOptions,
    playbackSpeedDefault,
    playbackSpeedButtonOptions,
    primaryPlaybackSpeedOptions,
    overflowPlaybackSpeedOptions,
    createDisplayProfileMutation,
    updateDisplayProfileMutation,
    setDefaultDisplayProfileMutation,
    deleteDisplayProfileMutation,
  } = useEEGDisplayProfile({
    startupSecondaryQueriesEnabled,
    queryClient,
    currentPlaybackSpeed: currentConfig?.playback_speed ?? 1,
    collapsePlaybackSpeedOptions,
    buildDisplayProfileConfigRef,
    setCurrentConfig: setCurrentConfig as React.Dispatch<
      React.SetStateAction<{ config_id?: string; playback_speed: number } | null>
    >,
  });
  // 60x+ is rapid review under soak: suspend annotation chrome / heavy UI so
  // waveform paint owns the main thread (blit/long-task budgets).
  const activeHighSpeedPlayback =
    isPlaybackRunning && (currentConfig?.playback_speed ?? 1) >= 60;
  const initialAutoFitRecordingIdRef = useRef<string | null>(null);
  const lastVisibleChannelRefitKeyRef = useRef<string | null>(null);
  const playbackSpeed = currentConfig?.playback_speed ?? 1;
  const currentPlaybackSpeed = playbackSpeed;
  const [, startDataWindowTransition] = useTransition();
  const [, startViewerInitTransition] = useTransition();
  // Dock-tab and collapse clicks are non-urgent: defer their re-render so the
  // click visual feedback (button highlight) paints within the INP budget.
  const [, startDockTransition] = useTransition();
  const [calipersActive, setCalipersActive] = useState(false);
  const [measurementMode, setMeasurementMode] = useState<"caliper" | "polarity">(
    "caliper",
  );
  const toggleCalipers = useCallback(() => {
    const enabling = !(calipersActive && measurementMode === "caliper");
    setMeasurementMode("caliper");
    setCalipersActive(enabling);
  }, [calipersActive, measurementMode]);
  const togglePolarityMeasure = useCallback(() => {
    const enabling = !(calipersActive && measurementMode === "polarity");
    setMeasurementMode("polarity");
    setCalipersActive(enabling);
  }, [calipersActive, measurementMode]);

  useEffect(() => {
    if (eegSelection) {
      setSegmentInspectorCollapsed(false);
    }
  }, [eegSelection]);
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [configPanelTab, setConfigPanelTab] = useState<
    "profile" | "waveform" | "layout" | "channels" | "calibration" | "export"
  >("profile");
  const [showAnnotationPanel, setShowAnnotationPanel] = useState(false); // Start collapsed
  const [showComparisonView, setShowComparisonView] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("compare") === "1") return true;
      return sessionStorage.getItem(COMPARISON_VIEW_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(
        COMPARISON_VIEW_STORAGE_KEY,
        String(showComparisonView),
      );
      const url = new URL(window.location.href);
      if (showComparisonView) {
        url.searchParams.set("compare", "1");
      } else {
        url.searchParams.delete("compare");
      }
      window.history.replaceState(window.history.state, "", url.toString());
    } catch {
      // ignore persistence failures
    }
  }, [showComparisonView]);

  const [showControlBar, setShowControlBar] = useState(false); // Start collapsed
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [catalogMontageElectrodes, setCatalogMontageElectrodes] = useState<
    string[]
  >([]);
  const [temporaryTraceHighlights, setTemporaryTraceHighlights] = useState<
    Record<string, string>
  >({});
  const [rendererReady, setRendererReady] = useState(false);
  const [canvasMounted, setCanvasMounted] = useState(false);
  /**
   * Keep the branded startup shell covering the viewer until the first
   * waveform channels are published into the renderer. Prevents the brief
   * empty-chrome flash between tile fetch resolve and loadChannels().
   */
  const [awaitingFirstWaveformPaint, setAwaitingFirstWaveformPaint] =
    useState(true);
  const [activeTab, setActiveTab] = useState<"historical" | "realtime">(
    "historical",
  );
  const [resolutionLevel, setResolutionLevel] = useState<number>(0);
  const {
    pausedHighSpeedInspectHold,
    pausedHighSpeedInspectHoldRef,
    commitPausedHighSpeedInspectHold,
    pendingPausedInspectWindowStartRef,
  } = useEEGPausedInspectHold({ isPlaying });
  const playbackTransportMode: EEGTileTransportMode =
    isPlaying || pausedHighSpeedInspectHold?.transportMode === "play"
      ? "play"
      : "inspect";
  const [showGrid, setShowGrid] = useState<boolean>(() => {
    try {
      const persisted = localStorage.getItem(SHOW_GRID_STORAGE_KEY);
      if (persisted === null) return true;
      return persisted === "true";
    } catch {
      return true;
    }
  });
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [macroOverviewZoomMode, setMacroOverviewZoomMode] =
    useState<MacroOverviewZoomMode>(readMacroOverviewZoomMode);
  const [macroOverviewCustomRange, setMacroOverviewCustomRange] =
    useState<NavigationTimelineVisibleRange | null>(readMacroOverviewCustomRange);
  const [macroOverviewCollapsed, setMacroOverviewCollapsed] = useState<boolean>(
    () => readMacroOverviewCollapsed(),
  );
  /** CSS px width of the dedicated waveform channel-label column (plot.left). */
  const [waveformPlotLeftPx, setWaveformPlotLeftPx] = useState(0);
  const syncWaveformPlotLeft = useCallback(() => {
    const renderer = rendererRef.current;
    const canvas = canvasRef.current;
    if (!renderer || !canvas) return;
    const plotLeft = renderer.getViewportMetrics()?.plotLeft ?? 0;
    const cssLeft =
      canvas.width > 0
        ? (plotLeft / canvas.width) * canvas.clientWidth
        : plotLeft;
    const next = Math.max(0, Math.round(cssLeft));
    setWaveformPlotLeftPx((prev) => (prev === next ? prev : next));
  }, []);
  const [reviewedRanges, setReviewedRanges] = useState<ReviewedTimeRange[]>([]);
  const lastReviewRecordTimeRef = useRef(0);
  const [showScaleMarkers, setShowScaleMarkers] = useState<boolean>(true);
  const [clinicalGridMode, setClinicalGridMode] = useState<boolean>(() => {
    try {
      const persisted = localStorage.getItem(CLINICAL_GRID_MODE_STORAGE_KEY);
      if (persisted === null) return true;
      return persisted === "true";
    } catch {
      return true;
    }
  });
  const [crosshairEnabled, setCrosshairEnabled] = useState<boolean>(true);
  const [crosshairCanvasPosition, setCrosshairCanvasPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [pinnedCursorTime, setPinnedCursorTime] = useState<number | null>(null);
  /** Trackit-style reading-line offset from the left edge of the current page. */
  const [pagePlayheadOffsetSeconds, setPagePlayheadOffsetSeconds] =
    useState(0);
  const pagePlayheadOffsetRef = useRef(0);
  const pagePlayheadDragRef = useRef<{
    active: boolean;
    pointerId: number | null;
    suppressClick: boolean;
  }>({ active: false, pointerId: null, suppressClick: false });
  pagePlayheadOffsetRef.current = pagePlayheadOffsetSeconds;
  const [goToTimeInput, setGoToTimeInput] = useState<string>("00:00:00");
  const [goToTimeModalOpen, setGoToTimeModalOpen] = useState(false);
  const [goToTimeModalInput, setGoToTimeModalInput] =
    useState<string>("00:00:00");
  const [goToTimeInputFocused, setGoToTimeInputFocused] = useState(false);
  const [showChannelGroupLabels, setShowChannelGroupLabels] =
    useState<boolean>(true); // EEG-UX-006
  // EEG-CUX-002: per-channel visibility — labels the reviewer has explicitly hidden
  const [hiddenChannels, setHiddenChannels] = useState<Set<string>>(new Set());
  // EEG-CUX-003: Channel Manager Panel open/close
  const [channelManagerOpen, setChannelManagerOpen] = useState<boolean>(false);
  const channelManagerTriggerRef = useRef<HTMLButtonElement | null>(null);
  /** Cached trigger-button position to avoid getBoundingClientRect during render. */
  const channelManagerPosRef = useRef<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  // Capture button rect before the portal renders to prevent layout-read during render
  useLayoutEffect(() => {
    if (channelManagerOpen && channelManagerTriggerRef.current) {
      const rect = channelManagerTriggerRef.current.getBoundingClientRect();
      channelManagerPosRef.current = { top: rect.bottom + 6, left: rect.left };
    }
  }, [channelManagerOpen]);
  const [recordingDuration, setRecordingDuration] = useState<number>(0); // Total duration of recording in seconds
  const [showVideoPanel, setShowVideoPanel] = useState<boolean>(false); // Sprint 12: Video panel toggle
  const [videoPanelLayout, setVideoPanelLayout] = useState<
    "compact" | "standard"
  >("compact");
  const [clinicianExportFormat, setClinicianExportFormat] =
    useState<ClinicianExportFormat>("edf");
  const [clinicianExportIncludeVideo, setClinicianExportIncludeVideo] =
    useState<boolean>(true);
  const [clinicianExportIncludeMetadata, setClinicianExportIncludeMetadata] =
    useState<boolean>(true);
  const [clinicianExportStatus, setClinicianExportStatus] = useState<
    string | null
  >(null);
  const [clinicianExportError, setClinicianExportError] = useState<
    string | null
  >(null);
  const [clinicianExportBusy, setClinicianExportBusy] =
    useState<boolean>(false);
  const [showPrintWaveformDialog, setShowPrintWaveformDialog] =
    useState<boolean>(false);
  const [printWaveformColorMode, setPrintWaveformColorMode] =
    useState<WaveformPrintColorMode>("color");
  const [printWaveformOrientation, setPrintWaveformOrientation] =
    useState<WaveformPrintOrientation>("landscape");
  const [printWaveformPageSize, setPrintWaveformPageSize] =
    useState<WaveformPrintPageSize>("A4");
  const [printWaveformError, setPrintWaveformError] = useState<string | null>(
    null,
  );
  const [showKeyboardShortcutsModal, setShowKeyboardShortcutsModal] =
    useState(false);
  const [eventMarkerPopoverOpen, setEventMarkerPopoverOpen] = useState(false);
  const [eventMarkerPopoverDismissSignal, setEventMarkerPopoverDismissSignal] =
    useState(0);
  const [dataTransferMetrics, setDataTransferMetrics] = useState<{
    size: number;
    time: number;
    level: number;
    format: string;
  } | null>(null);
  const useArrowFormat = true;
  const [autoResolution, setAutoResolution] = useState(true); // Auto-switch resolution at high speeds
  const eegThemeFollowsPortalRef = useRef(true);
  const eegDensityFollowsPortalRef = useRef(true);
  const [profileTheme, setProfileTheme] =
    useState<DisplayProfileBackgroundTheme>(() =>
      portalThemeToEegPaperTheme(settings.theme, settings.high_contrast),
    );
  const [profileGridDensity, setProfileGridDensity] =
    useState<DisplayProfileGridDensity>(
      () =>
        workspaceDensityToEegVisual(
          parsePortalWorkspaceDensity(settings.workspace_density),
        ).gridDensity,
    );
  const [profileChannelColors, setProfileChannelColors] = useState<
    Record<string, string>
  >({});
  const [profileWaveformThickness, setProfileWaveformThickness] = useState(
    FALLBACK_DISPLAY_PROFILE_POLICY.visual.waveform_thickness.default,
  );
  const [annotationOverlayOpacity, setAnnotationOverlayOpacity] =
    useState(0.2);
  const [segmentInspectorStatus, setSegmentInspectorStatus] = useState<
    string | null
  >(null);
  const closeSegmentInspector = useCallback(() => {
    setSegmentInspectorCollapsed(true);
    setEegSelection(null);
    setEegSelectionDraft(null);
    setSegmentInspectorStatus(null);
  }, []);
  const toggleSegmentInspector = useCallback(() => {
    setCalipersActive(false);
    if (segmentInspectorCollapsed) {
      setSegmentInspectorCollapsed(false);
      return;
    }
    closeSegmentInspector();
  }, [closeSegmentInspector, segmentInspectorCollapsed]);
  const [profileChannelSpacing, setProfileChannelSpacing] = useState(
    () =>
      workspaceDensityToEegVisual(
        parsePortalWorkspaceDensity(settings.workspace_density),
      ).channelSpacing,
  );
  const [waveformDisplayMode, setWaveformDisplayMode] =
    useState<WaveformDisplayMode>("stacked");
  const [allowTraceOverflow, setAllowTraceOverflow] = useState(
    DEFAULT_ALLOW_TRACE_OVERFLOW,
  );
  const [refitOnChannelChange, setRefitOnChannelChange] = useState(
    DEFAULT_REFIT_ON_CHANNEL_CHANGE,
  );
  const [profileLabelFontSize, setProfileLabelFontSize] = useState(
    FALLBACK_DISPLAY_PROFILE_POLICY.visual.label_font_size.default,
  );
  const [profileChannelPositionOffsets, setProfileChannelPositionOffsets] =
    useState<Record<string, number>>({});
  const {
    autoScreenDpi,
    setAutoScreenDpi,
    screenCalibration,
    setScreenCalibration,
    calibrationEnvironmentBaseline,
    calibrationStale,
    effectiveScreenDpi,
    effectivePixelsPerMm,
    calibrationScaleMultiplier,
    calibrationAutoPixelsPerMm,
    calibrationMeasuredVerticalPxPerMm,
    calibrationMeasuredHorizontalPxPerMm,
    calibrationVerticalDeltaPercent,
    calibrationHorizontalDeltaPercent,
    calibrationVerticalPass,
    calibrationHorizontalPass,
    calibrationOverallPass,
    calibrationStatus,
    calibrationBadgeLabel,
    calibrationBadgeTitle,
    markCalibrationReviewed,
  } = useEEGScreenCalibration();
  const [activeCustomizationTool, setActiveCustomizationTool] = useState<
    "bad-channels" | "filter-overrides" | null
  >(null);
  const [
    customizationQueryBootstrapReady,
    setCustomizationQueryBootstrapReady,
  ] = useState(false);
  const [showAnnotationQuickOpen, setShowAnnotationQuickOpen] = useState(false);
  const [selectedTrendProfileId, setSelectedTrendProfileId] = useState("");
  const [trendPreviewMessage, setTrendPreviewMessage] = useState<string | null>(
    null,
  );
  const [trendPreviewError, setTrendPreviewError] = useState<string | null>(
    null,
  );
  const [trendLookbackSeconds, setTrendLookbackSeconds] = useState<number>(300);
  const [trendLookaheadSeconds, setTrendLookaheadSeconds] =
    useState<number>(300);
  const [trendEvalActive, setTrendEvalActive] = useState(false);
  const [trendEvalIntervalSeconds, setTrendEvalIntervalSeconds] = useState(15);
  const [trendEvalStatus, setTrendEvalStatus] = useState<string | null>(null);
  const [trendEvalError, setTrendEvalError] = useState<string | null>(null);
  const [trendCorrelationStatus, setTrendCorrelationStatus] = useState<
    string | null
  >(null);
  const [quickSelectedTemplateId, setQuickSelectedTemplateId] = useState("");
  const [quickSelectedMacroId, setQuickSelectedMacroId] = useState("");
  const [templateFieldValues, setTemplateFieldValues] = useState<
    Record<string, string>
  >({});
  const [templateChannelMode, setTemplateChannelMode] = useState<
    "selected" | "all"
  >("selected");
  const [templateFieldError, setTemplateFieldError] = useState<string | null>(
    null,
  );
  const [rapidEventDraft, setRapidEventDraft] = useState<RapidEventDraft>({
    eventType: "medication_administered",
    medicationName: "",
    medicationDose: "",
    medicationRoute: "",
    procedureName: "",
    reason: "",
    expectedEffect: "",
    followUpWindowSeconds: "300",
    anchorTimeSeconds: null,
    selectedChannels: [],
  });
  const [rapidEventStatus, setRapidEventStatus] = useState<string | null>(null);
  const [rapidEventError, setRapidEventError] = useState<string | null>(null);
  const [clipScissorsModalOpen, setClipScissorsModalOpen] = useState(false);
  // Annotation-panel state (tab/filters/bulk selection/note composer/smart
  // suggestions) and consensus-review state now live in
  // useEEGAnnotationPanel / useEEGConsensusReview (called further below,
  // once their required derived inputs — e.g. clinicalAnnotationPanelVisible
  // — are available).
  const [dockBottomActiveTab, setDockBottomActiveTab] =
    useState<DockPanelKey | null>(null);
  const [viewerTrendTemplateOverrideId, setViewerTrendTemplateOverrideId] =
    useState<string>("");
  // EEG-CUX-004: popped-out panel tracking
  const [poppedOutPanels, setPoppedOutPanels] = useState<Set<DockPanelKey>>(
    new Set(),
  );
  const poppedOutWindowsRef = useRef<Map<DockPanelKey, Window>>(new Map());
  // EEG-CUX-004 AC#6: visible toast when browser blocks the pop-out window
  const [popupBlockedMessage, setPopupBlockedMessage] = useState<string | null>(
    null,
  );
  const popupBlockedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [clinicalNotice, setClinicalNotice] = useState<{
    tone: "success" | "error";
    message: string;
  } | null>(null);
  const clinicalNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const showClinicalNotice = useCallback(
    (tone: "success" | "error", message: string, durationMs = 7000) => {
      if (clinicalNoticeTimerRef.current) {
        clearTimeout(clinicalNoticeTimerRef.current);
      }
      setClinicalNotice({ tone, message });
      clinicalNoticeTimerRef.current = setTimeout(() => {
        setClinicalNotice(null);
        clinicalNoticeTimerRef.current = null;
      }, durationMs);
    },
    [],
  );
  // EEG-CUX-005: BroadcastChannel for syncing detached panels
  const eegSyncChannelRef = useRef<BroadcastChannel | null>(null);
  const {
    dockBottomHeightPx,
    setDockBottomHeightPx,
    dockRightWidthPx,
    setDockRightWidthPx,
    annotationPanelWidthPx,
    setAnnotationPanelWidthPx,
    dockBottomCollapsed,
    setDockBottomCollapsed,
    dockRightCollapsed,
    setDockRightCollapsed,
    eventsTrendsDockPlacement,
    setEventsTrendsDockPlacement,
    filterRowOpen,
    setFilterRowOpen,
    reviewToolsDrawerOpen,
    setReviewToolsDrawerOpen,
    dualCompareDrawerOpen,
    setDualCompareDrawerOpen,
    toolbarAutoHide,
    setToolbarAutoHide,
    signalCardVisible,
    setSignalCardVisible,
    outputToolbarSectionOrder,
    setOutputToolbarSectionOrder,
    draggingOutputToolbarSection,
    setDraggingOutputToolbarSection,
    workspaceControlsPinned,
    setWorkspaceControlsPinned,
    workspaceControlsPeekOpen,
    setWorkspaceControlsPeekOpen,
    dockPanelVisibility,
    setDockPanelVisibility,
    dockPanelOrder,
    setDockPanelOrder,
    activeDockResize,
    setActiveDockResize,
    dockResizeStartRef,
    isDockPanelVisible,
    moveDockPanel,
    toggleDockPanelVisibility,
    handleDockResizeStart,
    cancelWorkspaceControlsPeekHide,
    toggleToolbarAutoHide,
    getWorkspaceControlsPeekHideDelayMs,
    revealWorkspaceControlsPeek,
    scheduleHideWorkspaceControlsPeek,
    toggleWorkspaceControls,
    revealToolbarPeek,
    moveOutputToolbarSection,
    getLeftCardOrder,
    reviewToolsItemOrder,
    moveReviewToolsItem,
  } = useEEGViewerDockLayout({
    showConfigPanel,
    poppedOutPanels,
    dockBottomActiveTab,
  });
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document.querySelectorAll(".main-layout-scroll").forEach((node) => {
      (node as HTMLElement).scrollTop = 0;
    });
  }, [
    dockRightCollapsed,
    dockBottomCollapsed,
    reviewToolsDrawerOpen,
    showConfigPanel,
  ]);
  const [
    playbackControlsLockOnPlayback,
    setPlaybackControlsLockOnPlayback,
  ] = useState<boolean>(() => readPlaybackControlsLockOnPlaybackPreference());
  const [playbackWorkspaceControlsLocked, setPlaybackWorkspaceControlsLocked] =
    useState<boolean>(() => readPlaybackControlsLockOnPlaybackPreference());
  const wasPlaybackRunningRef = useRef(false);
  const [autoFitFeedback, setAutoFitFeedback] = useState<{
    message: string;
    previousSensitivity: number;
    newSensitivity: number;
    previousTimeScale: number;
    newTimeScale: number;
  } | null>(null);
  const [spectrogramConfig, setSpectrogramConfig] = useState<
    SpectrogramConfig | undefined
  >(undefined);
  const [dualMontagePreference, setDualMontagePreference] =
    useState<DualMontagePreference>(DEFAULT_DUAL_MONTAGE_PREFERENCE);
  const dualMontageDefaultsAppliedRef = useRef(false);
  const [dualMontageMessage, setDualMontageMessage] = useState<string | null>(
    null,
  );
  const [dualMontageError, setDualMontageError] = useState<string | null>(null);
  const currentConfigRef = useRef<EEGConfiguration | null>(currentConfig);
  const dataWindowStartRef = useRef<number>(dataWindowStart);
  // Tracks the last window start dispatched via setDataWindowStart so the RAF
  // tick never re-dispatches the same (or a lower) value across multiple frames
  // before the React state / useEffect mirror has had a chance to update
  // dataWindowStartRef.  Without this guard every tick fires a new transition
  // with an incrementally larger nextWindowStart → React batches them, commits
  // only the last (largest) value, and skips all intermediate windows.
  const scheduledWindowStartRef = useRef<number>(dataWindowStart);
  const recordingDurationRef = useRef<number>(recordingDuration);
  /**
   * Authoritative recording duration from metadata (selectedRecording.duration_seconds
   * or playbackManifest.logicalDurationMs).  When > 0 it caps all tile-derived
   * estimatedTotal values so a mid-recording 300s tile never inflates
   * recordingDuration past the real end of the recording.
   * Bug P fix: without this cap, e.g. tile ws=1740 with 300s data sets
   * estimatedTotal=2040 via Math.max, permanently overriding the metadata's
   * 1818s — letting the user navigate to currentTime=2020+ and see blank canvas.
   */
  const recordingMetaDurationRef = useRef<number>(0);
  const isPlayingRef = useRef<boolean>(isPlaying);
  const lightweightSoakPlaybackTimerRef = useRef<number | null>(null);
  const ultraRapidSoakWallTimerRef = useRef<number | null>(null);
  const commitIsPlaying = useCallback((nextIsPlaying: boolean) => {
    isPlayingRef.current = nextIsPlaying;
    setIsPlaying(nextIsPlaying);
  }, []);
  const wasPlayingRef = useRef<boolean>(isPlaying);
  /** Tracks raw EEGChannel arrays from the most-recent loadChannels call so the
   * PSD panel can access per-channel signal data without a public getter on the
   * renderer. Updated alongside every rendererRef.current.loadChannels() call. */
  const loadedChannelsRef = useRef<EEGChannel[]>([]);
  const liveTopographyBufferRecordingIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (viewerDeferredChromeReady) {
      return;
    }

    if (typeof window === "undefined" || process.env.NODE_ENV === "test") {
      setViewerDeferredChromeReady(true);
      return;
    }

    const handle = scheduleEegStartupTask({
      id: "deferred-chrome-activation",
      priority: "high",
      run: () => {
        measureEegStartupBlock("deferred-chrome-activation", () => {
          startViewerInitTransition(() => {
            setViewerDeferredChromeReady(true);
          });
        });
      },
    });

    // Safety: never leave chrome permanently deferred if playable mark is missed.
    const safetyTimeout = window.setTimeout(() => {
      handle.cancel();
      measureEegStartupBlock("deferred-chrome-activation-safety", () => {
        startViewerInitTransition(() => {
          setViewerDeferredChromeReady(true);
        });
      });
    }, 5000);

    return () => {
      handle.cancel();
      window.clearTimeout(safetyTimeout);
    };
  }, [startViewerInitTransition, viewerDeferredChromeReady]);

  // playbackControlsInteractive is marked by EEGPlaybackCore (transport slot).
  // Deferred chrome waits via eegStartupScheduler (Sprint 4C-006).
  const [liveTopographyBufferVersion, setLiveTopographyBufferVersion] =
    useState(0);
  const [liveScalpFieldEventHint, setLiveScalpFieldEventHint] = useState<{
    startSeconds: number;
    endSeconds: number;
    label: string;
    source?: "triage" | "catalog" | "spike" | "manual";
  } | null>(null);
  /**
   * The bufferWindowStart (seconds) of the data currently loaded into the
   * renderer via loadChannels().  Used to detect focus-refetch storms:
   * React Query refetches active queries on focus, returning data for the
   * window that is ALREADY displayed → the data loading effect would queue
   * pendingEegBufferRef with windowStart === rendererLoadedWindowStartRef,
   * causing the RAF tick to immediately call loadChannels() again with new
   * Float32Array objects → cold filter cache → 86-93ms RAF violation.
   * Keeping this ref lets the data loading effect skip the spurious refetch.
   */
  const rendererLoadedWindowStartRef = useRef<number | null>(null);
  /**
   * The record-time end (seconds) of data that has actually been loaded into the
   * renderer via loadChannels().  Kept in sync with rendererLoadedWindowStartRef.
   * Used in handlePlaybackTick so that `bufferEnd` is the actual data boundary
   * rather than `loadedWindowStart + computedBufferSize`, which shrinks when the
   * user reduces playback speed and creates a false freeze when the new smaller
   * bufferSize falls below the current playhead position.
   */
  const rendererLoadedBufferEndRef = useRef<number | null>(null);
  const rendererLoadedPlaybackContextRef = useRef<NonNullable<
    DecodedArrowTile["requestContext"]
  > | null>(null);
  // Holds the next buffer's processed EEGChannel data + metadata, queued during
  // active playback so we can load it into the renderer at the right RAF tick
  // instead of doing it immediately and blocking the animation frame.
  const pendingEegBufferRef = useRef<{
    channelData: EEGChannel[];
    windowStart: number;
    samplingRate: number;
    duration: number | null;
    requestContext: NonNullable<DecodedArrowTile["requestContext"]> | null;
    decodeTelemetry: DecodedArrowTile["decodeTelemetry"] | null;
    queuedAtMs: number;
  } | null>(null);
  const renderAnnotationsRef = useRef<Annotation[]>(EMPTY_ANNOTATIONS);
  const frozenAnnotationQueryTimeRef = useRef<number | null>(null);
  const reviewedTransitionTriggeredRef = useRef(false);
  const lastSideQuerySyncAtRef = useRef<number>(0);
  const pendingUiPublishRef = useRef<PlaybackClockUiPublishRequest | null>(
    null,
  );
  const lastPlaybackWaveformRenderAtMsRef = useRef<number>(
    Number.NEGATIVE_INFINITY,
  );
  const lastPlaybackWaveformRenderDurationMsRef = useRef<number>(0);
  const lastRenderedPlaybackTimeRef = useRef<number | null>(null);
  const goToTimeInputRef = useRef<HTMLInputElement | null>(null);
  const dualDrawerFirstControlRef = useRef<HTMLSelectElement | null>(null);
  const reviewDrawerFirstControlRef = useRef<HTMLButtonElement | null>(null);
  const channelQuickJumpInputRef = useRef<HTMLInputElement | null>(null);
  const channelScrollbarTrackRef = useRef<HTMLDivElement | null>(null);
  const channelScrollbarDragStateRef = useRef<{
    active: boolean;
    pointerOffsetY: number;
  }>({
    active: false,
    pointerOffsetY: 0,
  });
  const lastDockResizeSignalRef = useRef<string>("");
  const dockResizeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    if (params.get("openViewerVisualControls") === "1") {
      setSignalCardVisible(false);
      setDualCompareDrawerOpen(false);
      setReviewToolsDrawerOpen(false);
      setFilterRowOpen(false);
      setShowConfigPanel(true);
    }
  }, [location.search]);

  // Tile worker is now a module-level singleton (getSharedTileWorker / decodeArrowTileShared).
  // The old per-component useEffect that created and terminated the Worker on every
  // StrictMode unmount/remount was causing in-flight decodes to reject with
  // "EEG tile worker terminated", which forced the eegData query into an error
  // state and left the viewer stuck on the loading screen.

  const decodeArrowTileInWorker = useCallback(
    (buffer: ArrayBuffer): Promise<DecodedArrowTile> =>
      decodeArrowTileShared(buffer),
    [],
  );

  /**
   * Compute dynamic buffer size based on playback speed.
   * Keep first-paint buffers modest at clinical speeds (1-2x) so requests
   * complete quickly, while still scaling up for high-speed playback.
   */
  const getEffectiveBufferSize = useCallback(
    (
      speed: number,
      timeScale: number,
      transportMode: EEGTileTransportMode = "inspect",
    ) => {
      const safeTimeScale = Math.max(1, timeScale);
      // Sprint 4B: first playable paint uses one visible page until deferred
      // chrome/warmups are allowed to expand the buffer.
      if (!viewerDeferredChromeReady && speed <= 4) {
        return safeTimeScale;
      }
      if (speed <= 4) {
        if (transportMode === "play") {
          return Math.max(60, safeTimeScale * 6);
        }
        // Keep at least 3 pages buffered at clinical playback speeds.
        // 3× timeScale ensures the renderTime clamp (bufferEnd - timeScale) has
        // 2 full pages of look-ahead, so the visible page is always fully within
        // loaded data even while the next window is being fetched (~400ms latency).
        // Previously 2× which caused a half-page blank gap at the buffer boundary.
        return Math.max(30, safeTimeScale * 3);
      }

      if (transportMode === "inspect") {
        // Paused inspection should refine only the visible page plus a small
        // amount of context, not pull the oversized playback buffer.
        return Math.max(30, Math.min(180, safeTimeScale * 3));
      }

      // Active playback should keep enough overview data ahead of the playhead
      // to avoid clamping the clock while the next tile is fetched/decoded. At
      // rapid-review speeds, small page-relative buffers create constant
      // fetch/render churn: a 200s buffer at 60x covers only ~3.3 wall seconds.
      // Scale high-speed playback windows by wall-clock coverage, while the
      // auto-resolution path keeps the payload on overview levels.
      if (speed >= 50) {
        if (speed >= 200) return 1200;
        if (speed >= 150) return 900;
        return 600;
      }

      // Moderate playback speeds still benefit from page-relative windows:
      // enough look-ahead to hide fetches without pulling low-value hours of
      // data while morphology remains visually inspectable.
      const playPages = speed >= 16 ? 8 : 6;
      const maxPlayBuffer = speed >= 16 ? 180 : 120;
      return Math.max(60, Math.min(maxPlayBuffer, safeTimeScale * playPages));
    },
    [viewerDeferredChromeReady],
  );

  /**
   * Recommend a pyramid resolution level for a given playback speed.
   * At high speeds the user can't resolve individual waveform morphology;
   * downsampling keeps fetch sizes reasonable and avoids blank-canvas gaps.
   */
  const getAutoResolutionLevel = useCallback(
    (
      speed: number,
      transportMode: EEGTileTransportMode = "inspect",
    ): number => {
      if (transportMode === "inspect") {
        if (speed >= 100) return 1; // paused 100x review refines to L1
        if (speed >= 50) return 2; // paused high-speed review refines to L2
        if (speed >= 16) return 1;
        return 0;
      }
      if (speed >= 50) return 3; // rapid-review playback uses the coarsest overview
      if (speed >= 16) return 1;
      return 0;
    },
    [],
  );

  const shouldUseClinicalRawResolution = useCallback(
    (
      speed: number,
      timeScale: number,
      transportMode: EEGTileTransportMode,
    ): boolean =>
      speed <= 4 && (transportMode === "play" || Math.max(1, timeScale) <= 120),
    [],
  );

  /**
   * Prefetch threshold (in recording-seconds).
   * Inspect mode keeps a small local look-ahead around the visible page.
   * Play mode starts background fetches once roughly 75% of the current
   * playback buffer has been consumed, keeping the overview path responsive
   * without returning to the old 1000s+ look-ahead windows.
   */
  const getPrefetchThreshold = useCallback(
    (
      speed: number,
      timeScale: number,
      transportMode: EEGTileTransportMode = "inspect",
    ) => {
      if (transportMode !== "play") {
        return Math.max(timeScale + 2, Math.min(30, timeScale * 2));
      }

      const bufferSizeSec = getEffectiveBufferSize(speed, timeScale, "play");
      if (speed <= 2) return Math.max(timeScale + 2, speed * 3);
      return Math.max(timeScale + 2, Math.min(bufferSizeSec * 0.75, speed * 8));
    },
    [getEffectiveBufferSize],
  );

  /**
   * Advance-chunk size – how far we shift `dataWindowStart` when refetching.
   * Keep chunk <= lookahead so the viewport remains fully covered between
   * consecutive windows and we avoid partial-page rendering seams.
   */
  const getAdvanceChunk = useCallback(
    (bufferSize: number, timeScale: number) => {
      const safeTimeScale = Math.max(1, timeScale);
      const lookahead = Math.max(1, Math.floor(bufferSize - safeTimeScale));
      // Large playback buffers need materially coarser shift steps; advancing
      // a 200s+ window in 60s increments creates overlapping fetch storms.
      // For 600s+ rapid-review buffers, avoid first-boundary deadlocks by
      // handing off with at least a half-buffer runway. The earlier 450s step
      // left only ~150s of runway at 100x; when the visible tile aligned late,
      // the playhead could clamp at the first buffer edge before the pending
      // tile became flushable.
      if (bufferSize >= 600) {
        return Math.min(lookahead, bufferSize >= 1200 ? 600 : 300);
      }
      const coarse = Math.max(
        4,
        bufferSize >= 240
          ? Math.floor(bufferSize * 0.875)
          : Math.floor(bufferSize / (bufferSize >= 180 ? 2 : 3)),
      );
      const bounded = Math.max(1, Math.min(coarse, lookahead));
      // Canonical chunk steps improve cache locality for tile artifacts.
      const canonicalSteps = [
        1, 2, 5, 10, 15, 20, 30, 45, 60, 90, 120, 150, 180, 210, 300, 450, 600,
        900, 1200, 1500, 1800, 2400, 3000, 3600, 4500, 5400, 6300,
      ];
      const eligibleSteps = canonicalSteps.filter((step) => step <= lookahead);
      if (eligibleSteps.length === 0) {
        return bounded;
      }
      let nearest = eligibleSteps[0];
      let nearestDistance = Math.abs(nearest - bounded);
      for (const step of eligibleSteps) {
        const distance = Math.abs(step - bounded);
        if (distance < nearestDistance) {
          nearest = step;
          nearestDistance = distance;
        }
      }
      return Math.max(1, Math.min(nearest, lookahead));
    },
    [],
  );

  const alignWindowStartToBackend = useCallback(
    (
      windowStart: number,
      speed: number,
      timeScale: number,
      transportMode: EEGTileTransportMode,
      recordingDurationOverride?: number,
    ) =>
      alignTileWindowToBackend({
        windowStartSec: windowStart,
        bufferSizeSec: getEffectiveBufferSize(speed, timeScale, transportMode),
        speed,
        transportMode,
        recordingDurationSec:
          recordingDurationOverride ??
          (recordingDurationRef.current > 0
            ? recordingDurationRef.current
            : recordingDuration > 0
              ? recordingDuration
              : undefined),
      }).startSec,
    [getEffectiveBufferSize, recordingDuration],
  );

  // Fetch study details for header context
  const {
    data: studyDetails,
    isLoading: isLoadingStudyDetails,
    isFetching: isFetchingStudyDetails,
  } = useQuery({
    queryKey: ["study-details", studyId],
    queryFn: async () => {
      if (!studyId) return null;
      try {
        const response = await apiClient.get(`/eegstudy/record/${studyId}`);
        return response.data;
      } catch (error) {
        logger.error("[EEGViewer] Failed to fetch study details:", error);
        return null;
      }
    },
    enabled: !!studyId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // When an EEG import for this study completes, refresh recordings immediately
  // so the new record is available before the progress dock dismisses.
  const eegImportTasks = useOptionalEegImportTasks();
  const studyImportSnapshot = useMemo(
    () =>
      (eegImportTasks?.importTasks ?? [])
        .filter((task) => task.studyId === studyId)
        .map((task) => `${task.id}:${task.status}:${task.updatedAt}`)
        .join("|"),
    [eegImportTasks?.importTasks, studyId],
  );
  useEffect(() => {
    if (!studyId || !studyImportSnapshot) return;
    void queryClient.invalidateQueries({
      queryKey: ["study-details", studyId],
    });
  }, [queryClient, studyId, studyImportSnapshot]);
  const studyVideoWarmup = useStudyVideoPlaybackWarmup(studyId, {
    enabled: startupSecondaryQueriesEnabled && Boolean(studyId),
  });
  const videoPopout = useVideoPopout(studyId);
  const studyVideoRecordings = studyVideoWarmup.videos;
  const hasVideo = useMemo(() => {
    if (
      Boolean(studyDetails?.has_video) ||
      (studyDetails?._count?.videos ?? 0) > 0
    ) {
      return true;
    }
    return studyVideoRecordings.some(
      (video) =>
        Boolean(video.file_path?.trim()) || Boolean(video.storage_url),
    );
  }, [studyDetails, studyVideoRecordings]);
  const requestedRecordingId = (recordingId || "").trim();
  const studyRecordings = Array.isArray(studyDetails?.recordings)
    ? studyDetails.recordings
    : [];
  const selectedRecording =
    studyRecordings.find(
      (recording: { recording_id?: string }) =>
        recording.recording_id === requestedRecordingId,
    ) ||
    studyRecordings.find((recording: { is_primary?: boolean }) =>
      Boolean(recording.is_primary),
    ) ||
    studyRecordings[0];
  const recordingChannelMapAliasMaps = useMemo((): ChannelMapAliasMaps | null => {
    const recording = selectedRecording as
      | {
          channel_map_aliases?: Record<string, string> | null;
          channel_map_profile_id?: string | null;
        }
      | undefined;
    const fromSnapshot = buildAliasMapsFromSnapshot(
      recording?.channel_map_aliases ?? null,
    );
    if (fromSnapshot) return fromSnapshot;
    const profile = getChannelMapProfileById(recording?.channel_map_profile_id);
    if (profile) return buildAliasMaps(profile);
    return null;
  }, [
    (selectedRecording as { channel_map_aliases?: unknown } | undefined)
      ?.channel_map_aliases,
    (selectedRecording as { channel_map_profile_id?: string | null } | undefined)
      ?.channel_map_profile_id,
  ]);
  const channelMapNotice = recordingChannelMapAliasMaps
    ? `Channel map: ${
        getChannelMapProfileById(
          (
            selectedRecording as
              | { channel_map_profile_id?: string | null }
              | undefined
          )?.channel_map_profile_id,
        )?.displayName || LIFELINES_T4A_DEFAULT_PROFILE.displayName
      }`
    : null;
  const currentRecordingId =
    selectedRecording?.recording_id || requestedRecordingId;

  const dismissFirstWaveformShell = useCallback(() => {
    setAwaitingFirstWaveformPaint((awaiting) => (awaiting ? false : awaiting));
  }, []);

  const markFirstWaveformChannelsPublished = useCallback(
    (channelCount: number) => {
      if (channelCount <= 0) {
        return;
      }
      // Wait until loadChannels has committed and the canvas has a chance to paint.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dismissFirstWaveformShell();
        });
      });
    },
    [dismissFirstWaveformShell],
  );

  useEffect(() => {
    setAwaitingFirstWaveformPaint(true);
  }, [currentRecordingId]);

  const headerRecordingLabel = useMemo(() => {
    if (!selectedRecording) {
      return "N/A";
    }

    return resolveRecordingExamId({
      recordingIdFromEdf: selectedRecording.recording_id_from_edf,
      filename: selectedRecording.filename,
    });
  }, [selectedRecording?.filename, selectedRecording?.recording_id_from_edf]);
  const recordingStartIso = useMemo(() => {
    const fromRecording = (
      selectedRecording as { start_datetime?: string } | undefined
    )?.start_datetime;
    if (typeof fromRecording === "string" && fromRecording.trim().length > 0) {
      return fromRecording;
    }
    const fromStudy = studyDetails?.study_date;
    if (typeof fromStudy === "string" && fromStudy.trim().length > 0) {
      return fromStudy;
    }
    return null;
  }, [selectedRecording, studyDetails?.study_date]);
  const timelineRecordingDuration = useMemo(() => {
    const metaDuration = selectedRecording?.duration_seconds;
    if (
      typeof metaDuration === "number" &&
      Number.isFinite(metaDuration) &&
      metaDuration > 0
    ) {
      return metaDuration;
    }
    if (recordingMetaDurationRef.current > 0) {
      return recordingMetaDurationRef.current;
    }
    return recordingDuration;
  }, [recordingDuration, selectedRecording?.duration_seconds]);
  const normalizedCurrentRecordingId = (currentRecordingId || "").trim();
  const markLiveTopographyBufferUpdated = useCallback(
    (recordingId: string | null | undefined) => {
      liveTopographyBufferRecordingIdRef.current = recordingId?.trim() || null;
      setLiveTopographyBufferVersion((version) => version + 1);
    },
    [],
  );
  const buildPauseBoundaryRuntimeSnapshot = useCallback(() => {
    const pendingBuffer = pendingEegBufferRef.current;
    const pendingWindowEnd =
      pendingBuffer?.duration !== null && pendingBuffer?.duration !== undefined
        ? pendingBuffer.windowStart + pendingBuffer.duration
        : (pendingBuffer?.requestContext?.windowEnd ?? null);

    return {
      recordingId: currentRecordingId ?? null,
      isPlaying,
      isPlayingRef: isPlayingRef.current,
      playbackTransportMode,
      pausedHoldTransportMode:
        pausedHighSpeedInspectHoldRef.current?.transportMode ?? null,
      loadedWindowStart: rendererLoadedWindowStartRef.current,
      loadedBufferEnd: rendererLoadedBufferEndRef.current,
      pendingWindowStart: pendingBuffer?.windowStart ?? null,
      pendingWindowEnd,
      pendingTransportMode:
        pendingBuffer?.requestContext?.transportMode ?? null,
      pendingQueuedAtMs: pendingBuffer?.queuedAtMs ?? null,
      scheduledWindowStart: scheduledWindowStartRef.current,
      dataWindowStart: dataWindowStartRef.current,
      currentTime: currentTimeRef.current,
      speed: currentConfigRef.current?.playback_speed ?? null,
      timeScale: currentConfigRef.current?.time_scale ?? null,
    };
  }, [currentRecordingId, isPlaying, playbackTransportMode]);
  const publishPauseBoundaryRuntimeState = useCallback(
    (reason: string, extra: Record<string, unknown> = {}) => {
      if (typeof window === "undefined") {
        return;
      }

      const diagnosticsHost = window as unknown as {
        eegDiag?: {
          viewerRuntimeState?: Record<string, unknown>;
        };
      };
      const diagnostics = diagnosticsHost.eegDiag;
      if (!diagnostics) {
        return;
      }

      diagnostics.viewerRuntimeState = {
        t: Date.now(),
        reason,
        ...buildPauseBoundaryRuntimeSnapshot(),
        ...extra,
      };
    },
    [buildPauseBoundaryRuntimeSnapshot],
  );
  const recordPauseBoundaryEvent = useCallback(
    (event: string, extra: Record<string, unknown> = {}) => {
      if (typeof window === "undefined") {
        return;
      }

      const diagnosticsHost = window as unknown as {
        eegDiag?: {
          pauseBoundaryEvents?: Array<Record<string, unknown>>;
          viewerRuntimeState?: Record<string, unknown>;
        };
      };
      const diagnostics = diagnosticsHost.eegDiag;
      if (!diagnostics) {
        return;
      }

      if (!Array.isArray(diagnostics.pauseBoundaryEvents)) {
        diagnostics.pauseBoundaryEvents = [];
      }

      const entry = {
        t: Date.now(),
        event,
        ...buildPauseBoundaryRuntimeSnapshot(),
        ...extra,
      };

      diagnostics.pauseBoundaryEvents.push(entry);
      if (diagnostics.pauseBoundaryEvents.length > 240) {
        diagnostics.pauseBoundaryEvents.splice(
          0,
          diagnostics.pauseBoundaryEvents.length - 240,
        );
      }

      diagnostics.viewerRuntimeState = {
        ...entry,
        reason: event,
      };
    },
    [buildPauseBoundaryRuntimeSnapshot],
  );
  const focusedSpacingSessionStorageKey = useMemo(() => {
    if (!normalizedCurrentRecordingId) {
      return null;
    }

    const montageKey = (currentConfig?.montage_id || "default").trim();
    return `eeg-focused-spacing:${toolbarUserId}:${normalizedCurrentRecordingId}:${montageKey}`;
  }, [currentConfig?.montage_id, normalizedCurrentRecordingId, toolbarUserId]);
  const metadataNotchMode = useMemo(
    () => inferNotchModeFromRecordingMetadata(selectedRecording),
    [selectedRecording],
  );

  useEffect(() => {
    publishPauseBoundaryRuntimeState("state-sync");
  }, [
    publishPauseBoundaryRuntimeState,
    isPlaying,
    playbackTransportMode,
    pausedHighSpeedInspectHold?.transportMode,
    currentConfig?.playback_speed,
    currentConfig?.time_scale,
    dataWindowStart,
    resolutionLevel,
  ]);

  useEffect(() => {
    if (!normalizedCurrentRecordingId || !currentConfig || !metadataNotchMode) {
      return;
    }
    if (manualNotchOverrideByRecordingId[normalizedCurrentRecordingId]) {
      return;
    }
    if (
      autoNotchDefaultModeByRecordingId[normalizedCurrentRecordingId] ===
      metadataNotchMode
    ) {
      return;
    }

    const currentMode = getNotchModeFromFlags(
      currentConfig.notch_filter_60hz,
      currentConfig.notch_filter_50hz,
    );
    if (currentMode === metadataNotchMode) {
      setAutoNotchDefaultModeByRecordingId((current) => {
        if (current[normalizedCurrentRecordingId] === metadataNotchMode) {
          return current;
        }
        return {
          ...current,
          [normalizedCurrentRecordingId]: metadataNotchMode,
        };
      });
      return;
    }

    const notchFlags = getNotchFlagsForMode(metadataNotchMode);
    setCurrentConfig((previous) => {
      if (!previous) return previous;
      return normalizeViewerConfig({
        ...previous,
        ...notchFlags,
      });
    });
    setAutoNotchDefaultModeByRecordingId((current) => ({
      ...current,
      [normalizedCurrentRecordingId]: metadataNotchMode,
    }));
  }, [
    autoNotchDefaultModeByRecordingId,
    currentConfig,
    manualNotchOverrideByRecordingId,
    metadataNotchMode,
    normalizedCurrentRecordingId,
  ]);

  // CP-S17 clinical endpoints are recording-scoped. Avoid issuing requests
  // until the viewer has resolved an actual recording id.
  const clinicalScopeId = normalizedCurrentRecordingId.trim();
  const badChannelsQueryKey = useMemo(
    () => ["eeg-bad-channels", clinicalScopeId] as const,
    [clinicalScopeId],
  );
  const filterOverridesQueryKey = useMemo(
    () => ["eeg-channel-filters", clinicalScopeId] as const,
    [clinicalScopeId],
  );
  const currentWorkflowStatusCode = useMemo(() => {
    const fromWorkflowStatus = String(
      (
        studyDetails as
          | { workflowStatus?: { status_code?: string } }
          | undefined
      )?.workflowStatus?.status_code || "",
    )
      .trim()
      .toLowerCase();
    if (fromWorkflowStatus) {
      return fromWorkflowStatus;
    }
    const fromLegacyStatus = String(
      (studyDetails as { status?: string } | undefined)?.status || "",
    )
      .trim()
      .toLowerCase();
    return fromLegacyStatus;
  }, [studyDetails]);
  const isCurrentWorkflowTerminal = Boolean(
    (studyDetails as { workflowStatus?: { is_terminal?: boolean } } | undefined)
      ?.workflowStatus?.is_terminal,
  );
  const canAutoTransitionToReviewed = useMemo(
    () =>
      shouldAutoTransitionStudyToReviewed({
        currentStatusCode: currentWorkflowStatusCode,
      }),
    [currentWorkflowStatusCode],
  );
  const playbackPerf = usePlaybackPerf(currentRecordingId, {
    rendererMode: rendererMode.mode,
    fallbackReason:
      rendererMode.mode === "cpu" ? rendererMode.fallbackReason : "none",
    playbackSpeed: currentConfig?.playback_speed ?? 1,
    surface: showVideoPanel ? "eeg-dual-video" : "eeg-only",
    resolutionLevel,
    activeTab,
    dataWindowStart,
  });
  const playbackPerfSessionWasPlayingRef = useRef<boolean>(isPlaying);
  const highSpeedPlaybackStartedAtRef = useRef<number | null>(null);
  const lastHighSpeedBackgroundPrefetchWindowStartRef = useRef<number | null>(
    null,
  );
  const lastHighSpeedWindowTransitionAtMsRef = useRef<number | null>(null);
  const dataGapLeapHoldStartedAtMsRef = useRef<number | null>(null);
  const lastPausedHighSpeedPlaySeedKeyRef = useRef<string>("");

  // S1: IndexedDB tile cache — cache-first fetches, background warm-up
  const tileCache = useEEGTileCache();

  useEffect(() => {
    if (!currentRecordingId) return;
    const speed = currentConfig?.playback_speed ?? 1;
    if (!isPlaying || speed < 16) {
      const shouldDrainHighSpeedBackground = !isPlaying && speed >= 100;
      tileCache.cancelPrefetch(currentRecordingId, {
        reason: !isPlaying
          ? "viewer-not-playing"
          : "viewer-speed-below-prefetch-threshold",
        drainOwnerKeys: shouldDrainHighSpeedBackground
          ? ["play-background", "play-exact"]
          : undefined,
        isPlaying,
        speed,
        currentTimeSeconds: Number(currentTimeRef.current.toFixed(3)),
        dataWindowStartSeconds: Number(dataWindowStartRef.current.toFixed(3)),
      });
    }
  }, [currentConfig?.playback_speed, currentRecordingId, isPlaying, tileCache]);

  useEffect(() => {
    lastHighSpeedBackgroundPrefetchWindowStartRef.current = null;
    lastHighSpeedWindowTransitionAtMsRef.current = null;
    dataGapLeapHoldStartedAtMsRef.current = null;
    lastPausedHighSpeedPlaySeedKeyRef.current = "";
  }, [currentRecordingId]);

  useEffect(() => {
    const speed = currentConfig?.playback_speed ?? 1;
    if (isPlaying && speed >= 200) {
      return;
    }

    lastHighSpeedBackgroundPrefetchWindowStartRef.current = null;
    lastHighSpeedWindowTransitionAtMsRef.current = null;
  }, [currentConfig?.playback_speed, isPlaying]);

  useEffect(() => {
    reviewedTransitionTriggeredRef.current = false;
  }, [studyId, currentRecordingId]);

  useEffect(() => {
    if (!currentRecordingId) {
      setReviewedRanges([]);
      return;
    }
    setReviewedRanges(readReviewedRanges(currentRecordingId, toolbarUserId));
    lastReviewRecordTimeRef.current = 0;
  }, [currentRecordingId, toolbarUserId]);

  const recordReviewedViewport = useCallback(
    (start: number, end: number) => {
      if (!currentRecordingId) return;
      setReviewedRanges((previous) => {
        const merged = mergeReviewedRanges(previous, start, end);
        writeReviewedRanges(currentRecordingId, toolbarUserId, merged);
        return merged;
      });
    },
    [currentRecordingId, toolbarUserId],
  );
  const recordReviewedViewportRef = useRef(recordReviewedViewport);
  recordReviewedViewportRef.current = recordReviewedViewport;

  useEffect(() => {
    if (!studyId || !currentRecordingId || !isPlaying) return;
    if (reviewedTransitionTriggeredRef.current) return;
    if (currentWorkflowStatusCode === "reviewed" || isCurrentWorkflowTerminal) {
      reviewedTransitionTriggeredRef.current = true;
      return;
    }
    if (!canAutoTransitionToReviewed) {
      reviewedTransitionTriggeredRef.current = true;
      return;
    }
    reviewedTransitionTriggeredRef.current = true;

    apiClient
      .post(`/eegstudy/${studyId}/workflow-transition`, {
        toStatusCode: "reviewed",
        reason: "Playback started in EEG viewer",
      })
      .catch((error) => {
        const status = (error as any)?.response?.status;
        const message = String(
          (error as any)?.response?.data?.message ||
            (error as Error)?.message ||
            "",
        ).toLowerCase();
        const benignNoop =
          status === 400 &&
          (message.includes("already") ||
            message.includes("denied by policy") ||
            message.includes("requires") ||
            message.includes("terminal"));
        if (benignNoop) {
          logger.debug(
            "[EEGViewer] Auto workflow transition skipped (non-actionable)",
            {
              studyId,
              currentRecordingId,
              workflowStatus: currentWorkflowStatusCode || "unknown",
              status,
              message,
            },
          );
          return;
        }
        // Keep viewer playback uninterrupted if workflow automation fails.
        logger.warn(
          "[EEGViewer] Failed to transition study to reviewed:",
          error,
        );
      });
  }, [
    studyId,
    currentRecordingId,
    isPlaying,
    currentWorkflowStatusCode,
    isCurrentWorkflowTerminal,
    canAutoTransitionToReviewed,
  ]);

  useEffect(() => {
    apiClient.defaults.headers.common["X-Renderer-Mode"] = rendererMode.mode;
    if (rendererMode.mode === "cpu") {
      apiClient.defaults.headers.common["X-Renderer-Fallback"] =
        rendererMode.fallbackReason;
    } else {
      delete apiClient.defaults.headers.common["X-Renderer-Fallback"];
    }
  }, [rendererMode.fallbackReason, rendererMode.mode]);

  const queuePlaybackPrefetch = useCallback(
    (
      playheadSeconds: number,
      direction: "forward" | "backward",
      speed: number,
    ) => {
      if (!currentRecordingId || activeHighSpeedPlayback) return;

      const playheadMs = Math.max(0, Math.floor(playheadSeconds * 1000));
      const now = Date.now();
      const prefetchKey = `${currentRecordingId}:${Math.floor(playheadSeconds)}:${direction}:${Math.round(speed)}`;

      if (
        prefetchKey === lastPrefetchKeyRef.current &&
        now - lastPrefetchAtRef.current < 800
      ) {
        return;
      }
      if (now - lastPrefetchAtRef.current < 250) {
        return;
      }

      lastPrefetchAtRef.current = now;
      lastPrefetchKeyRef.current = prefetchKey;

      const viewportPx =
        canvasRef.current?.clientWidth ||
        (typeof window !== "undefined" ? window.innerWidth : 1200);

      postEegPlaybackPrefetch(
        currentRecordingId,
        {
          playheadMs,
          speed,
          direction,
          viewportPx,
          channels: selectedChannels.length > 0 ? selectedChannels : undefined,
        },
        { timeout: 3000 },
      ).catch((error) => {
        logger.warn("[EEGViewer] Prefetch request failed:", error);
      });
    },
    [activeHighSpeedPlayback, currentRecordingId, selectedChannels],
  );

  const schedulePlaybackDataWindowStart = useCallback(
    (nextWindowStart: number, speed: number) => {
      const nowMs =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      if (
        !shouldScheduleEEGViewerPlaybackWindowTransition({
          speed,
          nextWindowStart,
          scheduledWindowStart: scheduledWindowStartRef.current,
          lastScheduledAtMs: lastHighSpeedWindowTransitionAtMsRef.current,
          nowMs,
        })
      ) {
        return false;
      }

      scheduledWindowStartRef.current = nextWindowStart;
      if (speed >= 60) {
        lastHighSpeedWindowTransitionAtMsRef.current = nowMs;
      }
      emitPlaybackPerfEvent("playbackWindowScheduled", {
        nextWindowStart: Number(nextWindowStart.toFixed(3)),
        scheduledAtMs: Number(nowMs.toFixed(1)),
        loadedWindowStart: rendererLoadedWindowStartRef.current ?? -1,
        loadedBufferEnd: rendererLoadedBufferEndRef.current ?? -1,
        pendingWindowStart: pendingEegBufferRef.current?.windowStart ?? -1,
        speed,
      });
      // Playback window advancement is on the critical render path. If this
      // update is deferred via startTransition, a fast tile response can resolve
      // before dataWindowStart commits; the data-loading effect then compares the
      // new tile with the stale window and drops it, leaving playback clamped at
      // the previous buffer edge (observed at 00:01:05 on imported L0 EEG).
      setDataWindowStart(nextWindowStart);
      return true;
    },
    [],
  );

  useEffect(() => {
    const prevConfig = currentConfigRef.current;
    const prevSpeed = prevConfig?.playback_speed ?? 1;
    const prevTimeScale = prevConfig?.time_scale ?? 10;
    currentConfigRef.current = currentConfig;
    const newSpeed = currentConfig?.playback_speed ?? 1;
    const newTimeScale = currentConfig?.time_scale ?? 10;
    const loadedWS = rendererLoadedWindowStartRef.current ?? 0;
    const pendingWS = pendingEegBufferRef.current?.windowStart ?? null;
    const playbackContextChanged =
      Math.abs(newSpeed - prevSpeed) > 0.001 ||
      Math.abs(newTimeScale - prevTimeScale) > 0.001;

    if (
      playbackContextChanged &&
      pendingEegBufferRef.current !== null &&
      pendingWS !== null &&
      pendingWS > loadedWS + 0.25
    ) {
      logger.debug(
        "[EEGViewer] Playback context changed; discarding pending future buffer",
        {
          prevSpeed,
          newSpeed,
          prevTimeScale,
          newTimeScale,
          pendingWS,
          loadedWS,
        },
      );
      pendingEegBufferRef.current = null;
      scheduledWindowStartRef.current = loadedWS;
      return;
    }

    // When playback speed decreases, pending buffers from the previous high-speed
    // session have future windowStart values (e.g. ws=700s when playhead is at 60s).
    // The stale-buffer guard in the data-loading effect would discard any new
    // ws=current buffer because 0 < 700.  Clear pendingEegBufferRef and reset
    // scheduledWindowStartRef to the actual loaded window so the chain re-starts
    // from the current playhead position with the new (smaller) buffer size.
    if (newSpeed < prevSpeed * 0.75 && pendingEegBufferRef.current !== null) {
      const pendingWS = pendingEegBufferRef.current.windowStart;
      if (pendingWS > loadedWS + (currentConfig?.time_scale ?? 10) * 4) {
        logger.debug(
          "[EEGViewer] Speed decreased; discarding stale future pending buffer",
          {
            prevSpeed,
            newSpeed,
            pendingWS,
            loadedWS,
          },
        );
        pendingEegBufferRef.current = null;
        scheduledWindowStartRef.current = loadedWS;
      }
    }
  }, [currentConfig]);

  useEffect(() => {
    dataWindowStartRef.current = dataWindowStart;
  }, [dataWindowStart]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    const wasPlaying = playbackPerfSessionWasPlayingRef.current;
    playbackPerfSessionWasPlayingRef.current = isPlaying;
    if (!wasPlaying && isPlaying) {
      playbackPerf.reset();
    }
  }, [isPlaying, playbackPerf]);

  // Keep auto-resolution aligned with the active transport intent.
  // Resuming restores the lightweight overview level; pausing at high speed
  // refines the currently visible window in inspect mode.
  useEffect(() => {
    if (!autoResolution) return;

    const wasPlaying = wasPlayingRef.current;
    wasPlayingRef.current = isPlaying;
    const speed = currentConfig?.playback_speed ?? 1;
    const recommended = getAutoResolutionLevel(speed, playbackTransportMode);
    if (resolutionLevel !== recommended) {
      setResolutionLevel(recommended);
    }

    if (
      wasPlaying &&
      !isPlaying &&
      shouldCreatePausedPlayTransportHold(speed) &&
      pausedHighSpeedInspectHold?.transportMode !== "play"
    ) {
      const timeScale = currentConfig?.time_scale ?? 10;
      const inspectWindowStart =
        pendingPausedInspectWindowStartRef.current ??
        Math.max(
          0,
          Math.min(
            currentTimeRef.current,
            Math.max(0, recordingDurationRef.current - timeScale),
          ),
        );
      pendingPausedInspectWindowStartRef.current = null;
      pendingEegBufferRef.current = null;
      scheduledWindowStartRef.current = inspectWindowStart;
      dataWindowStartRef.current = inspectWindowStart;
      setDataWindowStart(inspectWindowStart);
    }
  }, [
    autoResolution,
    currentConfig?.playback_speed,
    currentConfig?.time_scale,
    getAutoResolutionLevel,
    isPlaying,
    pausedHighSpeedInspectHold?.transportMode,
    playbackTransportMode,
    resolutionLevel,
  ]);

  useEffect(() => {
    if (isPlaying || pausedHighSpeedInspectHold !== null) {
      return;
    }

    const inspectWindowStart = pendingPausedInspectWindowStartRef.current;
    if (inspectWindowStart === null) {
      return;
    }

    pendingPausedInspectWindowStartRef.current = null;
    pendingEegBufferRef.current = null;
    scheduledWindowStartRef.current = inspectWindowStart;
    dataWindowStartRef.current = inspectWindowStart;
    setDataWindowStart(inspectWindowStart);
  }, [isPlaying, pausedHighSpeedInspectHold]);

  useEffect(() => {
    if (isPlaying || pausedHighSpeedInspectHold?.transportMode !== "play") {
      return;
    }

    const loadedWindowStart = rendererLoadedWindowStartRef.current;
    const pendingBuffer = pendingEegBufferRef.current;
    const pendingWindowStart = pendingBuffer?.windowStart ?? null;
    const discardPausedPlayHoldPendingBuffer =
      pendingBuffer !== null &&
      shouldDiscardEEGViewerPausedPlayHoldBuffer({
        isPlaying: false,
        pausedHoldTransportMode: pausedHighSpeedInspectHold.transportMode,
        bufferWindowStart: pendingBuffer.windowStart,
        loadedWindowStart,
        incomingContext: pendingBuffer.requestContext,
      });
    const discardFuturePendingBuffer =
      shouldDiscardEEGViewerFuturePendingBufferOnPause({
        pendingWindowStart,
        loadedWindowStart,
      });
    const resetPausedPlayQueryWindow =
      shouldResetEEGViewerPausedPlayQueryWindow({
        loadedWindowStart,
        scheduledWindowStart: scheduledWindowStartRef.current,
        dataWindowStart,
        pendingWindowStart,
      });

    if (
      !discardPausedPlayHoldPendingBuffer &&
      !discardFuturePendingBuffer &&
      !resetPausedPlayQueryWindow
    ) {
      return;
    }

    recordPauseBoundaryEvent("pause-hold-window-restore", {
      loadedWindowStart,
      pendingWindowStart,
      discardPausedPlayHoldPendingBuffer,
      discardFuturePendingBuffer,
      resetPausedPlayQueryWindow,
    });

    logger.debug(
      "[EEGViewer] Paused play hold restored the currently loaded window",
      {
        loadedWindowStart,
        scheduledWindowStart: scheduledWindowStartRef.current,
        dataWindowStart,
        pendingWindowStart,
        discardPausedPlayHoldPendingBuffer,
        discardFuturePendingBuffer,
        resetPausedPlayQueryWindow,
      },
    );

    if (discardPausedPlayHoldPendingBuffer || discardFuturePendingBuffer) {
      pendingEegBufferRef.current = null;
    }

    if (loadedWindowStart !== null) {
      scheduledWindowStartRef.current = loadedWindowStart;
      dataWindowStartRef.current = loadedWindowStart;
      if (dataWindowStart !== loadedWindowStart) {
        setDataWindowStart(loadedWindowStart);
      }
    }

    if (currentRecordingId) {
      tileCache.cancelPrefetch(currentRecordingId);
    }
  }, [
    currentRecordingId,
    dataWindowStart,
    isPlaying,
    pausedHighSpeedInspectHold?.transportMode,
    recordPauseBoundaryEvent,
    tileCache,
  ]);

  useEffect(() => {
    recordingDurationRef.current = Math.max(
      recordingDurationRef.current,
      recordingDuration,
      recordingMetaDurationRef.current,
    );
  }, [recordingDuration]);

  // Update study context when study details are loaded
  useEffect(() => {
    if (studyDetails && studyDetails.patient) {
      const studyDate = recordingStartIso ? new Date(recordingStartIso) : null;

      // Update the ref immediately so handlePlaybackTick has the correct duration
      // before the low-priority transition below commits to React state.
      // Without this, the transition can be deferred for seconds (while React
      // processes concurrent work), leaving recordingDurationRef at 0 or at the
      // short first-tile estimate (e.g. 36 s).  The advance-check guard
      //   nextTime < currentRecordingDuration - advanceChunk
      // then never fires and the player freezes at bufferEnd - timeScale for the
      // entire first window until the user interaction forces a React flush.
      const metaDurationEager = selectedRecording?.duration_seconds;
      if (metaDurationEager && metaDurationEager > 0) {
        recordingDurationRef.current = Math.max(
          recordingDurationRef.current,
          metaDurationEager,
        );
        // Bug P fix: record the authoritative metadata duration so that
        // tile-derived estimatedTotal values are capped at the real end.
        if (recordingMetaDurationRef.current <= 0) {
          recordingMetaDurationRef.current = metaDurationEager;
        }
      }

      // Wrap non-urgent UI decoration state updates in startTransition so React
      // can yield during the initial 500ms reconciliation burst when study data
      // first arrives. Recording duration and study header data are derived from
      // server state — they don't need to block the critical rendering path.
      startViewerInitTransition(() => {
        const patientIdentity = studyDetails.patient || {};
        // Set recording duration from study metadata (authoritative source).
        // Bug K fix: use Math.max(prev, metaDuration) so that a late-arriving
        // or stale-refetch of studyDetails never lowers recordinDuration below
        // the value already accumulated from live tile data.  A plain assignment
        // could commit inside the same React transition batch as tile-data
        // setRecordingDuration((prev) => Math.max(prev, estimatedTotal)) calls,
        // becoming the last writer and overwriting the larger tile-derived value.
        const metaDuration = selectedRecording?.duration_seconds;
        if (metaDuration && metaDuration > 0) {
          // Bug P fix: update the metadata cap ref here too (transition may
          // commit after the eager path above, but value is the same).
          if (recordingMetaDurationRef.current <= 0) {
            recordingMetaDurationRef.current = metaDuration;
          }
          setRecordingDuration((prev) => Math.max(prev, metaDuration));
          logger.debug(
            `[EEGViewer] Recording duration set from metadata: ${metaDuration} seconds`,
          );
        }

        setStudyData({
          patientName: getPatientDisplayName(patientIdentity as any),
          patientMRN: getPatientRecordLabel(patientIdentity as any),
          patientDateOfBirth: patientIdentity.date_of_birth
            ? new Date(String(patientIdentity.date_of_birth)).toLocaleDateString(
                "en-US",
                {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                },
              )
            : undefined,
          studyId: studyDetails.eeg_study_id || studyId,
          recordingId: headerRecordingLabel,
          recordingTechnicalId: selectedRecording?.recording_id || undefined,
          recordingDate: studyDate
            ? studyDate.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "N/A",
          recordingTime: studyDate
            ? studyDate.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "",
          studyType: studyDetails.eegType?.type_name || "EEG Study",
        });
      });
    }
  }, [
    clearStudyData,
    headerRecordingLabel,
    recordingStartIso,
    selectedRecording?.duration_seconds,
    setStudyData,
    studyDetails,
    studyId,
  ]);

  useEffect(() => {
    return () => {
      // Clear study data only when EEGViewer unmounts to avoid
      // update loops when recording/query references churn.
      clearStudyData();
    };
  }, [clearStudyData]);

  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      try {
        await ensureEegChannelCatalogLoaded();
      } catch {
        // Keep viewer usable with montage-derived electrodes.
      }
      if (cancelled) return;
      setCatalogMontageElectrodes(getEegChannelCodesFromCatalog());
    };

    void loadCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch user configurations
  // Fetch user configurations for HP/LP dropdown catalogs.
  const { data: configurations } = useQuery({
    queryKey: ["eeg-config"],
    queryFn: async () => {
      try {
        const response = await apiClient.get("/eeg-config");
        const payload = response.data;
        if (Array.isArray(payload)) return payload as EEGConfiguration[];
        if (Array.isArray(payload?.data)) return payload.data as EEGConfiguration[];
        return [] as EEGConfiguration[];
      } catch {
        return [] as EEGConfiguration[];
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Fetch montages
  useQuery({
    queryKey: ["eeg-montages"],
    queryFn: async () => {
      const response = await apiClient.get("/eeg-montages");
      return (response.data?.data || response.data || []) as EEGMontage[];
    },
    enabled: false, // Disable for now since endpoint may not exist
    initialData: [],
  });

  // Fetch presets for HP/LP dropdown catalogs (DB-driven clinical values).
  const { data: presets } = useQuery({
    queryKey: ["eeg-presets"],
    queryFn: async () => {
      try {
        const response = await apiClient.get("/eeg-presets");
        const payload = response.data;
        if (Array.isArray(payload)) return payload as EEGPreset[];
        if (Array.isArray(payload?.data)) return payload.data as EEGPreset[];
        return [] as EEGPreset[];
      } catch {
        return [] as EEGPreset[];
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const presetFilterHz = useMemo(
    () => collectPresetFilterHz(presets),
    [presets],
  );
  const dbHighPassHz = useMemo(
    () => [
      ...presetFilterHz.highPass,
      ...(configurations ?? []).map((config) => config.high_pass_filter),
    ],
    [configurations, presetFilterHz.highPass],
  );
  const dbLowPassHz = useMemo(
    () => [
      ...presetFilterHz.lowPass,
      ...(configurations ?? []).map((config) => config.low_pass_filter),
    ],
    [configurations, presetFilterHz.lowPass],
  );

  const playbackSpeedStripRef = useRef<HTMLSpanElement>(null);
  const playbackControlsRef = useRef<HTMLDivElement>(null);
  const lockedTraceMouseNavTimerRef = useRef<number | null>(null);
  const lockedTraceMouseNavDirectionRef = useRef<1 | -1 | null>(null);
  const lockedTraceMouseNavStepRef = useRef(0);
  const lockedTraceMouseNavSuppressClickRef = useRef(false);
  useEffect(() => {
    if (overflowPlaybackSpeedOptions.length > 0) return;
    setShowExtendedPlaybackSpeeds(false);
  }, [overflowPlaybackSpeedOptions.length]);
  useLayoutEffect(() => {
    const controls = playbackControlsRef.current;
    const strip = playbackSpeedStripRef.current;
    if (!controls || !strip) return;

    const estimateFullSpeedStripWidth = () => {
      const buttonWidthPx = 36;
      const zoneSeparatorWidthPx = 9;
      const stripChromePx = 12;
      let zoneBreaks = 0;
      let previousZone: "careful" | "rapid" | "screening" | null = null;
      playbackSpeedButtonOptions.forEach((speed) => {
        const zone =
          speed <= 8 ? "careful" : speed <= 60 ? "rapid" : "screening";
        if (previousZone && previousZone !== zone) {
          zoneBreaks += 1;
        }
        previousZone = zone;
      });
      return (
        playbackSpeedButtonOptions.length * buttonWidthPx +
        zoneBreaks * zoneSeparatorWidthPx +
        stripChromePx
      );
    };

    const updateCollapseState = () => {
      const controlsWidth = controls.clientWidth;
      if (controlsWidth <= 0) return;
      if (controlsWidth >= 1680) {
        setCollapsePlaybackSpeedOptions((current) =>
          current === false ? current : false,
        );
        setShowExtendedPlaybackSpeeds(false);
        return;
      }
      const occupiedByOtherControls =
        controls.scrollWidth - strip.getBoundingClientRect().width;
      const availableForSpeeds = controlsWidth - occupiedByOtherControls - 8;
      const shouldCollapse =
        estimateFullSpeedStripWidth() > Math.max(0, availableForSpeeds);
      setCollapsePlaybackSpeedOptions((current) =>
        current === shouldCollapse ? current : shouldCollapse,
      );
      if (!shouldCollapse) {
        setShowExtendedPlaybackSpeeds(false);
      }
    };

    updateCollapseState();
    const resizeObserver = new ResizeObserver(updateCollapseState);
    resizeObserver.observe(controls);
    resizeObserver.observe(strip);
    window.addEventListener("resize", updateCollapseState);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateCollapseState);
    };
  }, [playbackSpeedButtonOptions]);
  useEffect(() => {
    if (!showExtendedPlaybackSpeeds) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (playbackSpeedStripRef.current?.contains(event.target as Node)) {
        return;
      }
      setShowExtendedPlaybackSpeeds(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [showExtendedPlaybackSpeeds]);

  const { data: waveformColorsResponse } = useQuery({
    queryKey: ["eeg-waveform-colors"],
    queryFn: async () => {
      const response = await apiClient.get("/eeg-waveform-colors");
      const payload = response?.data?.data ?? response?.data;
      return Array.isArray(payload) ? (payload as WaveformColorEntry[]) : [];
    },
    enabled: activeTab === "historical",
    initialData: readCachedWaveformColors,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
  });

  const waveformColors =
    waveformColorsResponse?.length > 0
      ? waveformColorsResponse
      : EEG_STANDARD_WAVEFORM_COLOR_DEFAULTS;
  useEffect(() => {
    if (waveformColorsResponse?.length > 0) {
      writeCachedWaveformColors(waveformColorsResponse as EegWaveformColorEntry[]);
    }
  }, [waveformColorsResponse]);
  useEffect(() => {
    if (typeof window === "undefined") {
      setCustomizationQueryBootstrapReady(false);
      return;
    }

    if (!clinicalScopeId || activeTab !== "historical") {
      setCustomizationQueryBootstrapReady(false);
      return;
    }

    if (activeCustomizationTool) {
      setCustomizationQueryBootstrapReady(true);
      return;
    }

    setCustomizationQueryBootstrapReady(false);

    const enableCustomizationQueries = () => {
      setCustomizationQueryBootstrapReady(true);
    };

    if (typeof window.requestIdleCallback === "function") {
      const idleHandle = window.requestIdleCallback(
        enableCustomizationQueries,
        { timeout: 2500 },
      );

      return () => {
        window.cancelIdleCallback(idleHandle);
      };
    }

    const timeoutHandle = window.setTimeout(enableCustomizationQueries, 1200);
    return () => {
      window.clearTimeout(timeoutHandle);
    };
  }, [activeCustomizationTool, activeTab, clinicalScopeId]);

  const customizationQueriesEnabled =
    !!clinicalScopeId &&
    activeTab === "historical" &&
    (customizationQueryBootstrapReady || activeCustomizationTool !== null);

  const { data: badChannelRows = [], refetch: refetchBadChannels } = useQuery({
    queryKey: badChannelsQueryKey,
    queryFn: async () => {
      if (!clinicalScopeId) return [] as BadChannelMark[];
      try {
        const response = await apiClient.get(
          `/eeg/recordings/${clinicalScopeId}/bad-channels`,
        );
        const payload = response?.data?.data ?? response?.data;
        return Array.isArray(payload) ? (payload as BadChannelMark[]) : [];
      } catch (error) {
        const status = (error as AxiosError)?.response?.status ?? null;
        if (status === 404) {
          logger.warn(
            "[EEGViewer] Bad-channel endpoint unavailable; using empty list.",
            {
              recordingId: clinicalScopeId,
              status,
            },
          );
          return [] as BadChannelMark[];
        }
        throw error;
      }
    },
    enabled: customizationQueriesEnabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const { data: filterOverrideRows = [], refetch: refetchFilterOverrides } =
    useQuery({
      queryKey: filterOverridesQueryKey,
      queryFn: async () => {
        if (!clinicalScopeId) return [] as FilterOverride[];
        try {
          const response = await apiClient.get(
            `/eeg/recordings/${clinicalScopeId}/channel-filters`,
          );
          const payload = response?.data?.data ?? response?.data;
          return Array.isArray(payload) ? (payload as FilterOverride[]) : [];
        } catch (error) {
          const status = (error as AxiosError)?.response?.status ?? null;
          if (status === 404) {
            logger.warn(
              "[EEGViewer] Channel-filter endpoint unavailable; using empty list.",
              {
                recordingId: clinicalScopeId,
                status,
              },
            );
            return [] as FilterOverride[];
          }
          throw error;
        }
      },
      enabled: customizationQueriesEnabled,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    });

  const {
    data: quickReviewTemplates = [],
    isFetching: isFetchingQuickReviewTemplates,
    error: quickReviewTemplatesError,
    refetch: refetchQuickReviewTemplates,
  } = useQuery({
    queryKey: ["eeg-review-templates", "viewer-quick-open"],
    queryFn: async () => {
      const response = await apiClient.get("/eeg/review-templates");
      const payload = response?.data?.data ?? response?.data;
      return Array.isArray(payload) ? (payload as ReviewTemplate[]) : [];
    },
    enabled: showAnnotationQuickOpen,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const {
    data: quickTemplateUsageSummary = null,
    isFetching: isFetchingQuickTemplateUsageSummary,
    error: quickTemplateUsageSummaryError,
    refetch: refetchQuickTemplateUsageSummary,
  } = useQuery({
    queryKey: ["eeg-review-template-usage", "viewer-quick-open"],
    queryFn: async () => {
      const response = await apiClient.get("/eeg/review-templates/usage", {
        params: { days: 30 },
      });
      const payload = response?.data?.data ?? response?.data;
      return payload as ReviewTemplateUsageSummary | null;
    },
    enabled: showAnnotationQuickOpen,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const selectedQuickReviewTemplate = useMemo(
    () =>
      quickReviewTemplates.find(
        (item) => item.template_id === quickSelectedTemplateId,
      ) || null,
    [quickReviewTemplates, quickSelectedTemplateId],
  );

  const selectedQuickTemplateDefinition = useMemo(
    () =>
      selectedQuickReviewTemplate
        ? parseReviewTemplateDefinition(selectedQuickReviewTemplate)
        : null,
    [selectedQuickReviewTemplate],
  );

  const selectedQuickTemplateConfiguredChannels = useMemo(() => {
    if (!selectedQuickReviewTemplate) return [] as string[];
    const config =
      selectedQuickReviewTemplate.config_json &&
      typeof selectedQuickReviewTemplate.config_json === "object"
        ? (selectedQuickReviewTemplate.config_json as Record<string, unknown>)
        : {};
    const channels = config.channels_affected;
    if (!Array.isArray(channels)) return [] as string[];
    return channels
      .filter((value): value is string => typeof value === "string")
      .map((value) => value.trim())
      .filter(Boolean);
  }, [selectedQuickReviewTemplate]);

  useEffect(() => {
    if (!selectedQuickTemplateDefinition) {
      setTemplateFieldValues({});
      setTemplateFieldError(null);
      return;
    }

    const initialValues: Record<string, string> = {};
    if (
      selectedQuickTemplateDefinition.defaultDurationSeconds !== null &&
      Number.isFinite(selectedQuickTemplateDefinition.defaultDurationSeconds)
    ) {
      initialValues.duration_seconds = String(
        selectedQuickTemplateDefinition.defaultDurationSeconds,
      );
    }
    for (const field of selectedQuickTemplateDefinition.structuredFields) {
      if (typeof field.default_value === "string") {
        initialValues[field.key] = field.default_value;
      }
    }
    setTemplateFieldValues(initialValues);
    setTemplateChannelMode("selected");
    setTemplateFieldError(null);
  }, [selectedQuickTemplateDefinition]);

  const {
    data: quickAnnotationDictionaries = [],
    isFetching: isFetchingQuickAnnotationDictionaries,
    error: quickAnnotationDictionariesError,
    refetch: refetchQuickAnnotationDictionaries,
  } = useQuery({
    queryKey: ["eeg-annotation-dictionaries", "viewer-quick-open"],
    queryFn: async () => {
      const response = await apiClient.get("/eeg/annotation-dictionaries");
      const payload = response?.data?.data ?? response?.data;
      return Array.isArray(payload) ? (payload as AnnotationDictionary[]) : [];
    },
    enabled: showAnnotationQuickOpen,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const {
    data: trendProfiles = [],
    isFetching: isFetchingTrendProfiles,
    error: trendProfilesError,
    refetch: refetchTrendProfiles,
  } = useQuery({
    queryKey: ["eeg-trend-threshold-profiles", "viewer"],
    queryFn: async () => {
      const response = await apiClient.get("/eeg/trend-threshold-profiles");
      const payload = response?.data?.data ?? response?.data;
      return Array.isArray(payload) ? (payload as TrendProfile[]) : [];
    },
    enabled:
      startupSecondaryQueriesEnabled && activeTab === "historical",
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
  const selectedTrendProfile = useMemo(
    () =>
      trendProfiles.find((profile) => profile.id === selectedTrendProfileId) ||
      null,
    [selectedTrendProfileId, trendProfiles],
  );

  const thresholdValue = useCallback(
    (key: string): number | null => {
      const raw = selectedTrendProfile?.thresholds_json?.[key];
      const value = Number(raw);
      return Number.isFinite(value) ? value : null;
    },
    [selectedTrendProfile],
  );

  useEffect(() => {
    const lookback = thresholdValue("lookback_seconds");
    const lookahead = thresholdValue("lookahead_seconds");
    const interval = thresholdValue("evaluation_interval_seconds");
    if (lookback !== null) {
      setTrendLookbackSeconds(Math.max(60, Math.floor(lookback)));
    }
    if (lookahead !== null) {
      setTrendLookaheadSeconds(Math.max(60, Math.floor(lookahead)));
    }
    if (interval !== null) {
      setTrendEvalIntervalSeconds(Math.max(5, Math.floor(interval)));
    }
  }, [selectedTrendProfileId, thresholdValue]);

  const {
    data: hourlyTrendSeries = [],
    error: hourlyTrendSeriesError,
    isFetching: isFetchingHourlyTrendSeries,
    refetch: refetchHourlyTrendSeries,
  } = useQuery({
    queryKey: ["eeg-trends-hourly", studyId],
    queryFn: async () => {
      try {
        const trendSeries = await timescaleAPI.getHourlyTrends(studyId, 24);
        return Array.isArray(trendSeries) ? trendSeries : [];
      } catch (error) {
        const status = (error as any)?.response?.status ?? null;
        const code = (error as any)?.response?.data?.code ?? null;
        const message =
          (error as any)?.response?.data?.message ||
          (error as any)?.response?.data?.error ||
          (error as Error)?.message ||
          "unknown_error";
        if (status === 503 && code === "feature_unavailable") {
          setTimescaleFeatureUnavailable(true);
          logger.info(
            "[EEGViewer] Timescale unavailable; disabling trend polling for this study.",
            {
              studyId,
              status,
              code,
            },
          );
          return [] as BackgroundTrendData[];
        }
        logger.warn("[EEGViewer] Hourly trends query failed", {
          studyId,
          activeTab,
          canQueryTimescaleTrends,
          status,
          message,
        });
        return [] as BackgroundTrendData[];
      }
    },
    enabled:
      startupSecondaryQueriesEnabled &&
      activeTab === "historical" &&
      Boolean(studyId) &&
      canQueryTimescaleTrends,
    retry: false,
    staleTime: 15000,
    refetchInterval: trendEvalActive ? 15000 : false,
  });

  const {
    data: realtimeTrendSeries = [],
    error: realtimeTrendSeriesError,
    isFetching: isFetchingRealtimeTrendSeries,
    refetch: refetchRealtimeTrendSeries,
  } = useQuery({
    queryKey: ["eeg-trends-realtime", studyId],
    queryFn: async () => {
      try {
        const trendSeries = await timescaleAPI.getRealtimeTrends(studyId, 120);
        return Array.isArray(trendSeries) ? trendSeries : [];
      } catch (error) {
        const status = (error as any)?.response?.status ?? null;
        const code = (error as any)?.response?.data?.code ?? null;
        const message =
          (error as any)?.response?.data?.message ||
          (error as any)?.response?.data?.error ||
          (error as Error)?.message ||
          "unknown_error";
        if (status === 503 && code === "feature_unavailable") {
          setTimescaleFeatureUnavailable(true);
          logger.info(
            "[EEGViewer] Timescale unavailable; disabling realtime trend polling for this study.",
            {
              studyId,
              status,
              code,
            },
          );
          return [] as BackgroundTrendData[];
        }
        logger.warn("[EEGViewer] Realtime trends query failed", {
          studyId,
          activeTab,
          canQueryTimescaleTrends,
          status,
          message,
        });
        return [] as BackgroundTrendData[];
      }
    },
    enabled:
      startupSecondaryQueriesEnabled &&
      activeTab === "historical" &&
      Boolean(studyId) &&
      canQueryTimescaleTrends,
    retry: false,
    staleTime: 10000,
    refetchInterval: trendEvalActive ? 10000 : false,
  });

  const {
    data: dualMontagePreferenceResponse,
    refetch: refetchDualMontagePreference,
  } = useQuery({
    queryKey: ["eeg-dual-montage-preference", "viewer"],
    queryFn: async () => {
      const response = await apiClient.get("/eeg/dual-montage-preference");
      return (response?.data?.data ??
        response?.data ??
        null) as Partial<DualMontagePreference> | null;
    },
    enabled:
      startupSecondaryQueriesEnabled && activeTab === "historical",
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const normalizedProfileChannelColorMap = useMemo(() => {
    const map = new Map<string, string>();
    Object.entries(profileChannelColors).forEach(([channelPair, color]) => {
      const normalizedKey = normalizeChannelPairKey(channelPair);
      const normalizedColor = normalizeHexColor(color);
      if (!normalizedKey || !normalizedColor) return;
      map.set(normalizedKey, normalizedColor);
    });
    return map;
  }, [profileChannelColors]);

  const temporaryTraceHighlightMap = useMemo(() => {
    const map = new Map<string, string>();
    Object.entries(temporaryTraceHighlights).forEach(([channelPair, color]) => {
      const normalizedKey = normalizeChannelPairKey(channelPair);
      const normalizedColor = normalizeHexColor(color);
      if (!normalizedKey || !normalizedColor) return;
      map.set(normalizedKey, normalizedColor);
    });
    return map;
  }, [temporaryTraceHighlights]);

  const activeBadChannelMap = useMemo(() => {
    const map = new Map<string, BadChannelMark>();
    badChannelRows.forEach((row) => {
      const normalizedKey = normalizeChannelPairKey(row.channel_name);
      if (!normalizedKey) return;
      if (!map.has(normalizedKey)) {
        map.set(normalizedKey, row);
      }
    });
    return map;
  }, [badChannelRows]);

  const activeFilterOverrideMap = useMemo(() => {
    const map = new Map<string, FilterOverride>();
    filterOverrideRows.forEach((row) => {
      const normalizedKey = normalizeChannelPairKey(row.channel_name);
      if (!normalizedKey) return;
      map.set(normalizedKey, row);
    });
    return map;
  }, [filterOverrideRows]);

  const defaultWaveformColorByPair = useMemo(() => {
    const globalMap = new Map<string, string>();
    const montageMapById = new Map<string, Map<string, string>>();

    waveformColors.forEach((entry) => {
      const normalizedKey = normalizeChannelPairKey(entry.channel_pair || "");
      const normalizedColor = normalizeHexColor(entry.color_hex);
      if (!normalizedKey || !normalizedColor) return;

      if (entry.montage_id) {
        if (!montageMapById.has(entry.montage_id)) {
          montageMapById.set(entry.montage_id, new Map<string, string>());
        }
        montageMapById
          .get(entry.montage_id)!
          .set(normalizedKey, normalizedColor);
        return;
      }

      if (!globalMap.has(normalizedKey)) {
        globalMap.set(normalizedKey, normalizedColor);
      }
    });

    return {
      globalMap,
      montageMapById,
    };
  }, [waveformColors]);

  // Maps a normalized electrode name (e.g. "fp1") to a hex color.
  // Fully DB-driven: electrode colors are stored in eeg_waveform_color with montage_id = null.
  // Pass 1: explicit single-electrode entries (no dash in channel_pair) — direct lookup.
  // Pass 2: infer from LB pair entries (positive electrode = left of dash) for legacy coverage.
  const electrodeColorMap = useMemo(() => {
    const map = new Map<string, string>();
    // Pass 1: explicit global electrode entries (e.g. channel_pair = "Fp1").
    waveformColors.forEach((entry) => {
      if (entry.montage_id) return;
      const key = normalizeChannelPairKey(entry.channel_pair || "");
      if (!key || key.includes("-")) return;
      const color = normalizeHexColor(entry.color_hex);
      if (color && !map.has(key)) map.set(key, color);
    });
    // Pass 2: derive positive electrode from global pair entries for any not yet mapped.
    waveformColors.forEach((entry) => {
      if (entry.montage_id) return;
      const pairKey = normalizeChannelPairKey(entry.channel_pair || "");
      const dashIdx = pairKey.indexOf("-");
      if (dashIdx <= 0) return;
      const electrode = pairKey.slice(0, dashIdx);
      if (!electrode) return;
      const color = normalizeHexColor(entry.color_hex);
      if (color && !map.has(electrode)) map.set(electrode, color);
    });
    return map;
  }, [waveformColors]);

  const currentMontage = useMemo(
    () =>
      resolveEffectiveViewerMontage(
        montages,
        currentConfig?.montage_id,
        loadedRecordingChannelNames,
        recordingChannelMapAliasMaps,
      ),
    [
      currentConfig?.montage_id,
      loadedRecordingChannelNames,
      montages,
      recordingChannelMapAliasMaps,
    ],
  );

  useEffect(() => {
    const resolved = resolveEffectiveViewerMontage(
      montages,
      currentConfig?.montage_id,
      loadedRecordingChannelNames,
      recordingChannelMapAliasMaps,
    );
    if (!resolved || !currentConfig) return;
    // Keep the select on "" for No Derivation so passthrough DB ids (filtered
    // out of the option list) do not leave the control on a phantom value.
    const nextMontageId = isPassthroughViewerMontage(resolved)
      ? ""
      : resolved.montage_id || "";
    if ((currentConfig.montage_id ?? "") === nextMontageId) return;
    setCurrentConfig((previous) =>
      previous ? { ...previous, montage_id: nextMontageId } : previous,
    );
  }, [
    currentConfig?.montage_id,
    loadedRecordingChannelNames,
    montages,
    recordingChannelMapAliasMaps,
  ]);

  // EEG-CUX-007: when the user switches to a montage variant, auto-apply its excluded channels
  useEffect(() => {
    if (
      !currentMontage?.is_variant ||
      !Array.isArray(currentMontage.excluded_channels)
    )
      return;
    setHiddenChannels(new Set(currentMontage.excluded_channels));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMontage?.variant_id]);

  const mergedWaveformColorByPair = useMemo(() => {
    const globalMap = new Map(defaultWaveformColorByPair.globalMap);
    const montageMapById = new Map<string, Map<string, string>>();

    defaultWaveformColorByPair.montageMapById.forEach((map, montageId) => {
      montageMapById.set(montageId, new Map(map));
    });

    // A1: merge backend waveform color defaults with display-profile channel_colors.
    // Profile colors always override backend defaults for the active montage context.
    normalizedProfileChannelColorMap.forEach((color, normalizedKey) => {
      if (currentMontage?.montage_id) {
        if (!montageMapById.has(currentMontage.montage_id)) {
          montageMapById.set(currentMontage.montage_id, new Map());
        }
        montageMapById
          .get(currentMontage.montage_id)!
          .set(normalizedKey, color);
        return;
      }

      globalMap.set(normalizedKey, color);
    });

    return { globalMap, montageMapById };
  }, [
    currentMontage?.montage_id,
    defaultWaveformColorByPair,
    normalizedProfileChannelColorMap,
  ]);

  // Stable refs so the color-resolve callbacks below can read current map
  // values without being in their dep arrays. This breaks the render-loop
  // chain: badChannelRows query → new Map → new callback → MontageEventBus
  // effect teardown/re-subscribe on every render during viewer load.
  const activeBadChannelMapRef = useRef(activeBadChannelMap);
  useEffect(() => {
    activeBadChannelMapRef.current = activeBadChannelMap;
  }, [activeBadChannelMap]);
  const activeFilterOverrideMapRef = useRef(activeFilterOverrideMap);
  useEffect(() => {
    activeFilterOverrideMapRef.current = activeFilterOverrideMap;
  }, [activeFilterOverrideMap]);
  const temporaryTraceHighlightMapRef = useRef(temporaryTraceHighlightMap);
  useEffect(() => {
    temporaryTraceHighlightMapRef.current = temporaryTraceHighlightMap;
  }, [temporaryTraceHighlightMap]);
  const mergedWaveformColorByPairRef = useRef(mergedWaveformColorByPair);
  useEffect(() => {
    mergedWaveformColorByPairRef.current = mergedWaveformColorByPair;
  }, [mergedWaveformColorByPair]);
  const electrodeColorMapRef = useRef(electrodeColorMap);
  useEffect(() => {
    electrodeColorMapRef.current = electrodeColorMap;
  }, [electrodeColorMap]);

  const resolveChannelColor = useCallback(
    (channelPair: string, montageId?: string) => {
      const normalizedKey = normalizeChannelPairKey(channelPair);
      if (!normalizedKey) return null;

      const temporaryHighlightColor =
        temporaryTraceHighlightMapRef.current.get(normalizedKey);
      if (temporaryHighlightColor) return temporaryHighlightColor;

      if (activeBadChannelMapRef.current.has(normalizedKey)) {
        return TRACE_BAD_CHANNEL_COLOR;
      }
      if (activeFilterOverrideMapRef.current.has(normalizedKey)) {
        return TRACE_FILTER_OVERRIDE_COLOR;
      }

      if (montageId) {
        const byMontage =
          mergedWaveformColorByPairRef.current.montageMapById.get(montageId);
        const montageColor = byMontage?.get(normalizedKey);
        if (montageColor) return montageColor;
      }

      const globalColor =
        mergedWaveformColorByPairRef.current.globalMap.get(normalizedKey);
      if (globalColor) return globalColor;

      // Positive-electrode fallback: for referential / average-reference montages whose
      // channel labels (e.g. "Fp1-AVG", "C3-AVG") do not match any channel_pair row in the
      // database, derive the color from the positive electrode part of the label so that
      // per-channel coloring works irrespective of montage type.
      const dashIdx = normalizedKey.indexOf("-");
      const positiveElectrode =
        dashIdx > 0 ? normalizedKey.slice(0, dashIdx) : normalizedKey;
      return electrodeColorMapRef.current.get(positiveElectrode) || null;
    },
    [], // stable – reads from refs at call time
  );

  /**
   * Returns a color ONLY for channels that have a true per-channel override:
   * bad-channel tint, temporary highlight, or filter-override tint.
   * Deliberately excludes DB / profile waveform-color defaults so that
   * uniform color-mode keeps hasPerChannelColors=false and the 2-stroke
   * batch render path stays active (~2 ms/frame vs ~57 ms/frame).
   */
  const resolveChannelOverrideColor = useCallback(
    (channelPair: string): string | null => {
      const normalizedKey = normalizeChannelPairKey(channelPair);
      if (!normalizedKey) return null;
      const highlight =
        temporaryTraceHighlightMapRef.current.get(normalizedKey);
      if (highlight) return highlight;
      if (activeBadChannelMapRef.current.has(normalizedKey))
        return TRACE_BAD_CHANNEL_COLOR;
      if (activeFilterOverrideMapRef.current.has(normalizedKey))
        return TRACE_FILTER_OVERRIDE_COLOR;
      return null;
    },
    [], // stable – reads from refs at call time
  );

  const workspaceTheme = useMemo(
    () =>
      resolveEegWorkspaceTheme({
        profileTheme,
        accessibilityHighContrast:
          settings.high_contrast || preferences.accessibility.highContrast,
      }),
    [
      preferences.accessibility.highContrast,
      profileTheme,
      settings.high_contrast,
    ],
  );
  const workspaceDensity = eegVisualToWorkspaceDensity(
    profileGridDensity,
    profileChannelSpacing,
  );

  const setProfileThemeFromUser: typeof setProfileTheme = useCallback(
    (value) => {
      eegThemeFollowsPortalRef.current = false;
      setProfileTheme(value);
    },
    [],
  );
  const setProfileGridDensityFromUser: typeof setProfileGridDensity =
    useCallback((value) => {
      eegDensityFollowsPortalRef.current = false;
      setProfileGridDensity(value);
    }, []);
  const setProfileChannelSpacingFromUser: typeof setProfileChannelSpacing =
    useCallback((value) => {
      eegDensityFollowsPortalRef.current = false;
      setProfileChannelSpacing(value);
    }, []);

  useEffect(() => {
    if (!eegThemeFollowsPortalRef.current) return;
    setProfileTheme(
      portalThemeToEegPaperTheme(settings.theme, settings.high_contrast),
    );
  }, [settings.high_contrast, settings.theme]);

  useEffect(() => {
    if (!eegDensityFollowsPortalRef.current) return;
    const visual = workspaceDensityToEegVisual(
      parsePortalWorkspaceDensity(settings.workspace_density),
    );
    setProfileGridDensity(visual.gridDensity);
    setProfileChannelSpacing(visual.channelSpacing);
  }, [settings.workspace_density]);

  const themeWaveformColor = useMemo(
    () => RENDER_THEME_MAP[workspaceTheme].waveformColor,
    [workspaceTheme],
  );

  const displayProfileExtractors = useMemo(
    () => createDisplayProfileConfigExtractors(displayProfilePolicy),
    [displayProfilePolicy],
  );

  useEffect(() => {
    if (dualMontageDefaultsAppliedRef.current) return;
    if (!dualMontagePreferenceResponse) return;
    dualMontageDefaultsAppliedRef.current = true;
    setDualMontagePreference(
      displayProfileExtractors.extractDualMontagePreferenceFromConfig({
        dual_montage_preference: dualMontagePreferenceResponse,
      }),
    );
  }, [displayProfileExtractors, dualMontagePreferenceResponse]);

  const { applyDisplayProfileToSession } = useEEGDisplayProfileSession({
    displayProfilePolicy,
    selectedDisplayProfile,
    currentConfig,
    profileTheme,
    profileGridDensity,
    profileChannelColors,
    profileWaveformThickness,
    profileChannelSpacing,
    profileLabelFontSize,
    profileChannelPositionOffsets,
    waveformDisplayMode,
    allowTraceOverflow,
    refitOnChannelChange,
    selectedTrendProfileId,
    dualMontagePreference,
    dockBottomHeightPx,
    dockRightWidthPx,
    annotationPanelWidthPx,
    dockBottomCollapsed,
    dockRightCollapsed,
    dockBottomActiveTab,
    dockPanelVisibility,
    dockPanelOrder,
    crosshairEnabled,
    showGrid,
    showChannelGroupLabels,
    showScaleMarkers,
    hiddenChannels,
    buildDisplayProfileConfigRef,
    extractors: displayProfileExtractors,
    setCurrentConfig: setCurrentConfig as React.Dispatch<
      React.SetStateAction<ViewerConfiguration | null>
    >,
    setProfileTheme: setProfileThemeFromUser,
    setProfileGridDensity: setProfileGridDensityFromUser,
    setProfileChannelColors,
    setProfileWaveformThickness,
    setProfileChannelSpacing: setProfileChannelSpacingFromUser,
    setProfileLabelFontSize,
    setProfileChannelPositionOffsets,
    setWaveformDisplayMode,
    setAllowTraceOverflow,
    setRefitOnChannelChange,
    setSelectedTrendProfileId,
    setDualMontagePreference,
    setDockBottomHeightPx,
    setDockRightWidthPx,
    setAnnotationPanelWidthPx,
    setDockBottomCollapsed,
    setDockRightCollapsed,
    setDockBottomActiveTab,
    setDockPanelVisibility,
    setDockPanelOrder,
    setCrosshairEnabled,
    setShowGrid,
    setShowChannelGroupLabels,
    setShowScaleMarkers,
    setHiddenChannels,
    rendererRef,
    syncRendererCanvas,
    mergeNotchFlags,
    normalizeViewerConfig: normalizeViewerConfig as (
      config: ViewerConfiguration,
    ) => ViewerConfiguration,
  });

  const getMontageChannelLabel = useCallback(
    (pair: { positive: string; negative?: string }) =>
      pair.negative ? `${pair.positive}-${pair.negative}` : pair.positive,
    [],
  );

  const montageAvailableElectrodesRef = useRef<string[]>([]);
  const selectedMontagePairIndexesRef = useRef<number[]>([]);
  const setSelectedTraceChannelsRef = useRef<
    ((labels: string[]) => void) | null
  >(null);
  const lastTraceSelectionRef = useRef<string | null>(null);

  const {
    viewerMontagePairsById,
    viewerMontagePairsDraft,
    viewerMontageDraftDirty,
    viewerMontageStatus,
    viewerMontageError,
    viewerMontageConflict,
    viewerMontageConflictRemote,
    isSavingViewerMontage,
    editingMontagePairIndex,
    setEditingMontagePairIndex,
    showMontageMobileSheet,
    setShowMontageMobileSheet,
    montageNotice,
    announceMontageChange,
    handleInsertViewerMontagePair,
    handleDuplicateViewerMontagePair,
    handleMoveViewerMontagePair,
    handleDeleteViewerMontagePairs,
    handleOpenViewerMontagePairEditor,
    handleSaveViewerMontagePairEdit,
    handleSaveViewerMontageDraft,
    handleOverwriteViewerMontageDraft,
    handleReloadViewerMontageConflictRemote,
  } = useEEGViewerMontageDraft({
    currentMontage,
    montageAvailableElectrodesRef,
    selectedMontagePairIndexesRef,
    setSelectedTraceChannelsRef,
    lastTraceSelectionRef,
    getMontageChannelLabel,
    queryClient,
  });

  // Tell the clinician when a 10-20 montage cannot bind (e.g. ChN-Ref BDF) and
  // the select is forced back to No Derivation. When a channel-map profile is
  // applied, Longitudinal should bind via aliases — skip this notice.
  const lastMontageFallbackNoticeKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const requestedId = (currentConfig?.montage_id ?? "").trim();
    if (!requestedId || loadedRecordingChannelNames.length === 0) return;
    const requested = montages?.find(
      (montage) => montage.montage_id === requestedId,
    );
    if (!requested || isPassthroughViewerMontage(requested)) return;
    if (
      montagePairsMatchRecordingChannels(
        requested.channel_pairs,
        loadedRecordingChannelNames,
        recordingChannelMapAliasMaps,
      )
    ) {
      return;
    }
    const noticeKey = `${requestedId}:${loadedRecordingChannelNames.length}`;
    if (lastMontageFallbackNoticeKeyRef.current === noticeKey) return;
    lastMontageFallbackNoticeKeyRef.current = noticeKey;
    announceMontageChange(
      "No Derivation",
      `${requested.montage_name || "Selected montage"} needs 10-20 electrode labels; this recording uses generic channel names. Showing No Derivation (raw).`,
    );
  }, [
    announceMontageChange,
    currentConfig?.montage_id,
    loadedRecordingChannelNames,
    montages,
    recordingChannelMapAliasMaps,
  ]);

  const activeMontageDraftPairs = useMemo(() => {
    const montageId = currentMontage?.montage_id;
    if (!montageId) return null;
    if (
      !Object.prototype.hasOwnProperty.call(viewerMontagePairsById, montageId)
    ) {
      return null;
    }
    return viewerMontagePairsById[montageId] || [];
  }, [currentMontage?.montage_id, viewerMontagePairsById]);

  const effectiveMontagePairs = useMemo(() => {
    const passthroughPairs = buildPassthroughMontagePairs(
      loadedRecordingChannelNames,
    ).map((pair, index) => normalizeMontageDraftPair(pair, index));
    if (!currentMontage) return passthroughPairs;
    const sourcePairs = activeMontageDraftPairs
      ? activeMontageDraftPairs
      : (currentMontage.channel_pairs || []).map((pair, index) =>
          normalizeMontageDraftPair(pair, index),
        );
    if (
      loadedRecordingChannelNames.length > 0 &&
      !montagePairsMatchRecordingChannels(
        sourcePairs,
        loadedRecordingChannelNames,
        recordingChannelMapAliasMaps,
      )
    ) {
      return passthroughPairs;
    }
    if (sourcePairs.length === 0) return passthroughPairs;
    return sourcePairs;
  }, [
    activeMontageDraftPairs,
    currentMontage,
    loadedRecordingChannelNames,
    recordingChannelMapAliasMaps,
  ]);

  // EEG-CUX-002: effectiveMontagePairs filtered to exclude hidden channels — used for renderer setMontage
  const visibleMontagePairs = useMemo(() => {
    if (hiddenChannels.size === 0) return effectiveMontagePairs;
    return effectiveMontagePairs.filter(
      (pair) => !hiddenChannels.has(getMontageChannelLabel(pair).trim()),
    );
  }, [effectiveMontagePairs, hiddenChannels, getMontageChannelLabel]);

  const effectiveMontageChannelLabels = useMemo(() => {
    const labels = new Set<string>();
    effectiveMontagePairs.forEach((pair) => {
      const label = getMontageChannelLabel(pair).trim();
      if (label) labels.add(label);
    });
    return Array.from(labels);
  }, [effectiveMontagePairs, getMontageChannelLabel]);

  const montageChannelDetails = useMemo(() => {
    if (!effectiveMontagePairs.length || !currentMontage?.montage_id) {
      return [] as Array<{
        label: string;
        index: number;
        positive: string;
        negative: string;
        color: string;
      }>;
    }
    return effectiveMontagePairs.map((pair, index) => {
      const label = getMontageChannelLabel(pair);
      return {
        label,
        index,
        positive: pair.positive,
        negative: pair.negative || "",
        color:
          resolveChannelColor(label, currentMontage.montage_id) ||
          themeWaveformColor,
      };
    });
  }, [
    currentMontage?.montage_id,
    effectiveMontagePairs,
    getMontageChannelLabel,
    mergedWaveformColorByPair,
    resolveChannelColor,
    themeWaveformColor,
  ]);

  // EEG-CUX-002: montageChannelDetails filtered to only visible (non-hidden) channels
  const visibleMontageChannelDetails = useMemo(
    () =>
      hiddenChannels.size === 0
        ? montageChannelDetails
        : montageChannelDetails.filter((ch) => !hiddenChannels.has(ch.label)),
    [montageChannelDetails, hiddenChannels],
  );

  const totalMontageChannels = visibleMontageChannelDetails.length;
  const allMontageChannelLabels = useMemo(
    () => montageChannelDetails.map((channel) => channel.label),
    [montageChannelDetails],
  );

  const {
    selectedTraceChannels,
    setSelectedTraceChannels,
    lockedTraceFocusLabel,
    setLockedTraceFocusLabel,
    channelRegionSelection,
    setChannelRegionSelection,
    channelQuickJumpInput,
    setChannelQuickJumpInput,
    traceFilterHp,
    setTraceFilterHp,
    traceFilterLp,
    setTraceFilterLp,
    traceFilterNotch,
    setTraceFilterNotch,
    traceHighlightColor,
    setTraceHighlightColor,
    selectedTraceActionStatus,
    setSelectedTraceActionStatus,
    selectedTraceActionError,
    setSelectedTraceActionError,
    isApplyingSelectedTraceAction,
    traceSelectionUndoStack,
    channelScrollOffset,
    setChannelScrollOffset,
    visibleChannelCount,
    setVisibleChannelCount,
    channelGainOverrides,
    setChannelGainOverrides,
    channelGainMenu,
    setChannelGainMenu,
    expandedSpacingChannelLabel,
    setExpandedSpacingChannelLabel,
    channelGainMenuRef,
    selectedTraceActionTargets,
    maxChannelScrollOffset,
    handleWaveformCanvasChannelSelection,
    estimateChannelAutoScaleGain,
    hideChannel,
    isolateChannel,
    showAllChannels,
    isolateChannels,
    applyChannelGainOverride,
    closeChannelGainMenu,
    toggleExpandedSpacingForLabel,
    handleChannelGainPresetSelect,
    handleChannelAutoScaleSelect,
    handleChannelHideSelect,
    handleChannelIsolateSelect,
    handleChannelShowAllSelect,
    handleChannelExpandedSpacingSelect,
    handleResetExpandedSpacingSelect,
    handleToggleTraceFocusLockSelect,
    clearTraceFocusLock,
    handleSelectedChannelSpacingToggle,
    handleResetAllGainsSelect,
    handleResetChannelAppearanceSelect,
    handleResetChannelGainSelect,
    pushTraceSelectionUndoEntry,
    applyBulkTraceSelection,
    handleSelectAllTraceChannels,
    handleClearAllTraceChannels,
    handleSelectVisibleTraceChannels,
    handleInvertTraceChannelSelection,
    handleSelectTraceChannelsByRegion,
    handleQuickJumpToChannelLabel,
    handleResetAllPerChannelOverrides,
    parseTraceFilterValues,
    handleMarkSelectedTraceChannelsBad,
    handleApplyFilterOverrideToSelectedTraces,
    handleRemoveFilterOverrideFromSelectedTraces,
    applyTemporaryHighlightToSelectedTraces,
    clearTemporaryHighlightFromSelectedTraces,
    handleUndoLastSelectedTraceAction,
  } = useEEGTraceChannelSelection({
    montageChannelDetails,
    visibleMontageChannelDetails,
    allMontageChannelLabels,
    clinicalScopeId,
    queryClient,
    badChannelsQueryKey,
    filterOverridesQueryKey,
    refetchBadChannels,
    refetchFilterOverrides,
    badChannelRows,
    filterOverrideRows,
    rendererRef,
    canvasRef,
    eegDataRef,
    currentTimeRef,
    dataWindowStart,
    currentConfig,
    profileChannelPositionOffsets,
    profileChannelColors,
    setProfileChannelPositionOffsets,
    setProfileChannelColors,
    focusedSpacingSessionStorageKey,
    dialog,
    hiddenChannels,
    setHiddenChannels,
    temporaryTraceHighlights,
    setTemporaryTraceHighlights,
    lastTraceSelectionRef,
  });
  setSelectedTraceChannelsRef.current = setSelectedTraceChannels;
  const visibleChannelStart = useMemo(
    () =>
      totalMontageChannels === 0
        ? 0
        : clampNumber(channelScrollOffset + 1, 1, totalMontageChannels),
    [channelScrollOffset, totalMontageChannels],
  );
  const visibleChannelEnd = useMemo(
    () =>
      totalMontageChannels === 0
        ? 0
        : clampNumber(
            channelScrollOffset + Math.max(1, visibleChannelCount),
            1,
            totalMontageChannels,
          ),
    [channelScrollOffset, totalMontageChannels, visibleChannelCount],
  );
  const visibleAutoFitMontageChannelDetails = useMemo(
    () =>
      visibleMontageChannelDetails.slice(
        channelScrollOffset,
        channelScrollOffset + Math.max(1, visibleChannelCount),
      ),
    [channelScrollOffset, visibleMontageChannelDetails, visibleChannelCount],
  );
  const visibleChannelRefitKey = useMemo(
    () => getVisibleChannelRefitKey(visibleAutoFitMontageChannelDetails),
    [visibleAutoFitMontageChannelDetails],
  );
  const rowHeightMode = useMemo(
    () => getRowHeightModeFromChannelSpacing(profileChannelSpacing),
    [profileChannelSpacing],
  );
  const channelScrollThumbHeightPercent = useMemo(() => {
    if (totalMontageChannels <= 0) return 100;
    return clampNumber(
      (Math.max(1, visibleChannelCount) / totalMontageChannels) * 100,
      8,
      100,
    );
  }, [totalMontageChannels, visibleChannelCount]);
  const channelScrollThumbTopPercent = useMemo(() => {
    if (maxChannelScrollOffset <= 0) return 0;
    return clampNumber(
      (channelScrollOffset / maxChannelScrollOffset) *
        (100 - channelScrollThumbHeightPercent),
      0,
      100 - channelScrollThumbHeightPercent,
    );
  }, [
    channelScrollOffset,
    channelScrollThumbHeightPercent,
    maxChannelScrollOffset,
  ]);
  const hasChannelOverflow = maxChannelScrollOffset > 0;

  useEffect(() => {
    setChannelScrollOffset((current) =>
      Math.min(current, maxChannelScrollOffset),
    );
  }, [maxChannelScrollOffset]);

  useEffect(() => {
    setChannelScrollOffset(0);
    setHiddenChannels(new Set()); // EEG-CUX-002: clear hidden channels when montage changes
  }, [currentConfig?.montage_id]);

  const editableMontagePair = useMemo(() => {
    if (editingMontagePairIndex === null || editingMontagePairIndex < 0) {
      return null;
    }
    if (editingMontagePairIndex >= effectiveMontagePairs.length) {
      return null;
    }
    const pair = effectiveMontagePairs[editingMontagePairIndex];
    const displayLabel = pair.label || defaultMontagePairLabel(pair);
    return {
      id: String(editingMontagePairIndex),
      label: displayLabel,
      positiveElectrode: pair.positive,
      negativeElectrode: pair.negative,
      color:
        normalizeHexColor(pair.color) ||
        resolveChannelColor(
          getMontageChannelLabel(pair),
          currentMontage?.montage_id || undefined,
        ) ||
        themeWaveformColor,
    } satisfies EditableChannelPair;
  }, [
    currentMontage?.montage_id,
    editingMontagePairIndex,
    effectiveMontagePairs,
    getMontageChannelLabel,
    resolveChannelColor,
    themeWaveformColor,
  ]);

  const montageAvailableElectrodes = useMemo(() => {
    const set = new Set<string>(catalogMontageElectrodes);
    effectiveMontagePairs.forEach((pair) => {
      if (pair.positive?.trim()) set.add(pair.positive.trim());
      if (pair.negative?.trim()) set.add(pair.negative.trim());
    });
    return Array.from(set).filter(Boolean);
  }, [catalogMontageElectrodes, effectiveMontagePairs]);
  montageAvailableElectrodesRef.current = montageAvailableElectrodes;

  const selectedTraceChannelDetails = useMemo(
    () =>
      montageChannelDetails
        .filter((channel) => selectedTraceChannels.includes(channel.label))
        .map((channel) => {
          const normalizedKey = normalizeChannelPairKey(channel.label);
          const filterOverride = normalizedKey
            ? activeFilterOverrideMap.get(normalizedKey) || null
            : null;
          const badChannel = normalizedKey
            ? activeBadChannelMap.get(normalizedKey) || null
            : null;
          const temporaryHighlightColor = normalizedKey
            ? temporaryTraceHighlightMap.get(normalizedKey) || null
            : null;
          const gainOverride = Object.prototype.hasOwnProperty.call(
            channelGainOverrides,
            channel.label,
          )
            ? channelGainOverrides[channel.label]
            : null;

          return {
            ...channel,
            badChannel,
            filterOverride,
            gainOverride,
            temporaryHighlightColor,
          };
        }),
    [
      activeBadChannelMap,
      activeFilterOverrideMap,
      channelGainOverrides,
      montageChannelDetails,
      selectedTraceChannels,
      temporaryTraceHighlightMap,
    ],
  );

  const focusedSpacingChannelOffsets = useMemo(() => {
    if (!expandedSpacingChannelLabel) {
      return {} as Record<string, number>;
    }

    const visibleLabels = visibleMontageChannelDetails.map(
      (channel) => channel.label,
    );
    const focusIndex = visibleLabels.indexOf(expandedSpacingChannelLabel);
    if (focusIndex < 0) {
      return {} as Record<string, number>;
    }

    const halfGap = FOCUSED_CHANNEL_SPACING_GAP_UNITS / 2;
    return visibleLabels.reduce<Record<string, number>>(
      (result, label, index) => {
        if (index < focusIndex) {
          result[label] = -halfGap;
        } else if (index > focusIndex) {
          result[label] = halfGap;
        }
        return result;
      },
      {},
    );
  }, [expandedSpacingChannelLabel, visibleMontageChannelDetails]);

  const effectiveChannelPositionOffsets = useMemo(() => {
    const spacingEntries = Object.entries(focusedSpacingChannelOffsets);
    if (spacingEntries.length === 0) {
      return profileChannelPositionOffsets;
    }

    const merged = { ...profileChannelPositionOffsets };
    spacingEntries.forEach(([label, deltaUnits]) => {
      const nextValue = clampNumber((merged[label] ?? 0) + deltaUnits, -2, 2);
      if (Math.abs(nextValue) < 0.0001) {
        delete merged[label];
        return;
      }
      merged[label] = nextValue;
    });

    return merged;
  }, [focusedSpacingChannelOffsets, profileChannelPositionOffsets]);

  const transformRendererChannelName = useCallback((name: string) => {
    // Avoid double-prefixing when raw channel names (e.g. "EEG FP1") are
    // passed directly — as happens with the No Derivation passthrough.
    const upper = name.toUpperCase();
    if (/^(EEG|ECG|EMG|EOG)\s/.test(upper)) return upper;
    return `EEG ${upper}`;
  }, []);

  const resolveRendererMontageInputName = useCallback(
    (name: string, availableChannelNames?: string[]) => {
      if (
        !Array.isArray(availableChannelNames) ||
        availableChannelNames.length === 0
      ) {
        return transformRendererChannelName(name);
      }

      const resolved = resolveRecordingChannelName(
        name,
        availableChannelNames,
        recordingChannelMapAliasMaps,
      );
      if (resolved) {
        return resolved;
      }

      return transformRendererChannelName(name);
    },
    [recordingChannelMapAliasMaps, transformRendererChannelName],
  );

  const buildRendererMontageChannels = useCallback(
    (
      channelPairs: Array<{
        positive: string;
        negative?: string;
        label?: string;
        color?: string;
        /** Optional: exact channel name to use for renderer lookup (bypasses
         *  transformRendererChannelName). Used by the No Derivation passthrough
         *  to preserve the original MNE/Arrow casing (e.g. "EEG Fp1-F3") so
         *  the renderer's exact-match channel lookup succeeds. */
        rendererPositiveName?: string;
      }>,
      montageId?: string,
      colorMode?: "uniform" | "alternating" | "by-type",
      availableChannelNames?: string[],
    ) =>
      channelPairs.map((pair) => {
        const label = getMontageChannelLabel(pair);
        // "uniform" mode: resolve only exception states (bad-channel tints,
        // temporary highlights, filter-override tints).  Returning null for
        // normal channels keeps _pcColorSet.size === 0 in the renderer so the
        // fast 2-stroke batch path fires (~2 ms/frame).
        // "by-type" / "alternating" modes: resolve the full DB-driven colour
        // so per-type and per-channel profile colours are applied correctly.
        const color =
          colorMode === "uniform"
            ? (resolveChannelOverrideColor(label) ?? undefined)
            : (resolveChannelColor(label, montageId) ?? undefined);
        return {
          label,
          positiveInput:
            pair.rendererPositiveName ??
            resolveRendererMontageInputName(
              pair.positive,
              availableChannelNames,
            ),
          negativeInput: pair.negative
            ? resolveRendererMontageInputName(
                pair.negative,
                availableChannelNames,
              )
            : undefined,
          color,
        } satisfies EEGMontageChannel;
      }),
    [
      getMontageChannelLabel,
      resolveChannelColor,
      resolveChannelOverrideColor,
      resolveRendererMontageInputName,
      transformRendererChannelName,
    ],
  );

  // Fetch EEG data for current time window - using buffered window to prevent request storm
  const { data: playbackManifest } = useQuery<EegPlaybackManifest | null>({
    queryKey: ["eeg-playback-manifest", studyId, currentRecordingId],
    queryFn: async () => {
      const recordingId = currentRecordingId;
      if (!recordingId) return null;
      markEegStartup("manifestRequested");
      const response = await getEegPlaybackManifest(recordingId);
      const manifest = response.data?.data || null;
      if (manifest) {
        markEegStartup("manifestReceived");
      }
      return manifest;
    },
    enabled: !!studyId && !!currentRecordingId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!studyId || !currentRecordingId) {
      return;
    }

    void prewarmEegPlaybackAccessToken(currentRecordingId);
  }, [currentRecordingId, studyId]);

  useEffect(() => {
    const logicalDurationMs =
      playbackManifest?.logicalDurationMs || playbackManifest?.durationMs || 0;
    if (logicalDurationMs > 0) {
      measureEegStartupBlock("manifest-duration-normalize", () => {
        const seconds = logicalDurationMs / 1000;
        if (seconds > 0) {
          // Update ref immediately so handlePlaybackTick advance-check guard
          // (nextTime < currentRecordingDuration - advanceChunk) uses the true
          // duration before the low-priority transition commits.
          recordingDurationRef.current = Math.max(
            recordingDurationRef.current,
            seconds,
          );
          // Bug P fix: manifest is also an authoritative source — prefer it
          // over tile estimates; only update if not already set by metadata.
          if (recordingMetaDurationRef.current <= 0) {
            recordingMetaDurationRef.current = seconds;
          }
          // Bug K fix: use Math.max(prev, seconds) so a stale manifest refetch
          // never lowers recordingDuration below the tile-data-derived value.
          startViewerInitTransition(() => {
            setRecordingDuration((prev) => Math.max(prev, seconds));
          });
        }
      });
    }
  }, [
    playbackManifest?.durationMs,
    playbackManifest?.logicalDurationMs,
    startViewerInitTransition,
  ]);

  // S1: Seed only the first clinical viewport early for LCP. Do not speculate
  // high-speed playback tiles on startup; that decode burst can contaminate
  // low-speed playback frame-gap evidence before the user/test selects a speed.
  useEffect(() => {
    const durationMs =
      playbackManifest?.logicalDurationMs || playbackManifest?.durationMs || 0;
    if (!currentRecordingId || durationMs <= 0) {
      return;
    }
    const startupPlaySeedSpeeds = [1];
    const startupPlaySeedIdentity: TileCacheIdentity = {
      montageId: currentConfig?.montage_id || null,
      channels: selectedChannels.length > 0 ? selectedChannels : undefined,
      timeScale: currentConfig?.time_scale ?? 10,
      highPassHz: currentConfig?.high_pass_filter ?? null,
      lowPassHz: currentConfig?.low_pass_filter ?? null,
      notchHz: currentConfig?.notch_filter_60hz
        ? 60
        : currentConfig?.notch_filter_50hz
          ? 50
          : null,
      sensitivityUvPerMm: currentConfig?.sensitivity_value ?? null,
    };
    for (const startupPlaySeedSpeed of startupPlaySeedSpeeds) {
      const startupPlaySeedTimeScale = autoResolution
        ? (getAutoTimeScaleForSpeed(startupPlaySeedSpeed) ??
          currentConfig?.time_scale ??
          10)
        : (currentConfig?.time_scale ?? 10);
      const startupPlaySeedLevel = getAutoResolutionLevel(
        startupPlaySeedSpeed,
        "play",
      );
      const startupPlaySeedBufferSec = getEffectiveBufferSize(
        startupPlaySeedSpeed,
        startupPlaySeedTimeScale,
        "play",
      );
      const startupPlaySeedAdvanceChunk = getAdvanceChunk(
        startupPlaySeedBufferSec,
        startupPlaySeedTimeScale,
      );
      tileCache.warmCache(
        currentRecordingId,
        durationMs,
        startupPlaySeedLevel,
        startupPlaySeedBufferSec,
        startupPlaySeedSpeed,
        false,
        [0, startupPlaySeedAdvanceChunk],
        startupPlaySeedIdentity,
      );
    }
    return () => tileCache.cancelWarm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoResolution,
    currentRecordingId,
    currentConfig?.montage_id,
    currentConfig?.time_scale,
    currentConfig?.high_pass_filter,
    currentConfig?.low_pass_filter,
    currentConfig?.notch_filter_60hz,
    currentConfig?.notch_filter_50hz,
    currentConfig?.sensitivity_value,
    getAdvanceChunk,
    getAutoResolutionLevel,
    getEffectiveBufferSize,
    playbackManifest?.durationMs,
    playbackManifest?.logicalDurationMs,
    selectedChannels,
    viewerDeferredChromeReady,
  ]);

  const effectivePlaybackSpeed = currentConfig?.playback_speed ?? 1;
  const effectiveTimeScale = currentConfig?.time_scale ?? 10;
  const deferredPausedHighSpeedInspect =
    !isPlaying && pausedHighSpeedInspectHold !== null;
  const eegDataPlaybackSpeed = deferredPausedHighSpeedInspect
    ? pausedHighSpeedInspectHold.playbackSpeed
    : effectivePlaybackSpeed;
  const eegDataTimeScale = deferredPausedHighSpeedInspect
    ? pausedHighSpeedInspectHold.timeScale
    : effectiveTimeScale;
  const eegDataTransportMode: EEGTileTransportMode =
    deferredPausedHighSpeedInspect
      ? pausedHighSpeedInspectHold.transportMode
      : playbackTransportMode;
  const effectiveUsesClinicalRawResolution = shouldUseClinicalRawResolution(
    effectivePlaybackSpeed,
    effectiveTimeScale,
    playbackTransportMode,
  );
  const eegDataUsesClinicalRawResolution = shouldUseClinicalRawResolution(
    eegDataPlaybackSpeed,
    eegDataTimeScale,
    eegDataTransportMode,
  );
  const effectiveResolutionLevel = deferredPausedHighSpeedInspect
    ? pausedHighSpeedInspectHold.resolutionLevel
    : effectiveUsesClinicalRawResolution
      ? 0
      : autoResolution
        ? getAutoResolutionLevel(effectivePlaybackSpeed, playbackTransportMode)
        : resolutionLevel;

  const eegDataResolutionLevel = deferredPausedHighSpeedInspect
    ? pausedHighSpeedInspectHold.resolutionLevel
    : eegDataUsesClinicalRawResolution
      ? 0
      : autoResolution
        ? getAutoResolutionLevel(eegDataPlaybackSpeed, eegDataTransportMode)
        : resolutionLevel;
  const eegDataBufferSize = getEffectiveBufferSize(
    eegDataPlaybackSpeed,
    eegDataTimeScale,
    eegDataTransportMode,
  );
  const eegDataAlignedWindow = useMemo(
    () =>
      alignTileWindowToBackend({
        windowStartSec: dataWindowStart,
        bufferSizeSec: eegDataBufferSize,
        speed: eegDataPlaybackSpeed,
        transportMode: eegDataTransportMode,
        recordingDurationSec:
          playbackManifest?.logicalDurationMs || playbackManifest?.durationMs
            ? (playbackManifest?.logicalDurationMs ||
                playbackManifest?.durationMs) / 1000
            : recordingDuration > 0
              ? recordingDuration
              : undefined,
      }),
    [
      dataWindowStart,
      eegDataBufferSize,
      eegDataPlaybackSpeed,
      eegDataTransportMode,
      playbackManifest?.durationMs,
      playbackManifest?.logicalDurationMs,
      recordingDuration,
    ],
  );

  const {
    data: eegData,
    refetch: refetchEegData,
    isLoading: isLoadingEegData,
    isPending: isPendingEegData,
    isFetching: isFetchingEegData,
    isPlaceholderData: isEegDataPlaceholder,
    error: eegDataError,
  } = useQuery({
    queryKey: [
      "eeg-data",
      studyId,
      currentRecordingId,
      eegDataAlignedWindow.startMs,
      eegDataAlignedWindow.endMs,
      eegDataResolutionLevel,
      useArrowFormat,
      selectedChannels.join(","),
      eegDataPlaybackSpeed,
      eegDataTimeScale,
      eegDataTransportMode,
      currentConfig?.montage_id || "",
    ], // Include resolution, format, speed, and montage (cache identity)
    queryFn: async ({ signal }) => {
      if (!studyId || !studyDetails) return null;

      // Resolution order: URL recordingId -> persisted primary recording -> first study recording.
      const activeRecordingId = currentRecordingId;
      if (!activeRecordingId) {
        logger.error("[EEGViewer] No recording found for study:", studyId);
        throw new Error(
          "No EEG recording found for this study. Please upload an EEG file first.",
        );
      }

      logger.debug(
        `[EEGViewer] Fetching EEG data for recording: ${activeRecordingId} (study: ${studyId})`,
      );

      // Dynamic buffer: scale with playback speed so high-speed playback
      // always has several wall-clock seconds of data ahead.
      const speed = eegDataPlaybackSpeed;
      const timeScale = eegDataTimeScale;
      const queryResolutionLevel = eegDataResolutionLevel;
      const transportMode = eegDataTransportMode;
      const bufferSize = eegDataBufferSize;
      const startTime = eegDataAlignedWindow.startSec;
      const endTime = eegDataAlignedWindow.endSec;
      const startMs = eegDataAlignedWindow.startMs;
      const endMs = eegDataAlignedWindow.endMs;
      const tileIdentity: TileCacheIdentity = {
        montageId: currentConfig?.montage_id || null,
        channels: selectedChannels.length > 0 ? selectedChannels : undefined,
        timeScale,
        highPassHz: currentConfig?.high_pass_filter ?? null,
        lowPassHz: currentConfig?.low_pass_filter ?? null,
        notchHz: currentConfig?.notch_filter_60hz
          ? 60
          : currentConfig?.notch_filter_50hz
            ? 50
            : null,
        sensitivityUvPerMm: currentConfig?.sensitivity_value ?? null,
      };
      const resolvedTileIdentity = resolveTileCacheIdentity(tileIdentity);
      const withTileRequestContext = (
        tile: DecodedArrowTile,
        actualTileLevel: number,
        windowBounds: { startSec: number; endSec: number } = {
          startSec: startTime,
          endSec: endTime,
        },
      ): DecodedArrowTile => ({
        ...tile,
        requestContext: {
          recordingId: activeRecordingId,
          transportMode,
          speed,
          timeScale,
          windowStart: windowBounds.startSec,
          windowEnd: windowBounds.endSec,
          requestedLevel: queryResolutionLevel,
          actualLevel: actualTileLevel,
        },
      });
      const withTransferMetrics = (
        tile: DecodedArrowTile,
        metrics: NonNullable<DecodedArrowTile["transferMetrics"]>,
      ): DecodedArrowTile => ({
        ...tile,
        transferMetrics: metrics,
      });
      const withDecodeTelemetry = (
        tile: DecodedArrowTile,
        telemetry: NonNullable<DecodedArrowTile["decodeTelemetry"]>,
      ): DecodedArrowTile => ({
        ...tile,
        decodeTelemetry: telemetry,
      });
      const emitDecodeRoundTrip = (
        decodeMs: number,
        details: {
          source: string;
          actualLevel: number;
          transferSizeBytes: number;
          transferTimeMs: number;
        },
      ) => {
        if (speed < 100 && decodeMs <= 20) {
          return;
        }

        emitPlaybackPerfEvent("tileDecodeRoundTrip", {
          recordingId: activeRecordingId,
          speed,
          transportMode,
          source: details.source,
          requestedLevel: queryResolutionLevel,
          actualLevel: details.actualLevel,
          decodeMs: Number(decodeMs.toFixed(1)),
          transferSizeBytes: details.transferSizeBytes,
          transferTimeMs: Number(details.transferTimeMs.toFixed(1)),
          startTimeSeconds: Number(startTime.toFixed(3)),
          endTimeSeconds: Number(endTime.toFixed(3)),
        });
      };

      const decodeTileBuffer = async ({
        buffer,
        decodeStartMs = startMs,
        decodeEndMs = endMs,
        requestedLevel = queryResolutionLevel,
        actualLevel = requestedLevel,
        source,
        transferSizeBytes,
        transferTimeMs,
        emitReuseEvent = true,
      }: {
        buffer: ArrayBuffer;
        decodeStartMs?: number;
        decodeEndMs?: number;
        requestedLevel?: number;
        actualLevel?: number;
        source: string;
        transferSizeBytes: number;
        transferTimeMs: number;
        emitReuseEvent?: boolean;
      }) => {
        const decodeStartedAt = performance.now();
        emitPlaybackPerfEvent("tileDecodeStart", {
          recordingId: activeRecordingId,
          speed,
          transportMode,
          source,
          requestedLevel,
          actualLevel,
          transferSizeBytes,
          transferTimeMs: Number(transferTimeMs.toFixed(1)),
          startTimeSeconds: Number((decodeStartMs / 1000).toFixed(3)),
          endTimeSeconds: Number((decodeEndMs / 1000).toFixed(3)),
        });
        const decodeResult = await decodeArrowTileSharedCached({
          recordingId: activeRecordingId,
          startMs: decodeStartMs,
          endMs: decodeEndMs,
          level: requestedLevel,
          speed,
          transportMode,
          buffer,
          identity: tileIdentity,
        });
        const decodeReadyAt = performance.now();
        const decodeElapsed = decodeReadyAt - decodeStartedAt;
        emitPlaybackPerfEvent("tileDecodeEnd", {
          recordingId: activeRecordingId,
          speed,
          transportMode,
          source,
          decodeSource: decodeResult.source,
          requestedLevel,
          actualLevel,
          decodeMs: Number(decodeElapsed.toFixed(1)),
          startTimeSeconds: Number((decodeStartMs / 1000).toFixed(3)),
          endTimeSeconds: Number((decodeEndMs / 1000).toFixed(3)),
        });

        if (decodeResult.source === "decoded-worker") {
          emitDecodeRoundTrip(decodeElapsed, {
            source,
            actualLevel,
            transferSizeBytes,
            transferTimeMs,
          });
        } else if (emitReuseEvent) {
          emitPlaybackPerfEvent("tileDecodeReuse", {
            recordingId: activeRecordingId,
            speed,
            transportMode,
            source: decodeResult.source,
            requestedLevel,
            actualLevel,
            waitMs: Number(decodeElapsed.toFixed(1)),
            transferSizeBytes,
            transferTimeMs: Number(transferTimeMs.toFixed(1)),
            startTimeSeconds: Number((decodeStartMs / 1000).toFixed(3)),
            endTimeSeconds: Number((decodeEndMs / 1000).toFixed(3)),
          });
        }

        return withDecodeTelemetry(decodeResult.tile, {
          startedAtMs: Number(decodeStartedAt.toFixed(1)),
          readyAtMs: Number(decodeReadyAt.toFixed(1)),
          waitMs: Number(decodeElapsed.toFixed(1)),
          source: decodeResult.source,
          requestedLevel,
          actualLevel,
          startTimeSeconds: Number((decodeStartMs / 1000).toFixed(3)),
          endTimeSeconds: Number((decodeEndMs / 1000).toFixed(3)),
        });
      };

      const alignmentRecordingDurationSec = [
        playbackManifest?.logicalDurationMs
          ? playbackManifest.logicalDurationMs / 1000
          : 0,
        playbackManifest?.durationMs ? playbackManifest.durationMs / 1000 : 0,
        recordingMetaDurationRef.current,
        recordingDurationRef.current,
        recordingDuration,
      ].reduce<number | undefined>((bestDuration, candidate) => {
        if (!Number.isFinite(candidate) || candidate <= 0) {
          return bestDuration;
        }
        if (bestDuration === undefined) {
          return candidate;
        }
        return Math.max(bestDuration, candidate);
      }, undefined);

      const primePrefetchedWindowDecode = (
        windowStartSec: number,
        requestedLevel: number,
      ) => {
        if (speed < 100 || transportMode !== "play") {
          return;
        }

        const alignedWindow = alignTileWindowToBackend({
          windowStartSec,
          bufferSizeSec: bufferSize,
          speed,
          transportMode,
          recordingDurationSec: alignmentRecordingDurationSec,
        });

        void (async () => {
          let prefetchedBuffer = await tileCache.getCached(
            activeRecordingId,
            alignedWindow.startMs,
            alignedWindow.endMs,
            requestedLevel,
            speed,
            transportMode,
            tileIdentity,
          );

          if (!prefetchedBuffer) {
            const joinedPrefetch = await waitForInFlightPrefetchTile(
              activeRecordingId,
              alignedWindow.startMs,
              alignedWindow.endMs,
              requestedLevel,
              speed,
              transportMode,
              tileIdentity,
            );
            if (!joinedPrefetch) {
              return;
            }
            prefetchedBuffer = await tileCache.getCached(
              activeRecordingId,
              alignedWindow.startMs,
              alignedWindow.endMs,
              requestedLevel,
              speed,
              transportMode,
              tileIdentity,
            );
          }

          if (!prefetchedBuffer) {
            return;
          }

          await decodeTileBuffer({
            buffer: prefetchedBuffer,
            decodeStartMs: alignedWindow.startMs,
            decodeEndMs: alignedWindow.endMs,
            requestedLevel,
            actualLevel: requestedLevel,
            source: "prefetch-prime",
            transferSizeBytes: prefetchedBuffer.byteLength,
            transferTimeMs: 0,
            emitReuseEvent: false,
          });
        })().catch(() => {
          /* non-fatal */
        });
      };

      const primePrefetchedWindowDecodes = (
        baseWindowStartSec: number,
        advanceChunkSec: number,
        requestedLevel: number,
        numWindows: number,
      ) => {
        for (let index = 1; index <= numWindows; index += 1) {
          primePrefetchedWindowDecode(
            baseWindowStartSec + advanceChunkSec * index,
            requestedLevel,
          );
        }
      };

      try {
        logger.debug(
          `[EEGViewer] Time window: ${startTime.toFixed(2)}s - ${endTime.toFixed(2)}s, Resolution: ${queryResolutionLevel}, Mode: ${transportMode}, Format: arrow`,
        );

        // ── S1: Cache-first fetch ─────────────────────────────────────────────
        let joinedPrefetchTile = false;
        let speculativePrefetchAdvanceChunk = 0;
        let speculativePrefetchWindowCount = 0;
        let speculativePrimeWindowCount = 0;
        let speculativePrefetchDeferred = false;
        let speculativePrefetchWarmTransfer = false;
        let cachedBuffer = await tileCache.getCached(
          activeRecordingId,
          startMs,
          endMs,
          queryResolutionLevel,
          speed,
          transportMode,
          tileIdentity,
        );

        if (!cachedBuffer) {
          const joinedPrefetch = await waitForInFlightPrefetchTile(
            activeRecordingId,
            startMs,
            endMs,
            queryResolutionLevel,
            speed,
            transportMode,
            tileIdentity,
          );
          joinedPrefetchTile = joinedPrefetch;
          if (joinedPrefetch) {
            cachedBuffer = await tileCache.getCached(
              activeRecordingId,
              startMs,
              endMs,
              queryResolutionLevel,
              speed,
              transportMode,
              tileIdentity,
            );
            if (cachedBuffer) {
              logger.debug(
                `[EEGViewer] [Prefetch JOIN] ${startTime.toFixed(0)}s-${endTime.toFixed(0)}s L${queryResolutionLevel} ${transportMode}`,
              );
            }
          }
        }

        const shouldStartEarlyExactTilePrefetch =
          speed >= 200 &&
          transportMode === "play" &&
          !showVideoPanel &&
          !cachedBuffer;
        const earlyExactTilePrefetchWindowCount =
          shouldStartEarlyExactTilePrefetch ? (speed >= 200 ? 2 : 1) : 0;
        const earlyExactTilePrefetchAdvanceChunk =
          speed >= 16 && transportMode === "play"
            ? getAdvanceChunk(bufferSize, timeScale)
            : 0;
        const keepProtectedExactTileRunway =
          speed >= 200 && transportMode === "play";
        const protectedExactTileRunwayWindowCount = keepProtectedExactTileRunway
          ? 1
          : 0;

        if (shouldStartEarlyExactTilePrefetch) {
          prefetchWindowsBackground(
            activeRecordingId,
            startTime,
            earlyExactTilePrefetchAdvanceChunk,
            bufferSize,
            queryResolutionLevel,
            earlyExactTilePrefetchWindowCount,
            alignmentRecordingDurationSec ?? recordingDurationRef.current,
            speed,
            transportMode,
            1,
            "play-exact",
            keepProtectedExactTileRunway,
            protectedExactTileRunwayWindowCount,
            tileIdentity,
          ).catch(() => {
            /* non-fatal */
          });
        }

        const loadStartTime = performance.now();
        emitPlaybackPerfEvent("tileRequestStart", {
          recordingId: activeRecordingId,
          speed,
          transportMode,
          requestedLevel: queryResolutionLevel,
          montageId: resolvedTileIdentity.montageId,
          channelSetHash: resolvedTileIdentity.channelSetHash,
          zoomBucket: resolvedTileIdentity.zoomBucket,
          startTimeSeconds: Number(startTime.toFixed(3)),
          endTimeSeconds: Number(endTime.toFixed(3)),
          source: cachedBuffer ? "cache" : "network",
        });

        let responseBuffer: ArrayBuffer;
        let actualLevel = queryResolutionLevel;
        // Bug B/C fix: capture the actual tile range returned by the backend.
        // The backend may return a shorter range than requested when the byte
        // budget is smaller than the requested window (high-speed playback).
        // Using the real header here ensures downstream code (Arrow decode,
        // rendererLoadedBufferEndRef, bufferEnd clamp) operates on the actual
        // data extent rather than the requested extent.
        let realTileRangeHeader: string | null = null;

        if (cachedBuffer) {
          responseBuffer = cachedBuffer;
          logger.debug(
            `[EEGViewer] [Cache HIT] ${startTime.toFixed(0)}s-${endTime.toFixed(0)}s L${queryResolutionLevel} ${transportMode}`,
          );
        } else {
          // Cache miss — fetch from network
          const response = await getEegPlaybackTiles(
            activeRecordingId,
            {
              startMs,
              endMs,
              level: queryResolutionLevel,
              speed,
              transportMode,
              requestOwner: "visible-main",
              channels:
                selectedChannels.length > 0 ? selectedChannels : undefined,
            },
            {
              timeout: Math.max(30000, bufferSize * 80), // avoid false timeouts on cold parquet reads
              headers: { Accept: "application/vnd.apache.arrow.stream" },
              responseType: "arraybuffer",
              signal,
            },
          );

          const levelHeader = response.headers?.["x-lod-level"];
          actualLevel = Number.isFinite(Number(levelHeader))
            ? Number(levelHeader)
            : queryResolutionLevel;

          // Capture the real range actually returned (may be shorter than requested)
          realTileRangeHeader =
            (response.headers?.["x-tile-range"] as string) ?? null;

          responseBuffer = response.data as ArrayBuffer;

          // Store in IndexedDB for next visit (clone before transfer to worker).
          // Skip during rapid-review play — IDB onsuccess floods LOAF under k6.
          if (!(transportMode === "play" && speed >= 60)) {
            tileCache
              .putCached(
                activeRecordingId,
                startMs,
                endMs,
                actualLevel,
                speed,
                responseBuffer.slice(0),
                transportMode,
                realTileRangeHeader,
                tileIdentity,
              )
              .catch(() => {
                /* non-fatal */
              });
          }
        }

        const loadEndTime = performance.now();
        const dataSize = responseBuffer.byteLength;
        const loadTime = loadEndTime - loadStartTime;
        emitPlaybackPerfEvent("tileResponse", {
          recordingId: activeRecordingId,
          speed,
          transportMode,
          requestedLevel: queryResolutionLevel,
          actualLevel,
          montageId: resolvedTileIdentity.montageId,
          channelSetHash: resolvedTileIdentity.channelSetHash,
          zoomBucket: resolvedTileIdentity.zoomBucket,
          startTimeSeconds: Number(startTime.toFixed(3)),
          endTimeSeconds: Number(endTime.toFixed(3)),
          source: cachedBuffer
            ? joinedPrefetchTile
              ? "prefetch-join"
              : "cache"
            : "network",
          transferSizeBytes: dataSize,
          transferTimeMs: Number(loadTime.toFixed(1)),
        });
        markEegStartup("firstTileTransferObserved");
        const playbackSurface = showVideoPanel ? "eeg-dual-video" : "eeg-only";
        const ultraRapidPlaySurface =
          transportMode === "play" &&
          speed >= 100 &&
          playbackSurface === "eeg-only";
        const sincePlaybackStartMs =
          speed >= 100 && highSpeedPlaybackStartedAtRef.current !== null
            ? Math.max(0, loadEndTime - highSpeedPlaybackStartedAtRef.current)
            : null;

        // ── S2: Speculative background prefetch of next windows ───────────────
        if (speed >= 16 && transportMode === "play") {
          // At 100x the active play buffer already spans ~240s. Walking 4+ more
          // overlapping windows immediately on cold start floods the first perf
          // sample with low-value transfer and competes with the first visible
          // play tile. Keep ultra-rapid playback to a single look-ahead window;
          // slower high-speed tiers can still prefetch more aggressively.
          const shouldDeferSpeculativePrefetch =
            ultraRapidPlaySurface && !cachedBuffer && loadTime > 1500;
          speculativePrefetchDeferred = shouldDeferSpeculativePrefetch;

          if (!shouldDeferSpeculativePrefetch) {
            const advanceChunk =
              earlyExactTilePrefetchAdvanceChunk ||
              getAdvanceChunk(bufferSize, timeScale);
            speculativePrefetchAdvanceChunk = advanceChunk;
            const warmVisibleTransfer =
              Boolean(cachedBuffer) ||
              loadTime <= (speed >= 200 ? 325 : speed >= 150 ? 375 : 450) ||
              (loadTime <= 700 && dataSize <= 4 * 1024 * 1024);
            speculativePrefetchWarmTransfer = warmVisibleTransfer;
            const playbackPrefetchPlan = resolveEEGViewerPlaybackPrefetchPlan({
              speed,
              transportMode,
              warmVisibleTransfer,
              sincePlaybackStartMs,
            });
            const shouldScheduleBackgroundPrefetch =
              shouldScheduleEEGViewerPlaybackBackgroundPrefetch({
                speed,
                transportMode,
                currentWindowStartSec: startTime,
                advanceChunkSec: advanceChunk,
                cadenceWindows: playbackPrefetchPlan.cadenceWindows,
                lastScheduledWindowStartSec:
                  lastHighSpeedBackgroundPrefetchWindowStartRef.current,
              });
            const numWindowsAhead = playbackPrefetchPlan.totalWindowCount;
            const backgroundPrefetchWindowCount =
              shouldScheduleBackgroundPrefetch
                ? Math.max(
                    0,
                    numWindowsAhead - earlyExactTilePrefetchWindowCount,
                  )
                : 0;
            const numWindowsToPrime =
              shouldScheduleBackgroundPrefetch && speed >= 100
                ? Math.min(
                    Math.max(
                      0,
                      numWindowsAhead - earlyExactTilePrefetchWindowCount,
                    ),
                    playbackPrefetchPlan.primeWindowCount,
                  )
                : 0;
            const protectedUltraRapidRunwayWindowCount =
              resolveEEGViewerProtectedUltraRapidRunwayWindowCount({
                speed,
                transportMode,
                warmVisibleTransfer,
              });
            const keepProtectedUltraRapidRunway =
              protectedUltraRapidRunwayWindowCount > 0;
            speculativePrefetchWindowCount = shouldScheduleBackgroundPrefetch
              ? numWindowsAhead
              : 0;
            speculativePrimeWindowCount = numWindowsToPrime;
            if (backgroundPrefetchWindowCount > 0) {
              lastHighSpeedBackgroundPrefetchWindowStartRef.current = startTime;
              prefetchWindowsBackground(
                activeRecordingId,
                startTime,
                advanceChunk,
                bufferSize,
                queryResolutionLevel,
                backgroundPrefetchWindowCount,
                alignmentRecordingDurationSec ?? recordingDurationRef.current,
                speed,
                transportMode,
                1 + earlyExactTilePrefetchWindowCount,
                "play-background",
                keepProtectedUltraRapidRunway,
                protectedUltraRapidRunwayWindowCount,
                tileIdentity,
              ).catch(() => {
                /* non-fatal */
              });
            }
            if (numWindowsToPrime > 0) {
              primePrefetchedWindowDecodes(
                startTime,
                advanceChunk,
                queryResolutionLevel,
                numWindowsToPrime,
              );
            }
          } else {
            logger.debug(
              "[EEGViewer] Skipping speculative high-speed prefetch after slow visible tile",
              {
                recordingId: activeRecordingId,
                speed,
                transportMode,
                transferSizeBytes: dataSize,
                transferTimeMs: Number(loadTime.toFixed(1)),
                startTimeSeconds: Number(startTime.toFixed(3)),
                endTimeSeconds: Number(endTime.toFixed(3)),
              },
            );
          }
        }

        // ── Build a response-like object so downstream code is unchanged ──────
        // Use the real x-tile-range from the network response so that Arrow
        // decode and rendererLoadedBufferEndRef reflect actual data extent.
        // Fall back to requested range only when serving from cache (the cache
        // key includes startMs/endMs so cached data always covers that window).
        const response = {
          data: responseBuffer,
          headers: {
            "x-lod-level": actualLevel.toString(),
            "x-tile-range": realTileRangeHeader ?? `${startMs}-${endMs}`,
            "x-tile-requested-range": `${startMs}-${endMs}`,
          },
        } as const;
        const actualTileWindow = parseTileRangeHeader(
          response.headers?.["x-tile-range"],
          startTime,
          endTime,
        );

        logger.debug(
          `[EEGViewer] [Arrow] Loaded ${(dataSize / 1024 / 1024).toFixed(2)} MB in ${loadTime.toFixed(0)}ms`,
        );
        const transferMetrics = {
          size: dataSize,
          time: loadTime,
          level: actualLevel,
          format: "arrow",
        };
        setDataTransferMetrics(transferMetrics);
        playbackPerf.recordTransfer(dataSize, loadTime);

        const shouldEmitPerfLog =
          speed >= 100 || playbackSurface === "eeg-dual-video";
        if (shouldEmitPerfLog) {
          const throughputMbps =
            loadTime > 0 ? (dataSize * 8) / (loadTime / 1000) / 1_000_000 : 0;
          const payload = {
            recordingId: activeRecordingId,
            speed,
            surface: playbackSurface,
            resolutionLevel: actualLevel,
            startTimeSeconds: Number(startTime.toFixed(3)),
            endTimeSeconds: Number(endTime.toFixed(3)),
            transferSizeBytes: dataSize,
            transferTimeMs: Number(loadTime.toFixed(1)),
            transferMbps: Number(throughputMbps.toFixed(2)),
          };

          const degradedTransfer = ultraRapidPlaySurface
            ? loadTime > 2500 ||
              (loadTime > 1000 && throughputMbps > 0 && throughputMbps < 35)
            : loadTime > 1200 || dataSize > 32 * 1024 * 1024;

          if (speed >= 200 && transportMode === "play") {
            const prefetchDebug = getPrefetchDebugSnapshot(activeRecordingId, {
              level: queryResolutionLevel,
              speed,
              transportMode,
              startMs,
              endMs,
              identity: tileIdentity,
            });
            emitPlaybackPerfEvent("highSpeedTransferProfile", {
              recordingId: activeRecordingId,
              speed,
              degradedTransfer,
              requestedLevel: queryResolutionLevel,
              actualLevel,
              requestedStartSeconds: Number(startTime.toFixed(3)),
              requestedEndSeconds: Number(endTime.toFixed(3)),
              actualStartSeconds: Number(actualTileWindow.startSec.toFixed(3)),
              actualEndSeconds: Number(actualTileWindow.endSec.toFixed(3)),
              transferSource: cachedBuffer
                ? joinedPrefetchTile
                  ? "prefetch-join"
                  : "cache"
                : "network",
              transferSizeBytes: dataSize,
              transferTimeMs: Number(loadTime.toFixed(1)),
              transferMbps: Number(throughputMbps.toFixed(2)),
              joinedPrefetchTile,
              prefetchAdvanceChunkSeconds: speculativePrefetchAdvanceChunk,
              prefetchWindowCount: speculativePrefetchWindowCount,
              primeWindowCount: speculativePrimeWindowCount,
              prefetchDeferred: speculativePrefetchDeferred,
              warmVisibleTransfer: speculativePrefetchWarmTransfer,
              prefetchControllerAborted: prefetchDebug.controllerAborted,
              prefetchControllerKey: prefetchDebug.controllerKey ?? "",
              prefetchRecordingInFlightCount:
                prefetchDebug.recordingInFlightCount,
              prefetchMatchingInFlightCount:
                prefetchDebug.matchingInFlightCount,
              prefetchExactTileInFlight: prefetchDebug.exactTileInFlight,
              prefetchAbortCount: prefetchDebug.abortCount,
              prefetchLastAbortAgeMs:
                prefetchDebug.lastAbortAgeMs === null
                  ? -1
                  : Number(prefetchDebug.lastAbortAgeMs.toFixed(1)),
              prefetchMatchingInFlightWindows:
                prefetchDebug.matchingInFlightWindows.join("|"),
            });
          }

          if (degradedTransfer) {
            logger.warn(
              "[EEGViewer] High-speed tile transfer degraded",
              payload,
            );
          } else {
            logger.debug("[EEGViewer] High-speed tile transfer", payload);
          }
        }

        const decodeResult = await decodeTileBuffer({
          buffer: responseBuffer,
          requestedLevel: queryResolutionLevel,
          actualLevel,
          source: cachedBuffer ? "cache" : "network",
          transferSizeBytes: dataSize,
          transferTimeMs: loadTime,
        });
        const requestedDurationSeconds = Math.max(0.001, endTime - startTime);
        const returnedRangeHeader = String(
          response.headers?.["x-tile-range"] || "",
        );
        const returnedRangeMatch = returnedRangeHeader.match(
          /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/,
        );
        const returnedDurationSeconds = returnedRangeMatch
          ? Math.max(
              0.001,
              (Number(returnedRangeMatch[2]) - Number(returnedRangeMatch[1])) /
                1000,
            )
          : requestedDurationSeconds;
        // Cap expectedDurationSeconds to the actual remaining recording length so
        // end-of-recording tiles (where requestedEnd > recordingEnd) do not spread
        // the real data samples across a much longer logical window.  Doing so
        // collapses derivedSamplingRate and the renderer maps waveforms at ~9% of
        // their correct width, producing the distorted crushed appearance at the end.
        const remainingRecordingSec =
          recordingDurationRef.current > 0
            ? Math.max(0.001, recordingDurationRef.current - startTime)
            : Infinity;
        const expectedDurationSeconds = Math.min(
          remainingRecordingSec,
          Math.max(0.001, returnedDurationSeconds || requestedDurationSeconds),
        );
        const sampleCount = decodeResult.numSamples ?? 0;
        const derivedSamplingRate =
          sampleCount > 0 ? sampleCount / expectedDurationSeconds : 0;
        const decodedSamplingRate = Number(decodeResult.sampling_rate || 0);
        const decodedDuration = Number(decodeResult.duration || 0);
        const hasSuspiciousDuration =
          !Number.isFinite(decodedDuration) ||
          decodedDuration <= 0 ||
          decodedDuration < expectedDurationSeconds * 0.85 ||
          decodedDuration > expectedDurationSeconds * 1.15;
        const hasSuspiciousSamplingRate =
          !Number.isFinite(decodedSamplingRate) ||
          decodedSamplingRate <= 0 ||
          (derivedSamplingRate > 0 &&
            (decodedSamplingRate < derivedSamplingRate * 0.85 ||
              decodedSamplingRate > derivedSamplingRate * 1.15));

        if (
          sampleCount > 1 &&
          derivedSamplingRate > 0 &&
          (hasSuspiciousDuration || hasSuspiciousSamplingRate)
        ) {
          // If the pyramid level tile is severely truncated (sample density
          // so low the waveform would be invisible), treat it like a missing
          // level and fall back to L0 — same path as the 409 handler below.
          // At high playback speeds the backend intentionally returns fewer
          // samples per second (e.g. ~6 Hz at speed=16), so the threshold
          // must scale inversely with speed. A hard floor of 0.5 Hz still
          // catches genuinely degenerate tiles / old poisoned cache entries.
          const MIN_EXPECTED_RATE_HZ = Math.max(0.5, 10 / Math.max(1, speed));
          const isSeverelyTruncated =
            actualLevel > 0 && derivedSamplingRate < MIN_EXPECTED_RATE_HZ;

          if (isSeverelyTruncated) {
            // Bulk-evict all stale tiles for this recording from IDB so every
            // subsequent treadmill step fetches fresh data.  Single-key eviction
            // is insufficient because each step uses a different cache key.
            if (
              cachedBuffer &&
              !purgedRecordingsRef.current.has(activeRecordingId)
            ) {
              purgedRecordingsRef.current.add(activeRecordingId);
              clearDecodedTilesForRecording(activeRecordingId);
              tileCache.clearRecording(activeRecordingId).catch(() => {});
            }
            logger.warn(
              `[EEGViewer] [Arrow] Level ${actualLevel} tile severely truncated (${derivedSamplingRate.toFixed(2)} Hz < ${MIN_EXPECTED_RATE_HZ.toFixed(2)} Hz at speed=${speed}); falling back to level 0`,
              {
                recordingId: activeRecordingId,
                requestedDurationSeconds: Number(
                  requestedDurationSeconds.toFixed(3),
                ),
                sampleCount,
                derivedSamplingRate: Number(derivedSamplingRate.toFixed(3)),
                level: actualLevel,
              },
            );
            const fallbackStartTime = performance.now();
            const fallbackResponse = await getEegPlaybackTiles(
              activeRecordingId,
              {
                startMs: startTime * 1000,
                endMs: endTime * 1000,
                level: 0,
                speed,
                transportMode,
                requestOwner: "visible-fallback-l0",
                channels:
                  selectedChannels.length > 0 ? selectedChannels : undefined,
              },
              {
                timeout: Math.max(30000, bufferSize * 80),
                headers: { Accept: "application/vnd.apache.arrow.stream" },
                responseType: "arraybuffer",
                signal,
              },
            );
            const fallbackLoadEnd = performance.now();
            const fallbackBuffer = fallbackResponse.data as ArrayBuffer;
            const fallbackTransferMetrics = {
              size: fallbackBuffer.byteLength,
              time: fallbackLoadEnd - fallbackStartTime,
              level: 0,
              format: "arrow",
            };
            logger.debug(
              `[EEGViewer] [Arrow] Level-0 fallback loaded ${(fallbackBuffer.byteLength / 1024 / 1024).toFixed(2)} MB in ${(fallbackLoadEnd - fallbackStartTime).toFixed(0)}ms`,
            );
            const fallbackDecodeResult = await decodeTileBuffer({
              buffer: fallbackBuffer,
              requestedLevel: 0,
              actualLevel: 0,
              source: "fallback-l0",
              transferSizeBytes: fallbackBuffer.byteLength,
              transferTimeMs: fallbackLoadEnd - fallbackStartTime,
            });
            return withTransferMetrics(
              withTileRequestContext(
                fallbackDecodeResult,
                0,
                parseTileRangeHeader(
                  fallbackResponse.headers?.["x-tile-range"] || "",
                  startTime,
                  endTime,
                ),
              ),
              fallbackTransferMetrics,
            );
          }

          // Stale IDB tile: it passed the severely-truncated threshold but still
          // has far fewer samples than the requested window (e.g. old 300s tile
          // cached before the Python 300s hard-cap was removed).  Bulk-clear
          // ALL cached tiles for this recording so every subsequent treadmill step
          // fetches fresh data from the server.  Guard prevents repeating per step.
          if (
            cachedBuffer &&
            !purgedRecordingsRef.current.has(activeRecordingId)
          ) {
            purgedRecordingsRef.current.add(activeRecordingId);
            clearDecodedTilesForRecording(activeRecordingId);
            tileCache.clearRecording(activeRecordingId).catch(() => {});
          }

          // If the suspicious tile came from the IDB cache, re-fetch from the
          // network instead of returning stale data.  Returning the old 300s
          // window would load the renderer with truncated data and leave the
          // canvas blank for the remainder of the session.  For fresh network
          // tiles (e.g. the legitimate short tail at end of recording) we keep
          // the existing metadata-patch path below.
          if (cachedBuffer) {
            logger.warn(
              `[EEGViewer] [IDB] Stale cached tile evicted; re-fetching L${queryResolutionLevel} from network`,
              {
                recordingId: activeRecordingId,
                level: queryResolutionLevel,
                startMs,
                endMs,
                decodedDuration,
                expectedDurationSeconds,
              },
            );
            const freshLoadStartTime = performance.now();
            const freshResp = await getEegPlaybackTiles(
              activeRecordingId,
              {
                startMs,
                endMs,
                level: queryResolutionLevel,
                speed,
                transportMode,
                requestOwner: "visible-refetch",
                channels:
                  selectedChannels.length > 0 ? selectedChannels : undefined,
              },
              {
                timeout: Math.max(30000, bufferSize * 80),
                headers: { Accept: "application/vnd.apache.arrow.stream" },
                responseType: "arraybuffer",
                signal,
              },
            );
            const freshLoadEnd = performance.now();
            const freshBuffer = freshResp.data as ArrayBuffer;
            tileCache
              .putCached(
                activeRecordingId,
                startMs,
                endMs,
                Number.isFinite(Number(freshResp.headers?.["x-lod-level"]))
                  ? Number(freshResp.headers["x-lod-level"])
                  : queryResolutionLevel,
                speed,
                freshBuffer.slice(0),
                transportMode,
                (freshResp.headers?.["x-tile-range"] as string) ?? null,
                tileIdentity,
              )
              .catch(() => {});
            const actualFreshLevel = Number.isFinite(
              Number(freshResp.headers?.["x-lod-level"]),
            )
              ? Number(freshResp.headers?.["x-lod-level"])
              : queryResolutionLevel;
            const freshTransferMetrics = {
              size: freshBuffer.byteLength,
              time: freshLoadEnd - freshLoadStartTime,
              level: actualFreshLevel,
              format: "arrow",
            };
            const freshDecodeResult = await decodeTileBuffer({
              buffer: freshBuffer,
              requestedLevel: queryResolutionLevel,
              actualLevel: actualFreshLevel,
              source: "refresh-network",
              transferSizeBytes: freshBuffer.byteLength,
              transferTimeMs: freshLoadEnd - freshLoadStartTime,
            });
            return withTransferMetrics(
              withTileRequestContext(
                freshDecodeResult,
                actualFreshLevel,
                parseTileRangeHeader(
                  freshResp.headers?.["x-tile-range"] || "",
                  startTime,
                  endTime,
                ),
              ),
              freshTransferMetrics,
            );
          }

          logger.warn("[EEGViewer] Normalized decoded tile timing metadata", {
            recordingId: activeRecordingId,
            requestedDurationSeconds: Number(
              requestedDurationSeconds.toFixed(3),
            ),
            returnedDurationSeconds: Number(expectedDurationSeconds.toFixed(3)),
            decodedDuration: Number(decodedDuration.toFixed(3)),
            decodedSamplingRate: Number(decodedSamplingRate.toFixed(3)),
            derivedSamplingRate: Number(derivedSamplingRate.toFixed(3)),
            sampleCount,
            level: actualLevel,
            speed,
          });
          return withTransferMetrics(
            withTileRequestContext(
              {
                ...decodeResult,
                // Bug O fix: use decodedSamplingRate (from Arrow metadata) rather than
                // derivedSamplingRate (sampleCount / requestedWindow).  When the LOD-1
                // pyramid ends before the requested window (e.g. ws=1740 tile has only
                // 18s of data for a 600s request), derivedSamplingRate collapses to ~3 Hz.
                // The renderer feeds this.sampleRate into the HP filter half-window:
                //   half = floor(sampleRate / cutoffHz) = floor(3/0.5) = 6 samples
                // …a 13-sample window that removes all EEG content below ~8 Hz, leaving
                // near-zero filtered output — visible as completely flat lines.
                // decodedSamplingRate is always the true per-sample rate from the Arrow
                // file and is unaffected by the requested window size.
                sampling_rate:
                  decodedSamplingRate > 0
                    ? decodedSamplingRate
                    : derivedSamplingRate,
                duration: expectedDurationSeconds,
                // Preserve the true Arrow-file data span so consumers can use it
                // for buffer-end tracking without inflating it to the window size.
                actualDataDuration:
                  decodedDuration > 0 ? decodedDuration : undefined,
              },
              actualLevel,
              actualTileWindow,
            ),
            transferMetrics,
          );
        }

        return withTransferMetrics(
          withTileRequestContext(decodeResult, actualLevel, actualTileWindow),
          transferMetrics,
        );
      } catch (error) {
        const status = (error as any)?.response?.status;
        const errorCode = String(
          (error as any)?.code || (error as any)?.name || "",
        ).toLowerCase();
        const errorMessage = String(
          (error as any)?.response?.data?.message ||
            (error as any)?.response?.data?.error ||
            (error as Error)?.message ||
            "",
        ).toLowerCase();
        const isAbortLike = isAbortLikeEegTileRequestError(error);
        const tileRequestDebugContext = {
          requestedStartSeconds: Number(startTime.toFixed(3)),
          requestedEndSeconds: Number(endTime.toFixed(3)),
          requestedLevel: queryResolutionLevel,
          speed,
          transportMode,
          status: status ?? -1,
          errorCode,
        };
        (error as any).__eegTileRequestDebugContext = tileRequestDebugContext;
        const missingPyramidLevel =
          status === 409 &&
          errorMessage.includes("pyramid level not available") &&
          queryResolutionLevel > 0;

        if (missingPyramidLevel) {
          logger.warn(
            `[EEGViewer] [Arrow] Pyramid level ${queryResolutionLevel} unavailable; retrying at level 0`,
          );
          const fallbackStartTime = performance.now();
          const fallbackResponse = await getEegPlaybackTiles(
            activeRecordingId,
            {
              startMs: startTime * 1000,
              endMs: endTime * 1000,
              level: 0,
              speed,
              transportMode,
              requestOwner: "visible-fallback-l0",
              channels:
                selectedChannels.length > 0 ? selectedChannels : undefined,
            },
            {
              timeout: Math.max(30000, bufferSize * 80),
              headers: { Accept: "application/vnd.apache.arrow.stream" },
              responseType: "arraybuffer",
              signal,
            },
          );
          const fallbackLoadEnd = performance.now();
          const fallbackSize = fallbackResponse.data.byteLength;
          const fallbackTime = fallbackLoadEnd - fallbackStartTime;
          const playbackSurface = showVideoPanel
            ? "eeg-dual-video"
            : "eeg-only";
          const fallbackLevelHeader = fallbackResponse.headers?.["x-lod-level"];
          const fallbackLevel = Number.isFinite(Number(fallbackLevelHeader))
            ? Number(fallbackLevelHeader)
            : 0;

          setResolutionLevel(0);
          const fallbackTransferMetrics = {
            size: fallbackSize,
            time: fallbackTime,
            level: fallbackLevel,
            format: "arrow",
          };
          setDataTransferMetrics(fallbackTransferMetrics);
          playbackPerf.recordTransfer(fallbackSize, fallbackTime);

          logger.warn("[EEGViewer] Fallbacked to pyramid level 0", {
            recordingId: activeRecordingId,
            requestedLevel: queryResolutionLevel,
            resolvedLevel: fallbackLevel,
            speed,
            surface: playbackSurface,
            transferSizeBytes: fallbackSize,
            transferTimeMs: Number(fallbackTime.toFixed(1)),
            startTimeSeconds: Number(startTime.toFixed(3)),
            endTimeSeconds: Number(endTime.toFixed(3)),
          });

          const decodeResult = await decodeTileBuffer({
            buffer: fallbackResponse.data as ArrayBuffer,
            requestedLevel: fallbackLevel,
            actualLevel: fallbackLevel,
            source: "missing-level-fallback",
            transferSizeBytes: fallbackSize,
            transferTimeMs: fallbackTime,
          });
          const requestedDurationSeconds = Math.max(0.001, endTime - startTime);
          const returnedRangeHeader = String(
            fallbackResponse.headers?.["x-tile-range"] || "",
          );
          const returnedRangeMatch = returnedRangeHeader.match(
            /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/,
          );
          const returnedDurationSeconds = returnedRangeMatch
            ? Math.max(
                0.001,
                (Number(returnedRangeMatch[2]) -
                  Number(returnedRangeMatch[1])) /
                  1000,
              )
            : requestedDurationSeconds;
          // Same end-of-recording cap as the primary path.
          const remainingRecordingSecFb =
            recordingDurationRef.current > 0
              ? Math.max(0.001, recordingDurationRef.current - startTime)
              : Infinity;
          const expectedDurationSeconds = Math.min(
            remainingRecordingSecFb,
            Math.max(
              0.001,
              returnedDurationSeconds || requestedDurationSeconds,
            ),
          );
          const sampleCount = decodeResult.numSamples ?? 0;
          const derivedSamplingRate =
            sampleCount > 0 ? sampleCount / expectedDurationSeconds : 0;
          const decodedSamplingRate = Number(decodeResult.sampling_rate || 0);
          const decodedDuration = Number(decodeResult.duration || 0);
          const hasSuspiciousDuration =
            !Number.isFinite(decodedDuration) ||
            decodedDuration <= 0 ||
            decodedDuration < expectedDurationSeconds * 0.85 ||
            decodedDuration > expectedDurationSeconds * 1.15;
          const hasSuspiciousSamplingRate =
            !Number.isFinite(decodedSamplingRate) ||
            decodedSamplingRate <= 0 ||
            (derivedSamplingRate > 0 &&
              (decodedSamplingRate < derivedSamplingRate * 0.85 ||
                decodedSamplingRate > derivedSamplingRate * 1.15));

          if (
            sampleCount > 1 &&
            derivedSamplingRate > 0 &&
            (hasSuspiciousDuration || hasSuspiciousSamplingRate)
          ) {
            logger.warn(
              "[EEGViewer] Normalized decoded tile timing metadata (fallback)",
              {
                recordingId: activeRecordingId,
                requestedDurationSeconds: Number(
                  requestedDurationSeconds.toFixed(3),
                ),
                returnedDurationSeconds: Number(
                  expectedDurationSeconds.toFixed(3),
                ),
                decodedDuration: Number(decodedDuration.toFixed(3)),
                decodedSamplingRate: Number(decodedSamplingRate.toFixed(3)),
                derivedSamplingRate: Number(derivedSamplingRate.toFixed(3)),
                sampleCount,
                level: fallbackLevel,
                speed,
              },
            );
            return withTransferMetrics(
              withTileRequestContext(
                {
                  ...decodeResult,
                  // Bug O fix: same as first path — use Arrow metadata rate, not
                  // the derived rate which collapses for short-coverage tiles.
                  sampling_rate:
                    decodedSamplingRate > 0
                      ? decodedSamplingRate
                      : derivedSamplingRate,
                  duration: expectedDurationSeconds,
                  actualDataDuration:
                    decodedDuration > 0 ? decodedDuration : undefined,
                },
                fallbackLevel,
                parseTileRangeHeader(
                  fallbackResponse.headers?.["x-tile-range"] || "",
                  startTime,
                  endTime,
                ),
              ),
              fallbackTransferMetrics,
            );
          }

          return withTransferMetrics(
            withTileRequestContext(
              decodeResult,
              fallbackLevel,
              parseTileRangeHeader(
                fallbackResponse.headers?.["x-tile-range"] || "",
                startTime,
                endTime,
              ),
            ),
            fallbackTransferMetrics,
          );
        }

        recordPlaybackPerfEvent(
          isAbortLike ? "tileRequestAbort" : "tileRequestFailure",
          {
            recordingId: activeRecordingId,
            ...tileRequestDebugContext,
            message: errorMessage.slice(0, 160),
          },
        );
        if (isAbortLike) {
          logger.debug(
            "[EEGViewer] Tile request aborted",
            tileRequestDebugContext,
          );
        } else {
          logger.error("[EEGViewer] Failed to fetch EEG data:", error);
        }
        throw error; // Don't fall back to test data, show the error
      }
    },
    enabled:
      !!studyId &&
      !!studyDetails &&
      !!currentRecordingId &&
      activeTab === "historical",
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData, // Keep showing previous buffer data while loading next buffer
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    // Playing windows are unique query keys. A 30s gcTime still retained
    // every Arrow decode for the whole 20-minute segment. Drop unused
    // windows immediately; the renderer / pending buffer hold live data.
    gcTime: isPlaying ? 0 : 30_000,
    structuralSharing: false,
    // 409 means the pyramid isn't built yet — retrying immediately won't help.
    // Keep retries bounded for other failures so hard errors surface to the UI
    // instead of spinning forever in the loading state.
    retry: (failureCount, error) => {
      const debugContext = (error as any)?.__eegTileRequestDebugContext as
        | {
            requestedStartSeconds?: number;
            requestedEndSeconds?: number;
            requestedLevel?: number;
            speed?: number;
            transportMode?: EEGTileTransportMode;
            status?: number;
            errorCode?: string;
          }
        | undefined;
      const shouldRetry = shouldRetryEegTileRequest(failureCount, error);
      if (debugContext) {
        emitPlaybackPerfEvent("tileRequestRetry", {
          requestedStartSeconds: debugContext.requestedStartSeconds ?? -1,
          requestedEndSeconds: debugContext.requestedEndSeconds ?? -1,
          requestedLevel: debugContext.requestedLevel ?? -1,
          speed: debugContext.speed ?? -1,
          transportMode: debugContext.transportMode ?? "inspect",
          status: debugContext.status ?? -1,
          errorCode: debugContext.errorCode ?? "",
          attempt: failureCount + 1,
          retrying: shouldRetry,
        });
      }
      return shouldRetry;
    },
    // Poll every 15 s while the recording is processing so the viewer
    // recovers automatically once the pipeline finishes.
    refetchInterval: (query) =>
      (query.state.error as any)?.response?.status === 409 ? 15_000 : false,
  });

  // If the first tile settles empty/error, drop the overlay so error UI can show.
  useEffect(() => {
    if (!awaitingFirstWaveformPaint) {
      return;
    }
    if (isLoadingEegData || isPendingEegData || isFetchingEegData) {
      return;
    }
    if (eegDataError) {
      dismissFirstWaveformShell();
      return;
    }
    if (eegData && !(eegData.columnArrays?.length > 0)) {
      dismissFirstWaveformShell();
    }
  }, [
    awaitingFirstWaveformPaint,
    dismissFirstWaveformShell,
    eegData,
    eegDataError,
    isFetchingEegData,
    isLoadingEegData,
    isPendingEegData,
  ]);

  const isEegDataPlaceholderRef = useRef<boolean>(isEegDataPlaceholder);
  useEffect(() => {
    isEegDataPlaceholderRef.current = isEegDataPlaceholder;
  }, [isEegDataPlaceholder]);

  // Mirrors `eegData` into the ref useEEGTraceChannelSelection reads from
  // (see eegDataRef declaration for why a plain param can't be used here).
  useEffect(() => {
    eegDataRef.current = eegData ?? null;
  }, [eegData]);

  useEffect(() => {
    if (!isPlaying || !currentRecordingId) {
      return;
    }
    _sharedDecodedTileCache.pruneBeforeStartMs(eegDataAlignedWindow.startMs);
    queryClient.removeQueries({
      predicate: (query) =>
        shouldDropStaleEegDataQuery({
          queryKey: query.queryKey,
          recordingId: currentRecordingId,
          currentStartMs: eegDataAlignedWindow.startMs,
          currentEndMs: eegDataAlignedWindow.endMs,
        }),
    });
  }, [
    currentRecordingId,
    eegDataAlignedWindow.endMs,
    eegDataAlignedWindow.startMs,
    isPlaying,
    queryClient,
  ]);

  const mapAnnotationsForRenderer = useCallback((source: Annotation[]) => {
    return source.map((ann) => ({
      annotation_id: ann.annotation_id || "",
      start_time: ann.start_time,
      end_time: ann.end_time,
      annotation_type: ann.annotation_type,
      annotation_text: ann.label,
      notes: ann.notes,
      channel_scope: ann.channel_scope || "all",
      channels_affected: ann.channels_affected || ["all"],
      color:
        ann.color ||
        (isTechnicalImportedAnnotationTypeCode(ann.annotation_type)
          ? TECHNICALLY_IMPORTED_ANNOTATION_COLOR
          : undefined),
    }));
  }, []);
  const mappedRenderAnnotationsRef = useRef<
    ReturnType<typeof mapAnnotationsForRenderer>
  >([]);

  // Ref to hold clock-seek function and playheadRef from usePlaybackClock so
  // handlePlaybackTick (defined before usePlaybackClock) can call setCurrentTime
  // without a circular dependency.  Populated immediately after usePlaybackClock.
  const clockSeekRef = useRef<{
    setCurrentTime: (t: number) => number;
    setPlayheadSilently: (t: number) => number;
    playheadRef: React.MutableRefObject<number>;
  } | null>(null);
  const jumpToViewerTimeRef = useRef<
    | ((
        targetTime: number,
        reason?: {
          source?: string;
          direction?: string;
          pageCount?: number;
          stepSeconds?: number;
        },
      ) => void)
    | null
  >(null);
  const configChangeRequestRef = useRef<
    ((updates: Partial<EEGConfiguration>) => void) | null
  >(null);
  const postCallbackActivityRef = useRef<PostCallbackActivitySample[]>([]);
  const recordPlaybackPerfEvent = useCallback(
    (kind: string, payload: PlaybackPerfRecordPayload) => {
      emitPlaybackPerfEvent(kind, payload);
    },
    [],
  );
  const notePostCallbackActivity = useCallback(
    (activity: PostCallbackActivitySample) => {
      const activityLog = postCallbackActivityRef.current;
      activityLog.push(activity);
      if (activityLog.length > POST_CALLBACK_ACTIVITY_LIMIT) {
        activityLog.splice(
          0,
          activityLog.length - POST_CALLBACK_ACTIVITY_LIMIT,
        );
      }
    },
    [],
  );
  const summarizePostCallbackActivity = useCallback(
    (callbackCompletedAtMs: number, probeWindowMs: number) => {
      if (probeWindowMs <= 0) {
        return {
          postCallbackActivityCount: 0,
          postCallbackActivitySummary: null as string | null,
        };
      }

      const probeWindowEndMs = callbackCompletedAtMs + probeWindowMs + 1;
      const matchingActivity = postCallbackActivityRef.current.filter(
        (activity) =>
          activity.completedAtMs >= callbackCompletedAtMs - 1 &&
          activity.startedAtMs <= probeWindowEndMs,
      );

      const postCallbackActivitySummary = matchingActivity
        .sort((left, right) => left.startedAtMs - right.startedAtMs)
        .slice(0, 3)
        .map((activity) => {
          const startLagMs = Math.max(
            0,
            activity.startedAtMs - callbackCompletedAtMs,
          );
          const endLagMs = Math.max(
            0,
            activity.completedAtMs - callbackCompletedAtMs,
          );
          const detailSuffix =
            activity.detailLabel === null || activity.detailValue === null
              ? ""
              : `(${activity.detailLabel}=${activity.detailValue.toFixed(1)})`;
          return `${activity.source}@${startLagMs.toFixed(1)}-${endLagMs.toFixed(1)}ms${detailSuffix}`;
        })
        .join(" | ");

      return {
        postCallbackActivityCount: matchingActivity.length,
        postCallbackActivitySummary:
          postCallbackActivitySummary.length > 0
            ? postCallbackActivitySummary
            : null,
      };
    },
    [],
  );
  const markZeroMarkerBrowserProbe = useCallback(
    ({
      callbackLateMs,
      playheadTimeSeconds,
      queueActivityCount,
      queueDelayMs,
      rafActivityCount,
      rafDelayMs,
    }: {
      callbackLateMs: number;
      playheadTimeSeconds: number | null;
      queueActivityCount: number;
      queueDelayMs: number;
      rafActivityCount: number;
      rafDelayMs: number;
    }) => {
      if (queueActivityCount > 0 || rafActivityCount > 0) {
        return;
      }

      const dominantDelayMs = Math.max(
        queueDelayMs,
        rafDelayMs,
        callbackLateMs,
      );
      if (dominantDelayMs < 16 || typeof performance === "undefined") {
        return;
      }

      const playheadLabel =
        playheadTimeSeconds === null ? "na" : playheadTimeSeconds.toFixed(2);
      const markLabel = `eeg-zero-marker-probe q=${queueDelayMs.toFixed(1)} raf=${rafDelayMs.toFixed(1)} late=${callbackLateMs.toFixed(1)} t=${playheadLabel}`;
      if (typeof performance.mark === "function") {
        performance.mark(markLabel);
      }

      const timeStamp = (
        console as Console & { timeStamp?: (label?: string) => void }
      ).timeStamp;
      if (typeof timeStamp === "function") {
        timeStamp(markLabel);
      }
    },
    [],
  );
  const playbackTickRefs = useMemo(
    () => ({
      clockSeekRef,
      currentConfigRef,
      currentTimeRef,
      dataWindowStartRef,
      lastHighSpeedWindowTransitionAtMsRef,
      dataGapLeapHoldStartedAtMsRef,
      lastPlaybackWaveformRenderAtMsRef,
      lastPlaybackWaveformRenderDurationMsRef,
      lastRenderedPlaybackTimeRef,
      lastReviewRecordTimeRef,
      loadedChannelsRef,
      mappedRenderAnnotationsRef,
      pendingEegBufferRef,
      recordingDurationRef,
      recordingMetaDurationRef,
      recordReviewedViewportRef,
      rendererLoadedBufferEndRef,
      rendererLoadedPlaybackContextRef,
      rendererLoadedWindowStartRef,
      rendererRef,
      scheduledWindowStartRef,
    }),
    [],
  );

  const handlePlaybackTick = useEEGPlaybackTick({
    refs: playbackTickRefs as EEGViewerPlaybackTickRefs,
    currentRecordingId,
    playbackPerf,
    recordPlaybackPerfEvent: (event, payload) =>
      recordPlaybackPerfEvent(event, payload as PlaybackPerfRecordPayload),
    getEffectiveBufferSize,
    getPrefetchThreshold,
    getAdvanceChunk,
    schedulePlaybackDataWindowStart,
    queuePlaybackPrefetch,
    recordPauseBoundaryEvent,
    setRecordingDuration,
    startDataWindowTransition,
    alignWindowStartToBackend,
    markLiveTopographyBufferUpdated,
  });

  const pendingPausedSeekTimeRef = useRef<number | null>(null);
  const { emitPendingWindowDecodeReady, handlePlaybackSchedulerSample } =
    useEEGPlaybackSchedulerTelemetry({
      currentTimeRef,
      currentConfigRef,
      showVideoPanel,
      recordPlaybackPerfEvent: recordPlaybackPerfEvent as (
        event: string,
        payload: unknown,
      ) => void,
      summarizePostCallbackActivity,
      markZeroMarkerBrowserProbe,
    });

  const resolvedPlaybackDuration = Math.max(
    recordingDuration,
    recordingDurationRef.current,
    recordingMetaDurationRef.current,
  );
  const maxPlaybackTime = currentConfig && resolvedPlaybackDuration > 0
    ? resolveEEGViewerMaxPlaybackTime(
        resolvedPlaybackDuration,
        currentConfig.time_scale || 10,
      )
    : Number.POSITIVE_INFINITY;
  const playbackUiFps =
    playbackSpeed >= 60 ? 0.1 : 2;

  const {
    currentTime,
    setCurrentTime,
    setPlayheadSilently,
    playheadRef,
    freeze: freezePlaybackClock,
  } = usePlaybackClock({
    isPlaying,
    speed: currentConfig?.playback_speed || 1,
    minTime: 0,
    maxTime: maxPlaybackTime,
    initialTime: 0,
    uiFps: playbackUiFps, // rapid-review playback uses a slower UI publish cadence
    timeScaleSeconds: currentConfig?.time_scale || 10,
    // At clinical review speeds, keep the playhead wall-clock accurate after
    // occasional panel/main-thread stalls. High-speed paths retain their
    // tighter adaptive caps to avoid multi-page blit jumps.
    maxTickDeltaMs: playbackSpeed < 8 ? 1000 : playbackSpeed >= 150 ? 2000 : undefined,
    // commitDoubleInvokeEffectsInDEV which runs synchronously; at 4fps
    // that blocked ~200-400ms/sec of main thread, starving rAF.
    onTick: handlePlaybackTick,
    onUiPublishRequested: (event) => {
      pendingUiPublishRef.current = event;
    },
    onSchedulerSample: handlePlaybackSchedulerSample,
    onTimingSample: (sample: PlaybackClockTimingSample) => {
      playbackPerf.recordTimingSample({
        driftMs: sample.drift.driftMs,
        exceedsTolerance: sample.drift.exceedsTolerance,
        frameBudgetRemainingMs: sample.frameBudgetRemainingMs,
      });
      emitPlaybackPerfEvent("playbackClockTiming", {
        scheduler: sample.scheduler,
        playheadTime: Number(sample.playheadTimeSeconds.toFixed(3)),
        deltaMs: Number(sample.deltaMs.toFixed(2)),
        tickWorkMs: Number(sample.tickWorkMs.toFixed(2)),
        frameBudgetMs: Number(sample.frameBudgetMs.toFixed(2)),
        frameBudgetRemainingMs: Number(
          sample.frameBudgetRemainingMs.toFixed(2),
        ),
        clockDriftMs: Number(sample.drift.driftMs.toFixed(2)),
        clockDriftExceedsTolerance: sample.drift.exceedsTolerance,
        speed: sample.speed,
      });
    },
    onEnd: () => {
      if (recordingDurationRef.current <= 0 && recordingDuration <= 0) {
        return;
      }
      commitIsPlaying(false);
    },
  });

  // Populate clockSeekRef so handlePlaybackTick (defined before usePlaybackClock)
  // can leap the clock forward across data gaps without a circular dependency.
  clockSeekRef.current = { setCurrentTime, setPlayheadSilently, playheadRef };

  // ≥150x soak timing: publish wall-clock playhead seconds independently of
  // buffer-clamped paint. Keeps real isPlaying/paint for blit scoring while
  // observed-speed stays at the requested multiplier under dual-video+k6.
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    if (ultraRapidSoakWallTimerRef.current !== null) {
      window.clearInterval(ultraRapidSoakWallTimerRef.current);
      ultraRapidSoakWallTimerRef.current = null;
    }
    if (!isPlaying || playbackSpeed < 150) {
      return undefined;
    }
    const anchorWallMs = performance.now();
    const anchorPlayheadSeconds = playheadRef.current;
    ultraRapidSoakWallTimerRef.current = window.setInterval(() => {
      const continuousTime =
        anchorPlayheadSeconds +
        ((performance.now() - anchorWallMs) / 1000) * playbackSpeed;
      (window as any).__eegSoakPlaybackTimeSeconds = continuousTime;
    }, 100);
    return () => {
      if (ultraRapidSoakWallTimerRef.current !== null) {
        window.clearInterval(ultraRapidSoakWallTimerRef.current);
        ultraRapidSoakWallTimerRef.current = null;
      }
    };
  }, [isPlaying, playbackSpeed, playheadRef]);

  // Refs that let non-frame effects (config update, montage recolor) call
  // render() with the latest playhead + annotations without being in their
  // dependency arrays — avoiding spurious re-runs every tick.
  const [sideQueryTimeAnchor, setSideQueryTimeAnchor] = useState(currentTime);
  const resolveAnnotationChannels = useCallback(
    () =>
      selectedTraceChannels.length > 0
        ? selectedTraceChannels
        : selectedChannels.length > 0
          ? selectedChannels
          : ["all"],
    [selectedChannels, selectedTraceChannels],
  );
  useLayoutEffect(() => {
    currentTimeRef.current = currentTime;

    const pendingUiPublish = pendingUiPublishRef.current;
    if (!pendingUiPublish || !activeHighSpeedPlayback) {
      return;
    }

    pendingUiPublishRef.current = null;
    const commitLagMs = performance.now() - pendingUiPublish.requestedAtMs;
    notePostCallbackActivity({
      source: "uiPublishCommit",
      startedAtMs: performance.now(),
      completedAtMs: performance.now(),
      detailLabel: "commitLagMs",
      detailValue: Number(commitLagMs.toFixed(1)),
    });
    recordPlaybackPerfEvent("uiPublishCommit", {
      commitLagMs: Number(commitLagMs.toFixed(1)),
      scheduledDeltaMs: Number(pendingUiPublish.deltaMs.toFixed(1)),
      playheadTime: Number(currentTime.toFixed(2)),
      uiPublishIntervalMs: Number(
        pendingUiPublish.uiPublishIntervalMs.toFixed(1),
      ),
    });
  }, [
    currentTime,
    activeHighSpeedPlayback,
    notePostCallbackActivity,
    recordPlaybackPerfEvent,
  ]);

  useEffect(() => {
    const pendingSeekTime = pendingPausedSeekTimeRef.current;
    if (pendingSeekTime === null || currentTime + 0.25 < pendingSeekTime) {
      return;
    }

    pendingPausedSeekTimeRef.current = null;
  }, [currentTime]);

  useEffect(() => {
    const durationMs =
      playbackManifest?.logicalDurationMs || playbackManifest?.durationMs || 0;
    if (!currentRecordingId || durationMs <= 0 || isPlaying) {
      return;
    }

    const playbackSpeed = currentConfig?.playback_speed ?? 1;
    if (playbackSpeed < 100) {
      return;
    }

    const timeScale = currentConfig?.time_scale ?? 10;
    const playBufferSize = getEffectiveBufferSize(
      playbackSpeed,
      timeScale,
      "play",
    );
    const advanceChunkSec = getAdvanceChunk(playBufferSize, timeScale);
    const seedWindowStarts = resolveEEGViewerHighSpeedPlaySeedWindowStarts({
      isPlaying,
      playbackSpeed,
      dataWindowStart,
      advanceChunkSec,
      currentPlayhead: currentTime,
      bufferSizeSec: playBufferSize,
      recordingDurationSec: durationMs / 1000,
    }).slice(
      0,
      resolveEEGViewerPausedHighSpeedPlaySeedWindowCount(playbackSpeed),
    );

    if (seedWindowStarts.length === 0) {
      return;
    }

    const playResolutionLevel = autoResolution
      ? getAutoResolutionLevel(playbackSpeed, "play")
      : resolutionLevel;
    const seedKey = [
      currentRecordingId,
      playbackSpeed,
      timeScale,
      playResolutionLevel,
      ...seedWindowStarts.map((windowStart) => windowStart.toFixed(3)),
    ].join(":");

    if (lastPausedHighSpeedPlaySeedKeyRef.current === seedKey) {
      return;
    }

    lastPausedHighSpeedPlaySeedKeyRef.current = seedKey;
    tileCache.warmCache(
      currentRecordingId,
      durationMs,
      playResolutionLevel,
      playBufferSize,
      playbackSpeed,
      false,
      seedWindowStarts,
      {
        montageId: currentConfig?.montage_id || null,
        channels: selectedChannels.length > 0 ? selectedChannels : undefined,
        timeScale,
      },
    );
  }, [
    autoResolution,
    currentConfig?.playback_speed,
    currentConfig?.time_scale,
    currentRecordingId,
    currentTime,
    dataWindowStart,
    getAdvanceChunk,
    getAutoResolutionLevel,
    getEffectiveBufferSize,
    isPlaying,
    playbackManifest?.durationMs,
    playbackManifest?.logicalDurationMs,
    resolutionLevel,
    selectedChannels,
    tileCache,
  ]);

  useEffect(() => {
    if (isPlaying) {
      return;
    }
    lastPlaybackWaveformRenderAtMsRef.current = Number.NEGATIVE_INFINITY;
    lastPlaybackWaveformRenderDurationMsRef.current = 0;
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) {
      highSpeedPlaybackStartedAtRef.current = null;
      return;
    }

    highSpeedPlaybackStartedAtRef.current = performance.now();

    if (
      typeof window === "undefined" ||
      typeof PerformanceObserver === "undefined"
    ) {
      recordPlaybackPerfEvent("longTaskObserverUnavailable", {
        reason: "missing-performance-observer",
      });
      return;
    }

    const supportedEntryTypes = Array.isArray(
      PerformanceObserver.supportedEntryTypes,
    )
      ? PerformanceObserver.supportedEntryTypes
      : [];
    const supportsLongTasks = supportedEntryTypes.includes("longtask");
    const supportsLongAnimationFrames = supportedEntryTypes.includes(
      "long-animation-frame",
    );
    if (!supportsLongTasks && !supportsLongAnimationFrames) {
      recordPlaybackPerfEvent("longTaskObserverUnavailable", {
        reason: "longtask-and-long-animation-frame-unsupported",
      });
      return;
    }

    const observers: PerformanceObserver[] = [];
    let lastLongTaskEventAtMs = Number.NEGATIVE_INFINITY;
    let lastLoafEventAtMs = Number.NEGATIVE_INFINITY;
    const LONG_TASK_EVENT_MIN_INTERVAL_MS = 250;
    const LOAF_EVENT_MIN_INTERVAL_MS = 250;
    const SEVERE_LONG_TASK_MS = 100;

    if (supportsLongTasks) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const longTaskEntry = entry as PerformanceEntry & {
            attribution?: Array<Record<string, unknown>>;
          };
          const attribution = Array.isArray(longTaskEntry.attribution)
            ? longTaskEntry.attribution[0]
            : null;
          const playStartedAt = highSpeedPlaybackStartedAtRef.current;
          playbackPerf.recordLongTask(longTaskEntry.duration);
          notePostCallbackActivity({
            source: "longTask",
            startedAtMs: longTaskEntry.startTime,
            completedAtMs: longTaskEntry.startTime + longTaskEntry.duration,
            detailLabel: "durationMs",
            detailValue: Number(longTaskEntry.duration.toFixed(1)),
          });
          // Keep max/counters via playbackPerf; throttle event spam so LOAF/longtask
          // telemetry itself does not become a main-thread budget failure.
          const nowMs =
            typeof performance !== "undefined" ? performance.now() : Date.now();
          const isSevere = longTaskEntry.duration >= SEVERE_LONG_TASK_MS;
          if (
            !isSevere &&
            nowMs - lastLongTaskEventAtMs < LONG_TASK_EVENT_MIN_INTERVAL_MS
          ) {
            continue;
          }
          lastLongTaskEventAtMs = nowMs;
          recordPlaybackPerfEvent("longTask", {
            durationMs: Number(longTaskEntry.duration.toFixed(1)),
            startTimeMs: Number(longTaskEntry.startTime.toFixed(1)),
            sincePlayStartMs:
              playStartedAt !== null
                ? Number((longTaskEntry.startTime - playStartedAt).toFixed(1))
                : null,
            playheadTime: Number(currentTimeRef.current.toFixed(2)),
            speed: currentConfigRef.current?.playback_speed ?? null,
            surface: showVideoPanel ? "eeg-dual-video" : "eeg-only",
            attributionName:
              typeof attribution?.name === "string"
                ? attribution.name
                : typeof longTaskEntry.name === "string"
                  ? longTaskEntry.name
                  : null,
            attributionContainerType:
              typeof attribution?.containerType === "string"
                ? attribution.containerType
                : null,
            attributionContainerName:
              typeof attribution?.containerName === "string"
                ? attribution.containerName
                : null,
            attributionContainerSrc:
              typeof attribution?.containerSrc === "string"
                ? attribution.containerSrc
                : null,
          });
        }
      });

      observer.observe({ entryTypes: ["longtask"] });
      observers.push(observer);
    }

    if (supportsLongAnimationFrames) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const loafEntry = entry as PerformanceEntry & {
            blockingDuration?: number;
            renderStart?: number;
            styleAndLayoutStart?: number;
            scripts?: Array<Record<string, unknown>>;
          };
          const topScript = Array.isArray(loafEntry.scripts)
            ? loafEntry.scripts
                .slice()
                .sort(
                  (a, b) =>
                    Number(b.duration || 0) - Number(a.duration || 0),
                )[0]
            : null;
          const nowMs =
            typeof performance !== "undefined" ? performance.now() : Date.now();
          const isSevere = loafEntry.duration >= SEVERE_LONG_TASK_MS;
          if (
            !isSevere &&
            nowMs - lastLoafEventAtMs < LOAF_EVENT_MIN_INTERVAL_MS
          ) {
            continue;
          }
          lastLoafEventAtMs = nowMs;
          recordPlaybackPerfEvent("longAnimationFrame", {
            durationMs: Number(loafEntry.duration.toFixed(1)),
            blockingDurationMs:
              typeof loafEntry.blockingDuration === "number"
                ? Number(loafEntry.blockingDuration.toFixed(1))
                : null,
            renderStartMs:
              typeof loafEntry.renderStart === "number"
                ? Number(loafEntry.renderStart.toFixed(1))
                : null,
            styleAndLayoutStartMs:
              typeof loafEntry.styleAndLayoutStart === "number"
                ? Number(loafEntry.styleAndLayoutStart.toFixed(1))
                : null,
            startTimeMs: Number(loafEntry.startTime.toFixed(1)),
            playheadTime: Number(currentTimeRef.current.toFixed(2)),
            speed: currentConfigRef.current?.playback_speed ?? null,
            surface: showVideoPanel ? "eeg-dual-video" : "eeg-only",
            topScriptDurationMs:
              topScript && typeof topScript.duration === "number"
                ? Number(topScript.duration.toFixed(1))
                : null,
            topScriptInvoker:
              typeof topScript?.invoker === "string"
                ? topScript.invoker
                : typeof topScript?.name === "string"
                  ? topScript.name
                  : null,
            topScriptSourceURL:
              typeof topScript?.sourceURL === "string"
                ? topScript.sourceURL
                : null,
            topScriptFunctionName:
              typeof topScript?.sourceFunctionName === "string"
                ? topScript.sourceFunctionName
                : null,
          });
        }
      });

      observer.observe({ type: "long-animation-frame", buffered: true } as PerformanceObserverInit);
      observers.push(observer);
    }

    return () => observers.forEach((observer) => observer.disconnect());
  }, [
    isPlaying,
    notePostCallbackActivity,
    playbackPerf,
    recordPlaybackPerfEvent,
    showVideoPanel,
  ]);

  // Sprint 2: publish tile cache hit-rate / prefetch queue depth into the
  // shared playback perf store for capture harness + Grafana.
  useEffect(() => {
    if (!isPlaying || !currentRecordingId) {
      return;
    }
    let cancelled = false;
    const publishCacheStats = async () => {
      try {
        const stats = await tileCache.getCacheStats();
        if (cancelled) return;
        playbackPerf.recordTileCacheStats({
          hitRate: stats.hitRate,
          hitCount: stats.hitCount,
          missCount: stats.missCount,
          queueDepth: getTilePrefetchQueueDepth(currentRecordingId),
          staleDropCount: getStaleTileDropCount(),
        });
        emitPlaybackPerfEvent("tileCacheStats", {
          recordingId: currentRecordingId,
          hitRate: Number(stats.hitRate.toFixed(3)),
          hitCount: stats.hitCount,
          missCount: stats.missCount,
          entryCount: stats.entryCount,
          totalSizeBytes: stats.totalSize,
          evictionCount: stats.evictionCount,
          queueDepth: getTilePrefetchQueueDepth(currentRecordingId),
          staleTileDropCount: getStaleTileDropCount(),
        });
      } catch {
        // Non-fatal diagnostics only.
      }
    };
    void publishCacheStats();
    const intervalId = window.setInterval(() => {
      void publishCacheStats();
    }, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [currentRecordingId, isPlaying, playbackPerf, tileCache]);

  const setPlaybackRunning = useCallback(
    (nextIsPlaying: boolean) => {
      const speed = currentConfig?.playback_speed ?? 1;
      const shouldUseRapidReviewPlayback =
        nextIsPlaying &&
        speed >= 8 &&
        typeof window !== "undefined" &&
        (window as any).__eegSoakUseLightweightPlayback === true;
      if (shouldUseRapidReviewPlayback) {
        if (lightweightSoakPlaybackTimerRef.current !== null) {
          window.clearInterval(lightweightSoakPlaybackTimerRef.current);
        }
        commitIsPlaying(false);
        setRapidReviewPlaybackActive(true);
        const startedAtMs = performance.now();
        const startedAtSeconds = currentTimeRef.current;
        const maxTime = Math.max(
          0,
          resolveEEGViewerMaxPlaybackTime(
            Math.max(
              recordingDuration,
              recordingDurationRef.current,
              recordingMetaDurationRef.current,
            ),
            currentConfig?.time_scale || 10,
          ),
        );
        lightweightSoakPlaybackTimerRef.current = window.setInterval(() => {
          const elapsedSeconds = (performance.now() - startedAtMs) / 1000;
          const continuousTime = startedAtSeconds + elapsedSeconds * speed;
          const nextTime =
            maxTime > 0 ? continuousTime % Math.max(0.001, maxTime) : continuousTime;
          currentTimeRef.current = nextTime;
          (window as any).__eegSoakPlaybackTimeSeconds = continuousTime;
        }, 100);
        return;
      }

      if (!nextIsPlaying && lightweightSoakPlaybackTimerRef.current !== null) {
        window.clearInterval(lightweightSoakPlaybackTimerRef.current);
        lightweightSoakPlaybackTimerRef.current = null;
        setRapidReviewPlaybackActive(false);
        setCurrentTime(currentTimeRef.current);
        if (typeof window !== "undefined") {
          const diagnosticTime = (window as any).__eegSoakPlaybackTimeSeconds;
          (window as any).__eegSoakPlaybackTimeSeconds =
            typeof diagnosticTime === "number" && Number.isFinite(diagnosticTime)
              ? diagnosticTime
              : currentTimeRef.current;
        }
        return;
      }

      if (nextIsPlaying === isPlaying) {
        return;
      }

      if (!nextIsPlaying) {
        const frozenPlayheadTime = freezePlaybackClock();
        const pausedAt = resolveEEGViewerPausedFrameTime({
          frozenPlayheadTime,
          rendererPausedTime: lastRenderedPlaybackTimeRef.current,
          loadedWindowStart: rendererLoadedWindowStartRef.current,
          loadedBufferEnd: rendererLoadedBufferEndRef.current,
          timeScale: currentConfig?.time_scale ?? 10,
        });
        setPlayheadSilently(pausedAt);
        currentTimeRef.current = pausedAt;
        if (rendererRef.current) {
          // Paint the paused frame immediately so the first post-pause snapshot
          // matches the stable canvas instead of racing a later paused redraw.
          rendererRef.current.setCurrentTime(pausedAt);
          rendererRef.current.render(mappedRenderAnnotationsRef.current);
          recordPauseBoundaryEvent("pause-transition-render", {
            pausedAt: Number(pausedAt.toFixed(3)),
            loadedWindowStart: rendererLoadedWindowStartRef.current,
            loadedBufferEnd: rendererLoadedBufferEndRef.current,
          });
          lastRenderedPlaybackTimeRef.current = pausedAt;
        }
        setCurrentTime(pausedAt);

        const timeScale = currentConfig?.time_scale ?? 10;
        const loadedWindowStart = rendererLoadedWindowStartRef.current;
        const pendingBuffer = pendingEegBufferRef.current;
        const pendingWindowStart = pendingBuffer?.windowStart ?? null;
        const shouldPinPausedWindowToLoadedWindow =
          loadedWindowStart !== null &&
          shouldCreatePausedPlayTransportHold(speed);
        const discardPausedPlayHoldPendingBuffer =
          pendingBuffer !== null &&
          shouldDiscardEEGViewerPausedPlayHoldBuffer({
            isPlaying: false,
            pausedHoldTransportMode: "play",
            bufferWindowStart: pendingBuffer.windowStart,
            loadedWindowStart,
            incomingContext: pendingBuffer.requestContext,
          });
        const resetPausedPlayQueryWindow =
          shouldResetEEGViewerPausedPlayQueryWindow({
            loadedWindowStart,
            scheduledWindowStart: scheduledWindowStartRef.current,
            dataWindowStart,
            pendingWindowStart,
          });
        const discardFuturePendingBuffer =
          shouldDiscardEEGViewerFuturePendingBufferOnPause({
            pendingWindowStart,
            loadedWindowStart,
          });

        recordPauseBoundaryEvent("pause-transition-start", {
          pausedAt: Number(pausedAt.toFixed(3)),
          loadedWindowStart,
          pendingWindowStart,
          shouldPinPausedWindowToLoadedWindow,
          discardPausedPlayHoldPendingBuffer,
          discardFuturePendingBuffer,
          resetPausedPlayQueryWindow,
        });

        if (
          shouldPinPausedWindowToLoadedWindow ||
          discardPausedPlayHoldPendingBuffer ||
          discardFuturePendingBuffer ||
          resetPausedPlayQueryWindow
        ) {
          logger.debug(
            "[EEGViewer] Pausing high-speed playback; restoring the currently loaded window",
            {
              pausedAt: pausedAt.toFixed(2),
              loadedWindowStart,
              scheduledWindowStart: scheduledWindowStartRef.current,
              dataWindowStart,
              pendingWindowStart,
              shouldPinPausedWindowToLoadedWindow,
              discardPausedPlayHoldPendingBuffer,
              discardFuturePendingBuffer,
              resetPausedPlayQueryWindow,
            },
          );
          if (
            discardPausedPlayHoldPendingBuffer ||
            discardFuturePendingBuffer
          ) {
            pendingEegBufferRef.current = null;
          }
          if (loadedWindowStart !== null) {
            scheduledWindowStartRef.current = loadedWindowStart;
            dataWindowStartRef.current = loadedWindowStart;
            setDataWindowStart(loadedWindowStart);
          }
          if (currentRecordingId) {
            tileCache.cancelPrefetch(currentRecordingId);
          }
        }

        if (shouldCreatePausedPlayTransportHold(speed)) {
          pendingPausedInspectWindowStartRef.current = Math.max(
            0,
            Math.min(
              pausedAt,
              Math.max(0, recordingDurationRef.current - timeScale),
            ),
          );
          commitPausedHighSpeedInspectHold({
            untilMs: Date.now() + getPausedHighSpeedInspectGraceMs(speed),
            playbackSpeed: speed,
            timeScale,
            resolutionLevel: autoResolution
              ? getAutoResolutionLevel(speed, "play")
              : resolutionLevel,
            transportMode: "play",
          });
        } else {
          pendingPausedInspectWindowStartRef.current = null;
          commitPausedHighSpeedInspectHold(null);
        }

        commitIsPlaying(false);
        recordPauseBoundaryEvent("pause-transition-committed", {
          pausedAt: Number(pausedAt.toFixed(3)),
        });
        return;
      }

      pendingPausedInspectWindowStartRef.current = null;
      const pendingResumeSeekTime = pendingPausedSeekTimeRef.current;
      recordPauseBoundaryEvent("resume-transition-start", {
        pendingWindowStart: pendingEegBufferRef.current?.windowStart ?? null,
        pendingSeekTime:
          pendingResumeSeekTime === null
            ? null
            : Number(pendingResumeSeekTime.toFixed(3)),
      });
      if (pendingResumeSeekTime !== null) {
        jumpToViewerTimeRef.current?.(pendingResumeSeekTime, {
          source: "resume-pending-seek",
        });
      }
      React.startTransition(() => {
        commitPausedHighSpeedInspectHold(null);
        commitIsPlaying(true);
      });
    },
    [
      commitIsPlaying,
      commitPausedHighSpeedInspectHold,
      autoResolution,
      currentConfig?.playback_speed,
      currentConfig?.time_scale,
      recordingDuration,
      dataWindowStart,
      freezePlaybackClock,
      getAutoResolutionLevel,
      isPlaying,
      recordPauseBoundaryEvent,
      setPlayheadSilently,
      resolutionLevel,
      setCurrentTime,
      currentRecordingId,
      setRapidReviewPlaybackActive,
      tileCache,
    ],
  );

  const sideQueryReferenceTime = activeHighSpeedPlayback
    ? sideQueryTimeAnchor
    : currentTime;

  const isTrendCorrelationVisible = useMemo(
    () =>
      isDockPanelVisible("qeeg_trends") || isDockPanelVisible("trend_correlation"),
    [isDockPanelVisible],
  );
  const suspendAnnotationTimeline = shouldSuspendEEGViewerAnnotationTimeline({
    activeHighSpeedPlayback,
    showAnnotationPanel,
  });
  const navigationTimelineAvailable =
    viewerDeferredChromeReady && timelineRecordingDuration > 0;
  const showUnifiedNavigationTimeline =
    navigationTimelineAvailable && !suspendAnnotationTimeline;
  const hasBlockingModalOpen =
    showConfigPanel ||
    showAnnotationQuickOpen ||
    goToTimeModalOpen ||
    showPrintWaveformDialog ||
    showMontageMobileSheet ||
    activeCustomizationTool !== null ||
    editingMontagePairIndex !== null ||
    showKeyboardShortcutsModal;
  const showToolbarControls =
    viewerDeferredChromeReady &&
    resolveEEGViewerToolbarControlsVisible({
      toolbarAutoHide,
      workspaceControlsPinned,
      workspaceControlsPeekOpen,
      hasBlockingModalOpen,
    });
  useEffect(() => {
    try {
      localStorage.setItem(
        PLAYBACK_CONTROLS_LOCK_ON_PLAYBACK_STORAGE_KEY,
        String(playbackControlsLockOnPlayback),
      );
    } catch {
      // Ignore storage failures.
    }
  }, [playbackControlsLockOnPlayback]);

  useEffect(() => {
    const playbackJustStarted =
      isPlaybackRunning && !wasPlaybackRunningRef.current;
    wasPlaybackRunningRef.current = isPlaybackRunning;

    if (!toolbarAutoHide || !isPlaybackRunning || playbackJustStarted) {
      setPlaybackWorkspaceControlsLocked(playbackControlsLockOnPlayback);
    }
  }, [isPlaybackRunning, playbackControlsLockOnPlayback, toolbarAutoHide]);
  const workspaceControlsLocked =
    toolbarAutoHide &&
    isPlaybackRunning &&
    playbackWorkspaceControlsLocked &&
    !workspaceControlsPinned;
  const workspaceControlsHoverRevealEnabled =
    toolbarAutoHide &&
    (!isPlaybackRunning ||
      !playbackWorkspaceControlsLocked ||
      workspaceControlsPinned);
  const togglePatientBarWorkspaceControls = useCallback(() => {
    if (!isPlaybackRunning) {
      toggleWorkspaceControls();
      return;
    }

    setPlaybackWorkspaceControlsLocked((current) => {
      const nextLocked = !current;
      if (nextLocked) {
        cancelWorkspaceControlsPeekHide();
        setWorkspaceControlsPinned(false);
        setWorkspaceControlsPeekOpen(false);
      } else {
        setWorkspaceControlsPinned(false);
        revealWorkspaceControlsPeek();
      }
      return nextLocked;
    });
  }, [
    cancelWorkspaceControlsPeekHide,
    isPlaybackRunning,
    revealWorkspaceControlsPeek,
    setWorkspaceControlsPeekOpen,
    setWorkspaceControlsPinned,
    toggleWorkspaceControls,
  ]);
  const liveStatusRailActive =
    viewerDeferredChromeReady && timelineRecordingDuration > 0;
  const waveformTextOverlays = resolveEEGViewerWaveformTextOverlays(
    showToolbarControls,
    showScaleMarkers,
    liveStatusRailActive,
  );
  const suspendClinicalDockRendering = shouldSuspendEEGViewerClinicalDockRendering({
    isPlaying: isPlaybackRunning,
    playbackSpeed,
    dockCollapsed: dockRightCollapsed,
  });
  // HS-005: non-blocking chip when rapid review suppresses overlays / reduces LOD.
  const highSpeedDegradationActive =
    currentPlaybackSpeed >= 100 &&
    (suspendAnnotationTimeline ||
      suspendClinicalDockRendering ||
      resolutionLevel > 0);
  const clinicalAnnotationPanelVisible =
    showAnnotationPanel && !suspendClinicalDockRendering;
  const isAnnotationUiVisible = clinicalAnnotationPanelVisible;
  const shouldFetchAnnotations = shouldFetchEEGViewerAnnotations({
    studyId,
    activeHighSpeedPlayback,
    showAnnotationPanel: clinicalAnnotationPanelVisible,
    annotationTimelineVisible: showUnifiedNavigationTimeline,
    viewerChromeReady: viewerDeferredChromeReady,
  });
  const isEventDetectionPanelVisible = useMemo(
    () => isDockPanelVisible("event_detection"),
    [isDockPanelVisible],
  );
  const shouldFetchInterventions =
    Boolean(studyId) &&
    !suspendClinicalDockRendering &&
    (!activeHighSpeedPlayback || isEventDetectionPanelVisible);

  const fitMontageChannelsToViewport = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || totalMontageChannels <= 0) return;

    const usableHeight = Math.max(80, canvas.clientHeight);
    const requiredSpacing =
      usableHeight / (totalMontageChannels * CHANNEL_SCROLL_HEIGHT_PX);
    const nextSpacing = snapDisplayProfileNumber(
      requiredSpacing,
      1,
      0.5,
      ROW_HEIGHT_CHANNEL_SPACING.tall,
      0.1,
    );
    setProfileChannelSpacing(nextSpacing);
    setChannelScrollOffset(0);
  }, [totalMontageChannels]);

  useEffect(() => {
    if (!clinicalAnnotationPanelVisible) return;

    fitMontageChannelsToViewport();
    const canvas = canvasRef.current;
    const waveformHost = canvas?.closest(".waveform-display") as HTMLElement | null;
    if (!canvas && !waveformHost) return;

    const observer = new ResizeObserver(() => fitMontageChannelsToViewport());
    if (canvas) observer.observe(canvas);
    if (waveformHost) observer.observe(waveformHost);
    return () => observer.disconnect();
  }, [
    annotationPanelWidthPx,
    clinicalAnnotationPanelVisible,
    fitMontageChannelsToViewport,
    totalMontageChannels,
  ]);

  if (shouldFetchAnnotations || frozenAnnotationQueryTimeRef.current === null) {
    frozenAnnotationQueryTimeRef.current = sideQueryReferenceTime;
  }

  const annotationQueryReferenceTime = shouldFetchAnnotations
    ? sideQueryReferenceTime
    : (frozenAnnotationQueryTimeRef.current ?? sideQueryReferenceTime);
  const requestedCurrentTime = resolveEEGViewerRequestedTime({
    currentTime,
    pendingSeekTime: pendingPausedSeekTimeRef.current,
  });
  const pagePlayheadAbsoluteTime = resolvePagePlayheadAbsoluteTime({
    viewportStartSeconds: requestedCurrentTime,
    offsetSeconds: pagePlayheadOffsetSeconds,
    timeScaleSeconds: currentConfig?.time_scale || 10,
  });
  const goToTimeDisplayValue = goToTimeInputFocused ? goToTimeInput : "";
  const goToTimeHoverLabel = formatViewerClockTime(pagePlayheadAbsoluteTime);

  const smartSuggestionByIdRef = useRef<
    Map<string, SmartAnnotationSuggestion>
  >(new Map());

  const {
    annotationPanelTab,
    setAnnotationPanelTab,
    annotationPanelTabIds,
    annotationOverlayError,
    setAnnotationOverlayError,
    smartSuggestionDrafts,
    setSmartSuggestionDrafts,
    smartSuggestionStatus,
    smartSuggestionError,
    annotationCategoryFilter,
    setAnnotationCategoryFilter,
    annotationAudienceStatusChip,
    handleResetAnnotationCategoryFilter,
    annotationReviewFilter,
    setAnnotationReviewFilter,
    annotationReviewStatus,
    setAnnotationReviewStatus,
    annotationReviewError,
    setAnnotationReviewError,
    selectedAnnotationIds,
    setSelectedAnnotationIds,
    annotationBulkStatus,
    annotationBulkError,
    bulkAnnotationTypeId,
    setBulkAnnotationTypeId,
    bulkChannelScopeMode,
    setBulkChannelScopeMode,
    bulkAnnotationNoteAppend,
    setBulkAnnotationNoteAppend,
    annotationNoteComposer,
    annotationNoteComposerValue,
    setAnnotationNoteComposerValue,
    annotationNoteComposerError,
    openAnnotationNoteComposer,
    closeAnnotationNoteComposer,
    submitAnnotationNoteComposer,
    selectedInterventionId,
    setSelectedInterventionId,
    responseObservedEffect,
    setResponseObservedEffect,
    responseNotes,
    setResponseNotes,
    responseSeverityDelta,
    setResponseSeverityDelta,
    responseWindowSeconds,
    setResponseWindowSeconds,
    interventionTimelineError,
    setInterventionTimelineError,
    dismissedSmartSuggestionIds,
    annotationTypeCatalog,
    annotationCatalogErrored,
    annotations,
    refetchAnnotations,
    activeAnnotationsQueryKey,
    pendingReviewAnnotations,
    refetchPendingReviewAnnotations,
    interventionEvents,
    refetchInterventions,
    isFetchingInterventions,
    interventionsError,
    selectedInterventionTimeline,
    refetchInterventionTimeline,
    isFetchingInterventionTimeline,
    annotationTypeByCode,
    resolveAnnotationCategory,
    resolveAnnotationTypeCategory,
    resolveAnnotationDisplayColor,
    categoryFilteredAnnotations,
    categoryFilteredPendingReviewAnnotations,
    renderAnnotations,
    persistedAnnotationById,
    pendingReviewIdSet,
    reviewFilteredAnnotations,
    reviewFilteredAnnotationIds,
    pendingReviewCount,
    selectedAnnotationIdSet,
    selectedAnnotations,
    selectedClipSegmentAnnotation,
    clipScissorsDisabledReason,
    selectedPendingAnnotationIds,
    allVisibleAnnotationsSelected,
    waveformAnnotationMarkers,
    createAnnotationMutation,
    recordSuggestionDecisionMutation,
    updateAnnotationMutation,
    createInterventionMutation,
    createInterventionResponseMutation,
    deleteAnnotationMutation,
    reviewAnnotationMutation,
    bulkReviewAnnotationsMutation,
    bulkDeleteAnnotationsMutation,
    bulkUpdateAnnotationsMutation,
    annotationBulkActionPending,
    submitInterventionResponse,
    handleAnnotationPanelTabKeyDown,
    updateSmartSuggestionDraft,
    rejectSmartSuggestion,
    acceptSmartSuggestion,
    handleWaveformAnnotationTimingUpdate,
    handleWaveformAnnotationEditNotes,
    handleWaveformAnnotationAssignSelectedChannels,
    handleWaveformAnnotationAssignChannels,
    handleWaveformAnnotationApprove,
    toggleAnnotationSelection,
    toggleSelectAllVisibleAnnotations,
    handleBulkReviewSelectedAnnotations,
    handleBulkDeleteSelectedAnnotations,
    handleBulkTypeUpdate,
    handleBulkChannelScopeUpdate,
    handleBulkNoteAppend,
  } = useEEGAnnotationPanel({
    studyId,
    currentRecordingId,
    clinicalAnnotationPanelVisible,
    shouldFetchAnnotations,
    shouldFetchInterventions,
    activeHighSpeedPlayback,
    currentTime,
    annotationQueryReferenceTime,
    sideQueryReferenceTime,
    timeScaleSeconds: currentConfig?.time_scale || 10,
    visibleMontageChannelDetails,
    selectedTraceChannels,
    getAnnotationChannels: resolveAnnotationChannels,
    smartSuggestionByIdRef,
    annotationAudienceSessionStorageKey,
    annotationPanelTabRefs,
    setRapidEventStatus,
    setRapidEventError,
    dialog,
    queryClient,
  });

  const shouldBuildTrendInsights = shouldComputeEEGViewerTrendInsights({
    viewerChromeReady: viewerDeferredChromeReady,
    showAnnotationPanel: clinicalAnnotationPanelVisible,
    trendCorrelationVisible:
      isTrendCorrelationVisible && !suspendClinicalDockRendering,
    trendEvalActive,
    hasSelectedIntervention: Boolean(selectedInterventionId),
  });

  const activeEventSetId =
    perspectiveState.activePerspective?.event_set_id ||
    perspectiveState.activePerspective?.clinical_defaults?.eventSetId ||
    null;
  const { data: viewerEventSets = [] } = useQuery({
    queryKey: ["eeg-event-sets", "viewer"],
    queryFn: fetchEventSets,
    enabled: viewerDeferredChromeReady,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const { data: viewerEventCatalog = [] } = useQuery({
    queryKey: ["eeg-event-catalog", "viewer"],
    queryFn: fetchEventCatalog,
    enabled: viewerDeferredChromeReady,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const { data: viewerTrendTemplates = [] } = useQuery({
    queryKey: ["eeg-trend-templates", "viewer"],
    queryFn: fetchTrendTemplates,
    enabled: viewerDeferredChromeReady,
    refetchOnWindowFocus: false,
  });
  const perspectiveTrendTemplateId =
    perspectiveState.activePerspective?.trend_template_id ||
    perspectiveState.activePerspective?.clinical_defaults?.trendTemplateId ||
    "";
  const activeViewerTrendTemplateId =
    viewerTrendTemplateOverrideId || perspectiveTrendTemplateId;
  const activeViewerTrendTemplate = useMemo(
    () =>
      viewerTrendTemplates.find(
        (template) => template.id === activeViewerTrendTemplateId,
      ) || null,
    [activeViewerTrendTemplateId, viewerTrendTemplates],
  );

  useEffect(() => {
    setViewerTrendTemplateOverrideId("");
  }, [perspectiveState.activePerspectiveId]);

  useEffect(() => {
    if (!activeViewerTrendTemplate) return;
    const transformations = new Set(
      activeViewerTrendTemplate.rows.map((row) => row.transformation),
    );
    setDockPanelVisibility((current) => ({
      ...current,
      trend_correlation: true,
      spectrogram:
        current.spectrogram || transformations.has("spectral"),
      psd_analysis:
        current.psd_analysis || transformations.has("spectral"),
    }));
  }, [activeViewerTrendTemplate]);
  const activeViewerEventSet = useMemo(
    () =>
      activeEventSetId
        ? viewerEventSets.find((eventSet) => eventSet.id === activeEventSetId) ||
          null
        : null,
    [activeEventSetId, viewerEventSets],
  );
  const activeViewerEventItems = useMemo(() => {
    if (!activeViewerEventSet) return [];
    const configuredIds = new Set(activeViewerEventSet.eventIds || []);
    return viewerEventCatalog.filter(
      (event) =>
        event.visibleInDock !== false &&
        (configuredIds.has(event.id) ||
          event.eventSetIds.includes(activeViewerEventSet.id)),
    );
  }, [activeViewerEventSet, viewerEventCatalog]);
  const eventItemForAnnotationType = useCallback(
    (type: AnnotationTypeCatalogEntry) => {
      const typeCode = normalizeAnnotationCatalogLink(type.type_code);
      const displayName = normalizeAnnotationCatalogLink(type.display_name);
      return activeViewerEventItems.find((event) => {
        const configuredCode = normalizeAnnotationCatalogLink(
          event.annotationTypeCode,
        );
        const eventId = normalizeAnnotationCatalogLink(event.id);
        const eventName = normalizeAnnotationCatalogLink(event.name);
        if (configuredCode) return configuredCode === typeCode;
        return (
          eventId === typeCode ||
          eventName === displayName ||
          (eventId === "seizure" && typeCode.startsWith("seizure_"))
        );
      });
    },
    [activeViewerEventItems],
  );
  const viewerAnnotationTypeCatalog = useMemo(() => {
    if (!activeViewerEventSet || activeViewerEventItems.length === 0) {
      return annotationTypeCatalog;
    }
    const scoped = annotationTypeCatalog.filter((type) =>
      Boolean(eventItemForAnnotationType(type)),
    );
    return scoped.length > 0 ? scoped : annotationTypeCatalog;
  }, [
    activeViewerEventItems.length,
    activeViewerEventSet,
    annotationTypeCatalog,
    eventItemForAnnotationType,
  ]);

  const quickActionAnnotationTypes = useMemo(() => {
    return viewerAnnotationTypeCatalog
      .filter((type) => {
        const eventItem = eventItemForAnnotationType(type);
        return eventItem?.defaultQuickAction ?? type.quick_action_enabled;
      })
      .sort((a, b) => {
        const eventA = eventItemForAnnotationType(a);
        const eventB = eventItemForAnnotationType(b);
        const configuredOrderA = eventA?.defaultShortcut ?? a.quick_action_order;
        const configuredOrderB = eventB?.defaultShortcut ?? b.quick_action_order;
        const orderA = Number.isFinite(configuredOrderA)
          ? Number(configuredOrderA)
          : Number.MAX_SAFE_INTEGER;
        const orderB = Number.isFinite(configuredOrderB)
          ? Number(configuredOrderB)
          : Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;
        return a.display_name.localeCompare(b.display_name);
      });
  }, [eventItemForAnnotationType, viewerAnnotationTypeCatalog]);

  const quickAddActionCatalog = useMemo(() => {
    return quickActionAnnotationTypes;
  }, [quickActionAnnotationTypes]);

  const selectableAnnotationTypes = useMemo(() => {
    return viewerAnnotationTypeCatalog;
  }, [viewerAnnotationTypeCatalog]);

  const {
    consensusReviewerSearch,
    setConsensusReviewerSearch,
    consensusSelectedReviewers,
    consensusModeEnabled,
    consensusSectionOpen,
    consensusBlindReviewMode,
    setConsensusBlindReviewMode,
    consensusResolutionMethod,
    setConsensusResolutionMethod,
    consensusActionStatus,
    consensusActionError,
    consensusAutoSummary,
    isFetchingConsensusAutoSummary,
    consensusReportSummary,
    isFetchingConsensusReportSummary,
    consensusDisagreementSummary,
    isFetchingConsensusDisagreementSummary,
    consensusReviewerSearchResults,
    consensusAssignmentSummary,
    isFetchingConsensusAssignmentSummary,
    consensusAssignedReviewers,
    consensusDisagreements,
    unresolvedConsensusDisagreements,
    consensusReviewerCount,
    hasPersistedConsensusAssignments,
    hasConsensusReviewerCoverage,
    hasConsensusDisagreementData,
    shouldShowConsensusWorkflow,
    canDisableManualConsensusMode,
    availableConsensusReviewerResults,
    assignConsensusReviewersMutation,
    resolveConsensusDisagreementsMutation,
    consensusActionPending,
    refreshConsensusData,
    addConsensusReviewer,
    removeConsensusReviewer,
    handleAssignConsensusReviewers,
    handleEnableConsensusMode,
    handleDisableConsensusMode,
    handleToggleConsensusSection,
    handleResolveConsensusDisagreements,
  } = useEEGConsensusReview({
    studyId,
    clinicalAnnotationPanelVisible,
  });

  // Lightweight event index — all annotation start_times for the recording (EEG-CTRL-008)
  // Lightweight event index — all annotation start_times for the recording (EEG-CTRL-008)
  const { data: eventIndexRaw = [] } = useQuery({
    queryKey: ["eeg-annotations-index", studyId, currentRecordingId],
    queryFn: async () => {
      const params = new URLSearchParams({ studyId });
      if (currentRecordingId) params.set("recordingId", currentRecordingId);
      const response = await apiClient.get(
        `/eeg-annotations/index?${params.toString()}`,
      );
      const payload = response?.data;
      const list: EventIndexItem[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      return list.sort((a, b) => a.start_time - b.start_time);
    },
    enabled: Boolean(studyId) && viewerDeferredChromeReady,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  // Enriched annotation items for the Annotation List dock panel
  const eventIndex = useMemo(
    () => eventIndexRaw.map((item) => item.start_time).sort((a, b) => a - b),
    [eventIndexRaw],
  );
  const eventIndexItems = useMemo(() => eventIndexRaw, [eventIndexRaw]);

  // Auto-scroll annotation list to the nearest annotation when currentTime changes
  useEffect(() => {
    if (!annotations?.length) return;
    if (!clinicalAnnotationPanelVisible) return;
    if (isPlaying && playbackSpeed >= 150) return;
    let nearest: (typeof annotations)[0] | null = null;
    let minDist = Infinity;
    for (const ann of annotations) {
      const dist = Math.abs(ann.start_time - currentTime);
      if (dist < minDist) {
        minDist = dist;
        nearest = ann;
      }
    }
    if (!nearest?.annotation_id) return;
    if (nearest.annotation_id === lastScrolledAnnotationIdRef.current) return;
    lastScrolledAnnotationIdRef.current = nearest.annotation_id;
    const el = annotationItemRefsMap.current.get(nearest.annotation_id);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [
    annotations,
    currentTime,
    isPlaying,
    playbackSpeed,
    clinicalAnnotationPanelVisible,
  ]);

  const shouldTrackHighSpeedSideQueries =
    shouldFetchAnnotations || shouldFetchInterventions;
  const highSpeedSideQuerySyncIntervalMs =
    playbackSpeed >= 200
      ? MAX_HIGH_SPEED_SIDE_QUERY_SYNC_INTERVAL_MS
      : playbackSpeed >= 150
        ? ULTRA_HIGH_SPEED_SIDE_QUERY_SYNC_INTERVAL_MS
        : HIGH_SPEED_SIDE_QUERY_SYNC_INTERVAL_MS;

  useEffect(() => {
    if (activeHighSpeedPlayback) {
      return;
    }

    lastSideQuerySyncAtRef.current = 0;
    setSideQueryTimeAnchor(currentTimeRef.current);
  }, [activeHighSpeedPlayback]);

  useEffect(() => {
    if (!activeHighSpeedPlayback) {
      return;
    }

    if (!shouldTrackHighSpeedSideQueries) {
      lastSideQuerySyncAtRef.current = 0;
      return;
    }

    const effectStartedAt = performance.now();
    const now = effectStartedAt;
    const previousSyncAt = lastSideQuerySyncAtRef.current;
    if (
      previousSyncAt > 0 &&
      now - previousSyncAt < highSpeedSideQuerySyncIntervalMs
    ) {
      return;
    }

    lastSideQuerySyncAtRef.current = now;
    React.startTransition(() => {
      setSideQueryTimeAnchor(currentTime);
    });
    const effectCompletedAt = performance.now();
    notePostCallbackActivity({
      source: "sideQuerySync",
      startedAtMs: effectStartedAt,
      completedAtMs: effectCompletedAt,
      detailLabel: "effectMs",
      detailValue: Number((effectCompletedAt - effectStartedAt).toFixed(1)),
    });
    recordPlaybackPerfEvent("sideQuerySync", {
      effectMs: Number((effectCompletedAt - effectStartedAt).toFixed(1)),
      currentTime: Number(currentTime.toFixed(2)),
      syncIntervalMs:
        previousSyncAt > 0 ? Number((now - previousSyncAt).toFixed(1)) : null,
      syncTargetMs: highSpeedSideQuerySyncIntervalMs,
      trackingVisibleSideQueries: shouldTrackHighSpeedSideQueries,
    });
  }, [
    activeHighSpeedPlayback,
    currentTime,
    highSpeedSideQuerySyncIntervalMs,
    notePostCallbackActivity,
    recordPlaybackPerfEvent,
    shouldTrackHighSpeedSideQueries,
  ]);

  const eegSelectionMetrics = useMemo<EEGSelectionMetrics | null>(() => {
    if (!eegSelection) return null;
    const duration = Math.max(0, eegSelection.endTime - eegSelection.startTime);
    const overlappingAnnotations = renderAnnotations.filter((annotation) => {
      const annotationEnd = annotation.end_time ?? annotation.start_time;
      return (
        annotationEnd >= eegSelection.startTime &&
        annotation.start_time <= eegSelection.endTime
      );
    });
    const sampleCount = Math.max(0, eegSelection.endSample - eegSelection.startSample);
    const loadedWindowStart = rendererLoadedWindowStartRef.current ?? dataWindowStartRef.current;
    const measuredChannels = resolveSelectionMeasuredChannels(
      loadedChannelsRef.current,
      eegSelection.channels,
      visibleMontageChannelDetails.length,
    );

    let peakAmplitude = 0;
    let peakToPeakAmplitude = 0;
    let maximumSlope = 0;
    let sumSquares = 0;
    let valueCount = 0;
    let highAmplitudeCount = 0;
    let flatSampleCount = 0;
    const dominantFrequencyInputs: number[] = [];
    const sampleRates: number[] = [];
    const spectra: EEGSelectionSpectrumBin[][] = [];
    const spectrograms: EEGSelectionSpectrogramCell[][] = [];

    measuredChannels.slice(0, 12).forEach((channel) => {
      if (!Number.isFinite(channel.sampleRate) || channel.sampleRate <= 0) {
        return;
      }
      const startIndex = clampNumber(
        Math.floor((eegSelection.startTime - loadedWindowStart) * channel.sampleRate),
        0,
        Math.max(0, channel.data.length - 1),
      );
      const endIndex = clampNumber(
        Math.ceil((eegSelection.endTime - loadedWindowStart) * channel.sampleRate),
        startIndex + 1,
        channel.data.length,
      );
      const samples: number[] = [];
      let channelMinimum = Number.POSITIVE_INFINITY;
      let channelMaximum = Number.NEGATIVE_INFINITY;
      let previousValue: number | null = null;
      for (let index = startIndex; index < endIndex; index += 1) {
        const value = Number(channel.data[index]);
        if (!Number.isFinite(value)) continue;
        const absValue = Math.abs(value);
        peakAmplitude = Math.max(peakAmplitude, absValue);
        channelMinimum = Math.min(channelMinimum, value);
        channelMaximum = Math.max(channelMaximum, value);
        if (previousValue !== null) {
          maximumSlope = Math.max(
            maximumSlope,
            Math.abs(value - previousValue) * channel.sampleRate,
          );
        }
        previousValue = value;
        sumSquares += value * value;
        valueCount += 1;
        if (absValue >= 150) highAmplitudeCount += 1;
        if (absValue <= 1) flatSampleCount += 1;
        if (samples.length < 2048) samples.push(value);
      }
      if (
        Number.isFinite(channelMinimum) &&
        Number.isFinite(channelMaximum)
      ) {
        peakToPeakAmplitude = Math.max(
          peakToPeakAmplitude,
          channelMaximum - channelMinimum,
        );
      }
      if (samples.length >= 8) {
        dominantFrequencyInputs.push(...samples);
        sampleRates.push(channel.sampleRate);
        spectra.push(buildSelectionSpectrum(samples, channel.sampleRate));
        spectrograms.push(buildSelectionSpectrogram(samples, channel.sampleRate));
      }
    });

    const rmsAmplitude = valueCount > 0 ? Math.sqrt(sumSquares / valueCount) : null;
    const representativeSampleRate =
      sampleRates.length > 0
        ? sampleRates.reduce((sum, value) => sum + value, 0) / sampleRates.length
        : (eegData?.sampling_rate ?? 0);
    const dominantFrequency = estimateSelectionDominantFrequency(
      dominantFrequencyInputs,
      representativeSampleRate,
    );
    const bandPower = buildSelectionBandPower(
      dominantFrequencyInputs,
      representativeSampleRate,
    );
    const spectrumBinCount = Math.max(
      0,
      ...spectra.map((spectrum) => spectrum.length),
    );
    const spectrum =
      spectrumBinCount > 0
        ? Array.from({ length: spectrumBinCount }, (_, index) => {
            const binsAtIndex = spectra
              .map((channelSpectrum) => channelSpectrum[index])
              .filter((bin): bin is EEGSelectionSpectrumBin => Boolean(bin));
            const hz =
              binsAtIndex.reduce((sum, bin) => sum + bin.hz, 0) /
              binsAtIndex.length;
            const power =
              binsAtIndex.reduce((sum, bin) => sum + bin.power, 0) /
              binsAtIndex.length;
            return { hz, power };
          })
        : [];
    const spectrogram = spectrograms.flatMap((cells) => cells).slice(0, 120);
    const snr =
      rmsAmplitude !== null
        ? Math.max(0, Math.min(60, 20 * Math.log10((rmsAmplitude + 1) / 2)))
        : null;
    const highAmplitudeRatio =
      valueCount > 0 ? highAmplitudeCount / valueCount : 0;
    const flatRatio = valueCount > 0 ? flatSampleCount / valueCount : 0;
    const artifactScore = Math.min(
      1,
      highAmplitudeRatio * 3 + flatRatio * 2 + (peakAmplitude > 300 ? 0.35 : 0),
    );
    const rhythmicScore =
      dominantFrequency !== null && dominantFrequency >= 2 && dominantFrequency <= 14
        ? 0.55
        : 0.18;
    const seizureLikelihood = Math.min(
      0.95,
      rhythmicScore +
        (rmsAmplitude !== null && rmsAmplitude > 35 ? 0.18 : 0) +
        (overlappingAnnotations.some((ann) =>
          ann.annotation_type.toLowerCase().includes("seizure"),
        )
          ? 0.22
          : 0),
    );
    const muscleArtifactLikelihood =
      dominantFrequency !== null && dominantFrequency >= 20
        ? Math.min(0.9, 0.35 + highAmplitudeRatio * 4)
        : Math.min(0.6, highAmplitudeRatio * 3);
    const electrodeArtifactLikelihood = Math.min(
      0.9,
      flatRatio * 4 + (peakAmplitude > 500 ? 0.25 : 0),
    );
    const requestedChannelList = eegSelection.channels.map((channel) =>
      channel.toLowerCase(),
    );
    const selectedTypeTokens = overlappingAnnotations.map((annotation) =>
      normalizeAnnotationTypeCode(annotation.annotation_type),
    );
    const similarEvents = renderAnnotations
      .filter((annotation) => !overlappingAnnotations.includes(annotation))
      .map<EEGSelectionSimilarEvent | null>((annotation) => {
        const typeCode = normalizeAnnotationTypeCode(annotation.annotation_type);
        const annotationEnd = annotation.end_time ?? annotation.start_time;
        const annotationChannels = Array.isArray(annotation.channels_affected)
          ? annotation.channels_affected.map((channel) => channel.toLowerCase())
          : [];
        const channelOverlap =
          annotationChannels.includes("all") || requestedChannelList.length === 0
            ? 1
            : requestedChannelList.filter((channel) =>
                annotationChannels.some(
                  (annotationChannel) =>
                    annotationChannel === channel ||
                    annotationChannel.includes(channel) ||
                    channel.includes(annotationChannel),
                ),
              ).length / requestedChannelList.length;
        const durationSimilarity =
          typeof annotation.end_time === "number"
            ? 1 /
              (1 +
                Math.abs(
                  Math.max(0.05, annotation.end_time - annotation.start_time) -
                    Math.max(0.05, duration),
                ))
            : 0.25;
        const distanceSeconds =
          annotation.start_time > eegSelection.endTime
            ? annotation.start_time - eegSelection.endTime
            : eegSelection.startTime - annotationEnd;
        const timeProximity = 1 / (1 + Math.max(0, distanceSeconds) / 30);
        const morphologyMatch = selectedTypeTokens.includes(typeCode) ? 1 : 0;
        const seizureSpectralBoost =
          dominantFrequency !== null &&
          dominantFrequency >= 2 &&
          dominantFrequency <= 14 &&
          typeCode.includes("seizure")
            ? 0.2
            : 0;
        const artifactSpectralBoost =
          artifactScore > 0.35 && typeCode.includes("artifact") ? 0.2 : 0;
        const score =
          channelOverlap * 0.32 +
          durationSimilarity * 0.22 +
          timeProximity * 0.18 +
          morphologyMatch * 0.18 +
          seizureSpectralBoost +
          artifactSpectralBoost;
        if (score <= 0.18) return null;
        const reasons = [
          channelOverlap >= 0.5 ? "channel overlap" : null,
          durationSimilarity >= 0.4 ? "duration" : null,
          morphologyMatch ? "morphology" : null,
          seizureSpectralBoost || artifactSpectralBoost
            ? "spectral context"
            : null,
          timeProximity >= 0.4 ? "nearby" : null,
        ].filter((reason): reason is string => Boolean(reason));
        return {
          annotationId: annotation.annotation_id,
          label: annotation.label || typeCode,
          typeCode,
          startTime: annotation.start_time,
          endTime: annotation.end_time,
          score: Math.min(1, score),
          reasons,
        };
      })
      .filter((event): event is EEGSelectionSimilarEvent => event !== null)
      .sort((left, right) => right.score - left.score)
      .slice(0, 5);

    return {
      duration,
      sampleCount,
      channelCount: eegSelection.channels.length,
      measuredChannelCount: measuredChannels.length,
      overlappingAnnotations,
      peakAmplitude: valueCount > 0 ? peakAmplitude : null,
      peakToPeakAmplitude: valueCount > 0 ? peakToPeakAmplitude : null,
      maximumSlope: valueCount > 1 ? maximumSlope : null,
      rmsAmplitude,
      dominantFrequency,
      bandPower,
      snr,
      artifactScore: valueCount > 0 ? artifactScore : null,
      seizureLikelihood: valueCount > 0 ? seizureLikelihood : null,
      muscleArtifactLikelihood: valueCount > 0 ? muscleArtifactLikelihood : null,
      electrodeArtifactLikelihood: valueCount > 0 ? electrodeArtifactLikelihood : null,
      spectrum,
      spectrogram,
      similarEvents,
      analysisSource:
        valueCount > 0 && duration <= 30 ? "loaded-buffer" : "needs-backend",
    };
  }, [
    dataWindowStart,
    eegData?.sampling_rate,
    eegSelection,
    renderAnnotations,
    visibleMontageChannelDetails.length,
  ]);

  useEffect(() => {
    renderAnnotationsRef.current = renderAnnotations;
  }, [renderAnnotations]);

  useEffect(() => {
    mappedRenderAnnotationsRef.current =
      mapAnnotationsForRenderer(renderAnnotations);
  }, [mapAnnotationsForRenderer, renderAnnotations]);

  const recordingStartMs = useMemo(() => {
    if (recordingStartIso) {
      const fromStudyStart = Date.parse(recordingStartIso);
      if (Number.isFinite(fromStudyStart)) {
        return fromStudyStart;
      }
    }
    const createdAt = (selectedRecording as { created_at?: string } | undefined)
      ?.created_at;
    if (!createdAt) return null;
    const parsed = new Date(createdAt).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }, [recordingStartIso, selectedRecording]);

  const viewerLiveStatus = useMemo(
    () =>
      buildEEGViewerLiveStatus({
        currentTime,
        totalDuration: timelineRecordingDuration,
        timeScale: currentConfig?.time_scale || 10,
        recordingStartMs,
      }),
    [
      currentTime,
      timelineRecordingDuration,
      currentConfig?.time_scale,
      recordingStartMs,
    ],
  );
  const showNavigationTimeTicksToggle =
    showUnifiedNavigationTimeline && !macroOverviewCollapsed;
  const effectiveShowNavigationTimeTicks =
    showTimeline && !macroOverviewCollapsed;
  const showMacroOverviewZoomControl =
    showUnifiedNavigationTimeline &&
    isNavigationTimelineSlidingWindow(timelineRecordingDuration);

  useEffect(() => {
    if (timelineRecordingDuration <= 0) {
      return;
    }

    setShowTimeline(readNavigationTimeTicksPreference(timelineRecordingDuration));
    setMacroOverviewCollapsed(
      readMacroOverviewCollapsed(timelineRecordingDuration),
    );
  }, [timelineRecordingDuration]);

  const handleToggleNavigationTimeTicks = useCallback(() => {
    setShowTimeline((current) => {
      const next = !current;
      writeNavigationTimeTicksPreference(next);
      return next;
    });
  }, []);

  const mergedTrendSeries = useMemo<BackgroundTrendData[]>(() => {
    const byTimestamp = new Map<string, BackgroundTrendData>();
    [...hourlyTrendSeries, ...realtimeTrendSeries].forEach((point) => {
      if (!point?.timestamp) return;
      byTimestamp.set(point.timestamp, point);
    });
    return Array.from(byTimestamp.values()).sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
  }, [hourlyTrendSeries, realtimeTrendSeries]);

  const trendPoints = useMemo<TrendPoint[]>(() => {
    if (!recordingStartMs) return [];
    return mergedTrendSeries
      .map((point, index) => {
        const pointMs = new Date(point.timestamp).getTime();
        if (!Number.isFinite(pointMs)) return null;
        return {
          id: `trend-${index}-${point.timestamp}`,
          timestamp: point.timestamp,
          timeSeconds: Math.max(0, (pointMs - recordingStartMs) / 1000),
          deltaPower:
            typeof point.delta_power === "number" ? point.delta_power : null,
          totalPower:
            typeof point.total_power === "number" ? point.total_power : null,
          alphaPower:
            typeof point.alpha_power === "number" ? point.alpha_power : null,
          asymmetryPct:
            typeof point.asymmetry_index === "number"
              ? Math.abs(point.asymmetry_index * 100)
              : null,
        };
      })
      .filter((point): point is TrendPoint => point !== null);
  }, [mergedTrendSeries, recordingStartMs]);

  const activeTrendPoint = useMemo<TrendPoint | null>(() => {
    if (!shouldBuildTrendInsights || !trendPoints.length) return null;
    return trendPoints.reduce<TrendPoint>((nearest, point) => {
      const nearestDistance = Math.abs(nearest.timeSeconds - currentTime);
      const candidateDistance = Math.abs(point.timeSeconds - currentTime);
      return candidateDistance < nearestDistance ? point : nearest;
    }, trendPoints[0]);
  }, [currentTime, shouldBuildTrendInsights, trendPoints]);

  const trendCorrelationChartPoints = useMemo(
    () =>
      trendPoints
        .map((point) => ({
          id: point.id,
          timeSeconds: point.timeSeconds,
          value: point.totalPower ?? point.deltaPower ?? point.alphaPower ?? 0,
        }))
        .filter((point) => Number.isFinite(point.value)),
    [trendPoints],
  );

  const currentTrendSample = useMemo<Record<string, number> | null>(() => {
    if (!activeTrendPoint) return null;
    return {
      dsa_low: activeTrendPoint.deltaPower ?? 0,
      dsa_high: activeTrendPoint.totalPower ?? 0,
      csa_threshold: activeTrendPoint.alphaPower ?? 0,
      asymmetry_pct: activeTrendPoint.asymmetryPct ?? 0,
    };
  }, [activeTrendPoint]);

  const trendMarkers = useMemo<TrendMarker[]>(() => {
    if (!shouldBuildTrendInsights || !trendPoints.length) {
      return EMPTY_TREND_MARKERS;
    }

    const dsaLowThreshold = thresholdValue("dsa_low");
    const dsaHighThreshold = thresholdValue("dsa_high");
    const csaThreshold = thresholdValue("csa_threshold");
    const asymmetryThreshold = thresholdValue("asymmetry_pct");
    const abruptShiftThreshold = thresholdValue("abrupt_power_shift_pct") ?? 35;

    const markers: TrendMarker[] = [];

    trendPoints.forEach((point, index) => {
      if (
        dsaLowThreshold !== null &&
        point.deltaPower !== null &&
        point.deltaPower < dsaLowThreshold
      ) {
        markers.push({
          id: `${point.id}-dsa-low`,
          timeSeconds: point.timeSeconds,
          timestamp: point.timestamp,
          type: "threshold_crossing",
          metric: "delta_power",
          label: `Delta power low (${point.deltaPower.toFixed(2)} < ${dsaLowThreshold.toFixed(2)})`,
          severity: "warning",
          value: point.deltaPower,
          threshold: dsaLowThreshold,
        });
      }

      if (
        dsaHighThreshold !== null &&
        point.totalPower !== null &&
        point.totalPower > dsaHighThreshold
      ) {
        markers.push({
          id: `${point.id}-dsa-high`,
          timeSeconds: point.timeSeconds,
          timestamp: point.timestamp,
          type: "threshold_crossing",
          metric: "total_power",
          label: `Total power high (${point.totalPower.toFixed(2)} > ${dsaHighThreshold.toFixed(2)})`,
          severity: "critical",
          value: point.totalPower,
          threshold: dsaHighThreshold,
        });
      }

      if (
        csaThreshold !== null &&
        point.alphaPower !== null &&
        point.alphaPower > csaThreshold
      ) {
        markers.push({
          id: `${point.id}-csa`,
          timeSeconds: point.timeSeconds,
          timestamp: point.timestamp,
          type: "threshold_crossing",
          metric: "alpha_power",
          label: `CSA threshold exceeded (${point.alphaPower.toFixed(2)} > ${csaThreshold.toFixed(2)})`,
          severity: "warning",
          value: point.alphaPower,
          threshold: csaThreshold,
        });
      }

      if (
        asymmetryThreshold !== null &&
        point.asymmetryPct !== null &&
        point.asymmetryPct > asymmetryThreshold
      ) {
        markers.push({
          id: `${point.id}-asym`,
          timeSeconds: point.timeSeconds,
          timestamp: point.timestamp,
          type: "asymmetry_spike",
          metric: "asymmetry_pct",
          label: `Asymmetry spike (${point.asymmetryPct.toFixed(1)}% > ${asymmetryThreshold.toFixed(1)}%)`,
          severity: "critical",
          value: point.asymmetryPct,
          threshold: asymmetryThreshold,
        });
      }

      if (index > 0) {
        const prev = trendPoints[index - 1];
        if (
          prev.totalPower !== null &&
          point.totalPower !== null &&
          Math.abs(prev.totalPower) > 0
        ) {
          const deltaPercent =
            (Math.abs(point.totalPower - prev.totalPower) /
              Math.abs(prev.totalPower)) *
            100;
          if (deltaPercent >= abruptShiftThreshold) {
            markers.push({
              id: `${point.id}-abrupt`,
              timeSeconds: point.timeSeconds,
              timestamp: point.timestamp,
              type: "abrupt_power_shift",
              metric: "total_power",
              label: `Abrupt power shift (${deltaPercent.toFixed(1)}%)`,
              severity:
                deltaPercent >= abruptShiftThreshold * 1.5
                  ? "critical"
                  : "warning",
              value: point.totalPower,
              deltaPercent,
              threshold: abruptShiftThreshold,
            });
          }
        }
      }
    });

    return markers.sort((a, b) => a.timeSeconds - b.timeSeconds);
  }, [shouldBuildTrendInsights, thresholdValue, trendPoints]);

  const smartAnnotationSuggestions = useMemo<
    SmartAnnotationSuggestion[]
  >(() => {
    if (!clinicalAnnotationPanelVisible || !shouldBuildTrendInsights) {
      return EMPTY_SMART_ANNOTATION_SUGGESTIONS;
    }

    const focusWindowSeconds = Math.max(
      90,
      (currentConfig?.time_scale || 10) * 12,
    );
    const nearbyMarkers = trendMarkers.filter(
      (marker) =>
        Math.abs(marker.timeSeconds - currentTime) <= focusWindowSeconds,
    );
    const sourceMarkers =
      nearbyMarkers.length > 0 ? nearbyMarkers : trendMarkers.slice(-30);

    const suggestions = sourceMarkers
      .map((marker) => {
        const isSeizureLike =
          marker.metric === "asymmetry_pct" ||
          marker.metric === "total_power" ||
          marker.type === "abrupt_power_shift";
        const annotationType = isSeizureLike ? "seizure_onset" : "artifact";
        const label = isSeizureLike
          ? "Suggested seizure pattern"
          : "Suggested artifact/suppression pattern";
        const color =
          marker.severity === "critical"
            ? "#ef4444"
            : marker.severity === "warning"
              ? "#f59e0b"
              : "#3b82f6";

        return {
          id: `smart-${marker.id}`,
          source: "trend_marker" as const,
          annotationType,
          label,
          color,
          startTime: marker.timeSeconds,
          defaultDurationSeconds: marker.severity === "critical" ? 6 : 3,
          confidence: deriveTrendSuggestionConfidence(marker),
          explanation: buildTrendSuggestionExplanation(marker),
          channels:
            selectedTraceChannels.length > 0
              ? selectedTraceChannels
              : selectedChannels.length > 0
                ? selectedChannels
                : ["all"],
          markerType: marker.type,
          metric: marker.metric,
        } satisfies SmartAnnotationSuggestion;
      })
      .filter(
        (suggestion) => !dismissedSmartSuggestionIds.includes(suggestion.id),
      )
      .sort((a, b) => {
        if (b.confidence !== a.confidence) return b.confidence - a.confidence;
        return (
          Math.abs(a.startTime - currentTime) -
          Math.abs(b.startTime - currentTime)
        );
      });

    return suggestions.slice(0, 10);
  }, [
    currentConfig?.time_scale,
    currentTime,
    dismissedSmartSuggestionIds,
    selectedChannels,
    selectedTraceChannels,
    shouldBuildTrendInsights,
    clinicalAnnotationPanelVisible,
    trendMarkers,
  ]);

  useEffect(() => {
    if (!smartAnnotationSuggestions.length) return;
    setSmartSuggestionDrafts((current) => {
      const next = { ...current };
      for (const suggestion of smartAnnotationSuggestions) {
        if (next[suggestion.id]) continue;
        next[suggestion.id] = {
          label: suggestion.label,
          startTime: suggestion.startTime.toFixed(1),
          durationSeconds: suggestion.defaultDurationSeconds.toFixed(1),
          notes: "",
        };
      }
      return next;
    });
  }, [smartAnnotationSuggestions]);

  const smartSuggestionById = useMemo(
    () => new Map(smartAnnotationSuggestions.map((entry) => [entry.id, entry])),
    [smartAnnotationSuggestions],
  );
  smartSuggestionByIdRef.current = smartSuggestionById;

  const selectedInterventionEvent = useMemo<InterventionEvent | null>(() => {
    const fromWindow = interventionEvents.find(
      (event) => event.intervention_id === selectedInterventionId,
    );
    if (fromWindow) return fromWindow;
    return selectedInterventionTimeline?.intervention || null;
  }, [
    interventionEvents,
    selectedInterventionId,
    selectedInterventionTimeline,
  ]);

  const eventTrendCorrelation =
    useMemo<EventTrendCorrelationSummary | null>(() => {
      if (!shouldBuildTrendInsights) return null;
      if (!selectedInterventionEvent) return null;
      if (!trendPoints.length) return null;

      const eventTimeSeconds = selectedInterventionEvent.event_time_seconds;
      const lookbackSeconds = Math.max(60, Math.floor(trendLookbackSeconds));
      const lookaheadSeconds = Math.max(60, Math.floor(trendLookaheadSeconds));

      const preWindowStart = eventTimeSeconds - lookbackSeconds;
      const postWindowEnd = eventTimeSeconds + lookaheadSeconds;
      const prePoints = trendPoints.filter(
        (point) =>
          point.timeSeconds >= preWindowStart &&
          point.timeSeconds < eventTimeSeconds,
      );
      const postPoints = trendPoints.filter(
        (point) =>
          point.timeSeconds > eventTimeSeconds &&
          point.timeSeconds <= postWindowEnd,
      );

      const average = (values: Array<number | null>): number | null => {
        const valid = values.filter(
          (value): value is number =>
            typeof value === "number" && Number.isFinite(value),
        );
        if (!valid.length) return null;
        return valid.reduce((sum, value) => sum + value, 0) / valid.length;
      };

      const preDeltaPower = average(prePoints.map((point) => point.deltaPower));
      const preTotalPower = average(prePoints.map((point) => point.totalPower));
      const preAsymmetry = average(
        prePoints.map((point) => point.asymmetryPct),
      );
      const postDeltaPower = average(
        postPoints.map((point) => point.deltaPower),
      );
      const postTotalPower = average(
        postPoints.map((point) => point.totalPower),
      );
      const postAsymmetry = average(
        postPoints.map((point) => point.asymmetryPct),
      );

      const preMarkerCount = trendMarkers.filter(
        (marker) =>
          marker.timeSeconds >= preWindowStart &&
          marker.timeSeconds < eventTimeSeconds,
      ).length;
      const postMarkerCount = trendMarkers.filter(
        (marker) =>
          marker.timeSeconds > eventTimeSeconds &&
          marker.timeSeconds <= postWindowEnd,
      ).length;

      return {
        interventionId: selectedInterventionEvent.intervention_id,
        interventionLabel: buildInterventionLabel(selectedInterventionEvent),
        eventTimeSeconds,
        lookbackSeconds,
        lookaheadSeconds,
        preWindow: {
          points: prePoints.length,
          avgDeltaPower: preDeltaPower,
          avgTotalPower: preTotalPower,
          avgAsymmetryPct: preAsymmetry,
        },
        postWindow: {
          points: postPoints.length,
          avgDeltaPower: postDeltaPower,
          avgTotalPower: postTotalPower,
          avgAsymmetryPct: postAsymmetry,
        },
        delta: {
          deltaPower:
            preDeltaPower !== null && postDeltaPower !== null
              ? postDeltaPower - preDeltaPower
              : null,
          totalPower:
            preTotalPower !== null && postTotalPower !== null
              ? postTotalPower - preTotalPower
              : null,
          asymmetryPct:
            preAsymmetry !== null && postAsymmetry !== null
              ? postAsymmetry - preAsymmetry
              : null,
        },
        markerCounts: {
          pre: preMarkerCount,
          post: postMarkerCount,
          total: preMarkerCount + postMarkerCount,
        },
      };
    }, [
      shouldBuildTrendInsights,
      selectedInterventionEvent,
      trendPoints,
      trendLookaheadSeconds,
      trendLookbackSeconds,
      trendMarkers,
    ]);

  const exportTrendCorrelationSummary = useCallback(
    (format: "json" | "csv") => {
      if (!eventTrendCorrelation) {
        setTrendCorrelationStatus(
          "Select an intervention and ensure trend samples are available first.",
        );
        return;
      }

      const prefix = `trend-correlation-${eventTrendCorrelation.interventionId}-${new Date().toISOString().replace(/[:.]/g, "-")}`;
      let content = "";
      let mimeType = "application/json";
      let extension = "json";

      if (format === "json") {
        content = JSON.stringify(
          {
            generated_at: new Date().toISOString(),
            profile_id: selectedTrendProfileId || null,
            profile_name: selectedTrendProfile?.name || null,
            summary: eventTrendCorrelation,
            markers: trendMarkers,
          },
          null,
          2,
        );
      } else {
        mimeType = "text/csv";
        extension = "csv";
        const rows = [
          [
            "intervention_id",
            "intervention_label",
            "event_time_seconds",
            "lookback_seconds",
            "lookahead_seconds",
            "pre_points",
            "post_points",
            "delta_power_change",
            "total_power_change",
            "asymmetry_change_pct",
            "pre_markers",
            "post_markers",
          ].join(","),
          [
            eventTrendCorrelation.interventionId,
            `"${eventTrendCorrelation.interventionLabel.replace(/"/g, '""')}"`,
            eventTrendCorrelation.eventTimeSeconds.toFixed(3),
            eventTrendCorrelation.lookbackSeconds,
            eventTrendCorrelation.lookaheadSeconds,
            eventTrendCorrelation.preWindow.points,
            eventTrendCorrelation.postWindow.points,
            eventTrendCorrelation.delta.deltaPower ?? "",
            eventTrendCorrelation.delta.totalPower ?? "",
            eventTrendCorrelation.delta.asymmetryPct ?? "",
            eventTrendCorrelation.markerCounts.pre,
            eventTrendCorrelation.markerCounts.post,
          ].join(","),
        ];
        content = rows.join("\n");
      }

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${prefix}.${extension}`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      setTrendCorrelationStatus(
        `Exported ${format.toUpperCase()} correlation summary for ${eventTrendCorrelation.interventionLabel}.`,
      );
    },
    [
      eventTrendCorrelation,
      selectedTrendProfile?.name,
      selectedTrendProfileId,
      trendMarkers,
    ],
  );

  const parseContentDispositionFilename = useCallback(
    (value?: string): string | null => {
      if (!value) return null;
      const match = value.match(/filename="?([^";]+)"?/i);
      if (!match?.[1]) return null;
      return match[1].trim();
    },
    [],
  );

  const triggerBlobDownload = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, []);

  const captureWaveformSnapshotDataUrl = useCallback(
    (options?: { grayscale?: boolean }): string => {
      const sourceCanvas = canvasRef.current;
      if (
        !sourceCanvas ||
        sourceCanvas.width === 0 ||
        sourceCanvas.height === 0
      ) {
        return "";
      }

      const snapshotCanvas = document.createElement("canvas");
      snapshotCanvas.width = sourceCanvas.width;
      snapshotCanvas.height = sourceCanvas.height;
      const snapshotCtx = snapshotCanvas.getContext("2d");
      if (!snapshotCtx) return "";

      if (options?.grayscale) {
        snapshotCtx.filter = "grayscale(100%) contrast(115%)";
      }
      snapshotCtx.drawImage(sourceCanvas, 0, 0);

      const overlayCanvas = sourceCanvas.parentElement?.querySelector(
        "canvas.eeg-caliper-overlay",
      );
      if (overlayCanvas instanceof HTMLCanvasElement) {
        snapshotCtx.drawImage(
          overlayCanvas,
          0,
          0,
          overlayCanvas.width,
          overlayCanvas.height,
          0,
          0,
          snapshotCanvas.width,
          snapshotCanvas.height,
        );
      }

      return snapshotCanvas.toDataURL("image/png");
    },
    [],
  );

  const buildClinicalReviewContext = useCallback(() => {
    return {
      reviewMode: perspectiveState.isClinicalMode
        ? ("clinical" as const)
        : ("research" as const),
      montageId: currentConfig?.montage_id ?? null,
      montageName: currentMontage?.montage_name ?? null,
      filters: {
        highPass: currentConfig?.high_pass_filter ?? null,
        lowPass: currentConfig?.low_pass_filter ?? null,
        notch60Hz: currentConfig?.notch_filter_60hz ?? null,
        notch50Hz: currentConfig?.notch_filter_50hz ?? null,
      },
      sensitivity: currentConfig?.sensitivity_value ?? null,
      timeScale: currentConfig?.time_scale ?? null,
      gridEnabled: showGrid,
      scaleMarkersEnabled: showScaleMarkers,
      displayProfileId: selectedDisplayProfile?.profile_id ?? null,
      displayProfileName: selectedDisplayProfile?.profile_name ?? null,
    };
  }, [
    currentConfig?.high_pass_filter,
    currentConfig?.low_pass_filter,
    currentConfig?.montage_id,
    currentConfig?.notch_filter_50hz,
    currentConfig?.notch_filter_60hz,
    currentConfig?.sensitivity_value,
    currentConfig?.time_scale,
    currentMontage?.montage_name,
    perspectiveState.isClinicalMode,
    selectedDisplayProfile?.profile_id,
    selectedDisplayProfile?.profile_name,
    showGrid,
    showScaleMarkers,
  ]);

  const buildPdfReportPayload = useCallback((): ClinicalReport | null => {
    if (!studyDetails) {
      return null;
    }

    const patient =
      (studyDetails as { patient?: Record<string, unknown> }).patient || {};
    const patientName = getPatientDisplayName(patient as any);
    const patientRecordLabel = getPatientRecordLabel(patient as any);
    const maskedBirthYear = getPatientBirthYearLabel(patient as any);
    const patientIsMasked = isMaskedPatient(patient as any);
    const rawGender = String(
      patient.sex || patient.gender || "other",
    ).toLowerCase();
    const gender: "male" | "female" | "other" = rawGender.startsWith("m")
      ? "male"
      : rawGender.startsWith("f")
        ? "female"
        : "other";
    const startedAtMs = recordingStartIso
      ? Date.parse(recordingStartIso)
      : Number.NaN;
    const timestampForSeconds = (seconds: number) =>
      Number.isFinite(startedAtMs)
        ? startedAtMs + seconds * 1000
        : seconds * 1000;
    const now = Date.now();
    const recordingLabel =
      (
        selectedRecording as
          | { filename?: string; recording_id_from_edf?: string }
          | undefined
      )?.recording_id_from_edf ||
      (selectedRecording as { filename?: string } | undefined)?.filename ||
      currentRecordingId ||
      studyId;
    const indications =
      String(
        (
          studyDetails as {
            clinical_indication?: string;
            reason_for_study?: string;
          }
        ).clinical_indication ||
          (studyDetails as { reason_for_study?: string }).reason_for_study ||
          "Clinical EEG review",
      ) || "Clinical EEG review";

    const reportAnnotations = (renderAnnotations || [])
      .slice()
      .sort((a, b) => a.start_time - b.start_time)
      .map((annotation) => ({
        id:
          annotation.annotation_id ||
          `${annotation.annotation_type}-${annotation.start_time}`,
        timestamp: timestampForSeconds(annotation.start_time),
        type: annotation.annotation_type,
        description:
          annotation.label ||
          annotation.notes ||
          annotation.description ||
          annotation.annotation_type,
        duration:
          typeof annotation.duration === "number"
            ? annotation.duration
            : typeof annotation.end_time === "number"
              ? Math.max(0, annotation.end_time - annotation.start_time)
              : undefined,
      }));

    const snapshotImage = captureWaveformSnapshotDataUrl();
    const snapshots = snapshotImage
      ? [
          {
            id: `snapshot-${now}`,
            timestamp: now,
            imageData: snapshotImage,
            caption: `Waveform snapshot at ${currentTime.toFixed(2)} seconds`,
            type: "waveform" as const,
          },
        ]
      : [];

    const report: ClinicalReport = {
      id: `clinical-export-${studyId}-${now}`,
      template: "standard",
      patientInfo: {
        id:
          String(
            patient.patient_id || patient.user_id || patient.mrn || studyId,
          ) || studyId,
        name: patientName || "Unknown Patient",
        dateOfBirth: patientIsMasked
          ? maskedBirthYear || "Masked"
          : patient.date_of_birth
            ? new Date(String(patient.date_of_birth)).toLocaleDateString(
                "en-US",
              )
            : "Unknown",
        gender,
        medicalRecordNumber: patientRecordLabel,
      },
      studyInfo: {
        studyDate: recordingStartIso
          ? new Date(recordingStartIso).toLocaleDateString("en-US")
          : new Date().toLocaleDateString("en-US"),
        studyTime: recordingStartIso
          ? new Date(recordingStartIso).toLocaleTimeString("en-US")
          : new Date().toLocaleTimeString("en-US"),
        duration:
          recordingDuration > 0
            ? recordingDuration
            : Number(
                (selectedRecording as { duration_seconds?: number } | undefined)
                  ?.duration_seconds || 0,
              ),
        physician: "Pending Physician Sign-off",
        technician: String(
          (selectedRecording as { edf_technician?: string } | undefined)
            ?.edf_technician || "N/A",
        ),
        indication: indications,
        medications: String(
          (studyDetails as { medications?: string }).medications ||
            "None reported",
        ),
      },
      sections: [
        {
          id: "technical-summary",
          title: "Technical Summary",
          order: 1,
          content:
            `Recording: ${recordingLabel}\n` +
            `Sensitivity: ${currentConfig?.sensitivity_value ?? "N/A"} uV/mm\n` +
            `Timebase: ${currentConfig?.time_scale ?? "N/A"} sec/page\n` +
            `Playback time position: ${currentTime.toFixed(2)}s`,
        },
        {
          id: "annotation-summary",
          title: "Annotation Summary",
          order: 2,
          content:
            reportAnnotations.length > 0
              ? reportAnnotations
                  .slice(0, 20)
                  .map(
                    (entry) =>
                      `${entry.type} @ ${entry.timestamp}: ${entry.description}`,
                  )
                  .join("\n")
              : "No annotations found for this recording window.",
        },
      ],
      snapshots,
      annotations: reportAnnotations,
      interpretation:
        consensusReportSummary &&
        typeof consensusReportSummary.consensusPercentage === "number"
          ? `Consensus score: ${consensusReportSummary.consensusPercentage.toFixed(1)}%.`
          : "Clinical review pending final physician interpretation.",
      conclusion:
        reportAnnotations.length > 0
          ? `Report includes ${reportAnnotations.length} annotations/events.`
          : "No marked events captured for this interval.",
      reviewContext: buildClinicalReviewContext(),
      createdAt: now,
      updatedAt: now,
    };

    return report;
  }, [
    buildClinicalReviewContext,
    captureWaveformSnapshotDataUrl,
    consensusReportSummary,
    currentConfig?.sensitivity_value,
    currentConfig?.time_scale,
    currentRecordingId,
    currentTime,
    recordingDuration,
    recordingStartIso,
    renderAnnotations,
    selectedRecording,
    studyDetails,
    studyId,
  ]);

  const runClinicianBundleExport = useCallback(
    async (format: Exclude<ClinicianExportFormat, "pdf">) => {
      if (!currentRecordingId) {
        setClinicianExportError("No active recording selected for export.");
        return;
      }

      const includeVideo =
        (format === "edf" || format === "edf_plus") &&
        clinicianExportIncludeVideo;
      const response = await postEegClinicalExport(
        currentRecordingId,
        {
          format,
          includeVideo,
          includeMetadata: clinicianExportIncludeMetadata,
          reviewContext: (() => {
            const context = buildClinicalReviewContext();
            return {
              reviewMode: context.reviewMode,
              montageId: context.montageId,
              montageName: context.montageName,
              highPassFilter: context.filters.highPass,
              lowPassFilter: context.filters.lowPass,
              notchFilter60Hz: context.filters.notch60Hz,
              notchFilter50Hz: context.filters.notch50Hz,
              sensitivity: context.sensitivity,
              timeScale: context.timeScale,
              gridEnabled: context.gridEnabled,
              scaleMarkersEnabled: context.scaleMarkersEnabled,
              displayProfileId: context.displayProfileId,
              displayProfileName: context.displayProfileName,
            };
          })(),
        },
        {
          responseType: "arraybuffer",
          timeout: 180000,
        },
      );
      const contentDispositionHeader = response.headers?.[
        "content-disposition"
      ] as string | undefined;
      const filename =
        parseContentDispositionFilename(contentDispositionHeader) ||
        `eeg_export_${format}_${Date.now()}.zip`;
      triggerBlobDownload(
        new Blob([response.data], { type: "application/zip" }),
        filename,
      );
      setClinicianExportStatus(`Export ready: ${filename}`);
    },
    [
      buildClinicalReviewContext,
      clinicianExportIncludeMetadata,
      clinicianExportIncludeVideo,
      currentRecordingId,
      parseContentDispositionFilename,
      triggerBlobDownload,
    ],
  );

  const runClinicianPdfExport = useCallback(async () => {
    const report = buildPdfReportPayload();
    if (!report) {
      setClinicianExportError("Unable to build report payload for PDF export.");
      return;
    }
    const filename = `eeg_report_${studyId}_${Date.now()}.pdf`;
    const { saveReportPDF } = await import("../utils/pdfExporter");
    await saveReportPDF(report, filename, {
      includeAnnotations: true,
      includeSnapshots: true,
    });
    setClinicianExportStatus(`PDF exported: ${filename}`);
  }, [buildPdfReportPayload, studyId]);

  const handleOpenPrintWaveformView = useCallback(() => {
    const sourceCanvas = canvasRef.current;
    if (
      !sourceCanvas ||
      sourceCanvas.width === 0 ||
      sourceCanvas.height === 0
    ) {
      setPrintWaveformError(
        "Waveform is not ready yet. Try again in a moment.",
      );
      return;
    }
    if (!currentConfig) {
      setPrintWaveformError("Viewer configuration is unavailable.");
      return;
    }

    const waveformDataUrl = captureWaveformSnapshotDataUrl({
      grayscale: printWaveformColorMode === "bw",
    });
    if (!waveformDataUrl) {
      setPrintWaveformError("Unable to prepare print image.");
      return;
    }

    const printWindow = window.open("", "_blank", "width=1280,height=900");
    if (!printWindow) {
      setPrintWaveformError(
        "Popup blocked. Please allow popups for this site and try again.",
      );
      return;
    }

    const recordingLabel = currentRecordingId || "N/A";
    const pageSizeSpec = `${printWaveformPageSize} ${printWaveformOrientation}`;
    const colorModeLabel =
      printWaveformColorMode === "bw" ? "Black & White" : "Color";
    const generatedLabel = new Date().toLocaleString();
    const title = "EEG Waveform Print View";
    const metadata = [
      `Study: ${studyId}`,
      `Patient: ${patientId}`,
      `Recording: ${recordingLabel}`,
      `Generated: ${generatedLabel}`,
      `Sensitivity: ${currentConfig.sensitivity_value} uV/mm`,
      `Time Scale: ${currentConfig.time_scale} sec/page`,
      `Playback Speed: ${Number(currentConfig.playback_speed || 1).toFixed(2)}x`,
      `Playhead Time: ${formatViewerClockTime(pagePlayheadAbsoluteTime)}`,
      `Channels: ${eegData?.channels?.length ?? 0}`,
      `Print Mode: ${colorModeLabel}, ${pageSizeSpec}`,
    ];

    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    const metadataHtml = metadata
      .map((item) => `<li>${escapeHtml(item)}</li>`)
      .join("");

    printWindow.document.open();
    printWindow.document.write(`<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <style>
      @page { size: ${pageSizeSpec}; margin: 10mm; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        background: #fff;
        color: #111;
        font-family: "Segoe UI", Arial, sans-serif;
      }
      .print-shell {
        width: 100%;
        min-height: 100vh;
        padding: 8mm;
      }
      .print-header {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 6mm;
      }
      .print-title {
        font-size: 18px;
        font-weight: 700;
      }
      .print-subtitle {
        font-size: 11px;
        color: #4b5563;
      }
      .print-meta {
        margin: 0 0 6mm 0;
        padding-left: 18px;
        columns: 2;
        column-gap: 8mm;
        font-size: 11px;
      }
      .print-meta li {
        margin-bottom: 3px;
      }
      .waveform-frame {
        width: 100%;
        border: 1px solid #1f2937;
        border-radius: 2px;
        padding: 2mm;
      }
      .waveform-image {
        width: 100%;
        height: auto;
        display: block;
      }
      .print-help {
        margin-top: 6mm;
        font-size: 11px;
        color: #6b7280;
      }
      .print-help button {
        border: 1px solid #475569;
        background: #fff;
        color: #111827;
        border-radius: 4px;
        padding: 4px 10px;
        cursor: pointer;
      }
      @media print {
        .print-help { display: none; }
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    </style>
  </head>
  <body>
    <main class="print-shell">
      <header class="print-header">
        <div class="print-title">${title}</div>
        <div class="print-subtitle">${escapeHtml(colorModeLabel)} · ${escapeHtml(
          pageSizeSpec,
        )}</div>
      </header>
      <ul class="print-meta">${metadataHtml}</ul>
      <section class="waveform-frame">
        <img class="waveform-image" src="${waveformDataUrl}" alt="EEG waveform snapshot for printing" />
      </section>
      <div class="print-help">
        <button type="button" onclick="window.print()">Print</button>
        <span>Use your browser print dialog to confirm destination and copies.</span>
      </div>
    </main>
  </body>
</html>`);
    printWindow.document.close();

    window.setTimeout(() => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch (error) {
        logger.warn("[EEGViewer] Unable to auto-open print dialog", error);
      }
    }, 160);

    setPrintWaveformError(null);
    setShowPrintWaveformDialog(false);
    setClinicianExportError(null);
    setClinicianExportStatus(
      `Print view opened (${colorModeLabel}, ${pageSizeSpec}).`,
    );
  }, [
    captureWaveformSnapshotDataUrl,
    currentConfig,
    currentRecordingId,
    eegData?.channels?.length,
    pagePlayheadAbsoluteTime,
    patientId,
    printWaveformColorMode,
    printWaveformOrientation,
    printWaveformPageSize,
    studyId,
  ]);

  const handleRunClinicianExport = useCallback(async () => {
    if (clinicianExportBusy) return;
    setClinicianExportError(null);
    setClinicianExportStatus(null);
    setClinicianExportBusy(true);
    try {
      if (clinicianExportFormat === "pdf") {
        await runClinicianPdfExport();
      } else {
        await runClinicianBundleExport(clinicianExportFormat);
      }
    } catch (error) {
      const normalized = toUserFacingApiError(error);
      setClinicianExportError(normalized.message);
    } finally {
      setClinicianExportBusy(false);
    }
  }, [
    clinicianExportBusy,
    clinicianExportFormat,
    runClinicianBundleExport,
    runClinicianPdfExport,
  ]);

  const recordPlaybackNavigationEvent = useCallback(
    (payload: Record<string, unknown>) => {
      const diagnosticsHost = window as unknown as {
        eegDiag?: {
          navigationEvents?: Array<Record<string, unknown>>;
        };
      };
      const diagnostics = diagnosticsHost.eegDiag;
      if (!diagnostics) {
        return;
      }

      if (!Array.isArray(diagnostics.navigationEvents)) {
        diagnostics.navigationEvents = [];
      }

      diagnostics.navigationEvents.push({
        t: Date.now(),
        ...payload,
      });

      if (diagnostics.navigationEvents.length > 120) {
        diagnostics.navigationEvents.splice(
          0,
          diagnostics.navigationEvents.length - 120,
        );
      }
    },
    [],
  );

  // TODO(eeg-viewer-seek-extraction): jumpToViewerTime intentionally stays
  // here rather than moving into `useEEGViewerSeek` — it is tightly coupled
  // to the same tile/buffer refs and renderer state that back
  // `handlePlaybackTick` (pendingEegBufferRef, rendererLoadedWindowStartRef,
  // scheduledWindowStartRef, tileCache prefetch, blit-cache invalidation,
  // etc.), so extracting it cleanly needs to happen alongside (or after) a
  // future handlePlaybackTick extraction. `navigateByPages`,
  // `navigateByFinePan`, `goToStart`, and `consumeDeferredPausedSeekTime`
  // already live in `useEEGViewerSeek` and call this function via an
  // injected `jumpToViewerTime` param.
  const jumpToViewerTime = useCallback(
    (
      targetTime: number,
      reason: {
        source?: string;
        direction?: string;
        pageCount?: number;
        stepSeconds?: number;
      } = {},
    ) => {
      const timeScale = currentConfig?.time_scale || 10;
      const speed = currentConfig?.playback_speed || 1;
      const exitsPausedPlayHold = shouldExitEEGViewerPausedPlayHoldOnManualSeek(
        {
          isPlaying: isPlayingRef.current,
          pausedHoldTransportMode:
            pausedHighSpeedInspectHoldRef.current?.transportMode,
        },
      );
      const navigationTransportMode: EEGTileTransportMode = exitsPausedPlayHold
        ? "inspect"
        : playbackTransportMode;
      const previousPlayhead =
        clockSeekRef.current?.playheadRef.current ?? currentTimeRef.current;
      const maxDuration =
        recordingMetaDurationRef.current > 0
          ? recordingMetaDurationRef.current
          : typeof selectedRecording?.duration_seconds === "number" &&
              selectedRecording.duration_seconds > 0
            ? selectedRecording.duration_seconds
            : recordingDuration;
      const maxTime =
        maxDuration > 0
          ? Math.max(0, maxDuration - timeScale)
          : 3600;
      const clampedTime = Math.max(0, Math.min(maxTime, targetTime));

      if (exitsPausedPlayHold) {
        recordPauseBoundaryEvent("manual-seek-exit-paused-play-hold", {
          targetTime: Number(clampedTime.toFixed(3)),
          loadedWindowStart: rendererLoadedWindowStartRef.current,
          pendingWindowStart: pendingEegBufferRef.current?.windowStart ?? null,
        });
        pendingPausedInspectWindowStartRef.current = null;
        commitPausedHighSpeedInspectHold(null);
        if (
          pendingEegBufferRef.current?.requestContext?.transportMode === "play"
        ) {
          pendingEegBufferRef.current = null;
        }
        if (currentRecordingId) {
          tileCache.cancelPrefetch(currentRecordingId);
        }
      }

      const bufferSize = getEffectiveBufferSize(
        speed,
        timeScale,
        navigationTransportMode,
      );
      const loadedWindowStart = rendererLoadedWindowStartRef.current;
      const loadedBufferEnd = rendererLoadedBufferEndRef.current;
      const advanceChunk = getAdvanceChunk(bufferSize, timeScale);
      const nextWindowStart = alignWindowStartToBackend(
        Math.floor(clampedTime / advanceChunk) * advanceChunk,
        speed,
        timeScale,
        navigationTransportMode,
        recordingDuration,
      );
      const pendingWindowStart =
        pendingEegBufferRef.current?.windowStart ?? null;
      const jumpedBackward = clampedTime + 0.25 < previousPlayhead;
      const pendingConflictsWithTarget =
        pendingWindowStart !== null &&
        (pendingWindowStart < nextWindowStart - 0.25 ||
          pendingWindowStart > nextWindowStart + advanceChunk + 0.25 ||
          (jumpedBackward && pendingWindowStart > clampedTime + 0.25));
      const resetsWindowStart = shouldResetEEGViewerWindowStartOnSeek({
        targetTime: clampedTime,
        dataWindowStart,
        bufferSize,
        timeScale,
        loadedWindowStart,
        loadedBufferEnd,
      });

      const pendingSeekBuffer = pendingEegBufferRef.current;
      const discardPendingBufferForPausedSeek =
        pendingSeekBuffer !== null &&
        shouldDiscardEEGViewerBackwardPendingBuffer({
          pendingWindowStart: pendingSeekBuffer.windowStart,
          loadedWindowStart: rendererLoadedWindowStartRef.current,
          loadedContext: rendererLoadedPlaybackContextRef.current,
          pendingContext: pendingSeekBuffer.requestContext,
        });
      const pendingDerivedDuration =
        pendingSeekBuffer?.channelData[0]?.sampleRate &&
        pendingSeekBuffer.channelData[0]?.data?.length
          ? pendingSeekBuffer.channelData[0].data.length /
            pendingSeekBuffer.channelData[0].sampleRate
          : null;
      const pendingDurationSeconds =
        pendingDerivedDuration ?? pendingSeekBuffer?.duration ?? null;
      const pendingBufferEnd =
        pendingSeekBuffer !== null && pendingDurationSeconds !== null
          ? pendingSeekBuffer.windowStart + pendingDurationSeconds
          : null;
      const deferPausedSeek = shouldDeferEEGViewerPausedSeekUntilBufferLoad({
        isPlaying: isPlayingRef.current,
        resetsWindowStart,
        targetTime: clampedTime,
        pendingWindowStart: discardPendingBufferForPausedSeek
          ? null
          : pendingSeekBuffer?.windowStart,
        pendingBufferEnd: discardPendingBufferForPausedSeek
          ? null
          : pendingBufferEnd,
        timeScale,
        reason, // pass the seek reason so annotation-timeline clicks skip deferral
      });

      if (deferPausedSeek) {
        pendingPausedSeekTimeRef.current = clampedTime;
      } else {
        pendingPausedSeekTimeRef.current = null;
        playbackPerf.recordSeekRequest();
        setCurrentTime(clampedTime);
        currentTimeRef.current = clampedTime;
      }

      recordPlaybackNavigationEvent({
        source: reason.source || "generic",
        direction: reason.direction || null,
        pageCount:
          typeof reason.pageCount === "number" ? reason.pageCount : null,
        stepSeconds:
          typeof reason.stepSeconds === "number"
            ? Number(reason.stepSeconds.toFixed(2))
            : null,
        targetTime: Number(targetTime.toFixed(2)),
        previousPlayhead: Number(previousPlayhead.toFixed(2)),
        clampedTime: Number(clampedTime.toFixed(2)),
        timeScale,
        speed,
        playbackTransportMode: navigationTransportMode,
        nextWindowStart,
        dataWindowStart,
        bufferSize,
        pendingWindowStart,
        pendingConflictsWithTarget,
        jumpedBackward,
        resetsWindowStart,
      });

      if (pendingConflictsWithTarget) {
        logger.debug("[EEGViewer] Seek discarded stale pending buffer", {
          previousPlayhead: previousPlayhead.toFixed(2),
          targetTime: clampedTime.toFixed(2),
          nextWindowStart,
          pendingWindowStart,
        });
        recordPauseBoundaryEvent("discard-pending-buffer-paused-seek", {
          targetTime: Number(clampedTime.toFixed(3)),
          nextWindowStart,
          pendingWindowStart,
        });
        pendingEegBufferRef.current = null;
      }

      if (resetsWindowStart) {
        // Reset scheduled ref so the RAF tick can re-advance from the new position.
        scheduledWindowStartRef.current = nextWindowStart;
        dataWindowStartRef.current = nextWindowStart;
        setDataWindowStart(nextWindowStart);
        queuePlaybackPrefetch(
          clampedTime,
          clampedTime >= playheadRef.current ? "forward" : "backward",
          speed,
        );
      }

      const pending = pendingEegBufferRef.current;
      if (
        pending &&
        !isPlayingRef.current &&
        !deferPausedSeek &&
        clampedTime + 0.25 >= pending.windowStart &&
        rendererRef.current &&
        !shouldDiscardEEGViewerBackwardPendingBuffer({
          pendingWindowStart: pending.windowStart,
          loadedWindowStart: rendererLoadedWindowStartRef.current,
          loadedContext: rendererLoadedPlaybackContextRef.current,
          pendingContext: pending.requestContext,
        })
      ) {
        pendingEegBufferRef.current = null;
        const previousLoadedWindowStart = rendererLoadedWindowStartRef.current;
        const isClinicalRawPendingFlush =
          pending.requestContext?.transportMode === "play" &&
          pending.requestContext.speed <= 4 &&
          (pending.requestContext.actualLevel ?? 0) === 0;
        const preserveBlitCache =
          !isClinicalRawPendingFlush &&
          (previousLoadedWindowStart === null ||
            pending.windowStart >= previousLoadedWindowStart);
        rendererRef.current.loadChannels(
          pending.channelData,
          preserveBlitCache,
        );
        loadedChannelsRef.current = pending.channelData;
        markFirstWaveformChannelsPublished(pending.channelData.length);
        markLiveTopographyBufferUpdated(currentRecordingId);
        rendererLoadedWindowStartRef.current = pending.windowStart;
        rendererLoadedPlaybackContextRef.current = pending.requestContext;
        const pendingDerivedDuration =
          pending.channelData[0]?.sampleRate &&
          pending.channelData[0]?.data?.length
            ? pending.channelData[0].data.length /
              pending.channelData[0].sampleRate
            : null;
        const pendingDurationSeconds =
          pendingDerivedDuration ?? pending.duration;
        rendererLoadedBufferEndRef.current =
          pendingDurationSeconds !== null
            ? pending.windowStart + pendingDurationSeconds
            : pending.windowStart + bufferSize;
        rendererRef.current.setDataTimeOffset(
          pending.windowStart,
          preserveBlitCache,
        );
        dataWindowStartRef.current = pending.windowStart;
        scheduledWindowStartRef.current = pending.windowStart;
        setDataWindowStart((current) =>
          current === pending.windowStart ? current : pending.windowStart,
        );
        logger.debug("[EEGViewer] Flushed pending buffer during paused seek", {
          targetTime: clampedTime.toFixed(2),
          pendingWindowStart: pending.windowStart,
          pendingDuration:
            pendingDurationSeconds === null
              ? null
              : pendingDurationSeconds.toFixed(2),
          preserveBlitCache,
        });
        recordPauseBoundaryEvent("flush-pending-buffer-paused-seek", {
          targetTime: Number(clampedTime.toFixed(3)),
          pendingWindowStart: pending.windowStart,
          pendingDuration:
            pendingDurationSeconds === null
              ? null
              : Number(pendingDurationSeconds.toFixed(3)),
          preserveBlitCache,
          pendingTransportMode: pending.requestContext?.transportMode ?? null,
        });
      }

      if (rendererRef.current) {
        rendererRef.current.invalidateBlitCache();
        // Bug Q fix: at the end of the recording the final tile may be shorter
        // than one full page.  Snap the display time up to loadedWindowStart
        // so waveforms start at the left edge of the canvas (blank on right).
        const bufEnd = rendererLoadedBufferEndRef.current;
        const immediateDisplayTime = deferPausedSeek
          ? currentTimeRef.current
          : clampedTime;
        const lwStart =
          rendererLoadedWindowStartRef.current ?? immediateDisplayTime;
        const isLastShort =
          bufEnd !== null &&
          bufEnd - timeScale < lwStart &&
          pendingEegBufferRef.current === null;
        const displayTime = isLastShort
          ? Math.max(immediateDisplayTime, lwStart)
          : immediateDisplayTime;
        rendererRef.current.setCurrentTime(displayTime);
        if (!deferPausedSeek && !isPlayingRef.current) {
          // Let the paused-frame effect perform the single post-seek render.
          lastRenderedPlaybackTimeRef.current = null;
        } else {
          rendererRef.current.render(
            mapAnnotationsForRenderer(renderAnnotations),
          );
          lastRenderedPlaybackTimeRef.current = displayTime;
        }
      }

      recordReviewedViewportRef.current?.(
        clampedTime,
        clampedTime + timeScale,
      );
    },
    [
      currentConfig?.playback_speed,
      currentConfig?.time_scale,
      currentRecordingId,
      commitPausedHighSpeedInspectHold,
      dataWindowStart,
      getAdvanceChunk,
      getEffectiveBufferSize,
      mapAnnotationsForRenderer,
      playbackTransportMode,
      playheadRef,
      queuePlaybackPrefetch,
      recordPauseBoundaryEvent,
      recordPlaybackNavigationEvent,
      renderAnnotations,
      recordingDuration,
      selectedRecording?.duration_seconds,
      setCurrentTime,
      tileCache,
    ],
  );

  const {
    navigateByPages,
    navigateByFinePan,
    goToStart,
    consumeDeferredPausedSeekTime,
  } = useEEGViewerSeek({
    pendingPausedSeekTimeRef,
    currentConfigTimeScale: currentConfig?.time_scale,
    currentConfigPlaybackSpeed: currentConfig?.playback_speed,
    currentTimeRef,
    rendererLoadedWindowStartRef,
    rendererLoadedBufferEndRef,
    recordingMetaDurationRef,
    recordingDurationRef,
    dataWindowStartRef,
    playbackTransportMode,
    getEffectiveBufferSize,
    setCurrentTime,
    jumpToViewerTime,
  });

  jumpToViewerTimeRef.current = jumpToViewerTime;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    (window as any).__eegSoakJumpToTime = (timeSeconds: number) => {
      const targetTime = Number(timeSeconds);
      if (!Number.isFinite(targetTime)) return;
      currentTimeRef.current = Math.max(0, targetTime);
      (window as any).__eegSoakPlaybackTimeSeconds = currentTimeRef.current;
      setCurrentTime(currentTimeRef.current);
      jumpToViewerTime(targetTime, { source: "soak-diagnostic-seek" });
    };
    return () => {
      delete (window as any).__eegSoakJumpToTime;
    };
  }, [jumpToViewerTime]);

  const syncPausedClockAfterImmediateLoad = useCallback(
    (
      nextTime: number,
      params: {
        bufferWindowStart: number;
        loadedBufferEnd: number | null;
        transportMode: EEGTileTransportMode | null | undefined;
      },
    ) => {
      const normalizedTime = Number.isFinite(nextTime)
        ? Math.max(0, nextTime)
        : 0;
      const previousPlayheadTime = playheadRef.current;

      currentTimeRef.current = normalizedTime;
      setCurrentTime(normalizedTime);

      if (Math.abs(previousPlayheadTime - normalizedTime) > 0.25) {
        recordPauseBoundaryEvent("sync-paused-clock-after-immediate-load", {
          previousPlayheadTime: Number(previousPlayheadTime.toFixed(3)),
          nextPlayheadTime: Number(normalizedTime.toFixed(3)),
          bufferWindowStart: params.bufferWindowStart,
          loadedBufferEnd:
            params.loadedBufferEnd === null
              ? null
              : Number(params.loadedBufferEnd.toFixed(3)),
          pendingTransportMode: params.transportMode ?? null,
        });
      }

      return normalizedTime;
    },
    [recordPauseBoundaryEvent, setCurrentTime],
  );

  const handleTimelineAnnotationClick = useCallback(
    (annotation: TimelineAnnotation) =>
      jumpToViewerTime(
        viewportStartForPlayheadAbsoluteTime({
          absoluteTimeSeconds: clampTimelineTimeToRecording(
            annotation.startTime,
            Math.max(
              recordingDurationRef.current,
              recordingMetaDurationRef.current,
              recordingDuration,
            ),
            currentConfig?.time_scale || 10,
          ),
          offsetSeconds: pagePlayheadOffsetRef.current,
          timeScaleSeconds: currentConfig?.time_scale || 10,
        }),
        {
          source: "annotation-timeline",
        },
      ),
    [currentConfig?.time_scale, jumpToViewerTime, recordingDuration],
  );

  // Minimap onSeek is a page-window start (band pan / page jump). Subtracting
  // the reading-line offset here would shift the page on every strip drag.
  const handleTimelineSeek = useCallback(
    (timeSeconds: number) =>
      jumpToViewerTime(timeSeconds, {
        source: "annotation-timeline",
      }),
    [jumpToViewerTime],
  );

  useEffect(() => {
    const scale = currentConfig?.time_scale || 10;
    setPagePlayheadOffsetSeconds((current) =>
      clampPagePlayheadOffsetSeconds(current, scale),
    );
  }, [currentConfig?.time_scale]);

  const handleEventDetectionClick = useCallback(
    (time: number) =>
      jumpToViewerTime(
        viewportStartForPlayheadAbsoluteTime({
          absoluteTimeSeconds: time,
          offsetSeconds: pagePlayheadOffsetRef.current,
          timeScaleSeconds: currentConfig?.time_scale || 10,
        }),
      ),
    [currentConfig?.time_scale, jumpToViewerTime],
  );

  const eventNavigationState = useMemo(
    () =>
      resolveEEGViewerEventNavigationState({
        eventIndex,
        currentTime: pagePlayheadAbsoluteTime,
      }),
    [eventIndex, pagePlayheadAbsoluteTime],
  );

  const jumpToNextEvent = useCallback(() => {
    if (eventNavigationState.nextEventTime !== null) {
      jumpToViewerTime(
        viewportStartForPlayheadAbsoluteTime({
          absoluteTimeSeconds: eventNavigationState.nextEventTime,
          offsetSeconds: pagePlayheadOffsetRef.current,
          timeScaleSeconds: currentConfig?.time_scale || 10,
        }),
      );
    }
  }, [
    currentConfig?.time_scale,
    eventNavigationState.nextEventTime,
    jumpToViewerTime,
  ]);

  const jumpToPrevEvent = useCallback(() => {
    if (eventNavigationState.previousEventTime !== null) {
      jumpToViewerTime(
        viewportStartForPlayheadAbsoluteTime({
          absoluteTimeSeconds: eventNavigationState.previousEventTime,
          offsetSeconds: pagePlayheadOffsetRef.current,
          timeScaleSeconds: currentConfig?.time_scale || 10,
        }),
      );
    }
  }, [
    currentConfig?.time_scale,
    eventNavigationState.previousEventTime,
    jumpToViewerTime,
  ]);
  const { previousEventCount, nextEventCount } = eventNavigationState;

  const videoPanelEventMarkers = useMemo<ExternalEventMarker[]>(() => {
    const playableDuration = Math.max(
      recordingDurationRef.current,
      recordingMetaDurationRef.current,
      recordingDuration,
    );
    const fromAnnotations = renderAnnotations
      .filter((annotation) =>
        isTimelineTimeWithinRecording(annotation.start_time, playableDuration),
      )
      .map((annotation, index) => ({
        id: `ann-${annotation.annotation_id || index}-${annotation.start_time}`,
        timestampMs: Math.max(0, annotation.start_time * 1000),
        label: annotation.label || annotation.annotation_type || "Annotation",
        eventType: annotation.annotation_type || "annotation",
        source: "eeg" as const,
      }));

    const fromEventIndex = eventIndex
      .filter((timePoint) =>
        isTimelineTimeWithinRecording(timePoint, playableDuration),
      )
      .map((timePoint, index) => ({
        id: `idx-${index}-${timePoint}`,
        timestampMs: Math.max(0, timePoint * 1000),
        label: `Event ${index + 1}`,
        eventType: "annotation",
        source: "eeg" as const,
      }));

    const merged = [...fromAnnotations, ...fromEventIndex].sort(
      (a, b) => a.timestampMs - b.timestampMs,
    );

    const deduped = new Map<string, ExternalEventMarker>();
    for (const marker of merged) {
      const key = `${Math.round(marker.timestampMs)}:${marker.label}`;
      if (!deduped.has(key)) {
        deduped.set(key, marker);
      }
    }

    return [...deduped.values()];
  }, [eventIndex, recordingDuration, renderAnnotations]);

  const eegVideoClockSnapshotRef = useRef<PlaybackClockSnapshot>({
    currentTimeMs: 0,
    isPlaying: false,
    speed: 1,
    durationMs: 0,
  });
  eegVideoClockSnapshotRef.current.currentTimeMs = Math.max(
    0,
    pagePlayheadAbsoluteTime * 1000,
  );
  eegVideoClockSnapshotRef.current.isPlaying = isPlaying;
  eegVideoClockSnapshotRef.current.speed = currentConfig?.playback_speed || 1;
  eegVideoClockSnapshotRef.current.durationMs = Math.max(
    0,
    recordingDuration * 1000,
  );

  const videoSlaveEegStateRef = useRef({
    speed: 1,
    isPlaying: false,
    currentTimeSeconds: 0,
    recordingDurationSeconds: 0,
  });
  videoSlaveEegStateRef.current.speed = currentConfig?.playback_speed || 1;
  videoSlaveEegStateRef.current.isPlaying = isPlaying;
  videoSlaveEegStateRef.current.currentTimeSeconds = pagePlayheadAbsoluteTime;
  videoSlaveEegStateRef.current.recordingDurationSeconds = recordingDuration;

  const handleVideoPanelPlaybackClockChange = useCallback(
    (update: PlaybackClockUpdate) => {
      const eegState = videoSlaveEegStateRef.current;
      if (
        typeof update.currentTimeMs === "number" &&
        Number.isFinite(update.currentTimeMs)
      ) {
        const targetSeconds = Math.max(0, update.currentTimeMs / 1000);
        if (
          shouldApplyVideoSlaveSeekToEeg({
            eegSpeed: eegState.speed,
            eegIsPlaying: eegState.isPlaying,
          }) &&
          Math.abs(targetSeconds - eegState.currentTimeSeconds) > 0.05
        ) {
          jumpToViewerTime(
            viewportStartForPlayheadAbsoluteTime({
              absoluteTimeSeconds: targetSeconds,
              offsetSeconds: pagePlayheadOffsetRef.current,
              timeScaleSeconds:
                currentConfigRef.current?.time_scale || 10,
            }),
          );
        }
      }

      if (
        typeof update.isPlaying === "boolean" &&
        update.isPlaying !== eegState.isPlaying
      ) {
        // Video slave must not pause EEG while rapid review is still mid-study
        // (jump-mode <video>.pause() and short fixture videos previously stopped
        // the master clock and froze soaks at ≥60x).
        const speed = Math.abs(eegState.speed || 1);
        if (
          !update.isPlaying &&
          eegState.isPlaying &&
          (speed > 2 ||
            (eegState.recordingDurationSeconds > 0 &&
              eegState.currentTimeSeconds <
                eegState.recordingDurationSeconds - 1))
        ) {
          return;
        }
        setPlaybackRunning(update.isPlaying);
      }

      if (typeof update.speed === "number" && Number.isFinite(update.speed)) {
        const normalizedSpeed = Math.max(0.25, update.speed);
        if (
          !shouldApplyVideoSlaveSpeedToEeg({
            eegSpeed: eegState.speed,
            videoReportedSpeed: normalizedSpeed,
          })
        ) {
          return;
        }
        const applyConfigChange = configChangeRequestRef.current;
        if (applyConfigChange) {
          applyConfigChange({ playback_speed: normalizedSpeed });
        } else {
          setCurrentConfig((previous) => {
            if (!previous) return previous;
            return { ...previous, playback_speed: normalizedSpeed };
          });
        }
      }
    },
    [jumpToViewerTime, setCurrentConfig, setPlaybackRunning],
  );

  const videoPlaybackController = useMemo<PlaybackTransportController>(
    () => ({
      snapshot: eegVideoClockSnapshotRef.current,
      seekToMs: (timeMs: number) =>
        handleVideoPanelPlaybackClockChange({ currentTimeMs: timeMs }),
      setPlaying: (nextIsPlaying: boolean) =>
        handleVideoPanelPlaybackClockChange({ isPlaying: nextIsPlaying }),
      setSpeed: (speed: number) =>
        handleVideoPanelPlaybackClockChange({ speed }),
    }),
    [handleVideoPanelPlaybackClockChange],
  );

  const handleCloseVideoPanel = useCallback(() => {
    setShowVideoPanel(false);
  }, []);

  useEffect(() => {
    if (showVideoPanel) return;

    videoPopout.setPopoutHandlers({
      onPopoutSpeedChange: (speed) =>
        handleVideoPanelPlaybackClockChange({
          speed: Math.max(0.25, speed),
        }),
      onPopoutPlayback: (message) => {
        const matchesPrimary =
          message.feed === "primary" && videoPopout.isPrimaryPopped;
        const matchesSecondary =
          message.feed === "secondary" && videoPopout.isSecondaryPopped;
        if (!matchesPrimary && !matchesSecondary) return;

        if (typeof message.videoTimeMs === "number") {
          handleVideoPanelPlaybackClockChange({
            currentTimeMs: Math.max(
              0,
              message.videoTimeMs + videoPopout.getVideoSyncOffsetMs(),
            ),
          });
        }
        if (typeof message.isPlaying === "boolean") {
          handleVideoPanelPlaybackClockChange({
            isPlaying: message.isPlaying,
          });
        }
        if (typeof message.speed === "number") {
          handleVideoPanelPlaybackClockChange({
            speed: Math.max(0.25, message.speed),
          });
        }
      },
    });

    return () => {
      videoPopout.setPopoutHandlers({});
    };
  }, [
    handleVideoPanelPlaybackClockChange,
    showVideoPanel,
    videoPopout,
  ]);

  useEffect(() => {
    if (showVideoPanel) return;
    if (!videoPopout.isPrimaryPopped && !videoPopout.isSecondaryPopped) return;

    const videoMs = Math.max(
      0,
      pagePlayheadAbsoluteTime * 1000 - videoPopout.getVideoSyncOffsetMs(),
    );
    videoPopout.updateSyncState({
      currentTimeMs: videoMs,
      isPlaying,
      speed: currentConfig?.playback_speed || 1,
      highSpeedMode: (currentConfig?.playback_speed || 1) > 2 ? "jump" : "normal",
    });
  }, [
    currentConfig?.playback_speed,
    isPlaying,
    pagePlayheadAbsoluteTime,
    showVideoPanel,
    videoPopout,
  ]);

  useEffect(() => {
    if (!videoPopout.isPrimaryPinned && !videoPopout.isSecondaryPinned) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest(".vpp")) return;
      videoPopout.requestPinnedFocus();
    };

    window.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [
    videoPopout.isPrimaryPinned,
    videoPopout.isSecondaryPinned,
    videoPopout.requestPinnedFocus,
  ]);

  const timelineAnnotations = useMemo<TimelineAnnotation[]>(() => {
    const base = renderAnnotations.map((annotation, index) => {
      const type = annotation.annotation_type || "annotation";
      const annotationCategory = resolveAnnotationCategory(annotation);
      const duration = Math.max(
        0.25,
        (annotation.end_time ?? annotation.start_time) - annotation.start_time,
      );
      const defaultColor =
        annotationCategory === "technical_imported"
          ? TECHNICALLY_IMPORTED_ANNOTATION_COLOR
          : type.includes("seizure")
            ? "#ef4444"
            : type.includes("artifact")
              ? "#f59e0b"
              : type.includes("spike")
                ? "#3b82f6"
                : "#8b5cf6";
      return {
        id:
          annotation.annotation_id ||
          `${type}-${annotation.start_time}-${index}`,
        startTime: annotation.start_time,
        endTime: annotation.start_time + duration,
        type,
        color: resolveAnnotationDisplayColor(annotation, defaultColor),
        label: annotation.label || type,
      };
    });
    const trend = trendMarkers.map((marker) => {
      const color =
        marker.severity === "critical"
          ? "#dc2626"
          : marker.severity === "warning"
            ? "#d97706"
            : "#2563eb";
      return {
        id: `trend-marker-${marker.id}`,
        startTime: marker.timeSeconds,
        endTime: marker.timeSeconds + 0.25,
        type: `trend_${marker.type}`,
        color,
        label: marker.label,
        description: `${marker.metric}=${marker.value.toFixed(2)}${
          marker.threshold !== undefined
            ? ` (threshold ${marker.threshold.toFixed(2)})`
            : ""
        }`,
      };
    });
    const segmentBoundaries =
      Array.isArray(playbackManifest?.segments) &&
      playbackManifest.segments.length > 1
        ? playbackManifest.segments.slice(0, -1).map((segment) => ({
            id: `segment-boundary-${segment.segmentId}`,
            startTime: Math.max(0, segment.cumulativeEndMs / 1000),
            endTime: Math.max(0, segment.cumulativeEndMs / 1000) + 0.25,
            type: "segment_boundary",
            color: "#64748b",
            label: `Segment ${segment.sequenceIndex + 1} boundary`,
            description: `Boundary after ${segment.sourceFilename}`,
          }))
        : [];
    const continuityWarnings =
      Array.isArray(playbackManifest?.segments) &&
      playbackManifest.segments.length > 1
        ? playbackManifest.segments.slice(1).flatMap((segment) => {
            const markers: TimelineAnnotation[] = [];
            const boundaryTime = Math.max(0, segment.cumulativeStartMs / 1000);

            if ((segment.gapBeforeMs || 0) > 0) {
              const gapSeconds = ((segment.gapBeforeMs || 0) / 1000).toFixed(2);
              markers.push({
                id: `segment-gap-${segment.segmentId}`,
                startTime: boundaryTime,
                endTime: boundaryTime + 0.25,
                type: "segment_gap",
                color: "#f59e0b",
                label: `Gap ${gapSeconds}s`,
                description: `Gap before segment ${segment.sequenceIndex + 1}`,
              });
            }

            if ((segment.overlapBeforeMs || 0) > 0) {
              const overlapSeconds = (
                (segment.overlapBeforeMs || 0) / 1000
              ).toFixed(2);
              markers.push({
                id: `segment-overlap-${segment.segmentId}`,
                startTime: boundaryTime,
                endTime: boundaryTime + 0.25,
                type: "segment_overlap",
                color: "#ef4444",
                label: `Overlap ${overlapSeconds}s`,
                description: `Overlap before segment ${segment.sequenceIndex + 1}`,
              });
            }

            return markers;
          })
        : [];
    // Full-recording annotation markers from the enriched event index.
    // Provides navigation ticks across the entire recording timeline
    // so clinicians can see ALL annotation positions at a glance.
    const playableDuration = Math.max(
      recordingDurationRef.current,
      recordingMetaDurationRef.current,
      recordingDuration,
    );
    const fullEventData = (eventIndexRaw || []).filter((item) =>
      isTimelineTimeWithinRecording(item.start_time, playableDuration),
    );
    const eventIndexMarkers: TimelineAnnotation[] = fullEventData.map(
      (item, idx) => {
        const typeCode = item.annotation_type || "annotation_index";
        const itemColor =
          item.color ||
          (typeCode.includes("seizure")
            ? "#ef4444"
            : typeCode.includes("artifact")
              ? "#f59e0b"
              : typeCode.includes("spike")
                ? "#3b82f6"
                : "#8b5cf6");
        return {
          id: `index-${item.annotation_id || `${typeCode}-${idx}`}`,
          startTime: item.start_time,
          endTime: item.start_time + 0.25,
          type: typeCode,
          color: itemColor,
          label: getAnnotationTypeDisplayName(typeCode, annotationTypeCatalog),
        };
      },
    );

    // Merge full-recording markers with viewport details.
    // Deduplicate by start_time — prefer viewport entries (richer data).
    const mergedByKey = new Map<string, TimelineAnnotation>();
    base.forEach((ann) => mergedByKey.set(`${ann.startTime}-${ann.type}`, ann));
    eventIndexMarkers.forEach((marker) => {
      const key = `${marker.startTime}-${marker.type}`;
      if (!mergedByKey.has(key)) {
        mergedByKey.set(key, marker);
      }
    });

    return [
      ...Array.from(mergedByKey.values()),
      ...trend,
      ...segmentBoundaries,
      ...continuityWarnings,
    ];
  }, [
    playbackManifest?.segments,
    renderAnnotations,
    resolveAnnotationCategory,
    resolveAnnotationDisplayColor,
    trendMarkers,
    eventIndexRaw,
    recordingDuration,
  ]);

  // Full-recording annotation list items for the Annotation List dock panel.
  // Derived from the enriched event index which has all annotations across
  // the entire recording, merged with detailed data from renderAnnotations.
  const annotationListItems = useMemo<AnnotationListItem[]>(() => {
    const seenIds = new Set<string>();
    const items: AnnotationListItem[] = [];

    // Process viewport annotations first (richer data — has review status, label)
    renderAnnotations.forEach((ann) => {
      if (
        !isTimelineTimeWithinRecording(
          ann.start_time,
          Math.max(
            recordingDurationRef.current,
            recordingMetaDurationRef.current,
            recordingDuration,
          ),
        )
      ) {
        return;
      }
      const id = ann.annotation_id || "";
      if (id && seenIds.has(id)) return;
      if (id) seenIds.add(id);
      const duration = Math.max(
        0.25,
        (ann.end_time ?? ann.start_time) - ann.start_time,
      );
      items.push({
        id: id || `render-${ann.start_time}-${ann.annotation_type}`,
        annotationId: ann.annotation_id || null,
        startTime: ann.start_time,
        endTime: ann.start_time + duration,
        typeCode: ann.annotation_type || "annotation",
        color: ann.color || "#8b5cf6",
        label: ann.label || ann.annotation_type || "Annotation",
        isReviewed: Boolean(ann.is_reviewed),
        recordingId: ann.recording_id,
      });
    });

    // Add remaining annotations from eventIndex (full-recording coverage)
    eventIndexRaw.forEach((item) => {
      if (
        !isTimelineTimeWithinRecording(
          item.start_time,
          Math.max(
            recordingDurationRef.current,
            recordingMetaDurationRef.current,
            recordingDuration,
          ),
        )
      ) {
        return;
      }
      const id = item.annotation_id || "";
      if (id && seenIds.has(id)) return;
      if (id) seenIds.add(id);
      const typeCode = item.annotation_type || "annotation_index";
      const itemColor = item.color || "#8b5cf6";
      items.push({
        id: `idx-${item.annotation_id || `${typeCode}-${item.start_time}`}`,
        annotationId: item.annotation_id || null,
        startTime: item.start_time,
        endTime: item.start_time + 0.25,
        typeCode,
        color: itemColor,
        label: getAnnotationTypeDisplayName(typeCode, annotationTypeCatalog),
        isReviewed: false,
      });
    });

    return items.sort((a, b) => a.startTime - b.startTime);
  }, [renderAnnotations, eventIndexRaw, recordingDuration]);

  const dockDerivedEvents = useMemo(() => {
    // S3: never invent detector confidence from annotation type strings.
    // Triage candidates come from persisted clinical events, optional local
    // detector runs, or hardened detector-run import — not human annotations.
    return {
      spikes: [] as DetectedSpike[],
      seizures: [] as SeizureEvent[],
    };
  }, []);

  const liveProjectedTopography = useMemo<LiveTopographyPreviewModel>(() => {
    const activeRecordingId = (currentRecordingId || "").trim();
    if (!activeRecordingId) {
      return createEmptyLiveTopographyPreview(
        "Select a recording",
        "Choose a recording to populate the live scalp preview.",
      );
    }

    const loadedChannelCount = loadedChannelsRef.current.length;
    // Keep sampling while Live Scalp is detached — otherwise collapsing the
    // main analysis dock (or switching to Catalog/Clinical) blanks the pop-out.
    const liveScalpDetached = poppedOutPanels.has("live_scalp");
    if (dockRightCollapsed && !liveScalpDetached) {
      return createEmptyLiveTopographyPreview(
        "Live preview paused",
        "Expand the analysis dock / Scalp panel to resume live topography sampling.",
        loadedChannelCount,
      );
    }

    if (
      shouldSuspendEEGViewerLiveTopographySampling({
        isPlaying,
        playbackSpeed,
        dockCollapsed: dockRightCollapsed && !liveScalpDetached,
      })
    ) {
      return createEmptyLiveTopographyPreview(
        `Live preview paused at ${playbackSpeed}x`,
        "High-speed playback suspends scalp preview sampling until playback settles.",
        loadedChannelCount,
      );
    }

    if (liveTopographyBufferRecordingIdRef.current !== activeRecordingId) {
      return createEmptyLiveTopographyPreview(
        "Waiting for waveform buffer",
        "The live scalp preview appears once the current recording window is loaded.",
      );
    }

    const channels = loadedChannelsRef.current;
    const bufferWindowStart = rendererLoadedWindowStartRef.current;
    const bufferWindowEnd = rendererLoadedBufferEndRef.current;

    if (!channels.length || bufferWindowStart === null) {
      return createEmptyLiveTopographyPreview(
        "Waiting for waveform buffer",
        "The live scalp preview appears once the current recording window is loaded.",
      );
    }

    const sampleTime =
      bufferWindowEnd !== null
        ? Math.min(Math.max(currentTime, bufferWindowStart), bufferWindowEnd)
        : Math.max(currentTime, bufferWindowStart);

    return buildLiveTopographyPreviewFromChannels({
      channels,
      sampleTimeSeconds: sampleTime,
      bufferStartSeconds: bufferWindowStart,
      bufferEndSeconds: bufferWindowEnd,
      statusPrefix: "Instant",
    });
  }, [
    currentRecordingId,
    currentTime,
    dockRightCollapsed,
    isPlaying,
    liveTopographyBufferVersion,
    playbackSpeed,
    poppedOutPanels,
  ]);

  const handleLiveScalpElectrodeFocus = useCallback(
    (label: string) => {
      const trimmed = label.trim();
      if (!trimmed) {
        return;
      }
      const montageLabels = montageChannelDetails.map(
        (channel) => channel.label,
      );
      const resolved = resolveLiveScalpElectrodeToChannelLabels(
        trimmed,
        montageLabels,
      );
      const primary = resolved[0] ?? trimmed;
      setLockedTraceFocusLabel(primary);
      setSelectedTraceChannels(
        resolved.length > 0 ? resolved : [trimmed],
      );
    },
    [
      montageChannelDetails,
      setLockedTraceFocusLabel,
      setSelectedTraceChannels,
    ],
  );

  const handleLiveScalpFieldEventHint = useCallback(
    (hint: {
      startSeconds: number;
      endSeconds: number;
      label: string;
      source?: "triage" | "catalog" | "spike" | "manual";
    }) => {
      setLiveScalpFieldEventHint(hint);
    },
    [],
  );

  const handleLiveScalpClearFieldEventHint = useCallback(() => {
    setLiveScalpFieldEventHint(null);
  }, []);

  const orderedDockPanels = useMemo(
    () =>
      dockPanelOrder
        .filter((panelKey) => isActiveDockPanelKey(panelKey))
        .filter((panelKey) =>
          Object.prototype.hasOwnProperty.call(dockPanelVisibility, panelKey),
        ),
    [dockPanelOrder, dockPanelVisibility],
  );
  const visibleOrderedDockPanels = useMemo(
    () => orderedDockPanels.filter((panelKey) => dockPanelVisibility[panelKey]),
    [dockPanelVisibility, orderedDockPanels],
  );
  const advancedDockBadChannels = useMemo(
    () =>
      Array.from(
        new Set([
          ...Array.from(activeBadChannelMap.keys()),
          ...Array.from(hiddenChannels || []),
        ]),
      ),
    [activeBadChannelMap, hiddenChannels],
  );
  const [advancedDockPlaybackTimes, setAdvancedDockPlaybackTimes] = useState<
    Partial<Record<DockPanelKey, number>>
  >({});
  const dockPanelRefreshVisibilityRevision = useMemo(() => {
    return DOCK_PANEL_PLAYBACK_REFRESH_PANELS.map((panelKey) =>
      isDockPanelVisible(panelKey) ? "1" : "0",
    ).join("");
  }, [
    dockBottomActiveTab,
    dockBottomCollapsed,
    dockPanelVisibility,
    dockRightCollapsed,
    isDockPanelVisible,
    poppedOutPanels,
  ]);
  useDockPanelPlaybackRefresh({
    isPlaybackRunning,
    isPanelVisible: isDockPanelVisible,
    readPlayheadSeconds: () => currentTimeRef.current,
    onPlaybackTimesChange: setAdvancedDockPlaybackTimes,
    visibilityRevision: dockPanelRefreshVisibilityRevision,
  });
  useEffect(() => {
    if (!isPlaybackRunning) {
      setAdvancedDockPlaybackTimes({});
    }
  }, [isPlaybackRunning]);
  const getAdvancedDockCurrentTime = useCallback(
    (panelKey: DockPanelKey) =>
      isPlaybackRunning
        ? (advancedDockPlaybackTimes[panelKey] ??
          advancedDockPlaybackTimes[DOCK_PANEL_PLAYBACK_REFRESH_PANELS[0]] ??
          currentTimeRef.current)
        : currentTime,
    [advancedDockPlaybackTimes, currentTime, isPlaybackRunning],
  );
  const rightDockPanelKey =
    visibleOrderedDockPanels[0] || orderedDockPanels[0] || null;
  const rightDockPanelVisible = rightDockPanelKey
    ? dockPanelVisibility[rightDockPanelKey]
    : false;
  const bottomDockPanelKeys = useMemo(
    () => visibleOrderedDockPanels.slice(1),
    [visibleOrderedDockPanels],
  );
  const analysisDockRailPanels = useMemo(
    () =>
      ANALYSIS_DOCK_RAIL_PANEL_KEYS.filter(
        (panelKey) =>
          dockPanelVisibility[panelKey] &&
          visibleOrderedDockPanels.includes(panelKey),
      ),
    [dockPanelVisibility, visibleOrderedDockPanels],
  );
  const hiddenDockPanelCount =
    orderedDockPanels.length - visibleOrderedDockPanels.length;
  const rightDockSummary = rightDockPanelVisible && rightDockPanelKey
    ? [
        getDockPanelLabel(rightDockPanelKey, {
          compact: eventsTrendsDockPlacement === "right",
        }),
        bottomDockPanelKeys.length > 0
          ? `${bottomDockPanelKeys.length} lower`
          : null,
        hiddenDockPanelCount > 0 ? `${hiddenDockPanelCount} hidden` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "No panels enabled";

  // Effective active tab: use persisted selection if still visible, otherwise first visible tab
  const effectiveBottomActiveTab: DockPanelKey | null =
    dockBottomActiveTab && bottomDockPanelKeys.includes(dockBottomActiveTab)
      ? dockBottomActiveTab
      : (bottomDockPanelKeys[0] ?? null);

  useEffect(() => {
    // Plotly resize handler listens to window resize; debounce so opening
    // multiple dock panels does not stampede layout work on the same frame.
    const nextSignal = [
      clinicalAnnotationPanelVisible ? 1 : 0,
      annotationPanelWidthPx,
      dockBottomCollapsed ? 1 : 0,
      dockBottomHeightPx,
      dockRightCollapsed ? 1 : 0,
      dockRightWidthPx,
    ].join(":");
    if (lastDockResizeSignalRef.current === nextSignal) {
      return;
    }
    lastDockResizeSignalRef.current = nextSignal;

    if (dockResizeDebounceRef.current !== null) {
      clearTimeout(dockResizeDebounceRef.current);
    }
    dockResizeDebounceRef.current = setTimeout(() => {
      dockResizeDebounceRef.current = null;
      window.dispatchEvent(new Event("resize"));
    }, 250);

    return () => {
      if (dockResizeDebounceRef.current !== null) {
        clearTimeout(dockResizeDebounceRef.current);
        dockResizeDebounceRef.current = null;
      }
    };
  }, [
    annotationPanelWidthPx,
    dockBottomCollapsed,
    dockBottomHeightPx,
    dockRightCollapsed,
    dockRightWidthPx,
    clinicalAnnotationPanelVisible,
  ]);

  const renderDockPanel = useCallback(
    (panelKey: DockPanelKey) => (
      <Suspense fallback={null}>
        <EEGAdvancedDockPanel
          panelKey={panelKey}
          annotationListItems={annotationListItems}
          annotationTypeCatalog={annotationTypeCatalog}
          eventIndexLength={eventIndex.length}
          onSeek={handleTimelineSeek}
          spikes={dockDerivedEvents.spikes}
          seizures={dockDerivedEvents.seizures}
          annotations={renderAnnotations}
          channelNames={eegData?.channels ?? EMPTY_CHANNEL_NAMES}
          onEventClick={handleEventDetectionClick}
          trendMarkers={trendMarkers}
          selectedTrendProfileName={selectedTrendProfile?.name || "none"}
          realtimeTrendSeriesCount={realtimeTrendSeries.length}
          hourlyTrendSeriesCount={hourlyTrendSeries.length}
          trendLookbackSeconds={trendLookbackSeconds}
          trendLookaheadSeconds={trendLookaheadSeconds}
          eventTrendCorrelation={eventTrendCorrelation}
          onExportTrendCorrelationSummary={exportTrendCorrelationSummary}
          trendCorrelationChartPoints={trendCorrelationChartPoints}
          currentTime={getAdvancedDockCurrentTime(panelKey)}
          timelineRecordingDuration={timelineRecordingDuration}
          activeTrendPointId={activeTrendPoint?.id ?? null}
          timeScaleSeconds={currentConfig?.time_scale || 10}
          selectedTraceChannels={selectedTraceChannels}
          channelSeries={loadedChannelsRef.current}
          samplingRate={eegData?.sampling_rate || 256}
          spectrogramConfig={spectrogramConfig}
          onSpectrogramConfigChange={setSpectrogramConfig}
          recordingId={currentRecordingId || ""}
          pyramidLevel={resolutionLevel}
          studyId={studyId}
          montageName={currentMontage?.montage_name ?? null}
          highPassHz={currentConfig?.high_pass_filter ?? null}
          lowPassHz={currentConfig?.low_pass_filter ?? null}
          notch50Hz={currentConfig?.notch_filter_50hz ?? false}
          notch60Hz={currentConfig?.notch_filter_60hz ?? false}
          badChannels={advancedDockBadChannels}
          reviewerId={user?.id || null}
          liveScalpPreview={liveProjectedTopography}
          liveScalpFieldEventHint={liveScalpFieldEventHint}
          onLiveScalpElectrodeFocus={handleLiveScalpElectrodeFocus}
          onLiveScalpFieldEventHint={handleLiveScalpFieldEventHint}
          onLiveScalpClearFieldEventHint={handleLiveScalpClearFieldEventHint}
        />
      </Suspense>
    ),
    [
      annotationListItems,
      annotationTypeCatalog,
      advancedDockBadChannels,
      currentConfig?.time_scale,
      currentConfig?.high_pass_filter,
      currentConfig?.low_pass_filter,
      currentConfig?.notch_filter_50hz,
      currentConfig?.notch_filter_60hz,
      currentMontage?.montage_name,
      currentRecordingId,
      getAdvancedDockCurrentTime,
      dockDerivedEvents.seizures,
      dockDerivedEvents.spikes,
      eventIndex.length,
      eventTrendCorrelation,
      exportTrendCorrelationSummary,
      eegData?.channels,
      eegData?.sampling_rate,
      handleEventDetectionClick,
      handleLiveScalpElectrodeFocus,
      handleLiveScalpFieldEventHint,
      handleLiveScalpClearFieldEventHint,
      handleTimelineSeek,
      hourlyTrendSeries.length,
      liveProjectedTopography,
      liveScalpFieldEventHint,
      renderAnnotations,
      resolutionLevel,
      selectedTrendProfile?.name,
      spectrogramConfig,
      studyId,
      timelineRecordingDuration,
      trendLookaheadSeconds,
      trendLookbackSeconds,
      trendMarkers,
      trendCorrelationChartPoints,
      activeTrendPoint?.id,
      selectedTraceChannels,
      user?.id,
    ],
  );

  // Save configuration mutation
  const saveConfigMutation = useMutation({
    mutationFn: async (config: Partial<EEGConfiguration>) => {
      // undefined config_id means the config has never been persisted — issue POST.
      // A real UUID means it already exists in the DB — issue PUT.
      // The UUID regex is kept as defence-in-depth to guard against any future
      // non-UUID strings leaking into config_id.
      const configId = currentConfig?.config_id;
      const isPersisted =
        !!configId &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          configId,
        );
      if (isPersisted) {
        return await apiClient.put(`/eeg-config/${configId}`, config);
      } else {
        // Merge currentConfig base fields so all required DB columns (e.g.
        // reference_type) are present even when `config` is a sparse partial.
        return await apiClient.post("/eeg-config", {
          reference_type: "common_average",
          ...currentConfig,
          ...config,
          // Ensure these fields are always set correctly for a new study config
          study_id: studyId,
          config_type: "study_specific",
          config_name: `Study Config - ${new Date().toLocaleString()}`,
          // Never forward an existing config_id into a POST (would conflict)
          config_id: undefined,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eeg-config"] });
    },
  });

  // Apply preset mutation
  const applyPresetMutation = useMutation({
    mutationFn: async (presetId: string) => {
      const response = await apiClient.post(`/eeg-presets/${presetId}/apply`, {
        studyId,
      });
      return response.data;
    },
    onSuccess: (data) => {
      setCurrentConfig(normalizeViewerConfig(data));
      queryClient.invalidateQueries({ queryKey: ["eeg-config"] });
    },
  });

  const saveDualMontagePreferenceMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.put(
        "/eeg/dual-montage-preference",
        dualMontagePreference,
      );
      return (response?.data?.data ??
        response?.data ??
        null) as Partial<DualMontagePreference> | null;
    },
    onSuccess: async (savedPreference) => {
      setDualMontageError(null);
      setDualMontageMessage("Dual-montage preferences saved.");
      if (savedPreference) {
        setDualMontagePreference((current) => ({
          ...current,
          left_montage_id: savedPreference.left_montage_id || null,
          right_montage_id: savedPreference.right_montage_id || null,
          sync_cursor: savedPreference.sync_cursor ?? true,
          sync_zoom: savedPreference.sync_zoom ?? true,
          sync_playback: savedPreference.sync_playback ?? true,
          diff_highlight: savedPreference.diff_highlight ?? false,
        }));
      }
      await refetchDualMontagePreference();
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setDualMontageMessage(null);
      setDualMontageError(normalized.message);
    },
  });

  const previewTrendProfileMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTrendProfileId) {
        throw new Error("Select a trend profile first.");
      }
      const sample = currentTrendSample || {
        dsa_low: currentConfig?.high_pass_filter ?? 0.5,
        dsa_high: currentConfig?.low_pass_filter ?? 70,
        csa_threshold: currentConfig?.sensitivity_value ?? 10,
        asymmetry_pct:
          activeTrendPoint?.asymmetryPct ??
          Math.min(
            100,
            (selectedTraceChannels.length || selectedChannels.length) * 5,
          ),
      };
      const response = await apiClient.post(
        `/eeg/trend-threshold-profiles/${selectedTrendProfileId}/preview`,
        {
          sample,
        },
      );
      return response?.data?.data ?? response?.data;
    },
    onSuccess: (data) => {
      const profileName =
        typeof data?.profile_name === "string" ? data.profile_name : "Selected";
      const alertCount =
        typeof data?.alert_count === "number" ? data.alert_count : 0;
      setTrendPreviewError(null);
      setTrendPreviewMessage(`${profileName}: ${alertCount} alert(s) matched.`);
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setTrendPreviewMessage(null);
      setTrendPreviewError(normalized.message);
    },
  });

  const activeTrendEvaluationMutation = useMutation({
    mutationFn: async (sample: Record<string, number>) => {
      if (!selectedTrendProfileId) {
        throw new Error("Select a trend profile first.");
      }
      const response = await apiClient.post(
        `/eeg/trend-threshold-profiles/${selectedTrendProfileId}/preview`,
        { sample },
      );
      return response?.data?.data ?? response?.data;
    },
    onSuccess: (data) => {
      const profileName =
        typeof data?.profile_name === "string" ? data.profile_name : "Selected";
      const alertCount =
        typeof data?.alert_count === "number" ? data.alert_count : 0;
      const sampleTime = activeTrendPoint?.timeSeconds ?? currentTime;
      setTrendEvalError(null);
      setTrendEvalStatus(
        `${profileName} live @ ${sampleTime.toFixed(1)}s: ${alertCount} alert(s).`,
      );
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setTrendEvalStatus(null);
      setTrendEvalError(normalized.message);
    },
  });

  const saveTrendWindowMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTrendProfileId || !selectedTrendProfile) {
        throw new Error("Select a trend profile first.");
      }
      const mergedThresholds = {
        ...(selectedTrendProfile.thresholds_json || {}),
        lookback_seconds: Math.max(60, Math.floor(trendLookbackSeconds)),
        lookahead_seconds: Math.max(60, Math.floor(trendLookaheadSeconds)),
        evaluation_interval_seconds: Math.max(
          5,
          Math.floor(trendEvalIntervalSeconds),
        ),
      };
      const response = await apiClient.put(
        `/eeg/trend-threshold-profiles/${selectedTrendProfileId}`,
        {
          thresholds_json: mergedThresholds,
        },
      );
      return response?.data?.data ?? response?.data;
    },
    onSuccess: async () => {
      setTrendPreviewError(null);
      setTrendPreviewMessage(
        "Saved lookback/lookahead settings to trend profile.",
      );
      await refetchTrendProfiles();
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setTrendPreviewMessage(null);
      setTrendPreviewError(normalized.message);
    },
  });

  useEffect(() => {
    if (!trendEvalActive) return;
    if (!selectedTrendProfileId) {
      setTrendEvalError(
        "Select a trend profile before enabling active evaluation.",
      );
      return;
    }
    if (!currentTrendSample) {
      setTrendEvalError("No trend sample available yet for active evaluation.");
      return;
    }

    setTrendEvalError(null);
    const evaluateNow = () => {
      if (activeTrendEvaluationMutation.isPending) return;
      activeTrendEvaluationMutation.mutate(currentTrendSample);
    };

    evaluateNow();
    const intervalMs = Math.max(5, trendEvalIntervalSeconds) * 1000;
    const timer = window.setInterval(evaluateNow, intervalMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [
    activeTrendEvaluationMutation,
    currentTrendSample,
    selectedTrendProfileId,
    trendEvalActive,
    trendEvalIntervalSeconds,
  ]);

  // Detect when canvas is mounted and set dimensions.
  // We must poll on every render until the canvas appears in the DOM — the
  // component may render several times before the canvas element exists.
  // Once canvasMounted is true the guard at the top makes each call O(1).
  useEffect(() => {
    if (canvasMounted) return; // already done — this path is O(1) during playback
    const canvas = canvasRef.current;
    logger.debug("[Canvas Mount Check]", {
      hasCanvas: !!canvas,
      canvasMounted,
    });
    if (canvas) {
      // Set canvas dimensions to match container
      const container = canvas.parentElement;
      if (container) {
        const width = container.clientWidth;
        const height = container.clientHeight;
        logger.debug("[Canvas Dimensions]", { width, height });
        canvas.width = width;
        canvas.height = height;
        canvasHeightRef.current = height;
      }
      setCanvasMounted(true);
      logger.debug("[Canvas] Mounted and ready");
    }
  }); // No dep array intentional: polls until canvas appears in DOM; guard makes it O(1) after

  // Add resize observer to update canvas size when container changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          // Always use the live ref — React may have replaced the element
          const liveCanvas = canvasRef.current;
          if (!liveCanvas) return;
          // Guard: setting canvas.width/height resets the bitmap even when the
          // value is unchanged, clearing all content and forcing a full blit-
          // cache invalidation on the next RAF frame.  React re-renders (e.g.
          // setDataWindowStart every 10s) trigger layout recalc → ResizeObserver
          // fires → unconditional canvas.width = width caused a visual flash
          // (full-canvas clear + full redraw) at every data-window boundary.
          const dimensionsChanged =
            Math.round(liveCanvas.width) !== Math.round(width) ||
            Math.round(liveCanvas.height) !== Math.round(height);
          if (!dimensionsChanged) return;
          liveCanvas.width = width;
          liveCanvas.height = height;
          canvasHeightRef.current = height;
          logger.debug("[Canvas Resize]", { width, height });
          // Blit cache was built at the old canvas width — explicitly invalidate
          // it so the renderer does a full redraw on the new dimensions rather
          // than relying on the blit-size mismatch check inside drawWaveforms().
          // Belt-and-suspenders: the size mismatch would already trigger a full
          // redraw, but this makes the intent explicit and ensures _blitDirty is
          // set before render() is called.
          rendererRef.current?.invalidateBlitCache();
          // Push CSS dimensions into renderer cache so resize() never reads
          // canvas.clientWidth/clientHeight in the hot RAF path.
          rendererRef.current?.notifyResize(width, height);
          // Trigger re-render with new dimensions (skip during playback — rAF tick handles it)
          if (rendererRef.current) {
            syncRendererCanvas();
            if (!isPlayingRef.current) {
              rendererRef.current.render(
                mapAnnotationsForRenderer(renderAnnotationsRef.current),
              );
            }
            syncWaveformPlotLeft();
          }
        }
      }
    });

    resizeObserver.observe(canvas.parentElement!);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    canvasMounted,
    mapAnnotationsForRenderer,
    syncRendererCanvas,
    syncWaveformPlotLeft,
    // renderAnnotations intentionally read via ref — avoids recreating the
    // observer on every annotation change; ref is always current at resize time
  ]);
  // Initialize renderer once when canvas is ready (only depends on canvas mount, NOT config)
  // Config changes are handled separately via updateConfig() to avoid destroying loaded data
  useEffect(() => {
    logger.debug("Renderer init useEffect triggered", {
      canvasMounted,
      hasCanvas: !!canvasRef.current,
      hasConfig: !!currentConfig,
    });

    if (!canvasMounted || !canvasRef.current || !currentConfig) return;

    // Only create renderer if one doesn't already exist
    if (rendererRef.current) {
      logger.debug("Renderer already exists, skipping recreation");
      return;
    }

    const canvas = canvasRef.current;
    const renderConfig: RenderConfig = {
      sensitivity: currentConfig?.sensitivity_value || 10,
      pixelsPerMm: effectivePixelsPerMm,
      timeScale: currentConfig?.time_scale || 10,
      highPassFilter: currentConfig?.high_pass_filter || 0.5,
      lowPassFilter: currentConfig?.low_pass_filter || 70,
      notchFilter: currentConfig?.notch_filter_60hz || false,
      notch50Hz: currentConfig?.notch_filter_50hz ?? false,
      removeDcOffset: currentConfig?.remove_dc_offset ?? false,
      polarity: currentConfig?.polarity ?? "negative-up",
      channelColorMode: currentConfig?.channel_color_mode ?? "uniform",
      channelGainOverrides,
      channelScrollOffset,
      visibleChannelCount,
      playbackSpeed: currentConfig?.playback_speed || 1,
      playbackTransportMode,
      showGrid,
      gridStyle: clinicalGridMode ? "clinical" : "standard",
      gridDensity: profileGridDensity,
      showTimeline: showTimeline && !showUnifiedNavigationTimeline,
      timelinePosition: "bottom",
      backgroundColor:
        RENDER_THEME_MAP[workspaceTheme]?.backgroundColor ?? "#000000",
      waveformColor: RENDER_THEME_MAP[workspaceTheme]?.waveformColor ?? "#e2e8f0",
      gridColor: withAlphaHex(
        RENDER_THEME_MAP[workspaceTheme]?.gridColorBase ?? "#5b6472",
        profileGridDensity === "low"
          ? "66"
          : profileGridDensity === "high"
            ? "bb"
            : "88",
      ),
      waveformThickness: profileWaveformThickness,
      channelSpacing: profileChannelSpacing,
      labelFontSize: profileLabelFontSize,
      channelPositionOffsets: effectiveChannelPositionOffsets,
      showChannelGroupLabels: true, // EEG-UX-006: on by default
      showScaleMarkers,
      physicalTimebaseTrusted:
        calibrationOverallPass === true && !calibrationStale,
      amplitudeScalingMode: clinicalGridMode ? "fixed" : "auto",
      persistentTimestamp: waveformTextOverlays.persistentTimestamp,
      recordingStartIso,
      displayMode: waveformDisplayMode,
      allowTraceOverflow,
      rendererMode: rendererMode.mode,
      annotationOpacity: annotationOverlayOpacity,
      // Sprint 4C-004: waveform-only first paint until deferred chrome unlocks HUD.
      minimalStartupPaint: !viewerDeferredChromeReady,
    };

    rendererRef.current = new EEGWaveformRenderer(canvas, renderConfig);
    rendererRef.current.setPagePlayheadOffsetSeconds(
      pagePlayheadOffsetRef.current,
    );
    // Seed the CSS dimension cache immediately so the first RAF frame doesn't
    // fall back to the layout-triggering clientWidth/clientHeight read.
    if (canvas.parentElement) {
      const { width, height } = canvas.parentElement.getBoundingClientRect();
      if (width > 0 && height > 0) {
        rendererRef.current.notifyResize(width, height);
      }
    }
    setRendererReady(true);
    logger.debug("EEGWaveformRenderer initialized and ready");
    // Align Study View / macro timeline with the dedicated label column.
    queueMicrotask(() => syncWaveformPlotLeft());
  }, [
    canvasMounted,
    currentConfig,
    channelGainOverrides,
    channelScrollOffset,
    effectiveChannelPositionOffsets,
    visibleChannelCount,
    effectivePixelsPerMm,
    showGrid,
    showScaleMarkers,
    waveformTextOverlays.persistentTimestamp,
    liveStatusRailActive,
    clinicalGridMode,
    playbackTransportMode,
    waveformDisplayMode,
    allowTraceOverflow,
    profileChannelSpacing,
    profileLabelFontSize,
    profileWaveformThickness,
    recordingStartIso,
    syncWaveformPlotLeft,
    rendererMode.mode,
    viewerDeferredChromeReady,
    calibrationOverallPass,
    calibrationStale,
    workspaceTheme,
  ]);

  useEffect(() => {
    return () => {
      rendererRef.current?.dispose();
      rendererRef.current = null;
      setRendererReady(false);
    };
  }, []);

  useEffect(() => {
    syncWaveformPlotLeft();
  }, [
    syncWaveformPlotLeft,
    currentConfig?.montage_id,
    loadedRecordingChannelNames,
    profileLabelFontSize,
    waveformDisplayMode,
    visibleChannelCount,
  ]);

  // Sprint 4C-004: unlock grid/labels/HUD after first playable + deferred chrome.
  useEffect(() => {
    if (!rendererReady || !rendererRef.current) return;
    syncRendererCanvas();
    rendererRef.current.updateConfig({
      minimalStartupPaint: !viewerDeferredChromeReady,
    });
    if (viewerDeferredChromeReady && !isPlayingRef.current) {
      measureEegStartupBlock("first-full-canvas-render", () => {
        rendererRef.current?.render([]);
      });
    }
  }, [rendererReady, syncRendererCanvas, viewerDeferredChromeReady]);

  useEffect(() => {
    document.body.setAttribute("data-eeg-workspace-theme", workspaceTheme);
    document.documentElement.setAttribute(
      "data-eeg-workspace-theme",
      workspaceTheme,
    );
    return () => {
      document.body.removeAttribute("data-eeg-workspace-theme");
      document.documentElement.removeAttribute("data-eeg-workspace-theme");
    };
  }, [workspaceTheme]);

  // CP-S17-002: Apply accessibility high-contrast preference to renderer theme
  useEffect(() => {
    if (!rendererReady || !rendererRef.current) return;
    const themeConfig = RENDER_THEME_MAP[workspaceTheme];
    const gridAlpha =
      profileGridDensity === "low"
        ? "66"
        : profileGridDensity === "high"
          ? "bb"
          : "88";
    syncRendererCanvas();
    rendererRef.current.updateConfig({
      backgroundColor: themeConfig.backgroundColor,
      waveformColor: themeConfig.waveformColor,
      gridColor: withAlphaHex(themeConfig.gridColorBase, gridAlpha),
      gridDensity: profileGridDensity,
      gridStyle: clinicalGridMode ? "clinical" : "standard",
      waveformThickness: profileWaveformThickness,
      channelSpacing: profileChannelSpacing,
      displayMode: waveformDisplayMode,
      labelFontSize: profileLabelFontSize,
      channelPositionOffsets: effectiveChannelPositionOffsets,
    });
    // When not playing the RAF loop is idle — fire a one-shot render so the
    // theme change takes effect immediately instead of waiting for user interaction.
    if (!isPlayingRef.current) {
      rendererRef.current.render(
        mapAnnotationsForRenderer(renderAnnotationsRef.current),
      );
    }
  }, [
    effectiveChannelPositionOffsets,
    profileChannelSpacing,
    profileGridDensity,
    profileLabelFontSize,
    workspaceTheme,
    profileWaveformThickness,
    clinicalGridMode,
    rendererReady,
    syncRendererCanvas,
    waveformDisplayMode,
  ]);

  useEffect(() => {
    const names = Array.isArray(eegData?.channels) ? eegData.channels : [];
    setLoadedRecordingChannelNames((previous) => {
      if (
        previous.length === names.length &&
        previous.every((name, index) => name === names[index])
      ) {
        return previous;
      }
      return names;
    });
  }, [eegData?.channels]);

  // Load EEG data and montage when data changes
  useEffect(() => {
    logger.debug("Data loading useEffect triggered", {
      rendererReady,
      isLoading: isLoadingEegData,
      hasData: !!eegData,
      hasChannels: !!(eegData && eegData.channels),
      hasSamples: !!(eegData && eegData.columnArrays?.length),
    });

    // Don't block on loading - keep showing existing data while new data loads
    if (!rendererReady || !rendererRef.current) return;

    // Skip if still loading and no data yet
    if (isLoadingEegData && !eegData) return;

    // ── keepPreviousData stale-data guard ─────────────────────────────────────
    // React Query's `placeholderData: keepPreviousData` keeps the PREVIOUS
    // window's data visible while the new window is fetching.  When the eager-
    // prefetch chain advances `dataWindowStart` quickly (via
    // startDataWindowTransition), the transition may not commit to React state
    // before the new tile's fetch resolves.  Without this guard the effect fires
    // with:
    //   eegData  = ws=60 data   (just resolved)
    //   dataWindowStart = 0     (stale — transition not yet committed)
    // → bufferWindowStart=0 hits the "already loaded at 0" guard → tile dropped.
    //
    // When `isPlaceholderData=true`, React Query is actively serving previous-
    // window data while a new fetch is in flight.  Return early; the effect will
    // re-run in two stages:
    //   Stage 1: `dataWindowStart` transition commits → isPlaceholderData still
    //            true → still return early (eegData is still stale)
    //   Stage 2: new tile arrives → eegData resolves → isPlaceholderData=false →
    //            effect runs with both dataWindowStart AND eegData aligned → ✓
    if (isEegDataPlaceholder) return;

    const expectedSpeed = currentConfig?.playback_speed ?? 1;
    const expectedTimeScale = currentConfig?.time_scale ?? 10;
    const expectedTransportMode = playbackTransportMode;
    const expectedRequestedLevel = shouldUseClinicalRawResolution(
      expectedSpeed,
      expectedTimeScale,
      expectedTransportMode,
    )
      ? 0
      : autoResolution
        ? getAutoResolutionLevel(expectedSpeed, expectedTransportMode)
        : resolutionLevel;
    const expectedWindow = alignTileWindowToBackend({
      windowStartSec: dataWindowStart,
      bufferSizeSec: getEffectiveBufferSize(
        expectedSpeed,
        expectedTimeScale,
        expectedTransportMode,
      ),
      speed: expectedSpeed,
      transportMode: expectedTransportMode,
      recordingDurationSec:
        recordingDurationRef.current > 0
          ? recordingDurationRef.current
          : recordingDuration > 0
            ? recordingDuration
            : undefined,
    });
    const tileRequestContext = eegData?.requestContext;
    const loadedTileRequestContext: NonNullable<
      DecodedArrowTile["requestContext"]
    > = tileRequestContext ?? {
      recordingId: currentRecordingId ?? "",
      transportMode: expectedTransportMode,
      speed: expectedSpeed,
      timeScale: expectedTimeScale,
      windowStart: expectedWindow.startSec,
      windowEnd: expectedWindow.endSec,
      requestedLevel: expectedRequestedLevel,
      actualLevel: expectedRequestedLevel,
    };
    if (
      isEEGViewerTilePayloadStale(tileRequestContext, {
        recordingId: currentRecordingId ?? "",
        transportMode: expectedTransportMode,
        speed: expectedSpeed,
        timeScale: expectedTimeScale,
        windowStart: expectedWindow.startSec,
      })
    ) {
      recordStaleTileDrop();
      logger.debug(
        "[EEGViewer] Ignoring stale tile payload for outdated playback context",
        {
          recordingId: currentRecordingId,
          dataWindowStart,
          expectedTransportMode,
          expectedSpeed,
          expectedTimeScale,
          payload: tileRequestContext,
        },
      );
      return;
    }

    const bufferWindowStart = loadedTileRequestContext.windowStart;
    if (
      shouldDiscardEEGViewerPausedPlayHoldBuffer({
        isPlaying: isPlayingRef.current,
        pausedHoldTransportMode:
          pausedHighSpeedInspectHoldRef.current?.transportMode,
        bufferWindowStart,
        loadedWindowStart: rendererLoadedWindowStartRef.current,
        incomingContext: loadedTileRequestContext,
      })
    ) {
      logger.debug(
        "[EEGViewer] Discarding late play buffer during paused play hold",
        {
          bufferWindowStart,
          loadedWindowStart: rendererLoadedWindowStartRef.current,
          pausedHoldTransportMode:
            pausedHighSpeedInspectHoldRef.current?.transportMode ?? null,
          currentTime: currentTimeRef.current,
          incomingContext: loadedTileRequestContext,
        },
      );
      recordPauseBoundaryEvent("discard-late-play-buffer", {
        bufferWindowStart,
        loadedWindowStart: rendererLoadedWindowStartRef.current,
        pendingTransportMode: loadedTileRequestContext.transportMode,
      });
      return;
    }

    if (eegData?.transferMetrics) {
      setDataTransferMetrics(eegData.transferMetrics);
    }

    // Transform and load EEG data
    if (eegData && eegData.channels && eegData.columnArrays?.length) {
      logger.debug("Transforming EEG data", {
        channelsCount: eegData.channels.length,
        numSamples: eegData.numSamples,
        firstChannelLength: eegData.columnArrays[0]?.length,
      });

      // columnArrays is already column-major Float32Array[] delivered zero-copy
      // from the decode worker as Transferables — no transpose needed here.
      const channelArrays = eegData.columnArrays;
      const numChannels = eegData.channels.length;

      const channelData: EEGChannel[] =
        eegData.preparedChannels?.length === numChannels
          ? eegData.preparedChannels
          : eegData.channels.map(
              (channelName: string, channelIndex: number) => ({
                name: channelName,
                data: channelArrays[channelIndex],
                sampleRate: eegData.sampling_rate || 256,
                unit: "μV",
                // Pass true min/max per bin from pyramid so the renderer can draw
                // accurate amplitude envelopes at compressed speeds (levels > 0).
                // These are already Float32Arrays (transferred from worker).
                envelopeMin: eegData.envelopeMin?.[channelIndex],
                envelopeMax: eegData.envelopeMax?.[channelIndex],
              }),
            );

      logger.debug("[EEGViewer] Loading real EEG data:", {
        channels: channelData.length,
        firstChannelSamples: channelData[0]?.data.length,
        valueMin: channelArrays[0]?.[0] ?? 0,
        valueMax: channelArrays[0]?.[channelArrays[0].length - 1] ?? 0,
      });

      // Update rolling median peak amplitude for auto-sensitivity calibration.
      // channelPeaks was populated in the single-pass transpose above — no extra pass needed.
      if (
        numChannels > 0 &&
        typeof eegData.observedMedianPeakUv === "number" &&
        Number.isFinite(eegData.observedMedianPeakUv)
      ) {
        // Clamp to clinical EEG range: values above 150 μV are artefacts.
        const clampedPeak = Math.min(eegData.observedMedianPeakUv, 150);
        // Exponential moving average (α=0.2) for slow, stable convergence.
        const prev = observedPeakUvRef.current;
        observedPeakUvRef.current =
          prev === null ? clampedPeak : prev * 0.8 + clampedPeak * 0.2;
      }

      // ── Deferred buffer swap during active playback ─────────────────────────
      // When playing, the renderer already has correct data for the current
      // visible window. Loading the *next* buffer immediately (triggered by
      // setDataWindowStart advancing the query key) causes a 500-600ms main-
      // thread stall as loadChannels() iterates all channels×samples and
      // sets montage. Instead, park the prepared channelData in a ref and
      // let handlePlaybackTick swap it in on the RAF tick when the playhead
      // actually enters the new window — zero perceptible stutter.
      // Bug I fix: use the true Arrow-file data span (actualDataDuration) for
      // buffer-end tracking instead of eegData.duration (which is set to the
      // full requested window size by tile normalization).  Without this,
      // rendererLoadedBufferEndRef is inflated by up to 2× the real data
      // extent, the renderTime clamp never triggers until the playhead is far
      // past the real data boundary, and the renderer draws blank canvas for
      // up to 15–20 real seconds per buffer window at 16x speed.
      //
      // Priority order:
      //  1. actualDataDuration — set by normalization when tile timing is
      //     suspicious; represents times[last]-times[0] from Arrow file.
      //  2. sample-count / sampling_rate — always reliable.
      //  3. eegData.duration — window size, used only as last resort.
      const effectiveDataDuration: number | null =
        eegData.actualDataDuration != null && eegData.actualDataDuration > 0
          ? eegData.actualDataDuration
          : eegData.sampling_rate && channelData[0]?.data.length
            ? channelData[0].data.length / eegData.sampling_rate
            : eegData.duration
              ? eegData.duration
              : null;

      if (isPlayingRef.current) {
        const sameLoadedWindow =
          bufferWindowStart === rendererLoadedWindowStartRef.current;
        const samePendingWindow =
          bufferWindowStart === pendingEegBufferRef.current?.windowStart;
        const loadedPlaybackContext = rendererLoadedPlaybackContextRef.current;
        const pendingPlaybackContext =
          pendingEegBufferRef.current?.requestContext ?? null;
        const contextChanged = (
          currentContext: NonNullable<
            DecodedArrowTile["requestContext"]
          > | null,
        ) =>
          !currentContext ||
          currentContext.transportMode !==
            loadedTileRequestContext.transportMode ||
          Math.abs(currentContext.speed - loadedTileRequestContext.speed) >
            0.001 ||
          Math.abs(
            currentContext.timeScale - loadedTileRequestContext.timeScale,
          ) > 0.001 ||
          currentContext.requestedLevel !==
            loadedTileRequestContext.requestedLevel ||
          currentContext.actualLevel !== loadedTileRequestContext.actualLevel;
        const loadedContextHandoff = contextChanged(loadedPlaybackContext);
        const loadedContextChanged = sameLoadedWindow && loadedContextHandoff;
        const pendingContextChanged =
          samePendingWindow && contextChanged(pendingPlaybackContext);
        const loadedDuration =
          sameLoadedWindow && rendererLoadedBufferEndRef.current !== null
            ? rendererLoadedBufferEndRef.current - bufferWindowStart
            : null;
        const durationChanged =
          sameLoadedWindow &&
          loadedDuration !== null &&
          effectiveDataDuration !== null &&
          Math.abs(effectiveDataDuration - loadedDuration) >
            Math.max(2, loadedDuration * 0.2);
        const loadedUsesEnvelope =
          sameLoadedWindow &&
          Boolean(
            loadedChannelsRef.current?.some(
              (channel) => channel.envelopeMin && channel.envelopeMax,
            ),
          );
        const nextUsesEnvelope = Boolean(
          channelData.some(
            (channel) => channel.envelopeMin && channel.envelopeMax,
          ),
        );
        const loadedSampleRate = sameLoadedWindow
          ? (loadedChannelsRef.current?.[0]?.sampleRate ?? null)
          : null;
        const nextSampleRate = channelData[0]?.sampleRate ?? null;
        const representationChanged =
          sameLoadedWindow &&
          ((loadedSampleRate !== null &&
            nextSampleRate !== null &&
            Math.abs(nextSampleRate - loadedSampleRate) >
              Math.max(1, loadedSampleRate * 0.05)) ||
            loadedUsesEnvelope !== nextUsesEnvelope);
        const shouldSkipSameWindowRefetch =
          (sameLoadedWindow || samePendingWindow) &&
          !loadedContextChanged &&
          !pendingContextChanged &&
          !durationChanged &&
          !representationChanged;

        // Guard against focus-refetch storms: React Query refetches active
        // queries on window-focus, returning the SAME data for windows already
        // loaded into (or already queued for) the renderer.  Without this guard
        // each refetch queues a new pending buffer with the same windowStart,
        // which the RAF tick flushes immediately via loadChannels() with new
        // Float32Array objects — clearing derivation and filter caches and
        // causing an 86-93ms cold-cache RAF violation for no visual reason.
        if (shouldSkipSameWindowRefetch) {
          // Bug fix: if the renderer already holds this window (loaded via the
          // not-playing path before isPlaying became true), a subsequent tile
          // query may return a better duration estimate (e.g. tile query returns
          // 600 s while the initial REST data reported 30 s).  Update
          // rendererLoadedBufferEndRef so the renderTime clamp in
          // handlePlaybackTick uses the actual data extent, not the stale
          // initial value.  Without this, bufferEnd would be stuck at the
          // initial duration (e.g. 30 s = 2×timeScale), clamping renderTime
          // at exactly timeScale for the entire first buffer window — showing
          // a frozen waveform for many seconds after pressing Play at 16x+.
          if (sameLoadedWindow) {
            if (effectiveDataDuration !== null) {
              rendererLoadedBufferEndRef.current = Math.max(
                rendererLoadedBufferEndRef.current ?? 0,
                bufferWindowStart + effectiveDataDuration,
              );
            }
          }
          return;
        }

        // Any accepted inspect -> play context handoff must publish the new
        // context immediately, even when the incoming play tile realigns to an
        // earlier windowStart than the currently loaded inspect window.
        // handlePlaybackTick checks context compatibility before it gets a
        // chance to flush pendingEegBufferRef, so keeping the old inspect
        // context here freezes playback at the inspect buffer edge while the
        // queued play buffer never gets applied.
        if (loadedContextHandoff) {
          rendererLoadedPlaybackContextRef.current = loadedTileRequestContext;
        }

        // Guard against stale out-of-order buffer arrivals causing backward
        // visual motion: when multiple windows in the SAME playback context
        // are in-flight (e.g. ws=10 and ws=20), the lower window can arrive
        // late after the higher one has already been queued or loaded.
        // Parking that stale ws=10 buffer over ws=20 would make
        // dataWindowStartRef.current jump backward on the next RAF flush,
        // shrinking bufferEnd and clamping renderTime to a lower value.
        //
        // Do not apply this monotonic-window rule across context changes.
        // Inspect -> play transitions legitimately realign the active tile a
        // few seconds earlier (for example inspect ws=64, play ws=60 at 8x),
        // and discarding that play tile leaves the renderer stuck on the old
        // inspect buffer while the transport clock keeps advancing.
        const loadedWS = rendererLoadedWindowStartRef.current ?? -1;
        const pendingWS = pendingEegBufferRef.current?.windowStart ?? -1;
        const { staleRelativeToLoaded, staleRelativeToPending } =
          resolveEEGViewerOutOfOrderBufferDiscard({
            bufferWindowStart,
            loadedWindowStart: loadedWS,
            pendingWindowStart: pendingWS,
            loadedContext: loadedPlaybackContext,
            pendingContext: pendingPlaybackContext,
            incomingContext: loadedTileRequestContext,
          });
        if (staleRelativeToLoaded || staleRelativeToPending) {
          logger.debug(
            "[EEGViewer] Discarding stale out-of-order buffer arrival",
            {
              bufferWindowStart,
              loadedWS,
              pendingWS,
              staleRelativeToLoaded,
              staleRelativeToPending,
            },
          );
          recordPauseBoundaryEvent("discard-out-of-order-buffer", {
            bufferWindowStart,
            loadedWindowStart: loadedWS,
            pendingWindowStart: pendingWS,
            staleRelativeToLoaded,
            staleRelativeToPending,
            pendingTransportMode: loadedTileRequestContext.transportMode,
          });
          return;
        }

        // Park the prepared buffer immediately so the RAF tick can flush it
        // as soon as the playhead enters the new window.
        const pendingDecodeTelemetry = eegData.decodeTelemetry ?? null;
        const pendingQueuedAtMs = emitPendingWindowDecodeReady({
          queueSource: "playing",
          windowStart: bufferWindowStart,
          requestContext: loadedTileRequestContext,
          decodeTelemetry: pendingDecodeTelemetry,
        });
        pendingEegBufferRef.current = {
          channelData,
          windowStart: bufferWindowStart,
          samplingRate: eegData.sampling_rate || 256,
          duration: effectiveDataDuration,
          requestContext: loadedTileRequestContext,
          decodeTelemetry: pendingDecodeTelemetry,
          queuedAtMs: pendingQueuedAtMs,
        };
        recordPlaybackPerfEvent("pendingBufferEnqueue", {
          queueSource: "playing",
          pendingWindowStart: Number(bufferWindowStart.toFixed(3)),
          pendingWindowEnd: Number(
            loadedTileRequestContext.windowEnd.toFixed(3),
          ),
          queuedAtMs: Number(pendingQueuedAtMs.toFixed(1)),
          speed: loadedTileRequestContext.speed,
          transportMode: loadedTileRequestContext.transportMode,
        });
        recordPauseBoundaryEvent("queue-playing-pending-buffer", {
          bufferWindowStart,
          pendingTransportMode: loadedTileRequestContext.transportMode,
          pendingQueuedAtMs,
        });

        // Pre-warm the renderer's derivation and filter caches for the
        // incoming buffer using the async batched variant.
        // preWarmForChannelsAsync yields between small batches and keeps the
        // HP-tail preparation inside those yielded chunks, so the React passive
        // effect that queues the warm work stays short and the warm path stops
        // front-loading a large synchronous block before the first yield.
        //
        // NOTE: we capture windowStart to guard against:
        // a) React StrictMode double-invoke: cleaned up before new run starts.
        // b) Late-arriving async warm for an already-flushed window: the guard
        //    inside preWarmForChannelsAsync checks self.channels !== newChannels
        //    and aborts, preventing stale derivation cache corruption.
        const rendererToWarm = rendererRef.current;
        const channelDataToWarm = channelData;
        const windowStartForPreWarm = bufferWindowStart;
        const cancelledRef = { cancelled: false };

        // First yield: defer past the current React passive-effects flush so
        // the warm work never runs inside the React scheduler's microtask.
        // MessageChannel fires as a macrotask after any queued RAF callbacks.
        const schedulePreWarm = () => {
          if (cancelledRef.cancelled) return;
          // Guard: if this window was already flushed into the renderer (loaded
          // cold during a non-playing path), skip to avoid overwriting with stale cache.
          if (
            (rendererLoadedWindowStartRef.current ?? -1) >=
            windowStartForPreWarm
          )
            return;
          if (!rendererToWarm) return;
          rendererToWarm
            .preWarmForChannelsAsync(channelDataToWarm, windowStartForPreWarm)
            .catch(() => {
              /* ignore errors in background warm */
            });
        };

        // Use requestIdleCallback when available (runs in idle time between frames).
        // Fall back to a single-frame delay via MessageChannel so the initial
        // React passive-effects task completes before the warm starts.
        if (typeof requestIdleCallback === "function") {
          const rICId = requestIdleCallback(schedulePreWarm, { timeout: 8000 });
          logger.debug(
            "[EEGViewer] Buffer queued for deferred load at windowStart:",
            bufferWindowStart,
          );
          if (effectiveDataDuration !== null) {
            const estimatedTotal = bufferWindowStart + effectiveDataDuration;
            // Bug P fix: cap at metadata duration.
            const cappedTotal =
              recordingMetaDurationRef.current > 0
                ? Math.min(estimatedTotal, recordingMetaDurationRef.current)
                : estimatedTotal;
            recordingDurationRef.current = Math.max(
              recordingDurationRef.current,
              cappedTotal,
            );
            setRecordingDuration((prev) => Math.max(prev, cappedTotal));
          }
          return () => {
            cancelledRef.cancelled = true;
            cancelIdleCallback(rICId);
          };
        } else {
          const { port1, port2 } = new MessageChannel();
          port1.onmessage = () => {
            port1.close();
            schedulePreWarm();
          };
          port2.postMessage(null);
          port2.close();
        }
        logger.debug(
          "[EEGViewer] Buffer queued for deferred load at windowStart:",
          bufferWindowStart,
        );
        // Still update recording duration estimate so the timeline doesn't shrink.
        // Bug J fix: also update the ref immediately so the tick handler's
        // advance-check guard (nextTime < recordingDurationRef.current - advanceChunk)
        // uses the correct value on the very next RAF frame, before the React batch
        // commits and the [recordingDuration] useEffect syncs the ref.
        if (effectiveDataDuration !== null) {
          const estimatedTotal = bufferWindowStart + effectiveDataDuration;
          // Bug P fix: cap at metadata duration.
          const cappedTotal =
            recordingMetaDurationRef.current > 0
              ? Math.min(estimatedTotal, recordingMetaDurationRef.current)
              : estimatedTotal;
          recordingDurationRef.current = Math.max(
            recordingDurationRef.current,
            cappedTotal,
          );
          setRecordingDuration((prev) => Math.max(prev, cappedTotal));
        }
        // Skip the immediate loadChannels — handlePlaybackTick will do it.
        // Return cleanup to mark cancelled so schedulePreWarm is a no-op on re-run.
        return () => {
          cancelledRef.cancelled = true;
        };
      } else {
        // Not playing: load immediately.

        // Bug H fix: if the user paused BEFORE the playhead reached this
        // window, don't call loadChannels()/setDataTimeOffset() now.  Doing so
        // would set dataTimeOffset=bufferWindowStart while
        // currentTimeRef.current < bufferWindowStart, causing the renderer to
        // draw at a negative sample index → completely blank canvas that
        // persists until the next seek/resume cycles the whole data path.
        //
        // Instead queue it as a pending buffer (same structure the PLAYING
        // path uses) so the RAF tick or seek handler can flush it the moment
        // the playhead enters the new window, at which point
        // currentTime >= bufferWindowStart will be true and the renderer will
        // draw at a non-negative offset.
        if (bufferWindowStart > (currentTimeRef.current ?? 0)) {
          const pendingDecodeTelemetry = eegData.decodeTelemetry ?? null;
          const pendingQueuedAtMs = emitPendingWindowDecodeReady({
            queueSource: "paused",
            windowStart: bufferWindowStart,
            requestContext: loadedTileRequestContext,
            decodeTelemetry: pendingDecodeTelemetry,
          });
          pendingEegBufferRef.current = {
            channelData,
            windowStart: bufferWindowStart,
            samplingRate: eegData.sampling_rate || 256,
            duration: effectiveDataDuration,
            requestContext: loadedTileRequestContext,
            decodeTelemetry: pendingDecodeTelemetry,
            queuedAtMs: pendingQueuedAtMs,
          };
          recordPlaybackPerfEvent("pendingBufferEnqueue", {
            queueSource: "paused",
            pendingWindowStart: Number(bufferWindowStart.toFixed(3)),
            pendingWindowEnd: Number(
              loadedTileRequestContext.windowEnd.toFixed(3),
            ),
            queuedAtMs: Number(pendingQueuedAtMs.toFixed(1)),
            speed: loadedTileRequestContext.speed,
            transportMode: loadedTileRequestContext.transportMode,
          });
          recordPauseBoundaryEvent("queue-paused-pending-buffer", {
            bufferWindowStart,
            pendingTransportMode: loadedTileRequestContext.transportMode,
            pendingQueuedAtMs,
          });
          // Still advance the duration estimate so the timeline scrubber doesn't shrink.
          // Bug J fix: also update ref eagerly (see PLAYING path comment above).
          if (effectiveDataDuration !== null) {
            const estimatedTotal = bufferWindowStart + effectiveDataDuration;
            // Bug P fix: cap at metadata duration.
            const cappedTotal =
              recordingMetaDurationRef.current > 0
                ? Math.min(estimatedTotal, recordingMetaDurationRef.current)
                : estimatedTotal;
            recordingDurationRef.current = Math.max(
              recordingDurationRef.current,
              cappedTotal,
            );
            setRecordingDuration((prev) => Math.max(prev, cappedTotal));
          }
          logger.debug(
            "[EEGViewer] Future window queued while paused (Bug H guard)",
            {
              bufferWindowStart,
              currentTime: currentTimeRef.current,
            },
          );
          return;
        }

        // Guard: skip the expensive loadChannels() call if this windowStart is
        // already loaded — focus-refetch storms return identical data and would
        // cause unnecessary 50-200ms main-thread stalls each time the user
        // switches tabs.  BUT we must NOT return early here because montages may
        // have resolved after the channel data was first loaded (race between
        // the /montages API and the EEG data fetch).  We fall through to the
        // montage-setup block so it can fire whenever montages become available.
        const channelsAlreadyLoaded =
          bufferWindowStart === rendererLoadedWindowStartRef.current;

        if (!channelsAlreadyLoaded) {
          // Sync canvas ref before any renderer operations — React may have replaced the DOM element
          syncRendererCanvas();
          measureEegStartupBlock("first-channel-publication", () => {
            rendererRef.current?.loadChannels(channelData);
          });
          loadedChannelsRef.current = channelData;
          markFirstWaveformChannelsPublished(channelData.length);
          markLiveTopographyBufferUpdated(currentRecordingId);
          rendererLoadedWindowStartRef.current = bufferWindowStart;
          rendererLoadedPlaybackContextRef.current = loadedTileRequestContext;

          // Track actual data end for the renderTime clamp in handlePlaybackTick.
          // Use effectiveDataDuration (true Arrow data span) — not eegData.duration
          // (which equals the full requested window size after normalization).
          if (effectiveDataDuration !== null) {
            rendererLoadedBufferEndRef.current =
              bufferWindowStart + effectiveDataDuration;
          }
        } else {
          // Channels already loaded for this window — avoid duplicate reloads
          // for focus-refetch storms, but DO reload when the same window is
          // re-fetched with a materially different data representation.
          // This happens when switching speeds at t=0:
          //   16x/L1 raw -> 100x/L3 envelope -> 1x/L0 raw
          // The old duration-only heuristic handled upgrades (30s -> 600s) but
          // missed downward/sideways swaps (1818s envelope -> 30s raw), leaving
          // the renderer drawing the previous speed's samples even though the new
          // tile fetched successfully.
          if (effectiveDataDuration !== null) {
            const laterDuration = effectiveDataDuration;
            const currentRendererDuration =
              (rendererLoadedBufferEndRef.current ?? bufferWindowStart) -
              bufferWindowStart;
            const currentLoadedSampleRate =
              loadedChannelsRef.current?.[0]?.sampleRate ?? null;
            const nextLoadedSampleRate = channelData[0]?.sampleRate ?? null;
            const currentUsesEnvelope = Boolean(
              loadedChannelsRef.current?.some(
                (channel) => channel.envelopeMin && channel.envelopeMax,
              ),
            );
            const nextUsesEnvelope = Boolean(
              channelData.some(
                (channel) => channel.envelopeMin && channel.envelopeMax,
              ),
            );
            const durationUpgrade =
              laterDuration > currentRendererDuration * 1.2 + 2;
            const durationRegression =
              laterDuration + 2 < currentRendererDuration * 0.8;
            const sampleRateChanged =
              currentLoadedSampleRate !== null &&
              nextLoadedSampleRate !== null &&
              Math.abs(nextLoadedSampleRate - currentLoadedSampleRate) >
                Math.max(1, currentLoadedSampleRate * 0.05);
            const representationChanged =
              durationRegression ||
              sampleRateChanged ||
              currentUsesEnvelope !== nextUsesEnvelope;
            const shouldReloadExistingWindow =
              durationUpgrade || representationChanged;

            if (shouldReloadExistingWindow) {
              syncRendererCanvas();
              rendererRef.current.loadChannels(channelData);
              loadedChannelsRef.current = channelData;
              markFirstWaveformChannelsPublished(channelData.length);
              markLiveTopographyBufferUpdated(currentRecordingId);
              rendererLoadedWindowStartRef.current = bufferWindowStart;
              rendererLoadedPlaybackContextRef.current =
                loadedTileRequestContext;
              rendererRef.current.setDataTimeOffset(bufferWindowStart);
              rendererLoadedBufferEndRef.current =
                bufferWindowStart + laterDuration;
              logger.debug(
                "[EEGViewer] Reloading renderer for same-window data change",
                {
                  prevDuration: currentRendererDuration.toFixed(1),
                  newDuration: laterDuration.toFixed(1),
                  prevSampleRate: currentLoadedSampleRate,
                  newSampleRate: nextLoadedSampleRate,
                  prevUsesEnvelope: currentUsesEnvelope,
                  newUsesEnvelope: nextUsesEnvelope,
                  bufferWindowStart,
                },
              );
            } else {
              // Same representation, better duration estimate.
              rendererLoadedBufferEndRef.current = Math.max(
                rendererLoadedBufferEndRef.current ?? 0,
                bufferWindowStart + laterDuration,
              );
            }
          }
        }
        if (!channelsAlreadyLoaded) {
          // Tell the renderer where sample[0] begins in absolute time.
          rendererRef.current.setDataTimeOffset(bufferWindowStart);
          logger.debug(
            "[EEGViewer] Renderer dataTimeOffset set to:",
            bufferWindowStart,
          );

          // Update recording duration from actual data.
          // Bug J fix: also update ref eagerly (see PLAYING path comment above).
          // Bug P fix: cap at metadata duration so 300s tile windows don't
          // inflate recordingDuration past the real recording end.
          if (effectiveDataDuration !== null) {
            const estimatedTotal = bufferWindowStart + effectiveDataDuration;
            const cappedTotal =
              recordingMetaDurationRef.current > 0
                ? Math.min(estimatedTotal, recordingMetaDurationRef.current)
                : estimatedTotal;
            recordingDurationRef.current = Math.max(
              recordingDurationRef.current,
              cappedTotal,
            );
            setRecordingDuration((prev) => Math.max(prev, cappedTotal));
            logger.debug("[EEGViewer] Recording duration estimated as:", {
              estimatedTotal: cappedTotal,
              bufferDuration: effectiveDataDuration,
            });
          }
        }
      }
    } else {
      logger.warn("[EEGViewer] No EEG data available");
      return;
    }

    // Skip montage update during active playback — channel data hasn't been
    // loaded into the renderer yet (deferred to next RAF tick via pendingEegBufferRef).
    // The existing montage is still valid for the renderer's current buffer.
    if (isPlayingRef.current) return;

    // Set montage based on current configuration
    const resolvedMontage = resolveEffectiveViewerMontage(
      montages,
      currentConfig?.montage_id,
      loadedRecordingChannelNames,
      recordingChannelMapAliasMaps,
    );
    if (montages && montages.length > 0 && resolvedMontage) {
      if (
        !isPassthroughViewerMontage(resolvedMontage) &&
        resolvedMontage.montage_id &&
        currentConfig?.montage_id !== resolvedMontage.montage_id
      ) {
        logger.debug("[EEGViewer] [Montage Fallback] Using montage:", {
          name: resolvedMontage.montage_name,
          id: resolvedMontage.montage_id,
        });
        setCurrentConfig((prev) =>
          prev
            ? { ...prev, montage_id: resolvedMontage.montage_id! }
            : prev,
        );
      } else if (
        isPassthroughViewerMontage(resolvedMontage) &&
        (currentConfig?.montage_id ?? "") !== ""
      ) {
        setCurrentConfig((prev) =>
          prev ? { ...prev, montage_id: "" } : prev,
        );
      }
      const currentMontage = resolvedMontage;
      if (currentMontage) {
        const activeMontageId =
          currentMontage.montage_id || currentConfig?.montage_id;
        const montagePairsForRenderer =
          activeMontageId &&
          Object.prototype.hasOwnProperty.call(
            viewerMontagePairsById,
            activeMontageId,
          )
            ? viewerMontagePairsById[activeMontageId] || []
            : currentMontage.channel_pairs || [];

        // No Derivation / passthrough: empty channel_pairs → build from raw data channels
        const effectivePairs =
          montagePairsForRenderer.length === 0 && eegData?.channels
            ? eegData.channels.map((ch: string) => ({
                positive: ch.replace(/^EEG\s+/i, ""),
                // Preserve the exact Arrow column name so the renderer's
                // exact-match lookup succeeds regardless of casing (e.g.
                // "EEG Fp1-F3" from MNE vs the uppercased form produced
                // by transformRendererChannelName).
                rendererPositiveName: ch,
              }))
            : montagePairsForRenderer;

        const montageChannels: EEGMontageChannel[] =
          buildRendererMontageChannels(
            effectivePairs,
            activeMontageId,
            currentConfig?.channel_color_mode ?? "uniform",
            eegData?.channels,
          );
        rendererRef.current.setMontage(montageChannels);
        logger.debug("[EEGViewer] Montage set:", {
          name: currentMontage.montage_name,
          channels: montageChannels,
        });

        // Trigger initial render after data and montage are loaded
        // Skip render during playback — the tick callback renders at 60fps;
        // firing here too wastes ~17ms and causes frame-time spikes.
        // Bug N fix: clamp renderTime to the newly-loaded buffer's valid range.
        // When resolution drops from LOD-1→LOD-0 at end-of-recording (paused),
        // the LOD-0 tile covers [ws, ws+600s] while currentTime may be at
        // ws+603s — beyond the loaded data boundary.  Without the clamp,
        // startSample > dataLength → empty subarray → all channels flat.
        // Bug Q fix: when the last tile is shorter than one page, snap
        // renderTime up to loadedWindowStart so waveforms start at the left
        // edge of the canvas (blank space pushed to the right).
        {
          const _bufEnd = rendererLoadedBufferEndRef.current;
          const _lwStart = rendererLoadedWindowStartRef.current ?? 0;
          const _ts = currentConfig?.time_scale ?? 10;
          const _isLastShort =
            _bufEnd !== null &&
            _bufEnd - _ts < _lwStart &&
            pendingEegBufferRef.current === null;
          let _safeTime = consumeDeferredPausedSeekTime();
          if (_bufEnd !== null && _safeTime > _bufEnd - _ts) {
            _safeTime = Math.max(0, _bufEnd - _ts);
          }
          if (_isLastShort) {
            _safeTime = Math.max(_safeTime, _lwStart);
          }
          _safeTime = syncPausedClockAfterImmediateLoad(_safeTime, {
            bufferWindowStart,
            loadedBufferEnd: _bufEnd,
            transportMode: loadedTileRequestContext.transportMode,
          });
          rendererRef.current.setCurrentTime(_safeTime);
        }
        if (!isPlayingRef.current) {
          measureEegStartupBlock("first-canvas-render", () => {
            markEegStartup("firstCanvasRender");
            if (
              typeof performance !== "undefined" &&
              typeof performance.mark === "function"
            ) {
              try {
                performance.mark("firstCanvasRender");
                performance.mark("eeg-startup:firstCanvasRender");
              } catch {
                /* ignore duplicate mark races */
              }
            }
            rendererRef.current?.render([]);
          });
          logger.debug("[EEGViewer] Initial render triggered after data load");
        }
      }
    } else if (!currentConfig?.montage_id && eegData?.channels) {
      // Explicit "No Derivation" selection (montage_id = null / ""):
      // build a passthrough from all channels in the loaded recording.
      const rawPairs = eegData.channels.map((ch: string) => ({
        positive: ch.replace(/^EEG\s+/i, ""),
        // Preserve the exact Arrow column name so the renderer's
        // exact-match lookup succeeds regardless of casing.
        rendererPositiveName: ch,
      }));
      const montageChannels: EEGMontageChannel[] = buildRendererMontageChannels(
        rawPairs,
        undefined,
        currentConfig?.channel_color_mode ?? "uniform",
        eegData?.channels,
      );
      rendererRef.current.setMontage(montageChannels);
      // Bug N fix: same LOD-switch clamp as in the montage path above.
      // Bug Q fix: same last-short-tile snap as in the montage path above.
      {
        const _bufEnd = rendererLoadedBufferEndRef.current;
        const _lwStart = rendererLoadedWindowStartRef.current ?? 0;
        const _ts = currentConfig?.time_scale ?? 10;
        const _isLastShort =
          _bufEnd !== null &&
          _bufEnd - _ts < _lwStart &&
          pendingEegBufferRef.current === null;
        let _safeTime = consumeDeferredPausedSeekTime();
        if (_bufEnd !== null && _safeTime > _bufEnd - _ts) {
          _safeTime = Math.max(0, _bufEnd - _ts);
        }
        if (_isLastShort) {
          _safeTime = Math.max(_safeTime, _lwStart);
        }
        _safeTime = syncPausedClockAfterImmediateLoad(_safeTime, {
          bufferWindowStart,
          loadedBufferEnd: _bufEnd,
          transportMode: loadedTileRequestContext.transportMode,
        });
        rendererRef.current.setCurrentTime(_safeTime);
      }
      if (!isPlayingRef.current) {
        rendererRef.current.render([]);
        logger.debug("[EEGViewer] No Derivation passthrough applied", {
          channelCount: montageChannels.length,
        });
      }
    }
  }, [
    autoResolution,
    buildRendererMontageChannels,
    currentConfig?.playback_speed,
    currentConfig?.time_scale,
    currentRecordingId,
    rendererReady,
    eegData,
    getAutoResolutionLevel,
    consumeDeferredPausedSeekTime,
    getEffectiveBufferSize,
    shouldUseClinicalRawResolution,
    montages,
    currentConfig?.montage_id,
    currentConfig?.channel_color_mode,
    isLoadingEegData,
    isEegDataPlaceholder,
    dataWindowStart,
    playbackTransportMode,
    recordPauseBoundaryEvent,
    resolutionLevel,
    syncPausedClockAfterImmediateLoad,
    viewerMontagePairsById,
    loadedRecordingChannelNames,
    recordingChannelMapAliasMaps,
    markFirstWaveformChannelsPublished,
  ]);

  // ─── CONFIG UPDATE EFFECT ────────────────────────────────────────────────────
  // Pushes display/filter settings into the renderer whenever they change.
  // Does NOT include currentTime or renderAnnotations — those live in the
  // per-frame render effect below so this never fires during playback ticks.
  useEffect(() => {
    if (!rendererReady || !rendererRef.current || !currentConfig) return;

    rendererRef.current.updateConfig({
      sensitivity: currentConfig.sensitivity_value,
      pixelsPerMm: effectivePixelsPerMm,
      timeScale: currentConfig.time_scale,
      highPassFilter: currentConfig.high_pass_filter,
      lowPassFilter: currentConfig.low_pass_filter,
      notchFilter: currentConfig.notch_filter_60hz,
      notch50Hz: currentConfig.notch_filter_50hz ?? false,
      removeDcOffset: currentConfig.remove_dc_offset ?? false,
      polarity: currentConfig.polarity ?? "negative-up",
      channelColorMode: currentConfig.channel_color_mode ?? "uniform",
      channelGainOverrides,
      channelScrollOffset,
      visibleChannelCount,
      playbackSpeed: currentConfig.playback_speed,
      playbackTransportMode,
      showGrid,
      gridStyle: clinicalGridMode ? "clinical" : "standard",
      gridDensity: profileGridDensity,
      backgroundColor:
        RENDER_THEME_MAP[workspaceTheme]?.backgroundColor ?? "#000000",
      waveformColor: RENDER_THEME_MAP[workspaceTheme]?.waveformColor ?? "#e2e8f0",
      gridColor: withAlphaHex(
        RENDER_THEME_MAP[workspaceTheme]?.gridColorBase ?? "#5b6472",
        profileGridDensity === "low"
          ? "66"
          : profileGridDensity === "high"
            ? "bb"
            : "88",
      ),
      showTimeline: showTimeline && !showUnifiedNavigationTimeline,
      showChannelGroupLabels: showChannelGroupLabels,
      showScaleMarkers,
      physicalTimebaseTrusted:
        calibrationOverallPass === true && !calibrationStale,
      displayMode: waveformDisplayMode,
      allowTraceOverflow,
      annotationOpacity: annotationOverlayOpacity,
      amplitudeScalingMode: clinicalGridMode ? "fixed" : "auto",
      persistentTimestamp: waveformTextOverlays.persistentTimestamp,
      recordingStartIso,
    });

    // Render once after config push so changes are immediately visible.
    // Skip during playback — the tick callback renders at 60fps and picks
    // up the new config on the very next frame.
    rendererRef.current.setCurrentTime(currentTimeRef.current);
    if (!isPlayingRef.current) {
      if (pausedHighSpeedInspectHoldRef.current?.transportMode === "play") {
        recordPauseBoundaryEvent("pause-config-render", {
          currentTime: Number(currentTimeRef.current.toFixed(3)),
          loadedWindowStart: rendererLoadedWindowStartRef.current,
          loadedBufferEnd: rendererLoadedBufferEndRef.current,
        });
      }
      try {
        rendererRef.current.render(
          mapAnnotationsForRenderer(renderAnnotationsRef.current),
        );
      } catch (error) {
        logger.error("EEG renderer failed after config sync", error);
      }
      logger.debug("Config update render called");
    }
  }, [
    rendererReady,
    currentConfig,
    showGrid,
    showTimeline,
    showUnifiedNavigationTimeline,
    showChannelGroupLabels,
    showScaleMarkers,
    waveformTextOverlays.persistentTimestamp,
    liveStatusRailActive,
    clinicalGridMode,
    profileGridDensity,
    workspaceTheme,
    effectivePixelsPerMm,
    channelGainOverrides,
    channelScrollOffset,
    visibleChannelCount,
    waveformDisplayMode,
    allowTraceOverflow,
    annotationOverlayOpacity,
    playbackTransportMode,
    recordingStartIso,
    mapAnnotationsForRenderer,
    calibrationOverallPass,
    calibrationStale,
  ]);

  // ─── PER-FRAME RENDER EFFECT ─────────────────────────────────────────────────
  // Fires when currentTime changes (10 fps clock ticks) or cursor/selection state
  // changes. During *active playback* the onTick callback already calls render()
  // at 60fps via requestAnimationFrame so we skip the redundant render here.
  // We still need to call it when paused (scrubbing, annotation hover, etc.).
  useEffect(() => {
    if (!rendererReady || !rendererRef.current) return;

    // Apply crosshair / pinned cursor state
    if (crosshairEnabled && crosshairCanvasPosition) {
      rendererRef.current.setCrosshairPosition(
        crosshairCanvasPosition.x,
        crosshairCanvasPosition.y,
      );
    } else {
      rendererRef.current.clearCrosshair();
    }
    rendererRef.current.setPinnedCursorTime(pinnedCursorTime);
    rendererRef.current.setPagePlayheadOffsetSeconds(pagePlayheadOffsetSeconds);
    rendererRef.current.setSelectedMontageChannelLabels(selectedTraceChannels);

    // During playback onTick drives renders at 60 fps — no need to double-render here
    if (isPlaying) return;

    if (
      pausedHighSpeedInspectHoldRef.current?.transportMode === "play" &&
      lastRenderedPlaybackTimeRef.current !== null &&
      Math.abs(currentTime - lastRenderedPlaybackTimeRef.current) <= 0.001
    ) {
      return;
    }

    rendererRef.current.setCurrentTime(currentTime);
    if (pausedHighSpeedInspectHoldRef.current?.transportMode === "play") {
      recordPauseBoundaryEvent("pause-frame-render", {
        currentTime: Number(currentTime.toFixed(3)),
        loadedWindowStart: rendererLoadedWindowStartRef.current,
        loadedBufferEnd: rendererLoadedBufferEndRef.current,
      });
    }
    rendererRef.current.render(mapAnnotationsForRenderer(renderAnnotations));
    lastRenderedPlaybackTimeRef.current = currentTime;
  }, [
    // Playback-tick drivers — intentionally the minimal set
    currentTime,
    renderAnnotations,
    isPlaying,
    // Renderer identity
    rendererReady,
    // Cursor / selection state that must be in sync with each frame
    crosshairCanvasPosition,
    crosshairEnabled,
    pinnedCursorTime,
    pagePlayheadOffsetSeconds,
    selectedTraceChannels,
    mapAnnotationsForRenderer,
  ]);

  // Re-apply montage colors whenever profile/default color maps change.
  // Does NOT include currentTime — uses the ref so it doesn't fire every tick.
  useEffect(() => {
    if (!rendererReady || !rendererRef.current) {
      return;
    }

    const activeMontage = resolveEffectiveViewerMontage(
      montages,
      currentConfig?.montage_id,
      loadedRecordingChannelNames,
      recordingChannelMapAliasMaps,
    );
    if (!activeMontage?.montage_id) return;

    syncRendererCanvas();
    const montageChannels = buildRendererMontageChannels(
      visibleMontagePairs,
      activeMontage.montage_id,
      currentConfig?.channel_color_mode ?? "uniform",
      eegData?.channels,
    );
    rendererRef.current.setMontage(montageChannels);
    rendererRef.current.setCurrentTime(currentTimeRef.current);
    // Skip render during playback — tick callback handles it at 60fps.
    if (!isPlayingRef.current) {
      rendererRef.current.render(
        mapAnnotationsForRenderer(renderAnnotationsRef.current),
      );
    }
  }, [
    buildRendererMontageChannels,
    currentConfig?.montage_id,
    currentConfig?.channel_color_mode,
    visibleMontagePairs,
    montages,
    rendererReady,
    syncRendererCanvas,
    mapAnnotationsForRenderer,
    loadedRecordingChannelNames,
    recordingChannelMapAliasMaps,
    eegData?.channels,
  ]);

  // Subscribe to MontageEventBus for real-time montage updates (Sprint M2 Integration)
  useEffect(() => {
    logger.debug("[MontageEventBus] Subscribing to montage:applied events");

    const unsubscribe = MontageEventBus.subscribe(
      "montage:applied",
      (eventData) => {
        // Narrow to MontageAppliedEvent (skip reverted events)
        if (!isMontageAppliedEvent(eventData)) return;
        const montageData: MontageAppliedEvent = eventData;

        logger.debug("[MontageEventBus] montage:applied event received", {
          montageId: montageData.montage_id,
          montageName: montageData.montage_name,
          montageType: montageData.montage_type,
          channelPairsCount: montageData.channel_pairs?.length || 0,
          timestamp: Date.now(),
        });

        // Update current configuration with new montage
        if (currentConfigRef.current) {
          const updatedConfig = {
            ...currentConfigRef.current,
            montage_id: montageData.montage_id,
          };
          setCurrentConfig(normalizeViewerConfig(updatedConfig));
          announceMontageChange(montageData.montage_name || "Custom montage");
          logger.debug(
            "[MontageEventBus] Updated currentConfig with new montage_id",
            {
              oldMontageId: currentConfigRef.current.montage_id,
              newMontageId: montageData.montage_id,
            },
          );
        }

        // Apply montage to renderer if available
        if (rendererRef.current && montageData.channel_pairs) {
          syncRendererCanvas();
          const montageChannels: EEGMontageChannel[] =
            buildRendererMontageChannels(
              montageData.channel_pairs as Array<{
                positive: string;
                negative?: string;
              }>,
              montageData.montage_id,
              currentConfigRef.current?.channel_color_mode ?? "uniform",
              eegData?.channels,
            );

          rendererRef.current.setMontage(montageChannels);
          logger.debug("[MontageEventBus] Applied montage to renderer", {
            montageName: montageData.montage_name,
            channelCount: montageChannels.length,
            firstChannel: montageChannels[0]?.label,
          });

          // Use refs so we don't need currentTime/renderAnnotations in the dep array
          rendererRef.current.setCurrentTime(currentTimeRef.current);
          // During playback the rAF tick owns rendering — skip extra render here
          if (!isPlayingRef.current) {
            rendererRef.current.render(
              mapAnnotationsForRenderer(renderAnnotationsRef.current),
            );
          }
          logger.debug(
            "[MontageEventBus] Re-rendered waveforms with new montage",
          );
        }
      },
    );

    // Cleanup subscription on unmount
    return () => {
      logger.debug(
        "[MontageEventBus] Unsubscribing from montage:applied events",
      );
      unsubscribe();
    };
  }, [
    announceMontageChange,
    buildRendererMontageChannels,
    // currentConfig intentionally read via currentConfigRef inside the callback
    // currentTime / renderAnnotations intentionally read via refs — not deps
    syncRendererCanvas,
  ]); // Re-subscribe only when stable references change, never on playback ticks

  // Load default configuration on mount
  useEffect(() => {
    if (configurations && configurations.length > 0) {
      const defaultConfig =
        configurations.find((c) => c.is_default) || configurations[0];
      // Only update if config ID actually changed (prevent infinite loop)
      if (currentConfig?.config_id !== defaultConfig?.config_id) {
        setCurrentConfig(normalizeViewerConfig(defaultConfig));
      }
    }
  }, [configurations, currentConfig?.config_id]);

  // Helper function to calculate config diff for change banner
  const calculateConfigDiff = useCallback(
    (
      current: EEGConfiguration | null,
      draft: Partial<EEGConfiguration> | null,
    ): ConfigChange[] => {
      if (!draft || !current) return [];

      const changes: ConfigChange[] = [];

      if (draft.montage_id && draft.montage_id !== current.montage_id) {
        changes.push({
          parameter: "Montage",
          oldValue: current.montage_id,
          newValue: draft.montage_id,
          category: "montage",
        });
      }

      if (
        draft.high_pass_filter !== undefined &&
        draft.high_pass_filter !== current.high_pass_filter
      ) {
        changes.push({
          parameter: "High-Pass Filter",
          oldValue: `${current.high_pass_filter} Hz`,
          newValue: `${draft.high_pass_filter} Hz`,
          category: "filter",
        });
      }

      if (
        draft.low_pass_filter !== undefined &&
        draft.low_pass_filter !== current.low_pass_filter
      ) {
        changes.push({
          parameter: "Low-Pass Filter",
          oldValue: `${current.low_pass_filter} Hz`,
          newValue: `${draft.low_pass_filter} Hz`,
          category: "filter",
        });
      }

      if (
        draft.notch_filter_60hz !== undefined ||
        draft.notch_filter_50hz !== undefined
      ) {
        const oldNotchMode = getNotchModeFromFlags(
          current.notch_filter_60hz,
          current.notch_filter_50hz,
        );
        const newNotchFlags = mergeNotchFlags(current, draft);
        const newNotchMode = getNotchModeFromFlags(
          newNotchFlags.notch_filter_60hz,
          newNotchFlags.notch_filter_50hz,
        );
        if (oldNotchMode === newNotchMode) {
          // no-op
        } else {
          changes.push({
            parameter: "Notch Filter",
            oldValue: getNotchModeLabel(oldNotchMode),
            newValue: getNotchModeLabel(newNotchMode),
            category: "filter",
          });
        }
      }

      if (
        draft.sensitivity_value !== undefined &&
        draft.sensitivity_value !== current.sensitivity_value
      ) {
        changes.push({
          parameter: "Gain",
          oldValue: `${current.sensitivity_value}µV/mm`,
          newValue: `${draft.sensitivity_value}µV/mm`,
          category: "gain",
        });
      }

      if (
        draft.time_scale !== undefined &&
        draft.time_scale !== current.time_scale
      ) {
        changes.push({
          parameter: "Time Scale",
          oldValue: `${current.time_scale}s/div`,
          newValue: `${draft.time_scale}s/div`,
          category: "timebase",
        });
      }

      return changes;
    },
    [],
  );

  // Handler for quick switching perspectives
  const handlePerspectiveSwitch = useCallback(
    async (perspective: any) => {
      await applyPerspective(perspective);
    },
    [applyPerspective],
  );

  // Auto-save configuration changes
  const handleConfigChange = useCallback(
    (updates: Partial<EEGConfiguration>) => {
      if (currentConfig) {
        if (
          typeof updates.playback_speed === "number" &&
          normalizedCurrentRecordingId
        ) {
          if (!isPlaying && (currentConfig.playback_speed ?? 1) >= 100) {
            const handoffPrefetchDebug = getPrefetchDebugSnapshot(
              normalizedCurrentRecordingId,
              {
                level: autoResolution
                  ? getAutoResolutionLevel(
                      currentConfig.playback_speed ?? 1,
                      "play",
                    )
                  : resolutionLevel,
                speed: currentConfig.playback_speed ?? 1,
                transportMode: "play",
              },
            );
            emitPlaybackPerfEvent("prefetchWindow", {
              phase: "handoff-snapshot",
              ownerKey: "play-background",
              recordingId: normalizedCurrentRecordingId,
              handoffStage: "speed-change",
              isPlaying,
              previousSpeed: currentConfig.playback_speed ?? 1,
              nextSpeed: updates.playback_speed,
              currentTimeSeconds: Number(currentTimeRef.current.toFixed(3)),
              dataWindowStartSeconds: Number(
                dataWindowStartRef.current.toFixed(3),
              ),
              prefetchControllerKey: handoffPrefetchDebug.controllerKey ?? "",
              prefetchControllerAborted: handoffPrefetchDebug.controllerAborted,
              prefetchExactTileInFlight: handoffPrefetchDebug.exactTileInFlight,
              prefetchMatchingInFlightCount:
                handoffPrefetchDebug.matchingInFlightCount,
              prefetchMatchingInFlightWindows:
                handoffPrefetchDebug.matchingInFlightWindows.join("|"),
              prefetchAbortCount: handoffPrefetchDebug.abortCount,
              prefetchLastAbortAgeMs:
                handoffPrefetchDebug.lastAbortAgeMs === null
                  ? -1
                  : Number(handoffPrefetchDebug.lastAbortAgeMs.toFixed(1)),
            });
          }
          cancelPrefetchRequests(normalizedCurrentRecordingId, {
            reason: "playback-speed-change",
            isPlaying,
            speed: currentConfig.playback_speed ?? 1,
            previousSpeed: currentConfig.playback_speed ?? 1,
            nextSpeed: updates.playback_speed,
            currentTimeSeconds: Number(currentTimeRef.current.toFixed(3)),
            dataWindowStartSeconds: Number(
              dataWindowStartRef.current.toFixed(3),
            ),
          });
        }
        const touchesNotch =
          typeof updates.notch_filter_60hz === "boolean" ||
          typeof updates.notch_filter_50hz === "boolean";
        if (touchesNotch && normalizedCurrentRecordingId) {
          setManualNotchOverrideByRecordingId((current) => {
            if (current[normalizedCurrentRecordingId]) return current;
            return {
              ...current,
              [normalizedCurrentRecordingId]: true,
            };
          });
        }
        const normalizedNotchUpdates = touchesNotch
          ? mergeNotchFlags(currentConfig, updates)
          : null;
        const nextPlaybackSpeed =
          typeof updates.playback_speed === "number"
            ? updates.playback_speed
            : null;
        if (
          !isPlaying &&
          nextPlaybackSpeed !== null &&
          nextPlaybackSpeed >= 100 &&
          nextPlaybackSpeed !== (currentConfig.playback_speed ?? 1)
        ) {
          commitPausedHighSpeedInspectHold({
            untilMs:
              Date.now() +
              getPausedHighSpeedInspectGraceMs(
                currentConfig.playback_speed ?? 1,
              ),
            playbackSpeed: currentConfig.playback_speed ?? 1,
            timeScale: currentConfig.time_scale ?? 10,
            resolutionLevel: autoResolution
              ? getAutoResolutionLevel(
                  currentConfig.playback_speed ?? 1,
                  "inspect",
                )
              : resolutionLevel,
            transportMode: "inspect",
          });
        }
        // At high playback speeds, auto-scale sensitivity for legibility (tied to autoResolution toggle).
        // Save the user's sensitivity before the first crossing into rapid-review territory so it
        // can be restored when they return to a normal review speed.
        let rapidCrossingDirection: "up" | "down" | null = null;
        const autoSensUpdates: Partial<EEGConfiguration> = (() => {
          if (typeof updates.playback_speed !== "number" || !autoResolution)
            return {};
          const prevSpeed = currentConfig.playback_speed ?? 1;
          const nextSpeed = updates.playback_speed;
          const prevIsRapid = getAutoSensitivityForSpeed(prevSpeed) !== null;
          const nextIsRapid = getAutoSensitivityForSpeed(nextSpeed) !== null;

          if (!prevIsRapid && nextIsRapid) {
            // Crossing up into rapid zone: save current sensitivity + time_scale,
            // then apply auto values calibrated to the new speed tier.
            rapidCrossingDirection = "up";
            prerapidSensitivityRef.current = currentConfig.sensitivity_value;
            prerapidTimeScaleRef.current = currentConfig.time_scale ?? 10;
            return {
              sensitivity_value: getAutoSensitivityForSpeed(
                nextSpeed,
                observedPeakUvRef.current,
                currentConfig.sensitivity_value,
              )!,
              time_scale: getAutoTimeScaleForSpeed(nextSpeed)!,
            };
          }
          if (prevIsRapid && nextIsRapid) {
            // Staying in rapid zone: update auto values if the speed tier changed
            // (e.g. crossing from 16-29× tier into 30-99× tier).
            const sens = getAutoSensitivityForSpeed(
              nextSpeed,
              observedPeakUvRef.current,
              prerapidSensitivityRef.current,
            )!;
            const ts = getAutoTimeScaleForSpeed(nextSpeed)!;
            const patch: Partial<EEGConfiguration> = {};
            if (sens !== currentConfig.sensitivity_value)
              patch.sensitivity_value = sens;
            if (ts !== (currentConfig.time_scale ?? 10)) patch.time_scale = ts;
            return patch;
          }
          if (prevIsRapid && !nextIsRapid) {
            // Crossing down: restore saved sensitivity + time_scale.
            rapidCrossingDirection = "down";
            const restoredSens = prerapidSensitivityRef.current;
            const restoredTs = prerapidTimeScaleRef.current;
            prerapidSensitivityRef.current = null;
            prerapidTimeScaleRef.current = null;
            const patch: Partial<EEGConfiguration> = {};
            if (restoredSens !== null) patch.sensitivity_value = restoredSens;
            if (restoredTs !== null) patch.time_scale = restoredTs;
            return patch;
          }
          return {};
        })();
        const newConfig = normalizeViewerConfig({
          ...currentConfig,
          ...updates,
          ...autoSensUpdates,
          ...(normalizedNotchUpdates ?? {}),
        });
        setCurrentConfig(newConfig);

        if (
          updates.montage_id !== undefined &&
          updates.montage_id !== currentConfig.montage_id
        ) {
          const resolvedMontage = resolveEffectiveViewerMontage(
            montages,
            newConfig.montage_id,
            loadedRecordingChannelNames,
            recordingChannelMapAliasMaps,
          );
          announceMontageChange(
            resolvedMontage?.montage_name ||
              (newConfig.montage_id ? "Custom montage" : "No derivation"),
          );
        }

        if (
          normalizedCurrentRecordingId &&
          (updates.sensitivity_value !== undefined ||
            updates.time_scale !== undefined)
        ) {
          writeEEGViewerSessionReviewConfig(
            toolbarUserId,
            normalizedCurrentRecordingId,
            {
              ...(updates.sensitivity_value !== undefined
                ? { sensitivity_value: newConfig.sensitivity_value }
                : {}),
              ...(updates.time_scale !== undefined
                ? { time_scale: newConfig.time_scale }
                : {}),
            },
          );
        }

        // Notify clinician when display settings are auto-changed at threshold crossing
        if (rapidCrossingDirection === "up") {
          const newSens = newConfig.sensitivity_value;
          const newTs = newConfig.time_scale;
          const prevTs = currentConfig.time_scale;
          const tsMsg =
            newTs !== prevTs ? ` · time scale → ${newTs}s/page` : "";
          toast.info(
            `Rapid-review mode: sensitivity auto-set to ${newSens} μV/mm${tsMsg}`,
            { toastId: "rapid-mode-enter", autoClose: 4000 },
          );
        } else if (rapidCrossingDirection === "down") {
          const restored = newConfig.sensitivity_value;
          toast.info(
            `Returned to standard review — sensitivity restored to ${restored} μV/mm`,
            { toastId: "rapid-mode-exit", autoClose: 3000 },
          );
        }

        // Auto-resolution: switch pyramid level when playback speed changes
        if (typeof updates.playback_speed === "number" && autoResolution) {
          const recommended = getAutoResolutionLevel(
            updates.playback_speed,
            isPlayingRef.current ? "play" : "inspect",
          );
          if (recommended !== resolutionLevel) {
            setResolutionLevel(recommended);
            const chunk = getAdvanceChunk(
              getEffectiveBufferSize(
                updates.playback_speed,
                newConfig.time_scale || 10,
                isPlayingRef.current ? "play" : "inspect",
              ),
              newConfig.time_scale || 10,
            );
            setDataWindowStart(
              alignWindowStartToBackend(
                Math.floor(currentTime / chunk) * chunk,
                updates.playback_speed,
                newConfig.time_scale || 10,
                isPlayingRef.current ? "play" : "inspect",
                recordingDurationRef.current,
              ),
            );
          }
        }

        // Immediately update renderer with new config
        if (rendererRef.current) {
          // Synchronously mirror sensitivity so the toolbar <select> value
          // is consistent with the canvas on the very next paint, eliminating
          // the brief flicker caused by React's async commit.
          pendingSensitivityRef.current = newConfig.sensitivity_value;
          rendererRef.current.updateConfig({
            sensitivity: newConfig.sensitivity_value,
            timeScale: newConfig.time_scale,
            highPassFilter: newConfig.high_pass_filter,
            lowPassFilter: newConfig.low_pass_filter,
            notchFilter: newConfig.notch_filter_60hz,
            notch50Hz: newConfig.notch_filter_50hz ?? false,
            removeDcOffset: newConfig.remove_dc_offset ?? false,
            polarity: newConfig.polarity ?? "negative-up",
            channelColorMode: newConfig.channel_color_mode ?? "uniform",
            channelGainOverrides,
            channelScrollOffset,
            visibleChannelCount,
            playbackSpeed: newConfig.playback_speed,
            annotationOpacity: annotationOverlayOpacity,
            gridDensity: profileGridDensity,
          });

          // Re-render immediately with current time (skip during playback — rAF tick handles it)
          rendererRef.current.setCurrentTime(currentTime);
          if (!isPlayingRef.current) {
            try {
              rendererRef.current.render(
                mapAnnotationsForRenderer(renderAnnotations),
              );
            } catch (error) {
              logger.error(
                "EEG renderer failed after a display-config change",
                error,
              );
            }
          }
        }

        // Debounce auto-save to backend. Wrap mutate in startTransition so the
        // onSuccess → queryClient.invalidateQueries re-render is treated as
        // non-urgent — it won't collide with the synchronous tile-load commit
        // that causes the 56ms [Violation] 'setTimeout' handler at recording start.
        const timeout = setTimeout(() => {
          const persistedUpdates = touchesNotch
            ? {
                ...updates,
                ...(normalizedNotchUpdates ?? {}),
              }
            : updates;
          React.startTransition(() => {
            saveConfigMutation.mutate(persistedUpdates);
          });
        }, 1000);

        return () => clearTimeout(timeout);
      }
    },
    [
      autoResolution,
      resolutionLevel,
      getAutoResolutionLevel,
      getAdvanceChunk,
      getEffectiveBufferSize,
      announceMontageChange,
      currentConfig,
      currentTime,
      channelGainOverrides,
      channelScrollOffset,
      mapAnnotationsForRenderer,
      montages,
      normalizedCurrentRecordingId,
      renderAnnotations,
      saveConfigMutation,
      setManualNotchOverrideByRecordingId,
      toolbarUserId,
      visibleChannelCount,
      loadedRecordingChannelNames,
      recordingChannelMapAliasMaps,
    ],
  );
  configChangeRequestRef.current = handleConfigChange;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const targetWindow = window as typeof window & {
      __eegViewerSetPlaybackSpeed?: (speed: number) => boolean;
      __eegViewerSetPlaying?: (nextIsPlaying: boolean) => boolean;
      __eegViewerGetPlaybackState?: () => {
        playbackSpeed: number;
        isPlaying: boolean;
        currentTimeSeconds: number;
        visibleTimeSeconds: number;
        rendererMetrics: ReturnType<EEGWaveformRenderer["getWaveformRenderMetrics"]>;
      };
    };
    targetWindow.__eegViewerSetPlaybackSpeed = (speed: number) => {
      const normalizedSpeed = Number(speed);
      if (!Number.isFinite(normalizedSpeed) || normalizedSpeed <= 0) {
        return false;
      }
      handleConfigChange({ playback_speed: normalizedSpeed });
      return true;
    };
    targetWindow.__eegViewerSetPlaying = (nextIsPlaying: boolean) => {
      setPlaybackRunning(Boolean(nextIsPlaying));
      return true;
    };
    targetWindow.__eegViewerGetPlaybackState = () => ({
      playbackSpeed: currentConfig?.playback_speed ?? 1,
      isPlaying: isPlaybackRunning,
      currentTimeSeconds: playheadRef.current,
      visibleTimeSeconds: currentTimeRef.current,
      rendererMetrics:
        rendererRef.current?.getWaveformRenderMetrics?.() ?? null,
    });
    return () => {
      delete targetWindow.__eegViewerSetPlaybackSpeed;
      delete targetWindow.__eegViewerSetPlaying;
      delete targetWindow.__eegViewerGetPlaybackState;
    };
  }, [
    currentConfig?.playback_speed,
    handleConfigChange,
    isPlaybackRunning,
    setPlaybackRunning,
  ]);

  const sessionReviewConfigAppliedRef = useRef<string>("");
  useEffect(() => {
    if (!normalizedCurrentRecordingId) return;
    const applyKey = `${toolbarUserId}:${normalizedCurrentRecordingId}`;
    if (sessionReviewConfigAppliedRef.current === applyKey) return;
    sessionReviewConfigAppliedRef.current = applyKey;

    const session = readEEGViewerSessionReviewConfig(
      toolbarUserId,
      normalizedCurrentRecordingId,
    );
    if (!session) return;

    const patch: Partial<EEGConfiguration> = {};
    if (session.sensitivity_value !== undefined) {
      patch.sensitivity_value = normalizeSensitivityValue(
        session.sensitivity_value,
      );
    }
    if (session.time_scale !== undefined) {
      patch.time_scale = session.time_scale;
    }
    if (Object.keys(patch).length === 0) return;

    handleConfigChange(patch);
  }, [handleConfigChange, normalizedCurrentRecordingId, toolbarUserId]);

  const getCanAutoFitSensitivity = useCallback((): boolean => {
    if (
      !rendererReady ||
      !currentConfig ||
      !eegData?.columnArrays?.length ||
      visibleAutoFitMontageChannelDetails.length <= 0 ||
      waveformDisplayMode !== "stacked"
    ) {
      return false;
    }
    const renderer = rendererRef.current;
    if (!renderer) return false;
    const viewportMetrics = renderer.getViewportMetrics();
    if (
      viewportMetrics.visibleChannelCount <= 0 ||
      !Number.isFinite(viewportMetrics.channelHeight) ||
      viewportMetrics.channelHeight <= 0
    ) {
      return false;
    }
    return true;
  }, [
    rendererReady,
    currentConfig,
    eegData?.columnArrays?.length,
    visibleAutoFitMontageChannelDetails.length,
    waveformDisplayMode,
  ]);

  const handleRowHeightModeChange = useCallback((mode: RowHeightMode) => {
    eegDensityFollowsPortalRef.current = false;
    setProfileChannelSpacing(getChannelSpacingForRowHeightMode(mode));
    setProfileGridDensity(rowHeightModeToGridDensity(mode));
  }, []);

  const handleAllowTraceOverflowChange = useCallback((nextValue: boolean) => {
    setAllowTraceOverflow(nextValue);
  }, []);

  const handleRefitOnChannelChange = useCallback((nextValue: boolean) => {
    setRefitOnChannelChange(nextValue);
  }, []);

  const handleDisplayModeChange = useCallback((mode: WaveformDisplayMode) => {
    setWaveformDisplayMode(mode);
  }, []);

  const handleApplySelectedDisplayProfile = useCallback(() => {
    if (!selectedDisplayProfile) {
      setDisplayProfileStatus(null);
      setDisplayProfileError("Select a display profile first.");
      return;
    }
    applyDisplayProfileToSession(selectedDisplayProfile);
    setDisplayProfileConflict(null);
    setDisplayProfileError(null);
    setDisplayProfileStatus(
      `Applied profile "${selectedDisplayProfile.profile_name}" to current session.`,
    );
  }, [applyDisplayProfileToSession, selectedDisplayProfile]);

  const handleChannelOffsetChange = useCallback(
    (channelLabel: string, rawValue: number) => {
      setProfileChannelPositionOffsets((prev) => {
        return applyChannelOffsetChange(
          prev,
          channelLabel,
          rawValue,
          displayProfilePolicy.visual.channel_position_offset,
        );
      });
    },
    [displayProfilePolicy.visual.channel_position_offset],
  );

  const handleResetChannelOffset = useCallback((channelLabel: string) => {
    setProfileChannelPositionOffsets((prev) => {
      return resetChannelOffset(prev, channelLabel);
    });
  }, []);

  const handleResetAllChannelOffsets = useCallback(() => {
    if (!effectiveMontageChannelLabels.length) return;
    setProfileChannelPositionOffsets((prev) => {
      return resetAllChannelOffsets(prev, effectiveMontageChannelLabels);
    });
  }, [effectiveMontageChannelLabels]);

  const handleNormalizeChannelOffsets = useCallback(() => {
    if (!effectiveMontageChannelLabels.length) return;
    setProfileChannelPositionOffsets((prev) => {
      return normalizeChannelOffsets(
        prev,
        effectiveMontageChannelLabels,
        displayProfilePolicy.visual.channel_position_offset,
      );
    });
  }, [
    displayProfilePolicy.visual.channel_position_offset,
    effectiveMontageChannelLabels,
  ]);

  const handleWaveformCtrlClickPin = useCallback(
    (event: MouseEvent) => {
      if (!rendererRef.current || !canvasRef.current || !currentConfig) return;
      if (!event.ctrlKey) return;

      const rect = canvasRef.current.getBoundingClientRect();
      if (rect.width <= 0) return;
      const canvasX =
        ((event.clientX - rect.left) / rect.width) * canvasRef.current.width;
      const relativeSeconds = rendererRef.current.canvasXToRelativeSeconds(
        canvasX,
      );
      const clickedTime = Math.max(0, currentTime + relativeSeconds);
      if (
        pinnedCursorTime !== null &&
        Math.abs(pinnedCursorTime - clickedTime) <= 0.1
      ) {
        setPinnedCursorTime(null);
        return;
      }
      setPinnedCursorTime(clickedTime);
    },
    [currentConfig, currentTime, pinnedCursorTime],
  );

  const handleWaveformCanvasContextMenu = useCallback(
    (event: MouseEvent) => {
      if (
        !rendererRef.current ||
        !canvasRef.current ||
        !visibleMontageChannelDetails.length
      ) {
        return;
      }
      event.preventDefault();

      const rect = canvasRef.current.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const canvasX =
        ((event.clientX - rect.left) / rect.width) * canvasRef.current.width;
      const canvasY =
        ((event.clientY - rect.top) / rect.height) * canvasRef.current.height;
      const hit = rendererRef.current.getMontageChannelHitAtCanvasPoint(
        canvasX,
        canvasY,
      );
      if (!hit) {
        setChannelGainMenu((current) => ({
          ...current,
          open: false,
          channelLabel: null,
        }));
        return;
      }

      const menuWidth = 240;
      const menuHeight = 440;
      const padding = 8;
      const playbackStripReserve = 56;
      const viewportWidth =
        typeof window !== "undefined" ? window.innerWidth : rect.right;
      const viewportHeight =
        typeof window !== "undefined" ? window.innerHeight : rect.bottom;
      const nextX = clampNumber(
        event.clientX,
        padding,
        Math.max(padding, viewportWidth - menuWidth - padding),
      );
      const maxY = Math.max(
        padding,
        viewportHeight - menuHeight - playbackStripReserve - padding,
      );
      // Prefer below the cursor; flip above when near the playback strip.
      let nextY = event.clientY;
      if (nextY > maxY) {
        nextY = Math.max(padding, event.clientY - menuHeight);
      }
      nextY = clampNumber(nextY, padding, maxY);

      setChannelGainMenu({
        open: true,
        x: nextX,
        y: nextY,
        channelLabel: hit.channel.label,
      });
    },
    [visibleMontageChannelDetails.length],
  );

  const resolveSegmentSelectionChannelScope = useCallback(
    (clientX?: number, clientY?: number): string[] => {
      if (selectedTraceChannels.length > 0) {
        return [...selectedTraceChannels];
      }
      if (
        typeof clientX === "number" &&
        typeof clientY === "number" &&
        canvasRef.current &&
        rendererRef.current
      ) {
        const rect = canvasRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          const hit = rendererRef.current.getMontageChannelHitAtCanvasPoint(
            ((clientX - rect.left) / rect.width) * canvasRef.current.width,
            ((clientY - rect.top) / rect.height) * canvasRef.current.height,
          );
          if (hit?.channel.label) {
            return [hit.channel.label];
          }
        }
      }
      return visibleMontageChannelDetails.map((channel) => channel.label);
    },
    [selectedTraceChannels, visibleMontageChannelDetails],
  );

  const resolveRectangularChannelScope = useCallback(
    (startClientY: number, endClientY: number): string[] => {
      if (!canvasRef.current || !rendererRef.current) {
        return visibleMontageChannelDetails.map((channel) => channel.label);
      }
      const rect = canvasRef.current.getBoundingClientRect();
      if (rect.height <= 0) {
        return visibleMontageChannelDetails.map((channel) => channel.label);
      }
      const displayTop = Math.min(startClientY, endClientY) - rect.top;
      const displayBottom = Math.max(startClientY, endClientY) - rect.top;
      const labels = rendererRef.current.getMontageChannelLabelsInDisplayYRange(
        displayTop,
        displayBottom,
        rect.height,
      );
      if (labels.length === 0) {
        return resolveSegmentSelectionChannelScope(undefined, startClientY);
      }
      if (selectedTraceChannels.length > 0) {
        const intersected = intersectChannelLabelLists(labels, selectedTraceChannels);
        return intersected.length > 0 ? intersected : labels;
      }
      return labels;
    },
    [
      resolveSegmentSelectionChannelScope,
      selectedTraceChannels,
      visibleMontageChannelDetails,
    ],
  );

  const getSegmentSelectionOverlayStyle = useCallback(
    (
      channelLabels: string[],
      leftPx: number,
      widthPx: number,
      verticalOverride?: { top: number; height: number },
    ): {
      left: string;
      width: string;
      top: string;
      height: string;
    } => {
      if (verticalOverride) {
        return {
          left: `${leftPx}px`,
          width: `${widthPx}px`,
          top: `${verticalOverride.top}px`,
          height: `${verticalOverride.height}px`,
        };
      }
      if (!canvasRef.current || !rendererRef.current) {
        return {
          left: `${leftPx}px`,
          width: `${widthPx}px`,
          top: "0",
          height: "100%",
        };
      }
      const rect = canvasRef.current.getBoundingClientRect();
      const verticalBounds = rendererRef.current.getSelectionOverlayBoundsDisplayCss(
        rect.height,
        channelLabels,
      );
      return {
        left: `${leftPx}px`,
        width: `${widthPx}px`,
        top: `${verticalBounds.top}px`,
        height: `${verticalBounds.height}px`,
      };
    },
    [],
  );

  const stopLockedTraceMouseNavigation = useCallback(() => {
    if (lockedTraceMouseNavTimerRef.current !== null) {
      window.clearInterval(lockedTraceMouseNavTimerRef.current);
      lockedTraceMouseNavTimerRef.current = null;
    }
    lockedTraceMouseNavDirectionRef.current = null;
    lockedTraceMouseNavStepRef.current = 0;
  }, []);

  const stepLockedTraceMouseNavigation = useCallback(() => {
    const direction = lockedTraceMouseNavDirectionRef.current;
    const stepSeconds = lockedTraceMouseNavStepRef.current;
    if (!direction || stepSeconds <= 0) return;
    const navigationBaseTime = resolveEEGViewerRequestedTime({
      currentTime: currentTimeRef.current,
      pendingSeekTime: pendingPausedSeekTimeRef.current,
    });
    jumpToViewerTime(navigationBaseTime + direction * stepSeconds, {
      source: "locked-trace-mouse-nav",
      direction: direction > 0 ? "forward" : "backward",
      stepSeconds,
    });
  }, [jumpToViewerTime]);

  const startLockedTraceMouseNavigation = useCallback(
    (event: MouseEvent): boolean => {
      if (!lockedTraceFocusLabel || event.button !== 0) return false;
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        return false;
      }
      if (!canvasRef.current || !currentConfig) return false;
      const rect = canvasRef.current.getBoundingClientRect();
      if (rect.width <= 0) return false;
      const distanceFromCenter = event.clientX - rect.left - rect.width / 2;
      const deadZonePx = Math.max(80, rect.width * 0.08);
      if (Math.abs(distanceFromCenter) < deadZonePx) return false;

      const direction: 1 | -1 = distanceFromCenter > 0 ? 1 : -1;
      const normalizedDistance = Math.min(
        1,
        (Math.abs(distanceFromCenter) - deadZonePx) /
          Math.max(1, rect.width / 2 - deadZonePx),
      );
      const pageSeconds = Math.max(1, currentConfig.time_scale || 10);
      const stepSeconds =
        normalizedDistance > 0.72
          ? pageSeconds
          : normalizedDistance > 0.36
            ? pageSeconds * 0.5
            : Math.max(0.5, pageSeconds * 0.1);

      event.preventDefault();
      event.stopPropagation();
      stopLockedTraceMouseNavigation();
      lockedTraceMouseNavSuppressClickRef.current = true;
      lockedTraceMouseNavDirectionRef.current = direction;
      lockedTraceMouseNavStepRef.current = stepSeconds;
      stepLockedTraceMouseNavigation();
      lockedTraceMouseNavTimerRef.current = window.setInterval(
        stepLockedTraceMouseNavigation,
        420,
      );
      return true;
    },
    [
      currentConfig,
      lockedTraceFocusLabel,
      stepLockedTraceMouseNavigation,
      stopLockedTraceMouseNavigation,
    ],
  );

  const createSelectionFromCanvasRange = useCallback(
    (
      startClientX: number,
      endClientX: number,
      createdFrom: EEGSelection["createdFrom"],
      startClientY?: number,
      endClientY?: number,
      rectangular = false,
    ) => {
      if (!canvasRef.current || !currentConfig) return null;
      const rect = canvasRef.current.getBoundingClientRect();
      if (rect.width <= 0) return null;
      const startX = clampNumber(Math.min(startClientX, endClientX) - rect.left, 0, rect.width);
      const endX = clampNumber(Math.max(startClientX, endClientX) - rect.left, 0, rect.width);
      const canvasWidth = Math.max(1, canvasRef.current.width);
      const renderer = rendererRef.current;
      const startTime = clampNumber(
        currentTime +
          (renderer
            ? renderer.canvasXToRelativeSeconds((startX / rect.width) * canvasWidth)
            : (startX / rect.width) * currentConfig.time_scale),
        0,
        Math.max(recordingDuration || 0, currentTime + currentConfig.time_scale),
      );
      const endTime = clampNumber(
        currentTime +
          (renderer
            ? renderer.canvasXToRelativeSeconds((endX / rect.width) * canvasWidth)
            : (endX / rect.width) * currentConfig.time_scale),
        startTime,
        Math.max(recordingDuration || 0, currentTime + currentConfig.time_scale),
      );
      const samplingRate = eegData?.sampling_rate || 256;
      const channels =
        rectangular &&
        typeof startClientY === "number" &&
        typeof endClientY === "number"
          ? resolveRectangularChannelScope(startClientY, endClientY)
          : resolveSegmentSelectionChannelScope(startClientX, startClientY);

      let boundedStart = Number(startTime.toFixed(3));
      let boundedEnd = Number(endTime.toFixed(3));
      if (rectangular && boundedEnd - boundedStart < 0.05) {
        const pad = (0.05 - (boundedEnd - boundedStart)) / 2;
        boundedStart = Number(Math.max(0, boundedStart - pad).toFixed(3));
        boundedEnd = Number((boundedEnd + pad).toFixed(3));
      }

      return {
        recordingId: normalizedCurrentRecordingId,
        startTime: boundedStart,
        endTime: boundedEnd,
        startSample: Math.max(0, Math.round(boundedStart * samplingRate)),
        endSample: Math.max(0, Math.round(boundedEnd * samplingRate)),
        channels,
        montageId: currentConfig.montage_id,
        createdFrom,
        captureMode: rectangular ? "rectangular" : "time-montage",
      } satisfies EEGSelection;
    },
    [
      currentConfig,
      currentTime,
      eegData?.sampling_rate,
      normalizedCurrentRecordingId,
      recordingDuration,
      resolveRectangularChannelScope,
      resolveSegmentSelectionChannelScope,
    ],
  );

  useEffect(() => {
    if (sharedSegmentRestoreAttemptedRef.current) {
      return;
    }
    if (!currentConfig || typeof window === "undefined") {
      return;
    }
    if (eegSelection) {
      sharedSegmentRestoreAttemptedRef.current = true;
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const rawStart = Number(params.get("segmentStart"));
    const rawEnd = Number(params.get("segmentEnd"));
    if (
      !Number.isFinite(rawStart) ||
      !Number.isFinite(rawEnd) ||
      rawEnd <= rawStart
    ) {
      sharedSegmentRestoreAttemptedRef.current = true;
      return;
    }
    const samplingRate = eegData?.sampling_rate || 256;
    const channels = (params.get("segmentChannels") || "")
      .split(",")
      .map((channel) => channel.trim())
      .filter(Boolean);
    const restoredSelection: EEGSelection = {
      recordingId: normalizedCurrentRecordingId,
      startTime: Number(rawStart.toFixed(3)),
      endTime: Number(rawEnd.toFixed(3)),
      startSample: Math.max(0, Math.round(rawStart * samplingRate)),
      endSample: Math.max(0, Math.round(rawEnd * samplingRate)),
      channels:
        channels.length > 0
          ? channels
          : selectedTraceChannels.length > 0
            ? selectedTraceChannels
            : visibleMontageChannelDetails.map((channel) => channel.label),
      montageId: currentConfig.montage_id,
      createdFrom: "context-menu",
    };
    sharedSegmentRestoreAttemptedRef.current = true;
    setEegSelection(restoredSelection);
    setSegmentInspectorStatus(
      t("ui.eegViewer.segmentInspector.status.sharedRestored"),
    );
    const nextTimeScale = Math.max(
      5,
      Math.ceil(restoredSelection.endTime - restoredSelection.startTime),
    );
    setCurrentConfig((previous) =>
      previous
        ? normalizeViewerConfig({ ...previous, time_scale: nextTimeScale })
        : previous,
    );
    setDataWindowStart(Math.max(0, restoredSelection.startTime));
  }, [
    currentConfig,
    eegData?.sampling_rate,
    eegSelection,
    normalizedCurrentRecordingId,
    normalizeViewerConfig,
    selectedTraceChannels,
    t,
    visibleMontageChannelDetails,
  ]);

  const handleWaveformChannelWheelScroll = useCallback(
    (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) return;
      if (totalMontageChannels <= visibleChannelCount) return;
      event.preventDefault();
      const delta = event.deltaY > 0 ? 1 : -1;
      setChannelScrollOffset((current) =>
        clampNumber(current + delta, 0, maxChannelScrollOffset),
      );
    },
    [maxChannelScrollOffset, totalMontageChannels, visibleChannelCount],
  );

  const updateChannelScrollOffsetFromPointer = useCallback(
    (clientY: number, pointerOffsetY: number) => {
      const track = channelScrollbarTrackRef.current;
      if (!track || maxChannelScrollOffset <= 0) return;
      const rect = track.getBoundingClientRect();
      if (rect.height <= 0) return;
      const thumbHeightPx =
        (channelScrollThumbHeightPercent / 100) * rect.height;
      const maxThumbTop = Math.max(0, rect.height - thumbHeightPx);
      const nextThumbTop = clampNumber(
        clientY - rect.top - pointerOffsetY,
        0,
        maxThumbTop,
      );
      const ratio = maxThumbTop > 0 ? nextThumbTop / maxThumbTop : 0;
      const nextOffset = Math.round(ratio * maxChannelScrollOffset);
      setChannelScrollOffset(
        clampNumber(nextOffset, 0, maxChannelScrollOffset),
      );
    },
    [channelScrollThumbHeightPercent, maxChannelScrollOffset],
  );

  const handleChannelScrollbarMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const track = channelScrollbarTrackRef.current;
      if (!track || maxChannelScrollOffset <= 0) return;
      event.preventDefault();
      event.stopPropagation();
      const rect = track.getBoundingClientRect();
      const thumbHeightPx =
        (channelScrollThumbHeightPercent / 100) * rect.height;
      const thumbTopPx = (channelScrollThumbTopPercent / 100) * rect.height;
      const thumbBottomPx = thumbTopPx + thumbHeightPx;
      const pointerY = event.clientY - rect.top;
      const isInsideThumb = pointerY >= thumbTopPx && pointerY <= thumbBottomPx;
      const pointerOffsetY = isInsideThumb
        ? pointerY - thumbTopPx
        : thumbHeightPx / 2;

      channelScrollbarDragStateRef.current = {
        active: true,
        pointerOffsetY,
      };

      updateChannelScrollOffsetFromPointer(event.clientY, pointerOffsetY);

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!channelScrollbarDragStateRef.current.active) return;
        updateChannelScrollOffsetFromPointer(
          moveEvent.clientY,
          channelScrollbarDragStateRef.current.pointerOffsetY,
        );
      };
      const onMouseUp = () => {
        channelScrollbarDragStateRef.current.active = false;
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [
      channelScrollThumbHeightPercent,
      channelScrollThumbTopPercent,
      maxChannelScrollOffset,
      updateChannelScrollOffsetFromPointer,
    ],
  );

  const inspectPointerRuntimeRef = useRef({
    calipersActive,
    createSelectionFromCanvasRange,
    crosshairEnabled,
    handleWaveformCanvasChannelSelection,
    handleWaveformCanvasContextMenu,
    handleWaveformChannelWheelScroll,
    handleWaveformCtrlClickPin,
    lockedTraceFocusLabel,
    mapAnnotationsForRenderer,
    pinnedCursorTime,
    renderAnnotations,
    resolveRectangularChannelScope,
    resolveSegmentSelectionChannelScope,
    startLockedTraceMouseNavigation,
    stopLockedTraceMouseNavigation,
    timeScaleSeconds: currentConfig?.time_scale || 10,
  });
  inspectPointerRuntimeRef.current = {
    calipersActive,
    createSelectionFromCanvasRange,
    crosshairEnabled,
    handleWaveformCanvasChannelSelection,
    handleWaveformCanvasContextMenu,
    handleWaveformChannelWheelScroll,
    handleWaveformCtrlClickPin,
    lockedTraceFocusLabel,
    mapAnnotationsForRenderer,
    pinnedCursorTime,
    renderAnnotations,
    resolveRectangularChannelScope,
    resolveSegmentSelectionChannelScope,
    startLockedTraceMouseNavigation,
    stopLockedTraceMouseNavigation,
    timeScaleSeconds: currentConfig?.time_scale || 10,
  };

  useEffect(() => {
    if (!rendererReady || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const host = canvas.parentElement;
    const releaseSelectionPointerCapture = (pointerId: number | null) => {
      if (pointerId == null) return;
      try {
        if (canvas.hasPointerCapture(pointerId)) {
          canvas.releasePointerCapture(pointerId);
        }
      } catch {
        /* capture already released */
      }
    };
    const resetSelectionDrag = (hasDragged = false) => {
      const pointerId = eegSelectionDragRef.current.pointerId;
      eegSelectionDragRef.current = {
        active: false,
        pointerId: null,
        startClientX: 0,
        startClientY: 0,
        hasDragged,
        channelLabels: [],
        rectangular: false,
      };
      releaseSelectionPointerCapture(pointerId);
    };
    const onCanvasClick = (event: MouseEvent) => {
      if (lockedTraceMouseNavSuppressClickRef.current) {
        lockedTraceMouseNavSuppressClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (pagePlayheadDragRef.current.suppressClick) {
        pagePlayheadDragRef.current.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (eegSelectionDragRef.current.hasDragged) {
        eegSelectionDragRef.current.hasDragged = false;
        return;
      }
      inspectPointerRuntimeRef.current.handleWaveformCtrlClickPin(event);
      inspectPointerRuntimeRef.current.handleWaveformCanvasChannelSelection(
        event,
      );
    };
    const onCanvasMouseDown = (event: MouseEvent) => {
      if (eegSelectionDragRef.current.active) {
        return;
      }
      if (
        inspectPointerRuntimeRef.current.startLockedTraceMouseNavigation(event)
      ) {
        return;
      }
    };
    const updateSelectionDragFromClientPoint = (
      clientX: number,
      clientY: number,
      shiftKey: boolean,
    ) => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const dragState = eegSelectionDragRef.current;
      if (!dragState.active) return;
      const rectangular = shouldUseRectangularSegmentSelection({
        shiftKey,
        startClientY: dragState.startClientY,
        currentClientY: clientY,
        alreadyRectangular: dragState.rectangular,
      });
      const deltaX = Math.abs(clientX - dragState.startClientX);
      const deltaY = Math.abs(clientY - dragState.startClientY);
      if (
        deltaX >= SEGMENT_SELECTION_DRAG_PX ||
        deltaY >= SEGMENT_SELECTION_DRAG_PX
      ) {
        eegSelectionDragRef.current.hasDragged = true;
      }
      eegSelectionDragRef.current.rectangular = rectangular;
      const nextCurrentX = clampNumber(clientX - rect.left, 0, rect.width);
      const nextCurrentY = clampNumber(clientY - rect.top, 0, rect.height);
      const nextChannelLabels = rectangular
        ? inspectPointerRuntimeRef.current.resolveRectangularChannelScope(
            dragState.startClientY,
            clientY,
          )
        : dragState.channelLabels;
      eegSelectionDragRef.current.channelLabels = nextChannelLabels;
      setEegSelectionDraft((current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          currentX: nextCurrentX,
          currentY: nextCurrentY,
          channelLabels: nextChannelLabels,
          rectangular,
        };
      });
    };
    const finishSelectionDrag = (clientX: number, clientY: number) => {
      if (!eegSelectionDragRef.current.active) return;
      const dragState = eegSelectionDragRef.current;
      resetSelectionDrag(dragState.hasDragged);
      setEegSelectionDraft(null);
      if (!dragState.hasDragged) return;
      const nextSelection =
        inspectPointerRuntimeRef.current.createSelectionFromCanvasRange(
          dragState.startClientX,
          clientX,
          "drag",
          dragState.startClientY,
          clientY,
          dragState.rectangular,
        );
      if (
        nextSelection &&
        nextSelection.endTime - nextSelection.startTime >= 0.05
      ) {
        setEegSelection(nextSelection);
        setSegmentInspectorStatus(null);
      }
    };
    const onWindowPointerMove = (event: PointerEvent) => {
      if (!eegSelectionDragRef.current.active) return;
      updateSelectionDragFromClientPoint(
        event.clientX,
        event.clientY,
        event.shiftKey,
      );
    };
    const onWindowPointerUp = (event: PointerEvent) => {
      inspectPointerRuntimeRef.current.stopLockedTraceMouseNavigation();
      if (!eegSelectionDragRef.current.active) return;
      if (
        eegSelectionDragRef.current.pointerId != null &&
        event.pointerId !== eegSelectionDragRef.current.pointerId
      ) {
        return;
      }
      window.removeEventListener("pointermove", onWindowPointerMove);
      window.removeEventListener("pointerup", onWindowPointerUp);
      window.removeEventListener("pointercancel", onWindowPointerUp);
      updateSelectionDragFromClientPoint(
        event.clientX,
        event.clientY,
        event.shiftKey,
      );
      finishSelectionDrag(event.clientX, event.clientY);
    };
    const onCanvasPointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || event.ctrlKey || event.metaKey) {
        return;
      }
      if (inspectPointerRuntimeRef.current.calipersActive) {
        return;
      }
      const eventTarget = event.target as HTMLElement | null;
      if (
        eventTarget?.closest(
          "button, input, select, textarea, [role='button'], .eeg-channel-gain-menu, .eeg-channel-scrollbar, .eeg-segment-inspector",
        )
      ) {
        return;
      }
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0) return;
      const canvasX =
        ((event.clientX - rect.left) / rect.width) * canvas.width;
      const timeScale =
        inspectPointerRuntimeRef.current.timeScaleSeconds || 10;
      const plotLeft =
        rendererRef.current?.getViewportMetrics().plotLeft ?? 0;

      // Trackit reading-line drag takes priority over segment selection.
      if (
        isPointerNearPagePlayhead({
          canvasX,
          offsetSeconds: pagePlayheadOffsetRef.current,
          timeScaleSeconds: timeScale,
          canvasWidth: canvas.width,
          plotLeft,
          slopPx: PAGE_PLAYHEAD_HIT_SLOP_PX,
        })
      ) {
        event.preventDefault();
        event.stopPropagation();
        pagePlayheadDragRef.current = {
          active: true,
          pointerId: event.pointerId,
          suppressClick: true,
        };
        canvas.style.cursor = "col-resize";
        const onPlayheadMove = (moveEvent: PointerEvent) => {
          if (!pagePlayheadDragRef.current.active) return;
          if (
            pagePlayheadDragRef.current.pointerId != null &&
            moveEvent.pointerId !== pagePlayheadDragRef.current.pointerId
          ) {
            return;
          }
          const moveRect = canvas.getBoundingClientRect();
          if (moveRect.width <= 0) return;
          const moveX =
            ((moveEvent.clientX - moveRect.left) / moveRect.width) *
            canvas.width;
          const nextOffset = canvasXToPagePlayheadOffsetSeconds({
            canvasX: moveX,
            timeScaleSeconds:
              inspectPointerRuntimeRef.current.timeScaleSeconds || 10,
            canvasWidth: canvas.width,
            plotLeft,
          });
          pagePlayheadOffsetRef.current = nextOffset;
          setPagePlayheadOffsetSeconds(nextOffset);
          if (!rendererRef.current) return;
          rendererRef.current.setPagePlayheadOffsetSeconds(nextOffset);
          rendererRef.current.setCurrentTime(currentTimeRef.current);
          if (!isPlayingRef.current) {
            rendererRef.current.render(
              inspectPointerRuntimeRef.current.mapAnnotationsForRenderer(
                inspectPointerRuntimeRef.current.renderAnnotations,
              ),
            );
          }
        };
        const onPlayheadUp = (upEvent: PointerEvent) => {
          if (
            pagePlayheadDragRef.current.pointerId != null &&
            upEvent.pointerId !== pagePlayheadDragRef.current.pointerId
          ) {
            return;
          }
          pagePlayheadDragRef.current = {
            active: false,
            pointerId: null,
            suppressClick: true,
          };
          canvas.style.cursor = "";
          window.removeEventListener("pointermove", onPlayheadMove);
          window.removeEventListener("pointerup", onPlayheadUp);
          window.removeEventListener("pointercancel", onPlayheadUp);
        };
        window.addEventListener("pointermove", onPlayheadMove);
        window.addEventListener("pointerup", onPlayheadUp);
        window.addEventListener("pointercancel", onPlayheadUp);
        try {
          canvas.setPointerCapture(event.pointerId);
        } catch {
          /* optional */
        }
        return;
      }

      if (
        inspectPointerRuntimeRef.current.lockedTraceFocusLabel &&
        !event.shiftKey &&
        !event.altKey
      ) {
        return;
      }
      const runtime = inspectPointerRuntimeRef.current;
      const rectangular = shouldUseRectangularSegmentSelection({
        shiftKey: event.shiftKey,
        startClientY: event.clientY,
        currentClientY: event.clientY,
        alreadyRectangular: false,
      });
      const anchorX = clampNumber(event.clientX - rect.left, 0, rect.width);
      const anchorY = clampNumber(event.clientY - rect.top, 0, rect.height);
      const initialChannelLabels = rectangular
        ? runtime.resolveRectangularChannelScope(event.clientY, event.clientY)
        : runtime.resolveSegmentSelectionChannelScope(
            event.clientX,
            event.clientY,
          );
      event.preventDefault();
      eegSelectionDragRef.current = {
        active: true,
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        hasDragged: false,
        channelLabels: initialChannelLabels,
        rectangular,
      };
      setEegSelectionDraft({
        anchorX,
        currentX: anchorX,
        anchorY,
        currentY: anchorY,
        channelLabels: initialChannelLabels,
        rectangular,
      });
      try {
        canvas.setPointerCapture(event.pointerId);
      } catch {
        /* Pointer capture is optional; window listeners still track the drag. */
      }
      window.addEventListener("pointermove", onWindowPointerMove);
      window.addEventListener("pointerup", onWindowPointerUp);
      window.addEventListener("pointercancel", onWindowPointerUp);
    };
    const onCanvasMouseMove = (event: MouseEvent) => {
      if (
        eegSelectionDragRef.current.active ||
        pagePlayheadDragRef.current.active
      ) {
        return;
      }
      const rect = canvasRef.current?.getBoundingClientRect();
      if (
        canvasRef.current &&
        rect &&
        rect.width > 0 &&
        rect.height > 0
      ) {
        const canvasX =
          ((event.clientX - rect.left) / rect.width) * canvasRef.current.width;
        const nearPlayhead = isPointerNearPagePlayhead({
          canvasX,
          offsetSeconds: pagePlayheadOffsetRef.current,
          timeScaleSeconds:
            inspectPointerRuntimeRef.current.timeScaleSeconds || 10,
          canvasWidth: canvasRef.current.width,
          plotLeft: rendererRef.current?.getViewportMetrics().plotLeft ?? 0,
          slopPx: PAGE_PLAYHEAD_HIT_SLOP_PX,
        });
        canvas.style.cursor = nearPlayhead ? "col-resize" : "";
      }
      if (!rendererRef.current || !canvasRef.current || !inspectPointerRuntimeRef.current.crosshairEnabled)
        return;
      if (!rect || rect.width <= 0 || rect.height <= 0) return;
      const canvasX =
        ((event.clientX - rect.left) / rect.width) * canvasRef.current.width;
      const canvasY =
        ((event.clientY - rect.top) / rect.height) * canvasRef.current.height;
      setCrosshairCanvasPosition({ x: canvasX, y: canvasY });
      rendererRef.current.setCrosshairPosition(canvasX, canvasY);
      rendererRef.current.setPinnedCursorTime(
        inspectPointerRuntimeRef.current.pinnedCursorTime,
      );
      rendererRef.current.setPagePlayheadOffsetSeconds(
        pagePlayheadOffsetRef.current,
      );
      rendererRef.current.setCurrentTime(currentTimeRef.current);
      // During playback the rAF tick already renders every frame; skip extra render
      if (!isPlayingRef.current) {
        rendererRef.current.render(
          inspectPointerRuntimeRef.current.mapAnnotationsForRenderer(
            inspectPointerRuntimeRef.current.renderAnnotations,
          ),
        );
      }
    };
    const onCanvasMouseLeave = () => {
      if (
        eegSelectionDragRef.current.active ||
        pagePlayheadDragRef.current.active
      ) {
        return;
      }
      canvas.style.cursor = "";
      if (!rendererRef.current) return;
      // Always clear the crosshair tooltip when the cursor leaves the canvas.
      // Without this, the readout box stays frozen at the last cursor position.
      setCrosshairCanvasPosition(null);
      rendererRef.current.clearCrosshair();
      rendererRef.current.setPinnedCursorTime(
        inspectPointerRuntimeRef.current.pinnedCursorTime,
      );
      rendererRef.current.setPagePlayheadOffsetSeconds(
        pagePlayheadOffsetRef.current,
      );
      rendererRef.current.setCurrentTime(currentTimeRef.current);
      // During playback the rAF tick already renders every frame; skip extra render
      if (!isPlayingRef.current) {
        rendererRef.current.render(
          inspectPointerRuntimeRef.current.mapAnnotationsForRenderer(
            inspectPointerRuntimeRef.current.renderAnnotations,
          ),
        );
      }
    };
    const onCanvasMouseUp = () => {
      inspectPointerRuntimeRef.current.stopLockedTraceMouseNavigation();
    };
    const onWindowBlur = () => {
      inspectPointerRuntimeRef.current.stopLockedTraceMouseNavigation();
    };
    const onCanvasContextMenu = (event: MouseEvent) => {
      inspectPointerRuntimeRef.current.handleWaveformCanvasContextMenu(event);
    };
    const onCanvasWheel = (event: WheelEvent) => {
      inspectPointerRuntimeRef.current.handleWaveformChannelWheelScroll(event);
    };
    canvas.addEventListener("click", onCanvasClick);
    const pointerDownTarget = host ?? canvas;
    pointerDownTarget.addEventListener("pointerdown", onCanvasPointerDown, true);
    canvas.addEventListener("mousedown", onCanvasMouseDown);
    canvas.addEventListener("mousemove", onCanvasMouseMove);
    window.addEventListener("mouseup", onCanvasMouseUp);
    window.addEventListener("blur", onWindowBlur);
    canvas.addEventListener("mouseleave", onCanvasMouseLeave);
    canvas.addEventListener("contextmenu", onCanvasContextMenu);
    canvas.addEventListener("wheel", onCanvasWheel, {
      passive: false,
    });
    return () => {
      window.removeEventListener("pointermove", onWindowPointerMove);
      window.removeEventListener("pointerup", onWindowPointerUp);
      window.removeEventListener("pointercancel", onWindowPointerUp);
      resetSelectionDrag();
      canvas.removeEventListener("click", onCanvasClick);
      pointerDownTarget.removeEventListener(
        "pointerdown",
        onCanvasPointerDown,
        true,
      );
      canvas.removeEventListener("mousedown", onCanvasMouseDown);
      canvas.removeEventListener("mousemove", onCanvasMouseMove);
      window.removeEventListener("mouseup", onCanvasMouseUp);
      window.removeEventListener("blur", onWindowBlur);
      canvas.removeEventListener("mouseleave", onCanvasMouseLeave);
      canvas.removeEventListener("contextmenu", onCanvasContextMenu);
      canvas.removeEventListener("wheel", onCanvasWheel);
    };
  }, [canvasMounted, rendererReady]);

  const getAnnotationChannels = resolveAnnotationChannels;

  const selectedMontagePairIndexes = useMemo(() => {
    if (!selectedTraceChannels.length) return [] as number[];
    const indexByLabel = new Map(
      montageChannelDetails.map((channel) => [channel.label, channel.index]),
    );
    const indexes = selectedTraceChannels
      .map((label) => indexByLabel.get(label))
      .filter((value): value is number => typeof value === "number");
    return Array.from(new Set(indexes)).sort((a, b) => a - b);
  }, [montageChannelDetails, selectedTraceChannels]);
  selectedMontagePairIndexesRef.current = selectedMontagePairIndexes;

  /**
   * Resolve a raw montage channel name against the available data channel names,
   * matching the same logic used by resolveRendererMontageInputName so that
   * auto-fit can find data even when data channels have an "EEG " prefix or
   * referential suffixes like "-Ref" (e.g. montage "Fp1" → data "Fp1-Ref").
   */
  const resolveAutoFitChannelName = useCallback(
    (rawName: string): string => {
      const dataChannels = eegData?.channels;
      if (!Array.isArray(dataChannels) || dataChannels.length === 0) {
        const upper = rawName.trim().toUpperCase();
        if (/^(EEG|ECG|EMG|EOG)\s/.test(upper)) return upper;
        return `EEG ${upper}`;
      }

      return (
        resolveRecordingChannelName(
          rawName,
          dataChannels,
          recordingChannelMapAliasMaps,
        ) ||
        ( /^(EEG|ECG|EMG|EOG)\s/.test(rawName.trim().toUpperCase())
          ? rawName.trim().toUpperCase()
          : `EEG ${rawName.trim().toUpperCase()}`)
      );
    },
    [eegData?.channels, recordingChannelMapAliasMaps],
  );

  const resolveAutoFitSensitivity = useCallback((): number | null => {
    const current = currentConfigRef.current;
    const renderer = rendererRef.current;
    if (!current || !renderer) return null;
    if (
      !eegData?.columnArrays?.length ||
      !visibleAutoFitMontageChannelDetails.length
    ) {
      return null;
    }

    const viewportMetrics = renderer.getViewportMetrics();
    if (
      viewportMetrics.visibleChannelCount <= 0 ||
      !Number.isFinite(viewportMetrics.channelHeight) ||
      viewportMetrics.channelHeight <= 0
    ) {
      return null;
    }

    // Resolve montage channel names against actual data channel names,
    // matching the renderer's resolveRendererMontageInputName logic
    // (handles "EEG " prefix, "-Ref" suffix, and topography anchor matching).
    const resolvedDetails = visibleAutoFitMontageChannelDetails.map((d) => ({
      ...d,
      positive: resolveAutoFitChannelName(d.positive),
      negative: d.negative ? resolveAutoFitChannelName(d.negative) : "",
    }));

    return calculateAutoFitSensitivityForWindow({
      eegData: {
        channels: eegData.channels,
        columnArrays: eegData.columnArrays,
        numSamples: eegData.numSamples,
        sampling_rate: eegData.sampling_rate,
      },
      montageChannelDetails: resolvedDetails,
      currentTimeSeconds: currentTime,
      dataWindowStartSeconds: dataWindowStart,
      timeScaleSeconds: current.time_scale ?? DEFAULT_EEG_CONFIG.time_scale,
      laneHeightPx: viewportMetrics.channelHeight,
      pixelsPerMm: viewportMetrics.pixelsPerMm || effectivePixelsPerMm,
    });
  }, [
    currentTime,
    dataWindowStart,
    eegData?.channels,
    eegData?.columnArrays,
    eegData?.numSamples,
    eegData?.sampling_rate,
    effectivePixelsPerMm,
    visibleAutoFitMontageChannelDetails,
    resolveAutoFitChannelName,
  ]);

  const applySensitivityToSession = useCallback(
    (nextSensitivity: number): boolean => {
      const current = currentConfigRef.current;
      if (!current) return false;

      const normalizedSensitivity = normalizeSensitivityValue(nextSensitivity);
      const nextConfig = normalizeViewerConfig({
        ...current,
        sensitivity_value: normalizedSensitivity,
      });

      currentConfigRef.current = nextConfig;
      setCurrentConfig(nextConfig);

      if (rendererRef.current) {
        syncRendererCanvas();
        pendingSensitivityRef.current = nextConfig.sensitivity_value;
        rendererRef.current.updateConfig({
          sensitivity: nextConfig.sensitivity_value,
        });
        rendererRef.current.setCurrentTime(currentTime);
        if (!isPlayingRef.current) {
          rendererRef.current.render(
            mapAnnotationsForRenderer(renderAnnotationsRef.current),
          );
        }
      }

      return true;
    },
    [currentTime, mapAnnotationsForRenderer, syncRendererCanvas],
  );

  const handleAutoFitSensitivity = useCallback(
    (mode: "manual" | "initial" = "manual"): boolean => {
      const current = currentConfigRef.current;
      if (!current) return false;

      const nextSensitivity = resolveAutoFitSensitivity();
      if (nextSensitivity === null) {
        if (mode === "manual") {
          setAutoFitFeedback({
            message: "Auto Fit needs visible waveform data before it can run.",
            previousSensitivity: current.sensitivity_value,
            newSensitivity: current.sensitivity_value,
            previousTimeScale: current.time_scale,
            newTimeScale: current.time_scale,
          });
        }
        return false;
      }

      if (mode === "initial") {
        // Keep ACNS/IFCN routine gain (7–10 µV/mm). Coarse auto-fit from a
        // DC/startup transient would flatten clinically useful EEG.
        if (nextSensitivity > 15) {
          return true;
        }
        if (nextSensitivity !== current.sensitivity_value) {
          applySensitivityToSession(nextSensitivity);
        }
        return true;
      }

      if (nextSensitivity !== current.sensitivity_value) {
        const prevSensitivity = current.sensitivity_value;
        const prevTimeScale = current.time_scale;
        handleConfigChange({ sensitivity_value: nextSensitivity });
        setAutoFitFeedback({
          message: `Auto Fit adjusted sensitivity from ${prevSensitivity} to ${nextSensitivity} uV/mm.`,
          previousSensitivity: prevSensitivity,
          newSensitivity: nextSensitivity,
          previousTimeScale: prevTimeScale,
          newTimeScale: prevTimeScale,
        });
      }

      return true;
    },
    [applySensitivityToSession, handleConfigChange, resolveAutoFitSensitivity],
  );

  const handleDismissAutoFit = useCallback(() => {
    setAutoFitFeedback(null);
  }, []);

  const handleRevertAutoFit = useCallback(() => {
    setAutoFitFeedback((prev) => {
      if (!prev) return null;
      const { previousSensitivity, previousTimeScale } = prev;
      handleConfigChange({
        sensitivity_value: previousSensitivity,
        time_scale: previousTimeScale,
      });
      return null;
    });
  }, [handleConfigChange]);

  const handleResetDisplay = useCallback(() => {
    const defaultRowHeightMode: RowHeightMode = "standard";
    const defaultChannelSpacing =
      getChannelSpacingForRowHeightMode(defaultRowHeightMode);

    setWaveformDisplayMode("stacked");
    setProfileChannelSpacing(defaultChannelSpacing);
    setAllowTraceOverflow(DEFAULT_ALLOW_TRACE_OVERFLOW);
    setRefitOnChannelChange(DEFAULT_REFIT_ON_CHANNEL_CHANGE);

    if (rendererRef.current) {
      syncRendererCanvas();
      rendererRef.current.updateConfig({
        displayMode: "stacked",
        channelSpacing: defaultChannelSpacing,
        allowTraceOverflow: DEFAULT_ALLOW_TRACE_OVERFLOW,
      });
      rendererRef.current.setCurrentTime(currentTimeRef.current);
      if (!isPlayingRef.current) {
        rendererRef.current.render(
          mapAnnotationsForRenderer(renderAnnotationsRef.current),
        );
      }
    }

    const nextSensitivity = resolveAutoFitSensitivity();
    const currentSensitivity = currentConfigRef.current?.sensitivity_value;
    if (
      nextSensitivity !== null &&
      currentSensitivity !== undefined &&
      nextSensitivity !== currentSensitivity
    ) {
      handleConfigChange({ sensitivity_value: nextSensitivity });
    }

    toast.info("Reset Display restored stacked clinical defaults.", {
      toastId: "eeg-reset-display",
      autoClose: 2500,
    });
  }, [
    handleConfigChange,
    mapAnnotationsForRenderer,
    resolveAutoFitSensitivity,
    syncRendererCanvas,
  ]);

  useEffect(() => {
    const recordingId = normalizedCurrentRecordingId;
    if (!recordingId) return;
    if (initialAutoFitRecordingIdRef.current === recordingId) return;
    if (!currentConfig || currentConfig.config_id) return;
    if (isFetchingDisplayProfiles && !displayProfilesResponse) return;
    if (displayProfiles.length > 0) return;
    if (!rendererReady) return;

    const session = readEEGViewerSessionReviewConfig(
      toolbarUserId,
      recordingId,
    );
    if (
      session?.sensitivity_value !== undefined ||
      session?.time_scale !== undefined
    ) {
      initialAutoFitRecordingIdRef.current = recordingId;
      return;
    }

    const resolved = handleAutoFitSensitivity("initial");
    if (resolved) {
      initialAutoFitRecordingIdRef.current = recordingId;
    }
  }, [
    currentConfig,
    displayProfiles.length,
    displayProfilesResponse,
    handleAutoFitSensitivity,
    isFetchingDisplayProfiles,
    normalizedCurrentRecordingId,
    rendererReady,
    toolbarUserId,
  ]);

  useEffect(() => {
    const nextKey = visibleChannelRefitKey;
    const shouldTriggerRefit = shouldTriggerRefitOnVisibleChannelChange({
      enabled: refitOnChannelChange,
      displayMode: waveformDisplayMode,
      rendererReady,
      hasCurrentConfig: !!currentConfig,
      hasVisibleWaveformData:
        !!eegData?.columnArrays?.length &&
        visibleAutoFitMontageChannelDetails.length > 0,
      previousKey: lastVisibleChannelRefitKeyRef.current,
      nextKey,
    });

    lastVisibleChannelRefitKeyRef.current = nextKey;

    if (!shouldTriggerRefit) {
      return;
    }

    handleAutoFitSensitivity("initial");
  }, [
    currentConfig,
    eegData?.columnArrays?.length,
    handleAutoFitSensitivity,
    refitOnChannelChange,
    rendererReady,
    visibleAutoFitMontageChannelDetails.length,
    visibleChannelRefitKey,
    waveformDisplayMode,
  ]);

  // EEG-CUX-004: pop-out a dock panel into a new browser window
  const popOutPanel = useCallback((panelKey: DockPanelKey) => {
    const panelLabel = DOCK_PANEL_LABELS[panelKey];
    const storedGeometry = (() => {
      try {
        const raw = sessionStorage.getItem(`eeg-popout-${panelKey}-geometry`);
        return raw
          ? (JSON.parse(raw) as { width: number; height: number })
          : null;
      } catch {
        return null;
      }
    })();
    const width = storedGeometry?.width ?? 1200;
    const height = storedGeometry?.height ?? 700;
    const win = openEegDetachedWindow({
      title: panelLabel,
      panelKey,
      windowName: `eeg-panel-${panelKey}`,
      width,
      height,
    });
    if (!win) {
      // EEG-CUX-004 AC#6: visible warning toast in the main window
      const msg = `Popup blocked. Allow popups for this site to detach "${panelLabel}" to a separate window.`;
      if (popupBlockedTimerRef.current)
        clearTimeout(popupBlockedTimerRef.current);
      setPopupBlockedMessage(msg);
      popupBlockedTimerRef.current = setTimeout(
        () => setPopupBlockedMessage(null),
        7000,
      );
      return;
    }
    poppedOutWindowsRef.current.set(panelKey, win);
    setPoppedOutPanels((prev) => new Set([...prev, panelKey]));
  }, []);

  // EEG-CUX-004: pop a panel back into the dock
  const popInPanel = useCallback((panelKey: DockPanelKey) => {
    const win = poppedOutWindowsRef.current.get(panelKey);
    if (win && !win.closed) win.close();
    poppedOutWindowsRef.current.delete(panelKey);
    setPoppedOutPanels((prev) => {
      const next = new Set(prev);
      next.delete(panelKey);
      return next;
    });
  }, []);

  const handleDetachedSyncMessage = useCallback((msg: EegSyncMessage) => {
    if (msg.type === "pop-in-request" && msg.panelKey) {
      popInPanel(msg.panelKey as DockPanelKey);
    }
  }, [popInPanel]);

  const eventsTrendsDockPanelContent =
    effectiveBottomActiveTab && bottomDockPanelKeys.length > 0 ? (
      suspendClinicalDockRendering ? (
        <div className="eeg-dock-panel-status">
          <Trans ns="features" i18nKey="ui.copy.c037a11658cd" />
        </div>
      ) : poppedOutPanels.has(effectiveBottomActiveTab) ? (
        <div className="eeg-dock-detached-placeholder">
          <span>
            ↗{" "}
            {getDockPanelLabel(effectiveBottomActiveTab, {
              compact: eventsTrendsDockPlacement === "right",
            })}{" "}
            <Trans ns="features" i18nKey="ui.copy.269412b14b8f" />
          </span>
          <button
            type="button"
            className="eeg-dock-popin-link"
            onClick={() => popInPanel(effectiveBottomActiveTab)}
          >
            <Trans ns="features" i18nKey="ui.copy.23c5427e0a2a" />
          </button>
        </div>
      ) : (
        renderDockPanel(effectiveBottomActiveTab)
      )
    ) : null;

  // renderEventsTrendsDock (and the EventsTrendsDockShell it renders) now
  // lives in EEGViewerDeferredChrome.tsx so EventsTrendsDockShell stays out
  // of the eager "feature-eeg-core" chunk — see EEGViewerDeferredChrome.tsx.

  const handleAnalysisDockRailClick = useCallback(
    (panelKey: DockPanelKey) => {
      const existing = poppedOutWindowsRef.current.get(panelKey);
      if (existing && !existing.closed) {
        existing.focus();
        return;
      }

      startDockTransition(() => {
        setDockBottomActiveTab(panelKey);
      });
      popOutPanel(panelKey);
    },
    [popOutPanel, startDockTransition],
  );

  // EEG-CUX-004: close all detached windows on viewer unmount
  useEffect(() => {
    return () => {
      poppedOutWindowsRef.current.forEach((win) => {
        if (!win.closed) win.close();
      });
    };
  }, []);

  // EEG-CUX-005: open/close BroadcastChannel when recordingId changes
  useEffect(() => {
    const prev = eegSyncChannelRef.current;
    if (prev) {
      prev.close();
      eegSyncChannelRef.current = null;
    }
    if (!currentRecordingId) return;
    const ch = createEegSyncChannel(currentRecordingId);
    eegSyncChannelRef.current = ch;
    return () => {
      ch?.close();
      eegSyncChannelRef.current = null;
    };
  }, [currentRecordingId]);

  // EEG-CUX-005: broadcast time-update to detached panels (RAF-throttled, only when panels are popped out)
  const _broadcastTimeUpdate = useMemo(
    () =>
      throttleToAnimationFrame((t: number, pageEnd: number) => {
        eegSyncChannelRef.current?.postMessage({
          type: "time-update",
          timeSeconds: t,
          pageEndSeconds: pageEnd,
        } satisfies EegSyncMessage);
      }),
    [],
  );

  useEffect(() => {
    if (poppedOutPanels.size === 0) return;
    _broadcastTimeUpdate(
      currentTime,
      currentTime + (currentConfig?.time_scale ?? 10),
    );
  }, [
    currentTime,
    poppedOutPanels.size,
    _broadcastTimeUpdate,
    currentConfig?.time_scale,
  ]);

  // EEG-CUX-005: broadcast montage change to detached panels
  useEffect(() => {
    if (poppedOutPanels.size === 0 || !currentConfig?.montage_id) return;
    eegSyncChannelRef.current?.postMessage({
      type: "montage-change",
      montageId: currentConfig.montage_id,
    } satisfies EegSyncMessage);
  }, [currentConfig?.montage_id, poppedOutPanels.size]);

  // EEG-CUX-005: broadcast filter change to detached panels
  useEffect(() => {
    if (poppedOutPanels.size === 0 || !currentConfig) return;
    eegSyncChannelRef.current?.postMessage({
      type: "filter-change",
      highPass: currentConfig.high_pass_filter ?? 0,
      lowPass: currentConfig.low_pass_filter ?? 0,
      notch60hz: currentConfig.notch_filter_60hz ?? false,
      notch50hz: currentConfig.notch_filter_50hz ?? false,
    } satisfies EegSyncMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentConfig?.high_pass_filter,
    currentConfig?.low_pass_filter,
    currentConfig?.notch_filter_60hz,
    currentConfig?.notch_filter_50hz,
    poppedOutPanels.size,
  ]);

  // EEG-CUX-005: broadcast gain change to detached panels
  useEffect(() => {
    if (poppedOutPanels.size === 0 || !currentConfig) return;
    eegSyncChannelRef.current?.postMessage({
      type: "gain-change",
      sensitivityUvPerMm: currentConfig.sensitivity_value ?? 10,
    } satisfies EegSyncMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConfig?.sensitivity_value, poppedOutPanels.size]);


  const getLiveAnnotationTimeBase = useCallback(() => {
    const clockPlayhead = clockSeekRef.current?.playheadRef.current;
    if (typeof clockPlayhead === "number" && Number.isFinite(clockPlayhead)) {
      return Math.max(0, clockPlayhead);
    }

    if (
      typeof currentTimeRef.current === "number" &&
      Number.isFinite(currentTimeRef.current)
    ) {
      return Math.max(0, currentTimeRef.current);
    }

    return Math.max(0, currentTime);
  }, [currentTime]);

  const getAnnotationAnchorTime = useCallback(() => {
    if (
      typeof pinnedCursorTime === "number" &&
      Number.isFinite(pinnedCursorTime)
    ) {
      return Math.max(0, pinnedCursorTime);
    }

    if (
      crosshairEnabled &&
      crosshairCanvasPosition &&
      currentConfig &&
      Number.isFinite(currentConfig.time_scale) &&
      currentConfig.time_scale > 0
    ) {
      const canvas = canvasRef.current;
      if (canvas) {
        const fallbackWidth = canvas.getBoundingClientRect().width;
        const canvasWidth = canvas.width > 0 ? canvas.width : fallbackWidth;
        if (canvasWidth > 0) {
          const baseTime = getLiveAnnotationTimeBase();
          const clampedX = clampNumber(
            crosshairCanvasPosition.x,
            0,
            canvasWidth,
          );
          const relativeSeconds = rendererRef.current
            ? rendererRef.current.canvasXToRelativeSeconds(clampedX)
            : (clampedX / canvasWidth) * currentConfig.time_scale;
          return Math.max(0, baseTime + relativeSeconds);
        }
      }
    }

    return getLiveAnnotationTimeBase();
  }, [
    crosshairCanvasPosition,
    crosshairEnabled,
    currentConfig,
    getLiveAnnotationTimeBase,
    pinnedCursorTime,
  ]);

  const getAnnotationContextSnapshot = useCallback(() => {
    const anchorTime = getAnnotationAnchorTime();
    const channels = [...resolveAnnotationChannels()];
    return { anchorTime, channels };
  }, [getAnnotationAnchorTime, resolveAnnotationChannels]);

  const primeRapidEventDraft = useCallback(
    (
      eventType: RapidEventType,
      snapshot?: {
        anchorTimeSeconds?: number;
        selectedChannels?: string[];
      },
    ) => {
      setRapidEventDraft((current) => ({
        ...current,
        eventType,
        anchorTimeSeconds:
          typeof snapshot?.anchorTimeSeconds === "number" &&
          Number.isFinite(snapshot.anchorTimeSeconds)
            ? Math.max(0, snapshot.anchorTimeSeconds)
            : current.anchorTimeSeconds,
        selectedChannels:
          snapshot?.selectedChannels && snapshot.selectedChannels.length > 0
            ? [...snapshot.selectedChannels]
            : current.selectedChannels,
      }));
    },
    [],
  );

  const submitRapidEvent = useCallback(
    (
      eventType: RapidEventType,
      overrides?: Partial<RapidEventDraft> & {
        reason?: string;
        eventTimeSeconds?: number;
        selectedChannels?: string[];
      },
    ) => {
      const merged: RapidEventDraft = {
        ...rapidEventDraft,
        ...overrides,
        eventType,
      };
      const liveCurrentTime = getAnnotationAnchorTime();
      const eventTimeSeconds =
        typeof overrides?.eventTimeSeconds === "number" &&
        Number.isFinite(overrides.eventTimeSeconds)
          ? Math.max(0, overrides.eventTimeSeconds)
          : typeof merged.anchorTimeSeconds === "number" &&
              Number.isFinite(merged.anchorTimeSeconds)
            ? Math.max(0, merged.anchorTimeSeconds)
            : Math.max(0, liveCurrentTime);
      const selectedChannelsSnapshot =
        overrides?.selectedChannels && overrides.selectedChannels.length > 0
          ? [...overrides.selectedChannels]
          : merged.selectedChannels.length > 0
            ? [...merged.selectedChannels]
            : [...resolveAnnotationChannels()];
      const reason =
        overrides?.reason !== undefined ? overrides.reason : merged.reason;
      const validation = validateRapidEventDraft({
        eventType,
        medicationName: merged.medicationName,
        medicationDose: merged.medicationDose,
        medicationRoute: merged.medicationRoute,
        procedureName: merged.procedureName,
        reason,
        expectedEffect: merged.expectedEffect,
        followUpWindowSeconds: merged.followUpWindowSeconds,
      });
      if (!validation.isValid) {
        setRapidEventStatus(null);
        setRapidEventError(
          validation.error || "Rapid event payload is invalid.",
        );
        return;
      }

      const label = RAPID_EVENT_LABELS[eventType];
      createInterventionMutation.mutate({
        event_type: eventType,
        event_time_seconds: eventTimeSeconds,
        medication_name: validation.normalized.medicationName,
        medication_dose: validation.normalized.medicationDose,
        medication_route: validation.normalized.medicationRoute,
        procedure_name: validation.normalized.procedureName,
        reason: validation.normalized.reason,
        expected_effect: validation.normalized.expectedEffect,
        follow_up_window_seconds: validation.normalized.followUpWindowSeconds,
        legacy_annotation_label: label,
        selected_channels: selectedChannelsSnapshot,
        legacy_annotation_channels: selectedChannelsSnapshot,
      });
    },
    [
      createInterventionMutation,
      getAnnotationAnchorTime,
      rapidEventDraft,
      resolveAnnotationChannels,
    ],
  );

  const submitRapidEventFromDraft = useCallback(() => {
    submitRapidEvent(rapidEventDraft.eventType);
  }, [rapidEventDraft.eventType, submitRapidEvent]);

  const applyTemplateAnnotation = useCallback(() => {
    if (!selectedQuickReviewTemplate || !selectedQuickTemplateDefinition)
      return;

    const requiredFields = new Set(
      selectedQuickTemplateDefinition.requiredFields,
    );
    const structuredValues: Record<string, unknown> = {};
    for (const field of selectedQuickTemplateDefinition.structuredFields) {
      const rawValue = templateFieldValues[field.key];
      const trimmed = (rawValue || "").trim();
      if (field.required && !trimmed) {
        setTemplateFieldError(
          `"${field.label}" is required for this template.`,
        );
        return;
      }
      if (!trimmed) {
        continue;
      }
      if (field.type === "number") {
        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed)) {
          setTemplateFieldError(`"${field.label}" must be a valid number.`);
          return;
        }
        structuredValues[field.key] = parsed;
        continue;
      }
      if (field.type === "boolean") {
        structuredValues[field.key] = trimmed === "true";
        continue;
      }
      structuredValues[field.key] = trimmed;
    }

    const templateNotes = (templateFieldValues.notes || "").trim();
    if (requiredFields.has("notes") && !templateNotes) {
      setTemplateFieldError("Notes are required for this template.");
      return;
    }

    const durationDraftRaw = templateFieldValues.duration_seconds;
    const parsedDuration =
      durationDraftRaw !== undefined && durationDraftRaw !== ""
        ? Number(durationDraftRaw)
        : selectedQuickTemplateDefinition.defaultDurationSeconds;
    const durationSeconds =
      typeof parsedDuration === "number" &&
      Number.isFinite(parsedDuration) &&
      parsedDuration > 0
        ? parsedDuration
        : null;
    if (requiredFields.has("duration_seconds") && durationSeconds === null) {
      setTemplateFieldError("Duration is required and must be greater than 0.");
      return;
    }

    const configuredChannels = selectedQuickTemplateConfiguredChannels;
    const selectedChannels = getAnnotationChannels();
    const channelMode =
      requiredFields.has("channels_affected") || templateChannelMode === "all"
        ? templateChannelMode
        : "selected";
    const effectiveChannels =
      configuredChannels.length > 0
        ? configuredChannels
        : channelMode === "all"
          ? ["all"]
          : selectedChannels;
    if (
      requiredFields.has("channels_affected") &&
      effectiveChannels.length === 0
    ) {
      setTemplateFieldError(
        "At least one channel is required for this template.",
      );
      return;
    }

    const structuredLines = Object.entries(structuredValues).map(
      ([key, value]) => {
        const field =
          selectedQuickTemplateDefinition.structuredFields.find(
            (entry) => entry.key === key,
          ) || null;
        const renderedValue =
          typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
        return `${field?.label || key}: ${renderedValue}`;
      },
    );
    const finalNotes = [templateNotes, ...structuredLines]
      .filter(Boolean)
      .join("\n");

    setTemplateFieldError(null);
    const anchorTime = getAnnotationAnchorTime();

    createAnnotationMutation.mutate({
      annotation_type: selectedQuickTemplateDefinition.annotationType,
      label: selectedQuickTemplateDefinition.annotationLabel,
      start_time: anchorTime,
      ...(durationSeconds !== null
        ? { end_time: anchorTime + durationSeconds }
        : {}),
      channels_affected:
        effectiveChannels.length > 0 ? effectiveChannels : ["all"],
      color: selectedQuickTemplateDefinition.annotationColor,
      ...(finalNotes ? { notes: finalNotes } : {}),
      template_usage: {
        template_id: selectedQuickReviewTemplate.template_id,
        template_name: selectedQuickReviewTemplate.name,
        template_category: selectedQuickReviewTemplate.category,
        required_fields: selectedQuickTemplateDefinition.requiredFields,
        structured_fields: structuredValues,
      },
    });
    setShowAnnotationQuickOpen(false);
    setTemplateFieldValues({});
    setTemplateFieldError(null);
  }, [
    createAnnotationMutation,
    getAnnotationAnchorTime,
    getAnnotationChannels,
    selectedQuickReviewTemplate,
    selectedQuickTemplateDefinition,
    selectedQuickTemplateConfiguredChannels,
    templateChannelMode,
    templateFieldValues,
  ]);

  const updateTemplateFieldValue = useCallback((key: string, value: string) => {
    setTemplateFieldValues((current) => ({ ...current, [key]: value }));
    setTemplateFieldError(null);
  }, []);

  const quickDictionaryMacros = useMemo(
    () =>
      quickAnnotationDictionaries.flatMap((dictionary) =>
        (dictionary.macros || []).map((macro) => ({
          key: `${dictionary.dict_id}:${macro.macro_id}`,
          dictionary,
          macro,
        })),
      ),
    [quickAnnotationDictionaries],
  );

  const applyDictionaryMacroAnnotation = useCallback(() => {
    if (!quickSelectedMacroId) return;
    const selectedMacro = quickDictionaryMacros.find(
      (entry) => entry.key === quickSelectedMacroId,
    );
    if (!selectedMacro) return;

    const phrase = selectedMacro.dictionary.phrases?.find(
      (candidate) => candidate.phrase_id === selectedMacro.macro.phrase_id,
    );
    const annotationLabel = phrase?.phrase_text.trim() || selectedMacro.macro.name;

    const anchorTime = getAnnotationAnchorTime();

    createAnnotationMutation.mutate({
      annotation_type: "dictionary_macro",
      label: annotationLabel,
      start_time: anchorTime,
      channels_affected: getAnnotationChannels(),
      color: "#8b5cf6",
    });
    setShowAnnotationQuickOpen(false);
  }, [
    createAnnotationMutation,
    getAnnotationAnchorTime,
    getAnnotationChannels,
    quickDictionaryMacros,
    quickSelectedMacroId,
  ]);

  const createSegmentAnnotation = useCallback(
    (annotationType: string, label: string, color: string) => {
      if (!eegSelection) return;
      createAnnotationMutation.mutate(
        {
          annotation_type: annotationType,
          label,
          start_time: eegSelection.startTime,
          end_time: eegSelection.endTime,
          channels_affected:
            eegSelection.channels.length > 0 ? eegSelection.channels : ["all"],
          color,
          notes: `Created from Smart Segment Inspector (${eegSelection.startTime.toFixed(2)}-${eegSelection.endTime.toFixed(2)}s).`,
          recording_id: eegSelection.recordingId ?? currentRecordingId ?? null,
        },
        {
          onSuccess: () => {
            toast.success(`${label} annotation created from selected segment.`, {
              toastId: `segment-annotation-${annotationType}`,
            });
          },
          onError: (error) => {
            toast.error(toUserFacingApiError(error).message, {
              toastId: `segment-annotation-${annotationType}-error`,
            });
          },
        },
      );
    },
    [createAnnotationMutation, currentRecordingId, eegSelection],
  );

  const exportSelectedSegment = useCallback(() => {
    if (!eegSelection || !eegSelectionMetrics) return;
    const payload = {
      generatedAt: new Date().toISOString(),
      studyId,
      recordingId: eegSelection.recordingId ?? currentRecordingId,
      selection: eegSelection,
      metrics: eegSelectionMetrics,
      display: {
        montageId: currentConfig?.montage_id ?? null,
        timeScale: currentConfig?.time_scale ?? null,
        sensitivity: currentConfig?.sensitivity_value ?? null,
        playbackTime: currentTimeRef.current,
      },
    };
    const filename = `eeg-segment-${Math.round(eegSelection.startTime * 1000)}-${Math.round(eegSelection.endTime * 1000)}.json`;
    triggerBlobDownload(
      new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      }),
      filename,
    );
    setSegmentInspectorStatus(
      t("ui.eegViewer.segmentInspector.status.exported", { filename }),
    );
    toast.success(
      t("ui.eegViewer.segmentInspector.status.exported", { filename }),
      {
        toastId: "segment-export",
      },
    );
  }, [
    currentConfig?.montage_id,
    currentConfig?.sensitivity_value,
    currentConfig?.time_scale,
    currentRecordingId,
    eegSelection,
    eegSelectionMetrics,
    studyId,
    t,
    triggerBlobDownload,
  ]);

  const shareSelectedSegment = useCallback(async () => {
    if (!eegSelection) return;
    const url = new URL(window.location.href);
    url.searchParams.set("recordingId", eegSelection.recordingId ?? currentRecordingId ?? "");
    url.searchParams.set("segmentStart", eegSelection.startTime.toFixed(3));
    url.searchParams.set("segmentEnd", eegSelection.endTime.toFixed(3));
    if (eegSelection.channels.length > 0) {
      url.searchParams.set("segmentChannels", eegSelection.channels.join(","));
    }
    try {
      await navigator.clipboard.writeText(url.toString());
      setSegmentInspectorStatus(
        t("ui.eegViewer.segmentInspector.status.linkCopied"),
      );
      toast.success(t("ui.eegViewer.segmentInspector.status.linkCopied"), {
        toastId: "segment-share-link",
      });
    } catch {
      setSegmentInspectorStatus(
        t("ui.eegViewer.segmentInspector.status.copyUnavailable"),
      );
      toast.info(url.toString(), {
        toastId: "segment-share-link-fallback",
        autoClose: 8000,
      });
    }
  }, [currentRecordingId, eegSelection, t]);

  const applyDualMontageSide = useCallback(
    (side: "left" | "right") => {
      const targetMontageId =
        side === "left"
          ? dualMontagePreference.left_montage_id
          : dualMontagePreference.right_montage_id;
      if (!targetMontageId) return;
      handleConfigChange({ montage_id: targetMontageId });
      setDualMontageError(null);
      setDualMontageMessage(
        `Applied ${side} dual-montage preset to the current view.`,
      );
    },
    [
      dualMontagePreference.left_montage_id,
      dualMontagePreference.right_montage_id,
      handleConfigChange,
    ],
  );

  const [dualReviewChannels, setDualReviewChannels] = useState<
    DualMontageChannelSeries[]
  >([]);

  useEffect(() => {
    if (!dualCompareDrawerOpen) return;
    setDualReviewChannels(
      loadedChannelsRef.current.map((channel) => ({
        name: channel.name,
        data: channel.data,
      })),
    );
  }, [
    dualCompareDrawerOpen,
    currentTime,
    dataWindowStart,
    isPlaying,
    currentRecordingId,
  ]);

  const resolveDualMontagePairs = useCallback(
    (montageId: string | null) => {
      if (!montageId) return [];
      if (
        Object.prototype.hasOwnProperty.call(viewerMontagePairsById, montageId)
      ) {
        return viewerMontagePairsById[montageId] || [];
      }
      const montage = montages?.find((item) => item.montage_id === montageId);
      return montage?.channel_pairs || [];
    },
    [montages, viewerMontagePairsById],
  );

  const dualLeftPairs = useMemo(
    () => resolveDualMontagePairs(dualMontagePreference.left_montage_id),
    [dualMontagePreference.left_montage_id, resolveDualMontagePairs],
  );
  const dualRightPairs = useMemo(
    () => resolveDualMontagePairs(dualMontagePreference.right_montage_id),
    [dualMontagePreference.right_montage_id, resolveDualMontagePairs],
  );
  const dualLeftMontageName =
    montages?.find(
      (montage) => montage.montage_id === dualMontagePreference.left_montage_id,
    )?.montage_name || "Left montage";
  const dualRightMontageName =
    montages?.find(
      (montage) =>
        montage.montage_id === dualMontagePreference.right_montage_id,
    )?.montage_name || "Right montage";

  const handleDualMontageSeek = useCallback(
    (timeSeconds: number) => {
      jumpToViewerTime(timeSeconds, { source: "dual-montage-review-seek" });
    },
    [jumpToViewerTime],
  );

  // PB-3 rapid event helpers
  const addMedicationEvent = useCallback(() => {
    const snapshot = getAnnotationContextSnapshot();
    primeRapidEventDraft("medication_administered", {
      anchorTimeSeconds: snapshot.anchorTime,
      selectedChannels: snapshot.channels,
    });
    setRapidEventStatus(
      `Medication workflow primed at ${snapshot.anchorTime.toFixed(1)}s. Fill dose/route and click Record Event.`,
    );
    setRapidEventError(null);
  }, [getAnnotationContextSnapshot, primeRapidEventDraft]);

  const addProcedureEvent = useCallback(() => {
    const snapshot = getAnnotationContextSnapshot();
    primeRapidEventDraft("procedure_marker", {
      anchorTimeSeconds: snapshot.anchorTime,
      selectedChannels: snapshot.channels,
    });
    setRapidEventStatus(
      `Procedure workflow primed at ${snapshot.anchorTime.toFixed(1)}s. Fill details and click Record Event.`,
    );
    setRapidEventError(null);
  }, [getAnnotationContextSnapshot, primeRapidEventDraft]);

  const addClinicalNoteEvent = useCallback(() => {
    const existingNote = rapidEventDraft.reason.trim();
    const snapshot = getAnnotationContextSnapshot();
    const liveCurrentTime = snapshot.anchorTime;
    openAnnotationNoteComposer({
      title: `Clinical note @ ${liveCurrentTime.toFixed(1)}s`,
      placeholder: "Enter a short clinical note",
      defaultValue: existingNote,
      requireText: true,
      onCancel: () => {
        primeRapidEventDraft("clinical_note", {
          anchorTimeSeconds: snapshot.anchorTime,
          selectedChannels: snapshot.channels,
        });
        setRapidEventStatus(
          "Clinical note workflow primed. Add context and click Record Event.",
        );
        setRapidEventError(null);
      },
      onSubmit: (note) => {
        const cleaned = note.trim();
        setRapidEventDraft((current) => ({
          ...current,
          eventType: "clinical_note",
          reason: cleaned,
          anchorTimeSeconds: snapshot.anchorTime,
          selectedChannels: [...snapshot.channels],
        }));
        submitRapidEvent("clinical_note", {
          reason: cleaned,
          eventTimeSeconds: liveCurrentTime,
          selectedChannels: snapshot.channels,
        });
      },
    });
  }, [
    getAnnotationContextSnapshot,
    openAnnotationNoteComposer,
    primeRapidEventDraft,
    rapidEventDraft.reason,
    submitRapidEvent,
  ]);

  const createQuickCatalogAnnotation = useCallback(
    (
      type: AnnotationTypeCatalogEntry,
      liveCurrentTime: number,
      selectedChannelsSnapshot: string[],
      notes?: string,
    ) => {
      const defaultDuration =
        typeof type.default_duration_seconds === "number" &&
        Number.isFinite(type.default_duration_seconds) &&
        type.default_duration_seconds > 0
          ? type.default_duration_seconds
          : type.supports_duration
            ? 1
            : null;
      const endTime =
        defaultDuration !== null
          ? liveCurrentTime + defaultDuration
          : undefined;

      const channels =
        selectedChannelsSnapshot.length > 0
          ? [...selectedChannelsSnapshot]
          : [...getAnnotationChannels()];
      const channelScope =
        type.supports_channel_scope === false ||
        channels.length === 0 ||
        channels.includes("all")
          ? "all"
          : "channel_set";

      createAnnotationMutation.mutate({
        recording_id: currentRecordingId || null,
        annotation_type: type.type_code,
        annotation_type_id: type.annotation_type_id,
        label: type.display_name,
        start_time: liveCurrentTime,
        ...(endTime !== undefined ? { end_time: endTime } : {}),
        channel_scope: channelScope,
        channels_affected: channelScope === "all" ? ["all"] : channels,
        color: normalizeHexColor(type.color) || undefined,
        ...(notes ? { notes } : {}),
      });
    },
    [createAnnotationMutation, currentRecordingId, getAnnotationChannels],
  );

  const addQuickActionAnnotation = useCallback(
    (type: AnnotationTypeCatalogEntry) => {
      const snapshot = getAnnotationContextSnapshot();
      const liveCurrentTime = snapshot.anchorTime;
      const mappedRapidEventType = toRapidEventType(type.type_code);

      if (mappedRapidEventType === "medication_administered") {
        addMedicationEvent();
        return;
      }
      if (mappedRapidEventType === "procedure_marker") {
        addProcedureEvent();
        return;
      }
      if (mappedRapidEventType === "clinical_note") {
        addClinicalNoteEvent();
        return;
      }
      if (mappedRapidEventType === "seizure_onset") {
        submitRapidEvent("seizure_onset", {
          eventTimeSeconds: liveCurrentTime,
          selectedChannels: snapshot.channels,
        });
        return;
      }
      if (mappedRapidEventType === "seizure_offset") {
        submitRapidEvent("seizure_offset", {
          eventTimeSeconds: liveCurrentTime,
          selectedChannels: snapshot.channels,
        });
        return;
      }

      if (type.supports_notes) {
        openAnnotationNoteComposer({
          title: `${type.display_name} notes @ ${liveCurrentTime.toFixed(1)}s`,
          placeholder: `Optional note for ${type.display_name}`,
          defaultValue: "",
          onSubmit: (note) => {
            createQuickCatalogAnnotation(
              type,
              liveCurrentTime,
              snapshot.channels,
              note.trim() || undefined,
            );
          },
        });
        return;
      }

      createQuickCatalogAnnotation(type, liveCurrentTime, snapshot.channels);
    },
    [
      addClinicalNoteEvent,
      addMedicationEvent,
      addProcedureEvent,
      createQuickCatalogAnnotation,
      getAnnotationContextSnapshot,
      openAnnotationNoteComposer,
      submitRapidEvent,
    ],
  );

  const {
    isMobileQuickToolbarViewport,
    floatingToolbarPreferences,
    setFloatingToolbarPreferences,
    floatingToolbarMetrics,
    floatingToolbarPopout,
    floatingToolbarRecentAnnotations,
    handleFloatingToolbarRecentAnnotationSelect,
    setFloatingToolbarEnabled,
    handleFloatingToolbarPopOut,
    handleFloatingToolbarPopIn,
    handleClinicalDockOpenFloatingToolbar,
    toggleFloatingToolbarEditMode,
    handleFloatingToolbarActionsChanged,
    floatingToolbarEventSetId,
    floatingToolbarListTypeOptions,
    floatingToolbarCategoryModeOptions,
    floatingToolbarActions,
    renderFloatingAnnotationToolbar,
  } = useEEGFloatingToolbar({
    toolbarUserId,
    recordingId,
    studyId,
    activeEventSetId,
    viewerEventSets,
    viewerEventCatalog,
    activeViewerEventItems,
    annotationListItems,
    annotationTypeByCode,
    annotationTypeCatalog,
    requestedCurrentTime,
    resolveAnnotationDisplayColor,
    resolveAnnotationTypeCategory,
    jumpToViewerTime,
    addQuickActionAnnotation,
    setShowAnnotationQuickOpen,
    setTrendPreviewMessage,
    setTrendPreviewError,
    popupBlockedTimerRef,
    setPopupBlockedMessage,
  });

  const handleClinicalDockOpenMacroPanel = useCallback(() => {
    if (!navigationTimelineAvailable) return;
    const wasCollapsed = macroOverviewCollapsed;
    if (wasCollapsed) {
      setMacroOverviewCollapsed(false);
      writeMacroOverviewCollapsed(false);
    }
    setShowTimeline(true);
    writeNavigationTimeTicksPreference(true);

    const focusMacroOverview = () => {
      const host = navigationTimelineRef.current;
      if (!host) return;
      const stack =
        (host.querySelector(
          ".eeg-navigation-timeline-stack",
        ) as HTMLElement | null) ?? host;
      const minimap =
        (host.querySelector(".waveform-minimap") as HTMLElement | null) ??
        null;
      const targets = [host, stack, minimap].filter(
        (el, index, all): el is HTMLElement =>
          !!el && all.indexOf(el) === index,
      );
      stack.scrollIntoView({ block: "nearest", behavior: "smooth" });
      for (const target of targets) {
        target.classList.add("eeg-macro-overview--flash");
      }
      window.setTimeout(() => {
        for (const target of targets) {
          target.classList.remove("eeg-macro-overview--flash");
        }
      }, 1000);
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        focusMacroOverview();
        // Expanding the long-study strip changes layout; refocus after paint.
        if (wasCollapsed) {
          window.requestAnimationFrame(focusMacroOverview);
        }
      });
    });
  }, [macroOverviewCollapsed, navigationTimelineAvailable]);

  const addSeizureOnset = () => {
    const type = quickActionAnnotationTypes.find(
      (item) => item.type_code === "seizure_onset",
    );
    if (type) addQuickActionAnnotation(type);
  };

  const addSeizureOffset = () => {
    const type = quickActionAnnotationTypes.find(
      (item) => item.type_code === "seizure_offset",
    );
    if (type) addQuickActionAnnotation(type);
  };

  const closeTopOverlay = useCallback(() => {
    if (annotationNoteComposer) {
      closeAnnotationNoteComposer(true);
      return true;
    }
    if (showPrintWaveformDialog) {
      setShowPrintWaveformDialog(false);
      return true;
    }
    if (showKeyboardShortcutsModal) {
      setShowKeyboardShortcutsModal(false);
      return true;
    }
    if (showConfigPanel) {
      setShowConfigPanel(false);
      return true;
    }
    if (showAnnotationQuickOpen) {
      setShowAnnotationQuickOpen(false);
      return true;
    }
    if (showMontageMobileSheet) {
      setShowMontageMobileSheet(false);
      return true;
    }
    if (editingMontagePairIndex !== null) {
      setEditingMontagePairIndex(null);
      return true;
    }
    if (activeCustomizationTool) {
      setActiveCustomizationTool(null);
      return true;
    }
    if (eventMarkerPopoverOpen) {
      setEventMarkerPopoverDismissSignal((value) => value + 1);
      return true;
    }
    if (calipersActive) {
      setCalipersActive(false);
      return true;
    }
    return false;
  }, [
    activeCustomizationTool,
    annotationNoteComposer,
    calipersActive,
    closeAnnotationNoteComposer,
    eventMarkerPopoverOpen,
    editingMontagePairIndex,
    showAnnotationQuickOpen,
    showConfigPanel,
    showKeyboardShortcutsModal,
    showPrintWaveformDialog,
    showMontageMobileSheet,
  ]);

  const stopPlayback = useCallback(() => {
    pendingPausedInspectWindowStartRef.current = null;
    commitPausedHighSpeedInspectHold(null);
    freezePlaybackClock();
    commitIsPlaying(false);
    jumpToViewerTime(0);
  }, [
    commitIsPlaying,
    commitPausedHighSpeedInspectHold,
    freezePlaybackClock,
    jumpToViewerTime,
  ]);

  const applyTimeScaleZoom = useCallback(
    (direction: "in" | "out") => {
      if (!currentConfig) return;
      const currentIndex = VIEWER_TIME_SCALE_STEPS.findIndex(
        (step) => step === currentConfig.time_scale,
      );
      const safeIndex = currentIndex >= 0 ? currentIndex : 1;
      const nextIndex =
        direction === "in"
          ? Math.max(0, safeIndex - 1)
          : Math.min(VIEWER_TIME_SCALE_STEPS.length - 1, safeIndex + 1);
      const nextValue = VIEWER_TIME_SCALE_STEPS[nextIndex];
      if (nextValue === currentConfig.time_scale) return;
      handleConfigChange({ time_scale: nextValue });
    },
    [currentConfig, handleConfigChange],
  );

  const applyChannelScrollDelta = useCallback(
    (delta: number) => {
      setChannelScrollOffset((current) =>
        clampNumber(current + delta, 0, maxChannelScrollOffset),
      );
    },
    [maxChannelScrollOffset],
  );

  const applyGoToTimeInput = useCallback(
    (rawInput: string) => {
      const parsedSeconds = parseViewerClockTime(rawInput);
      if (parsedSeconds === null) {
        setGoToTimeInput(
          formatViewerClockTime(
            resolvePagePlayheadAbsoluteTime({
              viewportStartSeconds: currentTime,
              offsetSeconds: pagePlayheadOffsetRef.current,
              timeScaleSeconds: currentConfig?.time_scale || 10,
            }),
          ),
        );
        return;
      }
      const timeScale = currentConfig?.time_scale || 10;
      const maxAbsolute =
        recordingDuration > 0
          ? Math.max(0, recordingDuration)
          : Number.POSITIVE_INFINITY;
      const absolute = clampNumber(parsedSeconds, 0, maxAbsolute);
      const viewportStart = viewportStartForPlayheadAbsoluteTime({
        absoluteTimeSeconds: absolute,
        offsetSeconds: pagePlayheadOffsetRef.current,
        timeScaleSeconds: timeScale,
        maxViewportStartSeconds:
          recordingDuration > 0
            ? Math.max(0, recordingDuration - timeScale)
            : undefined,
      });
      jumpToViewerTime(viewportStart, { source: "go-to-time-input" });
      setGoToTimeInput(formatViewerClockTime(absolute));
    },
    [
      currentConfig?.time_scale,
      currentTime,
      jumpToViewerTime,
      recordingDuration,
    ],
  );

  const pinCursorAtCurrentTime = useCallback(() => {
    const timeScale = currentConfig?.time_scale || 10;
    const absolute = resolvePagePlayheadAbsoluteTime({
      viewportStartSeconds: currentTime,
      offsetSeconds: pagePlayheadOffsetRef.current,
      timeScaleSeconds: timeScale,
    });
    const maxTime =
      recordingDuration > 0
        ? Math.max(0, recordingDuration)
        : Number.POSITIVE_INFINITY;
    setPinnedCursorTime(clampNumber(absolute, 0, maxTime));
  }, [currentConfig?.time_scale, currentTime, recordingDuration]);

  const toggleCrosshair = useCallback(() => {
    setCrosshairEnabled((current) => {
      const next = !current;
      if (!rendererRef.current) return next;

      if (!next) {
        setCrosshairCanvasPosition(null);
        rendererRef.current.clearCrosshair();
      } else {
        const canvas = canvasRef.current;
        const defaultX = canvas ? Math.max(0, canvas.width / 2) : 0;
        const defaultY = canvas ? Math.max(0, canvas.height / 2) : 0;
        const nextPosition = crosshairCanvasPosition ?? {
          x: defaultX,
          y: defaultY,
        };
        setCrosshairCanvasPosition(nextPosition);
        rendererRef.current.setCrosshairPosition(
          nextPosition.x,
          nextPosition.y,
        );
      }

      rendererRef.current.setPinnedCursorTime(pinnedCursorTime);
      rendererRef.current.setPagePlayheadOffsetSeconds(
        pagePlayheadOffsetRef.current,
      );
      rendererRef.current.setCurrentTime(currentTime);
      // During playback the rAF tick already renders every frame; skip extra render
      if (!isPlayingRef.current) {
        rendererRef.current.render(
          mapAnnotationsForRenderer(renderAnnotations),
        );
      }
      return next;
    });
  }, [
    crosshairCanvasPosition,
    currentTime,
    mapAnnotationsForRenderer,
    pinnedCursorTime,
    renderAnnotations,
  ]);

  const playbackSpeedSliderIndex = useMemo(() => {
    const speed = currentConfig?.playback_speed ?? 1;
    const nearest = getNearestPlaybackSpeedStep(speed);
    return Math.max(
      0,
      PLAYBACK_SPEED_SLIDER_STEPS.findIndex((step) => step === nearest),
    );
  }, [currentConfig?.playback_speed]);

  const applyPlaybackSpeedSliderIndex = useCallback(
    (sliderIndex: number) => {
      if (!currentConfig) return;
      const boundedIndex = clampNumber(
        Math.round(sliderIndex),
        0,
        PLAYBACK_SPEED_SLIDER_STEPS.length - 1,
      );
      const speed = PLAYBACK_SPEED_SLIDER_STEPS[boundedIndex];
      if (speed === currentConfig.playback_speed) return;
      handleConfigChange({ playback_speed: speed });
    },
    [currentConfig, handleConfigChange],
  );

  const currentTimeScaleIndex = useMemo(() => {
    const idx = VIEWER_TIME_SCALE_STEPS.findIndex(
      (step) => step === (currentConfig?.time_scale || 10),
    );
    return idx >= 0 ? idx : 1;
  }, [currentConfig?.time_scale]);
  const canZoomIn = currentTimeScaleIndex > 0;
  const canZoomOut = currentTimeScaleIndex < VIEWER_TIME_SCALE_STEPS.length - 1;
  const finePanStepSeconds = getFinePanStepSeconds(
    currentConfig?.time_scale || 10,
  );
  const pageNavigationStepSeconds = currentConfig?.time_scale || 10;

  const adjustSensitivityWithShortcut = useCallback(
    (direction: "increase" | "decrease") => {
      if (!currentConfig) return;

      const currentValue = currentConfig.sensitivity_value;
      let currentIndex = VIEWER_SENSITIVITY_STEPS.findIndex(
        (step) => step === currentValue,
      );
      if (currentIndex < 0) {
        currentIndex = VIEWER_SENSITIVITY_STEPS.reduce(
          (closestIndex, step, index) =>
            Math.abs(step - currentValue) <
            Math.abs(VIEWER_SENSITIVITY_STEPS[closestIndex] - currentValue)
              ? index
              : closestIndex,
          0,
        );
      }

      const nextIndex =
        direction === "increase"
          ? Math.max(0, currentIndex - 1)
          : Math.min(VIEWER_SENSITIVITY_STEPS.length - 1, currentIndex + 1);

      if (nextIndex === currentIndex) return;
      handleConfigChange({
        sensitivity_value: VIEWER_SENSITIVITY_STEPS[nextIndex],
      });
    },
    [currentConfig, handleConfigChange],
  );

  const cycleMontageWithShortcut = useCallback(
    (direction: 1 | -1) => {
      if (!currentConfig || !montages?.length) return;

      const currentIndex = montages.findIndex(
        (montage) => montage.montage_id === currentConfig.montage_id,
      );
      const startIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex =
        (startIndex + direction + montages.length) % montages.length;
      const nextMontageId = montages[nextIndex]?.montage_id;
      if (!nextMontageId || nextMontageId === currentConfig.montage_id) return;
      handleConfigChange({ montage_id: nextMontageId });
    },
    [currentConfig, handleConfigChange, montages],
  );

  const openAnnotationQuickAddWithShortcut = useCallback(() => {
    setShowAnnotationPanel(true);
    setAnnotationPanelTab("annotate");
    window.setTimeout(() => {
      const targetButton =
        annotationPanelRef.current?.querySelector<HTMLButtonElement>(
          ".annotation-quick-dynamic-btn, .annotation-tool-btn",
        ) || null;
      targetButton?.focus();
    }, 0);
  }, []);

  const openChannelToolsPanel = useCallback(() => {
    setShowAnnotationPanel(true);
    setAnnotationPanelTab("channels");
    const focusChannelToolsSection = (attempt = 0) => {
      const panel = annotationPanelRef.current;
      const section = channelToolsSectionRef.current;
      if (panel && section) {
        section.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });
        channelQuickJumpInputRef.current?.focus();
        if (!allMontageChannelLabels.length) {
          setSelectedTraceActionStatus(null);
          setSelectedTraceActionError(
            "No montage channels available yet. Load/select a montage to enable channel tools.",
          );
        }
        return;
      }

      if (attempt < 10) {
        window.setTimeout(() => focusChannelToolsSection(attempt + 1), 40);
      }
    };

    window.setTimeout(() => focusChannelToolsSection(), 0);
  }, [allMontageChannelLabels.length]);

  const openCalibrationPanel = useCallback(() => {
    setSignalCardVisible(false);
    setDualCompareDrawerOpen(false);
    setReviewToolsDrawerOpen(false);
    setFilterRowOpen(false);
    setConfigPanelTab("calibration");
    setShowConfigPanel(true);
    window.setTimeout(() => {
      const calibrationSection = configCalibrationSectionRef.current;
      calibrationSection?.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
      const firstInput =
        calibrationSection?.querySelector<HTMLInputElement>(
          'input[type="number"]',
        ) || null;
      firstInput?.focus();
    }, 0);
  }, []);

  const focusFirstDualDrawerControl = useCallback(() => {
    window.setTimeout(() => dualDrawerFirstControlRef.current?.focus(), 0);
  }, []);

  const focusFirstReviewDrawerControl = useCallback(() => {
    window.setTimeout(() => reviewDrawerFirstControlRef.current?.focus(), 0);
  }, []);

  const openDualCompareDrawer = useCallback(
    (focusFirstControl?: boolean) => {
      setDualCompareDrawerOpen(true);
      setSignalCardVisible(false);
      setReviewToolsDrawerOpen(false);
      setFilterRowOpen(false);
      setShowConfigPanel(false);
      setShowAnnotationPanel(false);
      if (toolbarAutoHide) {
        setWorkspaceControlsPeekOpen(true);
      }
      if (focusFirstControl) {
        focusFirstDualDrawerControl();
      }
    },
    [focusFirstDualDrawerControl, toolbarAutoHide],
  );

  const toggleDualCompareDrawer = useCallback(() => {
    setSignalCardVisible(false);
    setReviewToolsDrawerOpen(false);
    setFilterRowOpen(false);
    setShowConfigPanel(false);
    setShowAnnotationPanel(false);
    setDualCompareDrawerOpen(true);
    if (toolbarAutoHide) {
      setWorkspaceControlsPeekOpen(true);
    }
  }, [toolbarAutoHide]);

  const openReviewToolsDrawer = useCallback(
    (focusFirstControl?: boolean) => {
      setReviewToolsDrawerOpen(true);
      setSignalCardVisible(false);
      setDualCompareDrawerOpen(false);
      setFilterRowOpen(false);
      if (toolbarAutoHide) {
        setWorkspaceControlsPeekOpen(true);
      }
      if (focusFirstControl) {
        focusFirstReviewDrawerControl();
      }
    },
    [focusFirstReviewDrawerControl, toolbarAutoHide],
  );

  const toggleReviewToolsDrawer = useCallback(() => {
    setSignalCardVisible(false);
    setDualCompareDrawerOpen(false);
    setFilterRowOpen(false);
    setReviewToolsDrawerOpen(true);
    if (toolbarAutoHide) {
      setWorkspaceControlsPeekOpen(true);
    }
  }, [toolbarAutoHide]);

  const activateWorkspaceSection = useCallback(
    (sectionKey: OutputToolbarSectionKey) => {
      setSignalCardVisible(sectionKey === "signal");
      setDualCompareDrawerOpen(sectionKey === "dual");
      setReviewToolsDrawerOpen(sectionKey === "review");
      setFilterRowOpen(sectionKey === "filters");
      if (sectionKey === "dual") {
        setShowAnnotationPanel(false);
      }
      if (sectionKey === "settings") {
        setShowConfigPanel(true);
      } else if (sectionKey !== "review") {
        setShowConfigPanel(false);
      }
      if (toolbarAutoHide) {
        setWorkspaceControlsPeekOpen(true);
      }
    },
    [toolbarAutoHide],
  );

  const shouldHandleViewerShortcutEvent = useCallback(
    (event: KeyboardEvent) => {
      if (activeTab !== "historical") {
        return false;
      }

      const viewerRoot = viewerRootRef.current;
      const activeElement = document.activeElement as HTMLElement | null;
      if (
        !isEEGViewerShortcutFocusWithinViewer(
          viewerRoot,
          activeElement,
          event.target,
        )
      ) {
        return false;
      }

      if (event.key !== "Escape" && hasBlockingModalOpen) {
        return false;
      }

      if (event.repeat) {
        return false;
      }

      return true;
    },
    [activeTab, hasBlockingModalOpen],
  );

  const viewerKeyboardShortcuts = useMemo<KeyboardShortcut[]>(
    () => [
      {
        key: "Escape",
        description: "Close open overlay/modal",
        category: "Viewer",
        allowInInput: true,
        action: () => {
          closeTopOverlay();
        },
      },
      {
        key: "ArrowRight",
        description: "Fine pan right",
        category: "Navigation",
        action: () => navigateByFinePan(1),
      },
      {
        key: "PageDown",
        description: "Advance one page",
        category: "Navigation",
        action: () => navigateByPages(1, 1),
      },
      {
        key: "ArrowLeft",
        description: "Fine pan left",
        category: "Navigation",
        action: () => navigateByFinePan(-1),
      },
      {
        key: "PageUp",
        description: "Go back one page",
        category: "Navigation",
        action: () => navigateByPages(-1, 1),
      },
      {
        key: "Home",
        description: "Go to recording start",
        category: "Navigation",
        action: goToStart,
      },
      {
        key: "ArrowRight",
        shift: true,
        description: "Advance 10 pages",
        category: "Navigation",
        action: () => navigateByPages(1, 10),
      },
      {
        key: "ArrowLeft",
        shift: true,
        description: "Rewind 10 pages",
        category: "Navigation",
        action: () => navigateByPages(-1, 10),
      },
      {
        key: "PageDown",
        shift: true,
        description: "Advance 10 pages",
        category: "Navigation",
        action: () => navigateByPages(1, 10),
      },
      {
        key: "PageUp",
        shift: true,
        description: "Rewind 10 pages",
        category: "Navigation",
        action: () => navigateByPages(-1, 10),
      },
      {
        key: "+",
        shift: true,
        description: "Increase sensitivity",
        category: "Viewer Controls",
        action: () => adjustSensitivityWithShortcut("increase"),
      },
      {
        key: "=",
        description: "Increase sensitivity",
        category: "Viewer Controls",
        action: () => adjustSensitivityWithShortcut("increase"),
      },
      {
        key: "-",
        description: "Decrease sensitivity",
        category: "Viewer Controls",
        action: () => adjustSensitivityWithShortcut("decrease"),
      },
      {
        key: "_",
        shift: true,
        description: "Decrease sensitivity",
        category: "Viewer Controls",
        action: () => adjustSensitivityWithShortcut("decrease"),
      },
      {
        key: "=",
        ctrl: true,
        description: "Zoom in (time scale)",
        category: "Viewer Controls",
        action: () => applyTimeScaleZoom("in"),
      },
      {
        key: "+",
        ctrl: true,
        shift: true,
        description: "Zoom in (time scale)",
        category: "Viewer Controls",
        action: () => applyTimeScaleZoom("in"),
      },
      {
        key: "-",
        ctrl: true,
        description: "Zoom out (time scale)",
        category: "Viewer Controls",
        action: () => applyTimeScaleZoom("out"),
      },
      {
        key: "ArrowUp",
        description: "Scroll channels up",
        category: "Navigation",
        action: () => applyChannelScrollDelta(-1),
      },
      {
        key: "ArrowDown",
        description: "Scroll channels down",
        category: "Navigation",
        action: () => applyChannelScrollDelta(1),
      },
      {
        key: "m",
        description: "Next montage",
        category: "Viewer Controls",
        action: () => cycleMontageWithShortcut(1),
      },
      {
        key: "m",
        shift: true,
        description: "Previous montage",
        category: "Viewer Controls",
        action: () => cycleMontageWithShortcut(-1),
      },
      {
        key: " ",
        code: "Space",
        description: "Play/pause",
        category: "Viewer Controls",
        action: () => setPlaybackRunning(!isPlaybackRunning),
      },
      {
        key: "s",
        description: "Stop playback",
        category: "Viewer Controls",
        action: stopPlayback,
      },
      {
        key: "c",
        description: "Pin cursor at current time",
        category: "Viewer Controls",
        action: pinCursorAtCurrentTime,
      },
      {
        key: "c",
        shift: true,
        description: "Toggle measurement calipers (peak snap, slope, latency)",
        category: "Viewer Controls",
        action: toggleCalipers,
      },
      {
        key: "Delete",
        description: "Clear pinned cursor",
        category: "Viewer Controls",
        action: () => setPinnedCursorTime(null),
      },
      {
        key: "x",
        description: "Toggle crosshair cursor",
        category: "Viewer Controls",
        action: toggleCrosshair,
      },
      {
        key: "r",
        alt: true,
        shift: true,
        description: "Toggle review tools drawer",
        category: "Toolbar",
        action: () => toggleReviewToolsDrawer(),
      },
      {
        key: "d",
        alt: true,
        shift: true,
        description: "Toggle dual compare drawer",
        category: "Toolbar",
        action: () => {
          if (dualCompareDrawerOpen) {
            setDualCompareDrawerOpen(false);
            return;
          }
          openDualCompareDrawer(true);
        },
      },
      {
        key: "o",
        alt: true,
        shift: true,
        description: "Show workspace controls",
        category: "Toolbar",
        action: revealToolbarPeek,
      },
      {
        key: "t",
        alt: true,
        shift: true,
        description: "Toggle toolbar auto-hide",
        category: "Toolbar",
        action: toggleToolbarAutoHide,
      },
      {
        key: "f",
        alt: true,
        shift: true,
        description: "Toggle filter controls",
        category: "Toolbar",
        action: () => {
          if (toolbarAutoHide) {
            setWorkspaceControlsPeekOpen(true);
          }
          setFilterRowOpen((current) => !current);
        },
      },
      {
        key: "c",
        alt: true,
        shift: true,
        description: "Open channel tools",
        category: "Toolbar",
        action: () => {
          openReviewToolsDrawer(true);
          openChannelToolsPanel();
        },
      },
      {
        key: "l",
        alt: true,
        shift: true,
        description: "Open calibration settings",
        category: "Toolbar",
        action: () => {
          openReviewToolsDrawer();
          openCalibrationPanel();
        },
      },
      {
        key: "s",
        alt: true,
        shift: true,
        description: "Open viewer settings",
        category: "Toolbar",
        action: () => {
          setShowConfigPanel(true);
        },
      },
      {
        key: "e",
        alt: true,
        shift: true,
        description: "Run clinician export",
        category: "Toolbar",
        action: () => {
          void handleRunClinicianExport();
        },
      },
      {
        key: "p",
        alt: true,
        shift: true,
        description: "Open print waveform dialog",
        category: "Toolbar",
        action: () => {
          setPrintWaveformError(null);
          setShowPrintWaveformDialog(true);
        },
      },
      {
        key: "g",
        ctrl: true,
        description: "Open Go To Time dialog",
        category: "Navigation",
        action: () => {
          setGoToTimeModalInput(
            formatViewerClockTime(
              resolvePagePlayheadAbsoluteTime({
                viewportStartSeconds: currentTimeRef.current,
                offsetSeconds: pagePlayheadOffsetRef.current,
                timeScaleSeconds:
                  currentConfigRef.current?.time_scale || 10,
              }),
            ),
          );
          setGoToTimeModalOpen(true);
        },
      },
      {
        key: "a",
        ctrl: true,
        description: "Select all channels",
        category: "Channel Selection",
        action: handleSelectAllTraceChannels,
      },
      {
        key: "a",
        meta: true,
        description: "Select all channels",
        category: "Channel Selection",
        action: handleSelectAllTraceChannels,
      },
      {
        key: "a",
        ctrl: true,
        shift: true,
        description: "Clear channel selection",
        category: "Channel Selection",
        action: handleClearAllTraceChannels,
      },
      {
        key: "a",
        meta: true,
        shift: true,
        description: "Clear channel selection",
        category: "Channel Selection",
        action: handleClearAllTraceChannels,
      },
      {
        key: "i",
        ctrl: true,
        description: "Invert channel selection",
        category: "Channel Selection",
        action: handleInvertTraceChannelSelection,
      },
      {
        key: "i",
        meta: true,
        description: "Invert channel selection",
        category: "Channel Selection",
        action: handleInvertTraceChannelSelection,
      },
      {
        key: "v",
        ctrl: true,
        shift: true,
        description: "Select visible channels",
        category: "Channel Selection",
        action: handleSelectVisibleTraceChannels,
      },
      {
        key: "v",
        meta: true,
        shift: true,
        description: "Select visible channels",
        category: "Channel Selection",
        action: handleSelectVisibleTraceChannels,
      },
      {
        key: "j",
        ctrl: true,
        description: "Focus quick jump to channel",
        category: "Channel Selection",
        action: () => {
          setShowAnnotationPanel(true);
          window.setTimeout(() => channelQuickJumpInputRef.current?.focus(), 0);
        },
      },
      {
        key: "j",
        meta: true,
        description: "Focus quick jump to channel",
        category: "Channel Selection",
        action: () => {
          setShowAnnotationPanel(true);
          window.setTimeout(() => channelQuickJumpInputRef.current?.focus(), 0);
        },
      },
      {
        key: "a",
        description: "Open annotation quick-add",
        category: "Viewer Controls",
        action: openAnnotationQuickAddWithShortcut,
      },
      {
        key: "?",
        shift: true,
        description: "Open shortcut help",
        category: "Viewer Controls",
        action: () => setShowKeyboardShortcutsModal(true),
      },
      {
        key: "n",
        description: "Jump to next event",
        category: "Navigation",
        action: jumpToNextEvent,
      },
      {
        key: "p",
        description: "Jump to previous event",
        category: "Navigation",
        action: jumpToPrevEvent,
      },
      {
        key: "p",
        shift: true,
        description: "Toggle polarity (negative-up / positive-up)",
        category: "Viewer Controls",
        action: () =>
          handleConfigChange({
            polarity:
              currentConfig?.polarity === "positive-up"
                ? "negative-up"
                : "positive-up",
          }),
      },
      {
        key: "1",
        code: "Digit1",
        shift: true,
        alt: true,
        description: "Rapid event: seizure onset",
        category: "Viewer Controls",
        action: addSeizureOnset,
      },
      {
        key: "2",
        code: "Digit2",
        shift: true,
        alt: true,
        description: "Rapid event: seizure offset",
        category: "Viewer Controls",
        action: addSeizureOffset,
      },
      {
        key: "3",
        code: "Digit3",
        shift: true,
        alt: true,
        description: "Rapid event: medication",
        category: "Viewer Controls",
        action: addMedicationEvent,
      },
      {
        key: "4",
        code: "Digit4",
        shift: true,
        alt: true,
        description: "Rapid event: procedure",
        category: "Viewer Controls",
        action: addProcedureEvent,
      },
    ],
    [
      addMedicationEvent,
      addProcedureEvent,
      addSeizureOffset,
      addSeizureOnset,
      adjustSensitivityWithShortcut,
      applyChannelScrollDelta,
      applyTimeScaleZoom,
      closeTopOverlay,
      currentConfig?.polarity,
      cycleMontageWithShortcut,
      dualCompareDrawerOpen,
      setCurrentConfig,
      handleRunClinicianExport,
      handleClearAllTraceChannels,
      handleInvertTraceChannelSelection,
      handleSelectAllTraceChannels,
      handleSelectVisibleTraceChannels,
      goToStart,
      jumpToNextEvent,
      jumpToPrevEvent,
      navigateByFinePan,
      navigateByPages,
      openCalibrationPanel,
      openAnnotationQuickAddWithShortcut,
      openChannelToolsPanel,
      openDualCompareDrawer,
      openReviewToolsDrawer,
      pinCursorAtCurrentTime,
      revealToolbarPeek,
      stopPlayback,
      toggleCalipers,
      toggleCrosshair,
      toggleReviewToolsDrawer,
      toggleToolbarAutoHide,
      toolbarAutoHide,
      currentTime,
    ],
  );

  useKeyboardShortcuts({
    shortcuts: viewerKeyboardShortcuts,
    shouldHandleEvent: shouldHandleViewerShortcutEvent,
  });

  useEffect(() => {
    if (activeTab !== "historical") {
      return;
    }
    const viewerRoot = viewerRootRef.current;
    if (!viewerRoot) {
      return;
    }
    const activeElement = document.activeElement as HTMLElement | null;
    if (
      !isEEGViewerShortcutFocusWithinViewer(viewerRoot, activeElement, null)
    ) {
      viewerRoot.focus();
    }
  }, [activeTab]);

  if (!currentConfig) {
    return <EEGPlayableShell detail="Preparing viewer configuration…" />;
  }

  const isResolvingRecordingContext =
    activeTab === "historical" &&
    Boolean(studyId) &&
    (isLoadingStudyDetails ||
      (isFetchingStudyDetails && !studyDetails) ||
      (Boolean(studyDetails) && !currentRecordingId));

  if (isResolvingRecordingContext) {
    return <EEGPlayableShell detail="Resolving recording context…" />;
  }

  // Show loading state for EEG data — only on initial load when no data exists yet.
  // During buffer transitions (slider, playback), keepPreviousData keeps old data visible
  // and the inline waveform overlay shows a subtle spinner instead of replacing the whole page.
  if ((isLoadingEegData || isPendingEegData || isFetchingEegData) && !eegData) {
    return (
      <EEGPlayableShell
        detail={
          <>
            Loading waveform tiles…
            {studyId ? ` ${studyId}` : null}
          </>
        }
      />
    );
  }

  const currentRecording = selectedRecording ?? studyDetails?.recordings?.[0] ?? null;
  const currentProcessingStatus = currentRecording?.processing_status || null;
  const recordingCreatedAtMs = currentRecording?.created_at
    ? new Date(currentRecording.created_at).getTime()
    : null;
  const processingElapsedMinutes =
    recordingCreatedAtMs && Number.isFinite(recordingCreatedAtMs)
      ? Math.max(0, Math.floor((Date.now() - recordingCreatedAtMs) / 60000))
      : null;
  const isProcessingTakingLong =
    processingElapsedMinutes !== null && processingElapsedMinutes >= 30;

  // Show error state for EEG data
  if (eegDataError) {
    const isRecordingNotReady = (eegDataError as any)?.response?.status === 409;
    if (isRecordingNotReady) {
      const isArtifactRepairBlocked = isEegArtifactRepairConflict(
        eegDataError,
        currentProcessingStatus,
      );
      return (
        <div className="eeg-viewer-error">
          <h3>
            <Trans
              ns="features"
              i18nKey={
                isArtifactRepairBlocked
                  ? "ui.copy.e7a4c1b90321"
                  : "ui.copy.1f3573759eeb"
              }
            />
          </h3>
          <p>
            <Trans
              ns="features"
              i18nKey={
                isArtifactRepairBlocked
                  ? "ui.copy.e7a4c1b90322"
                  : "ui.copy.0982a7b2899f"
              }
            />
          </p>
          {currentProcessingStatus && (
            <p>
              <strong>
                <Trans ns="features" i18nKey="ui.copy.02d145b2e93d" />
              </strong>{" "}
              {currentProcessingStatus}
            </p>
          )}
          {!isArtifactRepairBlocked && processingElapsedMinutes !== null && (
            <p>
              <strong>
                <Trans ns="features" i18nKey="ui.copy.1b70515c2b65" />
              </strong>{" "}
              {processingElapsedMinutes}{" "}
              <Trans ns="features" i18nKey="ui.copy.04987fcab72a" />
              {processingElapsedMinutes === 1 ? "" : "s"}
            </p>
          )}
          {!isArtifactRepairBlocked && isProcessingTakingLong && (
            <p>
              <Trans ns="features" i18nKey="ui.copy.9573323bb35b" />
            </p>
          )}
          {!isArtifactRepairBlocked && (
            <button type="button" onClick={() => refetchEegData()}>
              <Trans ns="features" i18nKey="ui.copy.90a6ca1b533e" />
            </button>
          )}
          {(isArtifactRepairBlocked || isProcessingTakingLong) && (
            <div style={{ marginTop: "0.75rem" }}>
              <Link to={`/patients/${patientId}/studies/${studyId}`}>
                <Trans ns="features" i18nKey="ui.copy.4f96fe26853d" />
              </Link>
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="eeg-viewer-error">
        <h3>
          <Trans ns="features" i18nKey="ui.copy.fc74856b849a" />
        </h3>
        <p>
          <Trans ns="features" i18nKey="ui.copy.d766d0e9904b" />
          <code>{studyId}</code>
        </p>
        <p>
          <strong>
            <Trans ns="features" i18nKey="ui.copy.787aa1617c37" />
          </strong>{" "}
          {eegDataError.message}
        </p>
        <details>
          <summary>
            <Trans ns="features" i18nKey="ui.copy.764cb0960c6d" />
          </summary>
          <pre>{JSON.stringify(eegDataError, null, 2)}</pre>
        </details>
        <button onClick={() => refetchEegData()}>
          <Trans ns="features" i18nKey="ui.copy.9f5cd8a2e880" />
        </button>
        <button onClick={() => window.location.reload()}>
          <Trans ns="features" i18nKey="ui.copy.6e41ee3f309a" />
        </button>
      </div>
    );
  }

  // Show error if no EEG data is available
  if (
    !isLoadingEegData &&
    !isPendingEegData &&
    !isFetchingEegData &&
    (!eegData || !eegData.channels)
  ) {
    return (
      <div className="eeg-viewer-error">
        <h3>
          <Trans ns="features" i18nKey="ui.copy.be7d2732ce77" />
        </h3>
        <p>
          <Trans ns="features" i18nKey="ui.copy.c48d161076a5" />
          <code>{studyId}</code>
        </p>
        <p>
          <Trans ns="features" i18nKey="ui.copy.2266cd96b450" />
        </p>
        <ul>
          <li>
            <Trans ns="features" i18nKey="ui.copy.845667a4f0ef" />
          </li>
          <li>
            <Trans ns="features" i18nKey="ui.copy.63c6f9d6c71a" />
          </li>
          <li>
            <Trans ns="features" i18nKey="ui.copy.a469993cd64d" />
          </li>
        </ul>
        <button onClick={() => window.location.reload()}>
          <Trans ns="features" i18nKey="ui.copy.9f5cd8a2e880" />
        </button>
      </div>
    );
  }

  const filtersActive =
    currentConfig.high_pass_filter !== 0.5 ||
    currentConfig.low_pass_filter !== 70 ||
    currentConfig.notch_filter_60hz ||
    (currentConfig.notch_filter_50hz ?? false) ||
    (currentConfig.remove_dc_offset ?? false) ||
    (currentConfig.polarity ?? "negative-up") !== "negative-up" ||
    (currentConfig.channel_color_mode ?? "uniform") !== "uniform" ||
    currentConfig.playback_speed !== 1;

  const handleOpenMontageEditor = async () => {
    const montageId = currentConfig.montage_id;
    if (!montageId) return;

    const confirmed = await dialog.confirm({
      title: "Open montage editor?",
      message:
        "This action will leave the EEG Viewer and navigate to the Montage/Perspective editor. Any unsaved viewer or perspective changes will be lost.",
      confirmText: "Continue to editor",
      cancelText: "Stay in EEG Viewer",
      variant: "warning",
    });
    if (confirmed) {
      navigate(`/montages/editor/${montageId}`);
    }
  };

  const bindToolbarSectionReorder = (
    sectionKey: OutputToolbarSectionKey,
  ) => ({
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    },
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      const source = event.dataTransfer.getData(
        "text/plain",
      ) as OutputToolbarSectionKey;
      if (source) {
        moveOutputToolbarSection(source, sectionKey);
      }
      setDraggingOutputToolbarSection(null);
    },
  });

  const renderOutputToolbarSectionButton = (
    sectionKey: OutputToolbarSectionKey,
  ): React.ReactElement => {
    const baseTitle = "Drag to reorder buttons and left-side cards";
    const wrap = (button: React.ReactNode) => (
      <div
        key={`output-btn-${sectionKey}`}
        className={`eeg-output-reorder-item${
          draggingOutputToolbarSection === sectionKey ? " is-dragging" : ""
        }`}
        draggable={true}
        onDragStart={(event) => {
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", sectionKey);
          setDraggingOutputToolbarSection(sectionKey);
        }}
        {...bindToolbarSectionReorder(sectionKey)}
        onDragEnd={() => setDraggingOutputToolbarSection(null)}
      >
        {button}
      </div>
    );

    if (sectionKey === "clinical") {
      return wrap(
        <button
          type="button"
          className={`eeg-toolbar-filter-toggle${
            showAnnotationPanel ? " active" : ""
          }`}
          onClick={() => {
            setDualCompareDrawerOpen(false);
            setSignalCardVisible(false);
            setReviewToolsDrawerOpen(false);
            setFilterRowOpen(false);
            if (showAnnotationPanel) {
              setShowAnnotationPanel(false);
              return;
            }
            openChannelToolsPanel();
          }}
          aria-pressed={showAnnotationPanel}
          title={`${
            showAnnotationPanel ? "Hide Clinical Dock" : "Show Clinical Dock"
          } (${baseTitle})`}
        >
          Clinical Dock
        </button>,
      );
    }

    if (sectionKey === "review") {
      return wrap(
        <button
          type="button"
          className={`eeg-toolbar-filter-toggle${reviewToolsDrawerOpen ? " active" : ""}`}
          onClick={() => activateWorkspaceSection("review")}
          aria-expanded={reviewToolsDrawerOpen}
          aria-pressed={reviewToolsDrawerOpen}
          title={`Toggle review tools drawer (Alt+Shift+R) (${baseTitle})`}
        >
          <Trans ns="features" i18nKey="ui.copy.1de7ebcbd6c9" />
        </button>,
      );
    }


    if (sectionKey === "signal") {
      return wrap(
        <button
          type="button"
          className={`eeg-toolbar-filter-toggle${signalCardVisible ? " active" : ""}`}
          onClick={() => activateWorkspaceSection("signal")}
          aria-pressed={signalCardVisible}
          title={`${
            signalCardVisible
              ? "Hide Signal Setup card"
              : "Show Signal Setup card"
          } (${baseTitle})`}
        >
          <Trans ns="features" i18nKey="ui.copy.3cc6008d7d9b" />
        </button>,
      );
    }
    if (sectionKey === "dual") {
      return wrap(
        <button
          type="button"
          className={`eeg-toolbar-filter-toggle${dualCompareDrawerOpen ? " active" : ""}`}
          onClick={() => activateWorkspaceSection("dual")}
          aria-expanded={dualCompareDrawerOpen}
          aria-pressed={dualCompareDrawerOpen}
          title={`Toggle Dual Compare panel (Alt+Shift+D) (${baseTitle})`}
        >
          <Trans ns="features" i18nKey="ui.copy.826caf37d691" />
        </button>,
      );
    }
    if (sectionKey === "settings") {
      return wrap(
        <button
          type="button"
          className={`eeg-toolbar-filter-toggle${showConfigPanel ? " active" : ""}`}
          onClick={() => activateWorkspaceSection("settings")}
          aria-label="Settings"
          aria-pressed={showConfigPanel}
          title={`Open EEG viewer settings (Alt+Shift+S) (${baseTitle})`}
        >
          <Trans ns="features" i18nKey="ui.copy.c7f73bb54d92" />
        </button>,
      );
    }

    return wrap(
      <button
        type="button"
        className={`eeg-toolbar-filter-toggle${filterRowOpen ? " active open" : ""}`}
        onClick={() => activateWorkspaceSection("filters")}
        aria-pressed={filterRowOpen}
        aria-expanded={filterRowOpen}
        title={`${
          filtersActive
            ? "Filters active - click to edit"
            : "Show filter controls"
        } (${baseTitle})`}
      >
        <i className="bi bi-sliders2" />
        {" Filters"}
        {filtersActive && <span className="eeg-filter-dot" />}
      </button>,
    );
  };


  const calibrationWarningBanner = calibrationStale ? (
    <div className="viewer-inline-message viewer-inline-message--warning eeg-calibration-warning">
      <span>
        <Trans ns="features" i18nKey="ui.copy.d1f127b52945" />
      </span>
      <div className="eeg-calibration-warning__actions">
        <button
          type="button"
          className="eeg-calibration-warning__btn"
          onClick={() => setShowConfigPanel(true)}
        >
          <Trans ns="features" i18nKey="ui.copy.e29a79fe0c34" />
        </button>
        <button
          type="button"
          className="eeg-calibration-warning__btn eeg-calibration-warning__btn--secondary"
          onClick={markCalibrationReviewed}
        >
          <Trans ns="features" i18nKey="ui.copy.aaffb5dd5006" />
        </button>
      </div>
    </div>
  ) : null;

  // Sprint 4A extraction bag for EEGPlaybackCore / EEGViewerDeferredChrome —
  // see EEGViewerRuntimeContext.tsx. Deliberately permissive (bridges this
  // component's existing closures without a 200-prop refactor); prefer
  // trimming/typing fields as follow-up cleanup touches these call sites.
  const runtimeValue = {
      acceptSmartSuggestion,
      activeDockResize,
      activeViewerEventSet,
      addConsensusReviewer,
      addQuickActionAnnotation,
      allVisibleAnnotationsSelected,
      analysisDockRailPanels,
      annotationBulkActionPending,
      annotationBulkError,
      annotationBulkStatus,
      annotationCatalogErrored,
      annotationCategoryFilter,
      annotationItemRefsMap,
      annotationOverlayOpacity,
      annotationPanelRef,
      annotationPanelTab,
      annotationPanelTabIds,
      annotationPanelTabRefs,
      annotationPanelWidthPx,
      annotationReviewError,
      annotationReviewFilter,
      annotationReviewStatus,
      annotationTypeCatalog,
      applyGoToTimeInput,
      applyTemporaryHighlightToSelectedTraces,
      assignConsensusReviewersMutation,
      availableConsensusReviewerResults,
      bottomDockPanelKeys,
      buildInterventionLabel,
      bulkAnnotationNoteAppend,
      bulkAnnotationTypeId,
      bulkChannelScopeMode,
      bulkDeleteAnnotationsMutation,
      bulkReviewAnnotationsMutation,
      calipersActive,
      canDisableManualConsensusMode,
      canvasHeightRef,
      canvasRef,
      channelGainMenu,
      channelGainMenuRef,
      channelGainOverrides,
      channelQuickJumpInput,
      channelQuickJumpInputRef,
      channelRegionSelection,
      channelScrollbarTrackRef,
      channelScrollOffset,
      channelScrollThumbHeightPercent,
      channelScrollThumbTopPercent,
      channelToolsSectionRef,
      clampNumber,
      closeSegmentInspector,
      clearTemporaryHighlightFromSelectedTraces,
      clearTraceFocusLock,
      clinicalAnnotationPanelVisible,
      clipScissorsDisabledReason,
      clipScissorsModalOpen,
      closeChannelGainMenu,
      consensusActionError,
      consensusActionPending,
      consensusActionStatus,
      consensusAssignedReviewers,
      consensusAssignmentSummary,
      consensusAutoSummary,
      consensusBlindReviewMode,
      consensusDisagreements,
      consensusDisagreementSummary,
      consensusReportSummary,
      consensusResolutionMethod,
      consensusReviewerSearch,
      consensusSectionOpen,
      consensusSelectedReviewers,
      createAnnotationMutation,
      createInterventionMutation,
      createInterventionResponseMutation,
      createSegmentAnnotation,
      currentConfig,
      currentMontage,
      currentPlaybackSpeed,
      currentRecordingId,
      currentTime,
      currentTimeRef,
      dataTransferMetrics,
      deleteAnnotationMutation,
      dockBottomCollapsed,
      dockBottomHeightPx,
      dockDerivedEvents,
      dockRightCollapsed,
      dockRightWidthPx,
      eegData,
      eegDataError,
      eegSelection,
      eegSelectionDraft,
      eegSelectionMetrics,
      effectiveBottomActiveTab,
      effectiveShowNavigationTimeTicks,
      eventMarkerPopoverDismissSignal,
      eventsTrendsDockPanelContent,
      eventsTrendsDockPlacement,
      expandedSpacingChannelLabel,
      exportSelectedSegment,
      finePanStepSeconds,
      floatingToolbarPopout,
      floatingToolbarPreferences,
      formatViewerClockTime,
      getMontageChannelLabel,
      getSegmentSelectionOverlayStyle,
      goToStart,
      goToTimeDisplayValue,
      goToTimeHoverLabel,
      goToTimeInputFocused,
      goToTimeInputRef,
      handleAnalysisDockRailClick,
      handleAnnotationPanelTabKeyDown,
      handleApplyFilterOverrideToSelectedTraces,
      handleAssignConsensusReviewers,
      handleBulkChannelScopeUpdate,
      handleBulkDeleteSelectedAnnotations,
      handleBulkNoteAppend,
      handleBulkReviewSelectedAnnotations,
      handleBulkTypeUpdate,
      handleChannelAutoScaleSelect,
      handleChannelExpandedSpacingSelect,
      handleChannelGainPresetSelect,
      handleChannelHideSelect,
      handleChannelIsolateSelect,
      handleChannelScrollbarMouseDown,
      handleChannelShowAllSelect,
      handleClearAllTraceChannels,
      handleClinicalDockOpenFloatingToolbar,
      handleClinicalDockOpenMacroPanel,
      handleConfigChange,
      handleDeleteViewerMontagePairs,
      handleDisableConsensusMode,
      handleDockResizeStart,
      handleDuplicateViewerMontagePair,
      handleEnableConsensusMode,
      handleFloatingToolbarPopOut,
      handleInsertViewerMontagePair,
      handleInvertTraceChannelSelection,
      handleMarkSelectedTraceChannelsBad,
      handleMoveViewerMontagePair,
      handleOpenViewerMontagePairEditor,
      handleOverwriteViewerMontageDraft,
      handleQuickJumpToChannelLabel,
      handleReloadViewerMontageConflictRemote,
      handleRemoveFilterOverrideFromSelectedTraces,
      handleResetAllGainsSelect,
      handleResetAllPerChannelOverrides,
      handleResetChannelAppearanceSelect,
      handleResetChannelGainSelect,
      handleResetExpandedSpacingSelect,
      handleResolveConsensusDisagreements,
      handleSaveViewerMontageDraft,
      handleSelectAllTraceChannels,
      handleSelectedChannelSpacingToggle,
      handleSelectTraceChannelsByRegion,
      handleSelectVisibleTraceChannels,
      handleTimelineAnnotationClick,
      handleTimelineSeek,
      handleToggleConsensusSection,
      handleToggleNavigationTimeTicks,
      handleToggleTraceFocusLockSelect,
      handleUndoLastSelectedTraceAction,
      handleWaveformAnnotationApprove,
      handleWaveformAnnotationAssignChannels,
      handleWaveformAnnotationAssignSelectedChannels,
      handleWaveformAnnotationEditNotes,
      handleWaveformAnnotationTimingUpdate,
      handleWaveformCanvasContextMenu,
      hasChannelOverflow,
      hasVideo,
      highSpeedDegradationActive,
      hiddenChannels,
      montages,
      hourlyTrendSeries,
      hourlyTrendSeriesError,
      interventionEvents,
      interventionsError,
      interventionTimelineError,
      isApplyingSelectedTraceAction,
      isFetchingConsensusAutoSummary,
      isFetchingConsensusDisagreementSummary,
      isFetchingConsensusReportSummary,
      isFetchingEegData,
      isFetchingHourlyTrendSeries,
      isFetchingInterventions,
      isFetchingInterventionTimeline,
      isFetchingRealtimeTrendSeries,
      isFetchingTrendProfiles,
      isMobileQuickToolbarViewport,
      isPlaybackRunning,
      isPlaying,
      isSavingViewerMontage,
      jumpToNextEvent,
      jumpToPrevEvent,
      jumpToViewerTime,
      lastTraceSelectionRef,
      loadedChannelsRef,
      lockedTraceFocusLabel,
      macroOverviewCollapsed,
      macroOverviewCustomRange,
      macroOverviewZoomMode,
      maxChannelScrollOffset,
      measurementMode,
      montageChannelDetails,
      navigateByFinePan,
      navigateByPages,
      navigationTimelineAvailable,
      navigationTimelineRef,
      nextEventCount,
      openChannelToolsPanel,
      overflowPlaybackSpeedOptions,
      pageNavigationStepSeconds,
      pagePlayheadOffsetSeconds,
      pendingPausedSeekTimeRef,
      pendingReviewCount,
      playbackControlsRef,
      playbackSpeedStripRef,
      playheadRef,
      plotLeftPx: waveformPlotLeftPx,
      popInPanel,
      popOutPanel,
      poppedOutPanels,
      previewTrendProfileMutation,
      previousEventCount,
      primaryPlaybackSpeedOptions,
      queryClient,
      rapidEventDraft,
      rapidEventError,
      rapidEventStatus,
      realtimeTrendSeries,
      realtimeTrendSeriesError,
      recordingDuration,
      recordingStartMs,
      recordSuggestionDecisionMutation,
      refetchHourlyTrendSeries,
      refetchInterventions,
      refetchInterventionTimeline,
      refetchRealtimeTrendSeries,
      refetchTrendProfiles,
      refreshConsensusData,
      rejectSmartSuggestion,
      removeConsensusReviewer,
      renderDockPanel,
      rendererMode,
      rendererRef,
      resolveConsensusDisagreementsMutation,
      resolveEEGViewerRequestedTime,
      responseNotes,
      responseObservedEffect,
      responseSeverityDelta,
      responseWindowSeconds,
      reviewAnnotationMutation,
      reviewedRanges,
      reviewFilteredAnnotationIds,
      reviewFilteredAnnotations,
      rightDockPanelKey,
      rightDockPanelVisible,
      rightDockSummary,
      saveTrendWindowMutation,
      segmentInspectorCollapsed,
      segmentInspectorStatus,
      selectableAnnotationTypes,
      selectedAnnotationIds,
      selectedAnnotationIdSet,
      selectedClipSegmentAnnotation,
      selectedInterventionId,
      selectedInterventionTimeline,
      selectedMontagePairIndexes,
      selectedPendingAnnotationIds,
      selectedRecording,
      selectedTraceActionError,
      selectedTraceActionStatus,
      selectedTraceActionTargets,
      selectedTraceChannelDetails,
      selectedTraceChannels,
      selectedTrendProfileId,
      setActiveCustomizationTool,
      setAnnotationCategoryFilter,
      setAnnotationOverlayError,
      setAnnotationPanelTab,
      setAnnotationReviewError,
      setAnnotationReviewFilter,
      setAnnotationReviewStatus,
      setBulkAnnotationNoteAppend,
      setBulkAnnotationTypeId,
      setBulkChannelScopeMode,
      setCalipersActive,
      setChannelQuickJumpInput,
      setChannelRegionSelection,
      setClipScissorsModalOpen,
      setConfigPanelTab,
      setConsensusBlindReviewMode,
      setConsensusResolutionMethod,
      setConsensusReviewerSearch,
      setCurrentTime,
      setDataWindowStart,
      setDockBottomActiveTab,
      setDockBottomCollapsed,
      setDockRightCollapsed,
      setEegSelection,
      setEventMarkerPopoverOpen,
      setFloatingToolbarEnabled,
      setFloatingToolbarPreferences,
      setGoToTimeInput,
      setGoToTimeInputFocused,
      setMacroOverviewCollapsed,
      setMacroOverviewCustomRange,
      setMacroOverviewZoomMode,
      setPlaybackRunning,
      setRapidEventDraft,
      setResponseNotes,
      setResponseObservedEffect,
      setResponseSeverityDelta,
      setResponseWindowSeconds,
      setSelectedAnnotationIds,
      setSelectedInterventionId,
      setSelectedTraceChannels,
      setSelectedTrendProfileId,
      setSegmentInspectorCollapsed,
      setShowAnnotationPanel,
      setShowAnnotationQuickOpen,
      setShowComparisonView,
      setShowConfigPanel,
      setShowExtendedPlaybackSpeeds,
      setShowMontageMobileSheet,
      setShowVideoPanel,
      setTraceFilterHp,
      setTraceFilterLp,
      setTraceFilterNotch,
      setTraceHighlightColor,
      setTrendEvalActive,
      setTrendEvalIntervalSeconds,
      setTrendLookaheadSeconds,
      setTrendLookbackSeconds,
      setTrendPreviewError,
      setTrendPreviewMessage,
      shareSelectedSegment,
      shouldShowConsensusWorkflow,
      showAnnotationPanel,
      showComparisonView,
      showExtendedPlaybackSpeeds,
      showMacroOverviewZoomControl,
      showNavigationTimeTicksToggle,
      showTimeline,
      showVideoPanel,
      smartAnnotationSuggestions,
      smartSuggestionDrafts,
      smartSuggestionError,
      smartSuggestionStatus,
      startDockTransition,
      startLockedTraceMouseNavigation,
      stopPlayback,
      studyId,
      studyDetails,
      studyVideoWarmup,
      submitInterventionResponse,
      submitRapidEventFromDraft,
      suspendClinicalDockRendering,
      t,
      timelineAnnotations,
      timelineRecordingDuration,
      toggleAnnotationSelection,
      toggleFloatingToolbarEditMode,
      toggleSelectAllVisibleAnnotations,
      toRapidEventType,
      traceFilterHp,
      traceFilterLp,
      traceFilterNotch,
      traceHighlightColor,
      traceSelectionUndoStack,
      trendCorrelationStatus,
      trendEvalActive,
      trendEvalError,
      trendEvalIntervalSeconds,
      trendEvalStatus,
      trendLookaheadSeconds,
      trendLookbackSeconds,
      trendPreviewError,
      trendPreviewMessage,
      trendProfiles,
      trendProfilesError,
      unresolvedConsensusDisagreements,
      updateSmartSuggestionDraft,
      viewerDeferredChromeReady,
      clinicalOverlaysReady: viewerDeferredChromeReady,
      viewerMontageConflict,
      viewerMontageDraftDirty,
      viewerMontageError,
      viewerMontagePairsDraft,
      viewerMontageStatus,
      visibleMontageChannelDetails,
      waveformAnnotationMarkers,
      workspaceTheme,
      } as unknown as EEGViewerRuntimeContextValue;

  return (
    <>
      <EEGViewerRuntimeProvider value={runtimeValue}>
      <div
        ref={viewerRootRef}
        tabIndex={-1}
        aria-label="EEG Viewer"
        className="eeg-viewer eeg-viewer-container"
        data-eeg-paper-theme={workspaceTheme}
        data-eeg-workspace-theme={workspaceTheme}
        data-eeg-workspace-density={workspaceDensity}
      >
        {awaitingFirstWaveformPaint ? (
          <div
            className="eeg-viewer-startup-overlay"
            data-eeg-startup-overlay="true"
            aria-hidden={false}
          >
            <EEGPlayableShell detail="Loading waveform tiles…" />
          </div>
        ) : null}
        <div
          className="eeg-viewer-chrome-zone eeg-viewer-chrome-zone--top eeg-viewer-workspace-peek-zone"
        >
          <Suspense fallback={null}>
            <EEGPatientContextBar
              workspaceControlsHidden={toolbarAutoHide}
              workspaceControlsExpanded={workspaceControlsPinned}
              workspaceControlsLocked={workspaceControlsLocked}
              playbackActive={isPlaybackRunning}
              onToggleWorkspaceControls={togglePatientBarWorkspaceControls}
              recordingDurationSeconds={timelineRecordingDuration}
              liveStatus={viewerLiveStatus}
              montageNotice={montageNotice}
              channelMapNotice={channelMapNotice}
              unifiedNavigationTimelineVisible={showUnifiedNavigationTimeline}
              lockedTraceFocusLabel={lockedTraceFocusLabel}
              onClearTraceFocusLock={clearTraceFocusLock}
            />
          </Suspense>
        </div>

        <MobileReadOnlyBanner />

        {calibrationWarningBanner && !showToolbarControls ? (
          <div className="eeg-calibration-warning-strip">{calibrationWarningBanner}</div>
        ) : null}

        {/* Change Summary Banner - Phase A */}
        {perspectiveState.hasUnappliedChanges && currentConfig && (
          <ChangeSummaryBanner
            changes={calculateConfigDiff(
              currentConfig,
              perspectiveState.draftConfig,
            )}
            isClinicalMode={perspectiveState.isClinicalMode}
            onAcknowledge={() => {
              applyDraftConfig();
              if (perspectiveState.draftConfig) {
                setCurrentConfig(
                  normalizeViewerConfig({
                    ...currentConfig,
                    ...perspectiveState.draftConfig,
                  }),
                );
              }
            }}
            onRevert={discardDraft}
          />
        )}

        {/* UX-004: always-on signal state strip (clinical + research); identity stays in header */}
        <SignalStateStrip
          patientId={patientId}
          studyId={studyId}
          hidePatientStudy
          compact
          isClinicalMode={perspectiveState.isClinicalMode}
          perspectiveName={
            perspectiveState.activePerspective?.perspective_name ?? ""
          }
          perspectiveVersion={
            perspectiveState.activePerspective?.schema_version
          }
          montageName={currentMontage?.montage_name ?? "—"}
          referenceType={currentConfig?.reference_type ?? "average"}
          channelCount={totalMontageChannels}
          highPassFilter={currentConfig?.high_pass_filter ?? 0}
          lowPassFilter={currentConfig?.low_pass_filter ?? 0}
          notchFilter={Boolean(
            currentConfig?.notch_filter_60hz ||
              currentConfig?.notch_filter_50hz,
          )}
          notchFilter50hz={Boolean(
            currentConfig?.notch_filter_50hz &&
              !currentConfig?.notch_filter_60hz,
          )}
          polarity={currentConfig?.polarity ?? "negative-up"}
          gain={currentConfig?.sensitivity_value ?? 10}
          autoScale={false}
          timebase={currentConfig?.time_scale ?? 10}
          horizontalMmPerSec={
            calibrationOverallPass === true && !calibrationStale
              ? calculateHorizontalMmPerSecond(
                  canvasRef.current?.width ?? 0,
                  currentConfig?.time_scale ?? 10,
                  effectivePixelsPerMm,
                )
              : null
          }
          pyramidLevel={resolutionLevel}
          effectiveSamplingRate={Math.round(
            (eegData?.sampling_rate || 256) / 2 ** resolutionLevel,
          )}
          studyType={studyData?.studyType ?? null}
          montageLocked={perspectiveState.montageLocked}
          filtersLocked={perspectiveState.filtersLocked}
          hasUnappliedChanges={perspectiveState.hasUnappliedChanges}
        />

        <div
          className={`eeg-viewer-body${showVideoPanel ? " eeg-viewer-body--with-video" : ""}`}
        >
        {/* Tab Content */}
        {activeTab === "historical" && (
          <div className="eeg-tab-content">
            {/* Control Bar — EEG-UX-005: primary row + collapsible filter row */}
            {showToolbarControls ? (
            <div
              id="eeg-workspace-controls"
              className={`eeg-controls${toolbarAutoHide ? " eeg-controls--auto-hide" : ""} eeg-controls--expanded`}
              onMouseEnter={
                workspaceControlsHoverRevealEnabled
                  ? revealWorkspaceControlsPeek
                  : undefined
              }
              onMouseLeave={
                workspaceControlsHoverRevealEnabled
                  ? scheduleHideWorkspaceControlsPeek
                  : undefined
              }
              onFocusCapture={() => {
                if (toolbarAutoHide) {
                  revealWorkspaceControlsPeek();
                }
              }}
              onBlurCapture={(event) => {
                if (!toolbarAutoHide || workspaceControlsPinned) return;
                const nextFocused = event.relatedTarget as Node | null;
                if (!event.currentTarget.contains(nextFocused)) {
                  scheduleHideWorkspaceControlsPeek();
                }
              }}
            >
                <>
                  {/* Primary row: compact essentials + drawer toggles */}
                  <div
                    className={`eeg-toolbar-row eeg-toolbar-row--primary${filterRowOpen ? " has-filter-row-open" : ""}`}
                  >
                    <div
                      className="eeg-toolbar-left-stack"
                      onDragOver={(event) => {
                        if (!draggingOutputToolbarSection) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                      }}
                    >
                      {signalCardVisible && (
                        <div
                          className={`eeg-toolbar-cluster eeg-toolbar-cluster--signal${
                            draggingOutputToolbarSection === "signal"
                              ? " is-dragging"
                              : ""
                          }`}
                          style={{ order: getLeftCardOrder("signal") }}
                          {...bindToolbarSectionReorder("signal")}
                        >
                          <ToolbarOverflowRow
                            className="eeg-toolbar-cluster__content eeg-toolbar-cluster__content--overflow"
                            ariaLabel="Signal workspace controls"
                          >
                            <div className="control-group" key="montage">
                              <select
                                className="eeg-signal-select eeg-signal-select--montage"
                                aria-label="Montage"
                                value={currentConfig.montage_id ?? ""}
                                onChange={(e) =>
                                  handleConfigChange({
                                    // Preserve "" as explicit No Derivation.
                                    montage_id: e.target.value,
                                  })
                                }
                              >
                                {/* No Derivation passthrough option — always first */}
                                <option value="">
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.b0a914022aa0"
                                  />
                                </option>
                                {/* EEG-CUX-007: render base montages, with variants grouped under each */}
                                {(montages || [])
                                  .filter(
                                    (m) =>
                                      !m.is_variant &&
                                      m.montage_type !== "passthrough",
                                  )
                                  .map((base) => {
                                    const variants = (montages || []).filter(
                                      (m) =>
                                        m.is_variant &&
                                        m.base_montage_id === base.montage_id,
                                    );
                                    if (variants.length === 0) {
                                      return (
                                        <option
                                          key={base.montage_id}
                                          value={base.montage_id}
                                        >
                                          {base.montage_name}
                                        </option>
                                      );
                                    }
                                    return (
                                      <optgroup
                                        key={base.montage_id}
                                        label={base.montage_name}
                                      >
                                        <option value={base.montage_id}>
                                          {base.montage_name}
                                        </option>
                                        {variants.map((v) => (
                                          <option
                                            key={v.variant_id}
                                            value={v.variant_id}
                                          >
                                            {v.montage_name}
                                          </option>
                                        ))}
                                      </optgroup>
                                    );
                                  })}
                              </select>
                              <button
                                type="button"
                                className="eeg-control-link"
                                title="Edit this montage"
                                aria-label="Open montage editor"
                                disabled={!currentConfig.montage_id}
                                onClick={() => void handleOpenMontageEditor()}
                              >
                                <i className="bi bi-pencil-square" />
                              </button>
                            </div>

                            <div className="control-group" key="perspective">
                              <select
                                id="eeg-signal-perspective"
                                className="eeg-signal-select eeg-signal-select--perspective"
                                aria-label="Perspective"
                                value={perspectiveState.activePerspectiveId ?? ""}
                                onChange={(event) => {
                                  const nextPerspective = perspectives.find(
                                    (perspective) =>
                                      perspective.perspective_id ===
                                      event.target.value,
                                  );
                                  if (nextPerspective) {
                                    void handlePerspectiveSwitch(nextPerspective);
                                  }
                                }}
                                title="Apply a saved clinical review perspective"
                              >
                                <option value="">Current / custom</option>
                                {perspectives.map((perspective) => (
                                  <option
                                    key={perspective.perspective_id}
                                    value={perspective.perspective_id}
                                  >
                                    {perspective.perspective_name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="control-group" key="trend-template">
                              <select
                                id="eeg-signal-trend-template"
                                className="eeg-signal-select eeg-signal-select--trend-template"
                                aria-label="Trend template"
                                value={activeViewerTrendTemplateId}
                                onChange={(event) => {
                                  const templateId = event.target.value;
                                  setViewerTrendTemplateOverrideId(templateId);
                                  if (templateId) {
                                    setDockBottomCollapsed(false);
                                    setDockBottomActiveTab("qeeg_trends");
                                  }
                                }}
                                title="Apply the trend layout linked to this review context"
                              >
                                <option value="">No trend template</option>
                                {viewerTrendTemplates.map((template) => (
                                  <option key={template.id} value={template.id}>
                                    {template.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <EEGWaveformLayoutControls
                              key="layout-controls"
                              displayMode={waveformDisplayMode}
                              rowHeightMode={rowHeightMode}
                              allowTraceOverflow={allowTraceOverflow}
                              refitOnChannelChange={refitOnChannelChange}
                              rowHeightDisabled={
                                waveformDisplayMode === "butterfly"
                              }
                              onDisplayModeChange={handleDisplayModeChange}
                              onRowHeightModeChange={handleRowHeightModeChange}
                              onAllowTraceOverflowChange={
                                handleAllowTraceOverflowChange
                              }
                              onRefitOnChannelChange={
                                handleRefitOnChannelChange
                              }
                              onResetDisplay={handleResetDisplay}
                            />

                            <EEGWaveformVisibilityControls
                              key="visibility-controls"
                              currentSensitivity={
                                currentConfig.sensitivity_value
                              }
                              pendingSensitivity={pendingSensitivityRef.current}
                              sensitivitySteps={VIEWER_SENSITIVITY_STEPS}
                              canIncreaseSensitivity={
                                currentConfig.sensitivity_value >
                                VIEWER_SENSITIVITY_STEPS[0]
                              }
                              canDecreaseSensitivity={
                                currentConfig.sensitivity_value <
                                VIEWER_SENSITIVITY_STEPS[
                                  VIEWER_SENSITIVITY_STEPS.length - 1
                                ]
                              }
                              showAutoFit={false}
                              autoFitDisabled={!getCanAutoFitSensitivity()}
                              onIncreaseSensitivity={() =>
                                adjustSensitivityWithShortcut("increase")
                              }
                              onDecreaseSensitivity={() =>
                                adjustSensitivityWithShortcut("decrease")
                              }
                              onSensitivityChange={(nextValue) => {
                                pendingSensitivityRef.current = null;
                                handleConfigChange({
                                  sensitivity_value: nextValue,
                                });
                              }}
                              onAutoFit={() => {
                                void handleAutoFitSensitivity("manual");
                              }}
                            />

                            <div className="control-group" key="timescale">
                              <select
                                className="eeg-signal-select eeg-signal-select--timescale"
                                aria-label="Time scale"
                                value={currentConfig.time_scale}
                                onChange={(e) =>
                                  handleConfigChange({
                                    time_scale: parseInt(e.target.value),
                                  })
                                }
                              >
                                <option value="5">
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.f148d46a71b2"
                                  />
                                </option>
                                <option value="10">
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.ec3776c8f607"
                                  />
                                </option>
                                <option value="15">
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.c245ebc708e0"
                                  />
                                </option>
                                <option value="20">
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.a4611fde1346"
                                  />
                                </option>
                                <option value="30">
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.c81591e40621"
                                  />
                                </option>
                              </select>
                            </div>

                            <EEGWaveformAutoFitControl
                              key="auto-fit"
                              disabled={!getCanAutoFitSensitivity()}
                              onAutoFit={() => {
                                void handleAutoFitSensitivity("manual");
                              }}
                            />
                          </ToolbarOverflowRow>
                        </div>
                      )}

                      {reviewToolsDrawerOpen && (
                        <div
                          className={`eeg-toolbar-cluster eeg-toolbar-cluster--tools${
                            draggingOutputToolbarSection === "review"
                              ? " is-dragging"
                              : ""
                          }`}
                          style={{ order: getLeftCardOrder("review") }}
                          {...bindToolbarSectionReorder("review")}
                        >
                          <ToolbarOverflowRow
                            className="eeg-toolbar-cluster__content eeg-toolbar-cluster__content--overflow"
                            ariaLabel="Review tools controls"
                            itemOrder={reviewToolsItemOrder}
                            onReorder={moveReviewToolsItem}
                          >
                            <button
                              key="measure"
                              type="button"
                              className={`eeg-toolbar-filter-toggle eeg-toolbar-inspect-toggle${
                                calipersActive && measurementMode === "caliper"
                                  ? " active"
                                  : ""
                              }`}
                              onClick={toggleCalipers}
                              aria-label="Measurement calipers"
                              aria-pressed={
                                calipersActive && measurementMode === "caliper"
                              }
                              title={
                                calipersActive && measurementMode === "caliper"
                                  ? "Disable measurement calipers (Shift+C). Drag peak-to-peak; hold Alt to skip snap-to-peak."
                                  : "Measure duration, amplitude, slope, and inter-channel latency (Shift+C). Drag peak-to-peak after enabling. Drag to reorder."
                              }
                            >
                              <i className="bi bi-rulers" aria-hidden="true" />
                              Measure
                            </button>
                            <button
                              key="polarity"
                              type="button"
                              className={`eeg-toolbar-filter-toggle eeg-toolbar-inspect-toggle${
                                calipersActive && measurementMode === "polarity"
                                  ? " active"
                                  : ""
                              }`}
                              onClick={togglePolarityMeasure}
                              aria-label="Polarity measurement"
                              aria-pressed={
                                calipersActive && measurementMode === "polarity"
                              }
                              title={
                                calipersActive && measurementMode === "polarity"
                                  ? "Disable polarity measurement"
                                  : "Measure signed waveform polarity. Drag to reorder."
                              }
                            >
                              N
                            </button>
                            <button
                              key="inspector"
                              type="button"
                              className={`eeg-toolbar-filter-toggle eeg-toolbar-inspect-toggle${
                                !segmentInspectorCollapsed ? " active" : ""
                              }`}
                              onClick={toggleSegmentInspector}
                              aria-label="Smart Segment Inspector"
                              aria-pressed={!segmentInspectorCollapsed}
                              title={
                                segmentInspectorCollapsed
                                  ? "Show Smart Segment Inspector. Drag to reorder."
                                  : "Hide Smart Segment Inspector. Drag to reorder."
                              }
                            >
                              <i
                                className="bi bi-clipboard2-pulse"
                                aria-hidden="true"
                              />
                              Inspector
                            </button>
                            <div
                              className="control-group control-group--channel-review"
                              key="channel-review"
                              role="group"
                              aria-label="Channel visibility and paging controls"
                            >
                              {/* <span className="eeg-review-tools-inline-label">
                                <Trans
                                  ns="features"
                                  i18nKey="ui.copy.18e03e2a37ba"
                                />
                              </span> */}

                              <div className="eeg-channel-manager-anchor">
                                <button
                                  ref={(element) => {
                                    channelManagerTriggerRef.current = element;
                                    reviewDrawerFirstControlRef.current = element;
                                  }}
                                  type="button"
                                  className={`eeg-toolbar-filter-toggle${channelManagerOpen ? " active" : ""}`}
                                  title="Open channel manager"
                                  aria-expanded={channelManagerOpen}
                                  onClick={() => {
                                    // Capture button position BEFORE state update so
                                    // the portal renders at the correct position on
                                    // the very first frame instead of flashing at (0,0)
                                    // and then repositioning after useLayoutEffect.
                                    if (channelManagerTriggerRef.current) {
                                      const rect =
                                        channelManagerTriggerRef.current.getBoundingClientRect();
                                      channelManagerPosRef.current = {
                                        top: rect.bottom + 6,
                                        left: rect.left,
                                      };
                                    }
                                    setChannelManagerOpen((v) => !v);
                                  }}
                                >
                                  <Menu size={14} aria-hidden="true" />
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.babe3050e2e8"
                                  />
                                </button>
                                {channelManagerOpen
                                  ? createPortal(
                                      <div
                                        style={{
                                          position: "fixed",
                                          top: channelManagerPosRef.current.top,
                                          left: channelManagerPosRef.current
                                            .left,
                                          zIndex: 9998,
                                        }}
                                      >
                                        <Suspense fallback={null}>
                                          <ChannelManagerPanel
                                            channels={montageChannelDetails}
                                            hiddenChannels={hiddenChannels}
                                            onHideChannel={hideChannel}
                                            onShowChannel={(label) =>
                                              setHiddenChannels((prev) => {
                                                const next = new Set(prev);
                                                next.delete(label);
                                                return next;
                                              })
                                            }
                                            onIsolateChannels={isolateChannels}
                                            onShowAll={showAllChannels}
                                            onClose={() =>
                                              setChannelManagerOpen(false)
                                            }
                                            onColorChange={(label, color) =>
                                              setProfileChannelColors(
                                                (prev) => ({
                                                  ...prev,
                                                  [label]: color,
                                                }),
                                              )
                                            }
                                            currentMontageName={
                                              currentMontage?.is_variant
                                                ? undefined
                                                : (currentMontage?.montage_name ??
                                                  undefined)
                                            }
                                            onSaveAsVariant={
                                              currentMontage?.montage_id &&
                                              !currentMontage.is_variant
                                                ? async (name) => {
                                                    await createMontageVariant({
                                                      base_montage_id:
                                                        currentMontage.montage_id!,
                                                      name,
                                                      excluded_channels: [
                                                        ...hiddenChannels,
                                                      ],
                                                    });
                                                    await refreshMontages();
                                                  }
                                                : undefined
                                            }
                                          />
                                        </Suspense>
                                      </div>,
                                      document.body,
                                    )
                                  : null}
                              </div>

                              <span
                                className="eeg-channel-range-label eeg-channel-range-label--toolbar"
                                title="Visible montage channels"
                              >
                                <span className="eeg-toolbar-field-label">Ch</span>
                                <span className="eeg-toolbar-field-value">
                                  {visibleChannelStart}–{visibleChannelEnd}
                                  <span className="eeg-channel-range-label__total">
                                    of {totalMontageChannels || 0}
                                  </span>
                                </span>
                              </span>


                              {hasChannelOverflow && (
                                <>
                                  <button
                                    type="button"
                                    className="eeg-toolbar-filter-toggle"
                                    onClick={() => applyChannelScrollDelta(-1)}
                                    disabled={channelScrollOffset <= 0}
                                    title="Scroll visible channel window up"
                                  >
                                    <ArrowUp />
                                    <Trans
                                      ns="features"
                                      i18nKey="ui.copy.2038bdec9210"
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    className="eeg-toolbar-filter-toggle"
                                    onClick={() => applyChannelScrollDelta(1)}
                                    disabled={
                                      channelScrollOffset >=
                                      maxChannelScrollOffset
                                    }
                                    title="Scroll visible channel window down"
                                  >
                                    <ArrowDown />
                                    <Trans
                                      ns="features"
                                      i18nKey="ui.copy.bf93e5ce8bc1"
                                    />
                                  </button>
                                </>
                              )}
                              {selectedTraceChannels.length > 0 && (
                                <button
                                  type="button"
                                  className="eeg-toolbar-filter-toggle active"
                                  onClick={handleClearAllTraceChannels}
                                  title="Clear channel isolation and show all channels"
                                  aria-label="Show all channels"
                                >
                                  <i className="bi bi-view-list" />
                                  {selectedTraceChannels.length === 1
                                    ? "Show all channels"
                                    : `Show all (${selectedTraceChannels.length})`}
                                </button>
                              )}
                              {hiddenChannels.size > 0 && (
                                <button
                                  className="eeg-hidden-channels-badge eeg-hidden-channels-badge--toolbar"
                                  onClick={showAllChannels}
                                  title={`${hiddenChannels.size} channel${hiddenChannels.size === 1 ? " is" : "s are"} hidden — click to restore all`}
                                >
                                  {hiddenChannels.size}{" "}
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.99d72c7fc3e2"
                                  />
                                </button>
                              )}
                              {annotationAudienceStatusChip && (
                                <button
                                  type="button"
                                  className="eeg-toolbar-filter-toggle active"
                                  onClick={handleResetAnnotationCategoryFilter}
                                  title={annotationAudienceStatusChip.title}
                                >
                                  <span className="eeg-filter-dot" />
                                  {annotationAudienceStatusChip.label}
                                </button>
                              )}
                            </div>

                            <label
                              key="hp"
                              className="eeg-toolbar-field"
                            >
                              <span className="eeg-toolbar-field-label">HP</span>
                              <EegFilterFrequencySelect
                                kind="high-pass"
                                value={currentConfig.high_pass_filter}
                                catalog={CLINICAL_HIGH_PASS_HZ}
                                extraValues={dbHighPassHz}
                                ariaLabel="High-pass filter frequency in Hz"
                                title="High-pass (low-cut) filter frequency in Hz"
                                onChange={(value) =>
                                  handleConfigChange({
                                    high_pass_filter: value,
                                  })
                                }
                              />
                              <span className="eeg-toolbar-field-unit">Hz</span>
                            </label>
                            <label
                              key="lp"
                              className="eeg-toolbar-field"
                            >
                              <span className="eeg-toolbar-field-label">LP</span>
                              <EegFilterFrequencySelect
                                kind="low-pass"
                                value={currentConfig.low_pass_filter}
                                catalog={CLINICAL_LOW_PASS_HZ}
                                extraValues={dbLowPassHz}
                                ariaLabel="Low-pass filter frequency in Hz"
                                title="Low-pass (high-cut) filter frequency in Hz"
                                onChange={(value) =>
                                  handleConfigChange({
                                    low_pass_filter: value,
                                  })
                                }
                              />
                              <span className="eeg-toolbar-field-unit">Hz</span>
                            </label>
                            <div
                              key="notch"
                              className="eeg-toolbar-notch-control"
                              role="group"
                              aria-label="Notch filter"
                            >
                              <span className="eeg-toolbar-field-label">Notch</span>
                              <div className="eeg-toolbar-segmented">
                                {(
                                  [
                                    ["off", "Off"],
                                    ["50hz", "50 Hz"],
                                    ["60hz", "60 Hz"],
                                  ] as const
                                ).map(([mode, label]) => {
                                  const active =
                                    getNotchModeFromFlags(
                                      currentConfig.notch_filter_60hz,
                                      currentConfig.notch_filter_50hz,
                                    ) === mode;
                                  return (
                                    <button
                                      key={mode}
                                      type="button"
                                      className={`eeg-toolbar-segmented__btn${
                                        active ? " is-active" : ""
                                      }`}
                                      aria-pressed={active}
                                      title={
                                        mode === "off"
                                          ? "Disable mains notch filter"
                                          : `Apply ${label} mains notch`
                                      }
                                      onClick={() =>
                                        handleConfigChange(
                                          getNotchFlagsForMode(mode),
                                        )
                                      }
                                    >
                                      {label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* --- Moved from Signal Setup: Cursor --- */}
                            <div
                              className="control-group control-group--cursor"
                              key="crosshair"
                              role="group"
                              aria-label="Cursor"
                            >
                              <span className="eeg-toolbar-field-label">Cursor</span>
                              <button
                                type="button"
                                className={`ctrl-step-btn${crosshairEnabled ? " active" : ""}`}
                                onClick={toggleCrosshair}
                                aria-pressed={crosshairEnabled}
                                aria-label="Crosshair cursor"
                                title={
                                  crosshairEnabled
                                    ? "Crosshair enabled. Move mouse over waveform; click to keep reference cursor with Ctrl+Click."
                                    : "Enable live crosshair overlay"
                                }
                              >
                                {crosshairEnabled ? "On" : "Off"}
                              </button>
                            </div>

                            <div
                              className="control-group control-group--calibration"
                              key="calibration"
                              role="group"
                              aria-label="Screen calibration"
                            >
                              <span className="eeg-toolbar-field-label">Cal</span>
                              <span
                                className={`eeg-calibration-badge-inline eeg-calibration-badge-inline--${calibrationStatus}`}
                                title={calibrationBadgeTitle}
                                aria-label={calibrationBadgeTitle}
                              >
                                {calibrationBadgeLabel}
                              </span>
                              <button
                                type="button"
                                className="eeg-toolbar-filter-toggle"
                                onClick={openCalibrationPanel}
                                title="Open screen calibration helper"
                              >
                                <Trans
                                  ns="features"
                                  i18nKey="ui.copy.61feacb17def"
                                />
                              </button>
                            </div>
                            <button
                              key="compare"
                              type="button"
                              className={`eeg-toolbar-filter-toggle${showComparisonView ? " active" : ""}`}
                              onClick={() => setShowComparisonView((v) => !v)}
                              title="Compare two recordings side-by-side, overlay, or difference view"
                            >
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.8d105cf44d39"
                              />
                            </button>
                            <button
                              key="export"
                              type="button"
                              className="eeg-toolbar-filter-toggle"
                              onClick={() => void handleRunClinicianExport()}
                              disabled={clinicianExportBusy}
                              title="Run clinician export with current export settings (Alt+Shift+E)"
                            >
                              {clinicianExportBusy ? "Exporting..." : "Export"}
                            </button>
                            <button
                              key="print"
                              type="button"
                              className="eeg-toolbar-filter-toggle"
                              onClick={() => {
                                setPrintWaveformError(null);
                                setShowPrintWaveformDialog(true);
                              }}
                              title="Open print-ready waveform view (Alt+Shift+P)"
                            >
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.5b221e9c2a45"
                              />
                            </button>

                            {/* EEG-UX-004: Event count badge */}
                            {(() => {
                              const total =
                                dockDerivedEvents.spikes.length +
                                dockDerivedEvents.seizures.length;
                              if (total === 0) return null;
                              const variant =
                                dockDerivedEvents.seizures.length > 0
                                  ? "red"
                                  : total > 5
                                    ? "orange"
                                    : "blue";
                              return (
                                <span
                                  key="event-count"
                                  className={`eeg-event-count-badge eeg-event-count-badge--${variant}`}
                                  title={`${total} detected event${total !== 1 ? "s" : ""}`}
                                >
                                  {total}
                                </span>
                              );
                            })()}
                          </ToolbarOverflowRow>
                        </div>
                      )}

                      {/* Filter row: infrequently-changed controls (collapsible) */}
                      {filterRowOpen && (
                        <div
                          className={`eeg-toolbar-row eeg-toolbar-row--filters${
                            draggingOutputToolbarSection === "filters"
                              ? " is-dragging"
                              : ""
                          }`}
                          style={{ order: getLeftCardOrder("filters") }}
                          {...bindToolbarSectionReorder("filters")}
                        >
                      <span className="eeg-toolbar-cluster__label eeg-toolbar-row__label eeg-toolbar-row__label--hidden">
                            <Trans
                              ns="features"
                              i18nKey="ui.copy.96e578211aa2"
                            />
                          </span>
                          <div className="control-group">
                            <EegFilterFrequencySelect
                              kind="high-pass"
                              value={currentConfig.high_pass_filter}
                              catalog={CLINICAL_HIGH_PASS_HZ}
                              extraValues={dbHighPassHz}
                              ariaLabel="High-pass filter"
                              onChange={(value) =>
                                handleConfigChange({
                                  high_pass_filter: value,
                                })
                              }
                            />
                            <span>
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.97aaf5bb4065"
                              />
                            </span>
                          </div>

                          <div className="control-group">
                            <EegFilterFrequencySelect
                              kind="low-pass"
                              value={currentConfig.low_pass_filter}
                              catalog={CLINICAL_LOW_PASS_HZ}
                              extraValues={dbLowPassHz}
                              ariaLabel="Low-pass filter"
                              onChange={(value) =>
                                handleConfigChange({
                                  low_pass_filter: value,
                                })
                              }
                            />
                            <span>
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.97aaf5bb4065"
                              />
                            </span>
                          </div>

                          <div className="control-group">
                            <select
                              aria-label="Notch filter"
                              value={getNotchModeFromFlags(
                                currentConfig.notch_filter_60hz,
                                currentConfig.notch_filter_50hz,
                              )}
                              onChange={(e) =>
                                handleConfigChange(
                                  getNotchFlagsForMode(
                                    e.target.value as NotchMode,
                                  ),
                                )
                              }
                              className="small-input"
                              title="Select one notch mode (Off, 50Hz, or 60Hz)."
                            >
                              <option value="off">
                                <Trans
                                  ns="features"
                                  i18nKey="ui.copy.e3de5ab0ca4c"
                                />
                              </option>
                              <option value="60hz">
                                <Trans
                                  ns="features"
                                  i18nKey="ui.copy.2d31147257df"
                                />
                              </option>
                              <option value="50hz">
                                <Trans
                                  ns="features"
                                  i18nKey="ui.copy.61560309e78a"
                                />
                              </option>
                            </select>
                          </div>

                          <div className="control-group">
                            <label className="control-inline-check">
                              <input
                                type="checkbox"
                                checked={
                                  currentConfig.remove_dc_offset ?? false
                                }
                                onChange={(e) =>
                                  handleConfigChange({
                                    remove_dc_offset: e.target.checked,
                                  })
                                }
                              />
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.fe357414193b"
                              />
                            </label>
                          </div>

                          <div className="control-group">
                            <button
                              type="button"
                              className="ctrl-step-btn"
                              aria-label="Toggle polarity"
                              onClick={() =>
                                handleConfigChange({
                                  polarity:
                                    currentConfig.polarity === "positive-up"
                                      ? "negative-up"
                                      : "positive-up",
                                })
                              }
                              title={`Polarity convention (${currentConfig.polarity === "positive-up" ? "Positive Up" : "Negative Up"})`}
                            >
                              {currentConfig.polarity === "positive-up"
                                ? "+↑"
                                : "−↑"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="eeg-toolbar-cluster eeg-toolbar-cluster--actions">
                      <ToolbarOverflowRow
                        className="eeg-toolbar-cluster__content eeg-toolbar-cluster__content--overflow"
                        ariaLabel="Viewer action controls"
                      >
                          {outputToolbarSectionOrder
                            .filter((section) =>
                              [
                                "clinical",
                                "signal",
                                "dual",
                                "filters",
                                "settings",
                                "review",
                              ].includes(section),
                            )
                            .map(renderOutputToolbarSectionButton)}
                          <button
                            key="keyboard-shortcuts"
                            type="button"
                            className="eeg-toolbar-filter-toggle eeg-toolbar-shortcuts-btn"
                            onClick={() => setShowKeyboardShortcutsModal(true)}
                            aria-label="Open keyboard shortcuts"
                            title="Keyboard shortcuts (?)"
                          >
                            ?
                          </button>
                      </ToolbarOverflowRow>
                    </div>
                  </div>
                  {calibrationWarningBanner && (
                    <div className="eeg-toolbar-status-row eeg-toolbar-status-row--expanded">
                      {calibrationWarningBanner}
                    </div>
                  )}
                  {autoFitFeedback && (
                    <div className="eeg-toolbar-status-row eeg-toolbar-status-row--expanded">
                      <div className="viewer-inline-message eeg-autofit-banner">
                        <span>{autoFitFeedback.message}</span>
                        <div className="eeg-autofit-banner__actions">
                          {autoFitFeedback.message.includes(
                            "adjusted sensitivity",
                          ) ? (
                            <>
                              <button
                                type="button"
                                className="eeg-autofit-banner__btn eeg-autofit-banner__btn--primary"
                                onClick={handleDismissAutoFit}
                              >
                                <Trans
                                  ns="features"
                                  i18nKey="ui.copy.466fc492747c"
                                />
                              </button>
                              <button
                                type="button"
                                className="eeg-autofit-banner__btn eeg-autofit-banner__btn--secondary"
                                onClick={handleRevertAutoFit}
                              >
                                <Trans
                                  ns="features"
                                  i18nKey="ui.copy.272607a7bd01"
                                />
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="eeg-autofit-banner__btn eeg-autofit-banner__btn--primary"
                              onClick={handleDismissAutoFit}
                            >
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.70afe9eff3f2"
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
            </div>
            ) : null}

            {(dualMontageMessage || dualMontageError) && (
              <div
                className={`viewer-inline-message ${dualMontageError ? "viewer-inline-message--error" : ""}`}
              >
                {dualMontageError || dualMontageMessage}
              </div>
            )}
            {(playbackManifest?.totalSegments || 1) > 1 && (
              <div className="viewer-inline-message">
                <Trans ns="features" i18nKey="ui.copy.f2d4f8cce0e4" />
                {playbackManifest?.totalSegments || 1}{" "}
                <Trans ns="features" i18nKey="ui.copy.159def7677f2" />
              </div>
            )}
            {annotationOverlayError && (
              <div className="viewer-inline-message viewer-inline-message--error">
                {annotationOverlayError}
              </div>
            )}

            {/* Waveform Display with Annotation Panel */}
            <div
              className={`eeg-content${showAnnotationPanel ? "" : " eeg-content--clinical-collapsed"}`}
            >
              <EEGPlaybackCore slot="waveform" />

              {viewerDeferredChromeReady &&
              floatingToolbarPreferences.enabled &&
              !floatingToolbarPopout.isPoppedOut
                ? renderFloatingAnnotationToolbar("pinned")
                : null}

              {dualCompareDrawerOpen ? (
                <DualMontageReviewFloatingPanel
                  open={dualCompareDrawerOpen}
                  onClose={() => setDualCompareDrawerOpen(false)}
                >
<div className="control-group control-group--dual-montage">
                              <select
                                ref={dualDrawerFirstControlRef}
                                aria-label="Dual compare left montage"
                                value={
                                  dualMontagePreference.left_montage_id || ""
                                }
                                onChange={(event) =>
                                  setDualMontagePreference((current) => ({
                                    ...current,
                                    left_montage_id: event.target.value || null,
                                  }))
                                }
                              >
                                <option value="">
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.17f9f0a831f9"
                                  />
                                </option>
                                {montages?.map((montage) => (
                                  <option
                                    key={`left-${montage.montage_id}`}
                                    value={montage.montage_id}
                                  >
                                    {montage.montage_name}
                                  </option>
                                ))}
                              </select>
                              <select
                                aria-label="Dual compare right montage"
                                value={
                                  dualMontagePreference.right_montage_id || ""
                                }
                                onChange={(event) =>
                                  setDualMontagePreference((current) => ({
                                    ...current,
                                    right_montage_id:
                                      event.target.value || null,
                                  }))
                                }
                              >
                                <option value="">
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.16bd9e817cba"
                                  />
                                </option>
                                {montages?.map((montage) => (
                                  <option
                                    key={`right-${montage.montage_id}`}
                                    value={montage.montage_id}
                                  >
                                    {montage.montage_name}
                                  </option>
                                ))}
                              </select>
                              <label className="eeg-toolbar-field eeg-toolbar-field--check">
                                <span className="eeg-toolbar-field-label">
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.905f63092ff2"
                                  />
                                </span>
                                <input
                                  type="checkbox"
                                  aria-label="Sync playback, cursor, and zoom"
                                  checked={
                                    dualMontagePreference.sync_playback &&
                                    dualMontagePreference.sync_cursor &&
                                    dualMontagePreference.sync_zoom
                                  }
                                  onChange={(event) => {
                                    const enabled = event.target.checked;
                                    setDualMontagePreference((current) => ({
                                      ...current,
                                      sync_playback: enabled,
                                      sync_cursor: enabled,
                                      sync_zoom: enabled,
                                    }));
                                  }}
                                />
                              </label>
                              <label className="eeg-toolbar-field eeg-toolbar-field--check">
                                <span className="eeg-toolbar-field-label">
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.d23a1c9fb35f"
                                  />
                                </span>
                                <input
                                  type="checkbox"
                                  aria-label="Difference highlight"
                                  checked={dualMontagePreference.diff_highlight}
                                  onChange={(event) =>
                                    setDualMontagePreference((current) => ({
                                      ...current,
                                      diff_highlight: event.target.checked,
                                    }))
                                  }
                                />
                              </label>
                              <button
                                type="button"
                                className="eeg-toolbar-filter-toggle"
                                onClick={() =>
                                  saveDualMontagePreferenceMutation.mutate()
                                }
                                disabled={
                                  saveDualMontagePreferenceMutation.isPending
                                }
                              >
                                <Trans
                                  ns="features"
                                  i18nKey="ui.copy.efc007a393f6"
                                />
                              </button>
                              <button
                                type="button"
                                className="eeg-toolbar-filter-toggle"
                                onClick={() => applyDualMontageSide("left")}
                                disabled={
                                  !dualMontagePreference.left_montage_id
                                }
                              >
                                <Trans
                                  ns="features"
                                  i18nKey="ui.copy.06db4d45f384"
                                />
                              </button>
                              <button
                                type="button"
                                className="eeg-toolbar-filter-toggle"
                                onClick={() => applyDualMontageSide("right")}
                                disabled={
                                  !dualMontagePreference.right_montage_id
                                }
                              >
                                <Trans
                                  ns="features"
                                  i18nKey="ui.copy.66bb033b12b1"
                                />
                              </button>
                            </div>
                            {(dualMontageMessage || dualMontageError) && (
                              <div
                                className={`dual-montage-review__status${
                                  dualMontageError ? " is-error" : ""
                                }`}
                                role="status"
                              >
                                {dualMontageError || dualMontageMessage}
                              </div>
                            )}
                            <DualMontageReviewPanel
                              leftMontageName={dualLeftMontageName}
                              rightMontageName={dualRightMontageName}
                              leftPairs={dualLeftPairs}
                              rightPairs={dualRightPairs}
                              channels={dualReviewChannels}
                              currentTime={currentTime}
                              duration={Math.max(recordingDuration, 0.001)}
                              syncPlayback={dualMontagePreference.sync_playback}
                              syncCursor={dualMontagePreference.sync_cursor}
                              syncZoom={dualMontagePreference.sync_zoom}
                              diffHighlight={
                                dualMontagePreference.diff_highlight
                              }
                              timeScaleSeconds={
                                Number(currentConfig?.time_scale) > 0
                                  ? Number(currentConfig?.time_scale)
                                  : 10
                              }
                              onSeek={handleDualMontageSeek}
                            />
                </DualMontageReviewFloatingPanel>
              ) : null}


              {/* Dock toggles + clinical annotation panel + right dock — lazy chrome */}
              {viewerDeferredChromeReady ? (
                <Suspense fallback={null}>
                  <EEGViewerDeferredChrome slot="content" />
                </Suspense>
              ) : null}
            </div>

            <div className="eeg-viewer-bottom-chrome">
              {viewerDeferredChromeReady ? (
                <Suspense fallback={null}>
                  <EEGViewerDeferredChrome slot="bottom" />
                </Suspense>
              ) : null}

              {/* Playback Controls */}
              <EEGPlaybackCore slot="transport" />
            </div>

            {goToTimeModalOpen && (
              <div className="keyboard-shortcuts-modal">
                <div
                  className="keyboard-shortcuts-modal__dialog"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="go-to-time-modal-title"
                >
                  <div className="keyboard-shortcuts-modal__header">
                    <h3 id="go-to-time-modal-title">
                      <Trans ns="features" i18nKey="ui.copy.863a2c5aac82" />
                    </h3>
                    <button
                      type="button"
                      className="keyboard-shortcuts-modal__close"
                      aria-label="Close Go To Time dialog"
                      onClick={() => setGoToTimeModalOpen(false)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="eeg-go-to-time-modal-body">
                    <input
                      type="text"
                      value={goToTimeModalInput}
                      className="eeg-go-to-time-input eeg-go-to-time-input--modal"
                      onChange={(event) =>
                        setGoToTimeModalInput(event.target.value)
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          applyGoToTimeInput(event.currentTarget.value);
                          setGoToTimeModalOpen(false);
                        }
                        if (event.key === "Escape") {
                          setGoToTimeModalOpen(false);
                        }
                      }}
                      autoFocus
                    />
                    <div className="annotation-tool-actions">
                      <button
                        type="button"
                        onClick={() => {
                          applyGoToTimeInput(goToTimeModalInput);
                          setGoToTimeModalOpen(false);
                        }}
                      >
                        <Trans ns="features" i18nKey="ui.copy.2e0b45f2a456" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setGoToTimeModalOpen(false)}
                      >
                        <Trans ns="features" i18nKey="ui.copy.77dfd2135f4d" />
                      </button>
                    </div>
                    <p className="annotation-inline-hint">
                      <Trans ns="features" i18nKey="ui.copy.78d6f4cf2dff" />
                    </p>
                  </div>
                </div>
              </div>
            )}

            {showPrintWaveformDialog && (
              <div className="keyboard-shortcuts-modal">
                <div
                  className="keyboard-shortcuts-modal__dialog"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="print-waveform-modal-title"
                >
                  <div className="keyboard-shortcuts-modal__header">
                    <h3 id="print-waveform-modal-title">
                      <Trans ns="features" i18nKey="ui.copy.4913b17e9595" />
                    </h3>
                    <button
                      type="button"
                      className="keyboard-shortcuts-modal__close"
                      aria-label="Close Print Waveform dialog"
                      onClick={() => setShowPrintWaveformDialog(false)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="eeg-go-to-time-modal-body">
                    <div className="config-subsection">
                      <label className="config-subsection__label">
                        <Trans ns="features" i18nKey="ui.copy.983524e76b23" />
                      </label>
                      <select
                        value={printWaveformColorMode}
                        onChange={(event) =>
                          setPrintWaveformColorMode(
                            event.target.value as WaveformPrintColorMode,
                          )
                        }
                      >
                        <option value="color">
                          <Trans ns="features" i18nKey="ui.copy.1d0c8304baed" />
                        </option>
                        <option value="bw">
                          <Trans ns="features" i18nKey="ui.copy.b160e5b8b941" />
                        </option>
                      </select>
                    </div>
                    <div className="config-grid-two">
                      <div className="config-subsection">
                        <label className="config-subsection__label">
                          <Trans ns="features" i18nKey="ui.copy.86e4e3875420" />
                        </label>
                        <select
                          value={printWaveformOrientation}
                          onChange={(event) =>
                            setPrintWaveformOrientation(
                              event.target.value as WaveformPrintOrientation,
                            )
                          }
                        >
                          <option value="landscape">
                            <Trans
                              ns="features"
                              i18nKey="ui.copy.7dc4bea61853"
                            />
                          </option>
                          <option value="portrait">
                            <Trans
                              ns="features"
                              i18nKey="ui.copy.92b6b1bf71e0"
                            />
                          </option>
                        </select>
                      </div>
                      <div className="config-subsection">
                        <label className="config-subsection__label">
                          <Trans ns="features" i18nKey="ui.copy.f3e1d7bb2d76" />
                        </label>
                        <select
                          value={printWaveformPageSize}
                          onChange={(event) =>
                            setPrintWaveformPageSize(
                              event.target.value as WaveformPrintPageSize,
                            )
                          }
                        >
                          <option value="A4">
                            <Trans
                              ns="features"
                              i18nKey="ui.copy.de69a629f9c3"
                            />
                          </option>
                          <option value="Letter">
                            <Trans
                              ns="features"
                              i18nKey="ui.copy.ee14050617b7"
                            />
                          </option>
                          <option value="Legal">
                            <Trans
                              ns="features"
                              i18nKey="ui.copy.902c91d94e0f"
                            />
                          </option>
                        </select>
                      </div>
                    </div>
                    {printWaveformError && (
                      <p className="config-message config-message--error">
                        {printWaveformError}
                      </p>
                    )}
                    <div className="annotation-tool-actions">
                      <button
                        type="button"
                        onClick={handleOpenPrintWaveformView}
                      >
                        <Trans ns="features" i18nKey="ui.copy.05c020bf2f14" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPrintWaveformDialog(false)}
                      >
                        <Trans ns="features" i18nKey="ui.copy.77dfd2135f4d" />
                      </button>
                    </div>
                    <p className="annotation-inline-hint">
                      <Trans ns="features" i18nKey="ui.copy.f8ad6c595d39" />
                    </p>
                  </div>
                </div>
              </div>
            )}

            {showKeyboardShortcutsModal && (
              <Suspense fallback={null}>
                <KeyboardShortcutsModal
                  open={showKeyboardShortcutsModal}
                  onClose={() => setShowKeyboardShortcutsModal(false)}
                />
              </Suspense>
            )}

            {/* Configuration Panel */}
            {showConfigPanel && (
              <Suspense fallback={null}>
                <ConfigSettingsPanel
              show={showConfigPanel}
              defaultTab={configPanelTab}
              onClose={() => setShowConfigPanel(false)}
            >
              {(activeTab) => (
                <ConfigSettingsPanelContent
                  activeTab={activeTab}
                  onClose={() => setShowConfigPanel(false)}
                  // Profile tab
                  perspectiveState={perspectiveState}
                  perspectives={perspectives}
                  handlePerspectiveSwitch={handlePerspectiveSwitch}
                  toggleMontageLock={toggleMontageLock}
                  toggleFiltersLock={toggleFiltersLock}
                  setClinicalMode={setClinicalMode}
                  displayProfileStatus={displayProfileStatus}
                  setDisplayProfileStatus={setDisplayProfileStatus}
                  displayProfileError={displayProfileError}
                  setDisplayProfileError={setDisplayProfileError}
                  displayProfileConflict={displayProfileConflict}
                  setDisplayProfileConflict={setDisplayProfileConflict}
                  refetchDisplayProfiles={refetchDisplayProfiles}
                  selectedDisplayProfileId={selectedDisplayProfileId}
                  setSelectedDisplayProfileId={setSelectedDisplayProfileId}
                  displayProfiles={displayProfiles}
                  selectedDisplayProfile={selectedDisplayProfile}
                  handleApplySelectedDisplayProfile={
                    handleApplySelectedDisplayProfile
                  }
                  updateDisplayProfileMutation={updateDisplayProfileMutation}
                  setDefaultDisplayProfileMutation={
                    setDefaultDisplayProfileMutation
                  }
                  deleteDisplayProfileMutation={deleteDisplayProfileMutation}
                  createDisplayProfileMutation={createDisplayProfileMutation}
                  newDisplayProfileName={newDisplayProfileName}
                  setNewDisplayProfileName={setNewDisplayProfileName}
                  isFetchingDisplayProfiles={isFetchingDisplayProfiles}
                  // Waveform tab
                  profileTheme={profileTheme}
                  setProfileTheme={setProfileThemeFromUser}
                  profileGridDensity={profileGridDensity}
                  setProfileGridDensity={setProfileGridDensityFromUser}
                  profileWaveformThickness={profileWaveformThickness}
                  setProfileWaveformThickness={setProfileWaveformThickness}
                  annotationOverlayOpacity={annotationOverlayOpacity}
                  setAnnotationOverlayOpacity={setAnnotationOverlayOpacity}
                  profileChannelSpacing={profileChannelSpacing}
                  setProfileChannelSpacing={setProfileChannelSpacingFromUser}
                  profileLabelFontSize={profileLabelFontSize}
                  setProfileLabelFontSize={setProfileLabelFontSize}
                  displayProfilePolicy={displayProfilePolicy}
                  currentConfig={currentConfig}
                  handleConfigChange={handleConfigChange}
                  playbackSpeedOptions={playbackSpeedOptions}
                  onOpenFilterRow={() => activateWorkspaceSection("filters")}
                  waveformDisplayMode={waveformDisplayMode}
                  rowHeightMode={rowHeightMode}
                  allowTraceOverflow={allowTraceOverflow}
                  refitOnChannelChange={refitOnChannelChange}
                  handleDisplayModeChange={handleDisplayModeChange}
                  handleRowHeightModeChange={handleRowHeightModeChange}
                  handleAllowTraceOverflowChange={
                    handleAllowTraceOverflowChange
                  }
                  handleRefitOnChannelChange={handleRefitOnChannelChange}
                  handleResetDisplay={handleResetDisplay}
                  // Layout tab – Channel Selection
                  handleSelectAllTraceChannels={handleSelectAllTraceChannels}
                  handleClearAllTraceChannels={handleClearAllTraceChannels}
                  handleSelectVisibleTraceChannels={
                    handleSelectVisibleTraceChannels
                  }
                  handleInvertTraceChannelSelection={
                    handleInvertTraceChannelSelection
                  }
                  channelRegionSelection={channelRegionSelection}
                  setChannelRegionSelection={setChannelRegionSelection}
                  handleSelectTraceChannelsByRegion={
                    handleSelectTraceChannelsByRegion
                  }
                  channelQuickJumpInput={channelQuickJumpInput}
                  setChannelQuickJumpInput={setChannelQuickJumpInput}
                  handleQuickJumpToChannelLabel={handleQuickJumpToChannelLabel}
                  setShowAnnotationPanel={setShowAnnotationPanel}
                  setAnnotationPanelTab={setAnnotationPanelTab}
                  // Layout tab – Dock Visibility
                  dockBottomCollapsed={dockBottomCollapsed}
                  setDockBottomCollapsed={setDockBottomCollapsed}
                  dockRightCollapsed={dockRightCollapsed}
                  setDockRightCollapsed={setDockRightCollapsed}
                  eventsTrendsDockPlacement={eventsTrendsDockPlacement}
                  setEventsTrendsDockPlacement={setEventsTrendsDockPlacement}
                  dockPanelVisibility={dockPanelVisibility}
                  dockPanelOrder={dockPanelOrder}
                  toggleDockPanelVisibility={toggleDockPanelVisibility}
                  moveDockPanel={moveDockPanel}
                  showAnnotationPanel={showAnnotationPanel}
                  floatingToolbarEnabled={floatingToolbarPreferences.enabled}
                  setFloatingToolbarEnabled={setFloatingToolbarEnabled}
                  // Layout tab – Saved Configs
                  configurations={configurations}
                  setCurrentConfig={setCurrentConfig}
                  normalizeViewerConfig={normalizeViewerConfig}
                  // Layout tab – Resolution
                  resolutionLevel={resolutionLevel}
                  setResolutionLevel={setResolutionLevel}
                  autoResolution={autoResolution}
                  setAutoResolution={setAutoResolution}
                  getAdvanceChunk={getAdvanceChunk}
                  getEffectiveBufferSize={getEffectiveBufferSize}
                  setDataWindowStart={setDataWindowStart}
                  alignWindowStartToBackend={alignWindowStartToBackend}
                  currentTime={currentTime}
                  playbackTransportMode={playbackTransportMode}
                  recordingDuration={recordingDuration}
                  // Layout tab – Display Options
                  showGrid={showGrid}
                  setShowGrid={setShowGrid}
                  showChannelGroupLabels={showChannelGroupLabels}
                  setShowChannelGroupLabels={setShowChannelGroupLabels}
                  showScaleMarkers={showScaleMarkers}
                  setShowScaleMarkers={setShowScaleMarkers}
                  clinicalGridMode={clinicalGridMode}
                  setClinicalGridMode={setClinicalGridMode}
                  toolbarAutoHide={toolbarAutoHide}
                  toggleToolbarAutoHide={toggleToolbarAutoHide}
                  playbackControlsLockOnPlayback={
                    playbackControlsLockOnPlayback
                  }
                  setPlaybackControlsLockOnPlayback={
                    setPlaybackControlsLockOnPlayback
                  }
                  // Layout tab – Data Options
                  rendererPreference={rendererPreference}
                  setRendererPreference={setRendererPreference}
                  rendererMode={rendererMode}
                  // Channels tab
                  currentMontage={currentMontage}
                  effectiveMontageChannelLabels={effectiveMontageChannelLabels}
                  profileChannelPositionOffsets={profileChannelPositionOffsets}
                  handleChannelOffsetChange={handleChannelOffsetChange}
                  handleNormalizeChannelOffsets={handleNormalizeChannelOffsets}
                  handleResetAllChannelOffsets={handleResetAllChannelOffsets}
                  handleResetChannelOffset={handleResetChannelOffset}
                  effectiveMontagePairs={effectiveMontagePairs}
                  getMontageChannelLabel={getMontageChannelLabel}
                  resolveChannelColor={resolveChannelColor}
                  themeWaveformColor={themeWaveformColor}
                  setProfileChannelColors={setProfileChannelColors}
                  // Calibration tab
                  configCalibrationSectionRef={configCalibrationSectionRef}
                  autoScreenDpi={autoScreenDpi}
                  calibrationAutoPixelsPerMm={calibrationAutoPixelsPerMm}
                  effectiveScreenDpi={effectiveScreenDpi}
                  effectivePixelsPerMm={effectivePixelsPerMm}
                  calibrationScaleMultiplier={calibrationScaleMultiplier}
                  screenCalibration={screenCalibration}
                  setScreenCalibration={setScreenCalibration}
                  markCalibrationReviewed={markCalibrationReviewed}
                  calibrationMeasuredVerticalPxPerMm={
                    calibrationMeasuredVerticalPxPerMm
                  }
                  calibrationMeasuredHorizontalPxPerMm={
                    calibrationMeasuredHorizontalPxPerMm
                  }
                  calibrationVerticalDeltaPercent={
                    calibrationVerticalDeltaPercent
                  }
                  calibrationHorizontalDeltaPercent={
                    calibrationHorizontalDeltaPercent
                  }
                  calibrationVerticalPass={calibrationVerticalPass}
                  calibrationHorizontalPass={calibrationHorizontalPass}
                  calibrationOverallPass={calibrationOverallPass}
                  // Export tab
                  clinicianExportStatus={clinicianExportStatus}
                  clinicianExportError={clinicianExportError}
                  clinicianExportFormat={clinicianExportFormat}
                  setClinicianExportFormat={setClinicianExportFormat}
                  handleRunClinicianExport={handleRunClinicianExport}
                  clinicianExportBusy={clinicianExportBusy}
                  clinicianExportIncludeMetadata={
                    clinicianExportIncludeMetadata
                  }
                  setClinicianExportIncludeMetadata={
                    setClinicianExportIncludeMetadata
                  }
                  clinicianExportIncludeVideo={clinicianExportIncludeVideo}
                  setClinicianExportIncludeVideo={
                    setClinicianExportIncludeVideo
                  }
                  // Footer
                  saveConfigMutation={saveConfigMutation}
                  setShowConfigPanel={setShowConfigPanel}
                />
              )}
            </ConfigSettingsPanel>
              </Suspense>
            )}

            {showAnnotationQuickOpen && (
              <div className="config-panel-modal">
                <div className="config-panel eeg-customization-modal">
                  <h3 className="eeg-customization-modal__heading">
                    <Trans ns="features" i18nKey="ui.copy.38ab82eabd83" />
                  </h3>
                  <button
                    className="close-btn"
                    onClick={() => setShowAnnotationQuickOpen(false)}
                  >
                    ×
                  </button>
                  <p className="config-subsection__hint">
                    <Trans ns="features" i18nKey="ui.copy.61cc173bf4dc" />{" "}
                    {currentTime.toFixed(1)}
                    <Trans ns="features" i18nKey="ui.copy.2a6d803c850a" />
                  </p>
                  <div className="quick-open-grid">
                    <div className="quick-open-column">
                      <h4>
                        <Trans ns="features" i18nKey="ui.copy.cd3771cae741" />
                      </h4>
                      <select
                        className="annotation-inline-select"
                        value={quickSelectedTemplateId}
                        onChange={(event) =>
                          setQuickSelectedTemplateId(event.target.value)
                        }
                      >
                        <option value="">
                          <Trans ns="features" i18nKey="ui.copy.422dc6e531ba" />
                        </option>
                        {quickReviewTemplates.map((template) => (
                          <option
                            key={template.template_id}
                            value={template.template_id}
                          >
                            {template.name} ({template.category})
                          </option>
                        ))}
                      </select>
                      {selectedQuickTemplateDefinition && (
                        <div className="quick-open-template-fields">
                          <div className="quick-open-template-meta">
                            <span>
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.ee3fb11d05c9"
                              />{" "}
                              {selectedQuickTemplateDefinition.annotationType}
                            </span>
                            <span>
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.f70a9228e736"
                              />{" "}
                              {selectedQuickTemplateDefinition.annotationLabel}
                            </span>
                            <span
                              className="quick-open-template-color-chip"
                              style={{
                                backgroundColor:
                                  selectedQuickTemplateDefinition.annotationColor,
                              }}
                            >
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.1d0c8304baed"
                              />
                            </span>
                          </div>

                          <label
                            className="config-subsection__label"
                            htmlFor="template-notes"
                          >
                            <Trans
                              ns="features"
                              i18nKey="ui.copy.70440046a3dc"
                            />
                            {selectedQuickTemplateDefinition.requiredFields.includes(
                              "notes",
                            )
                              ? " *"
                              : ""}
                          </label>
                          <textarea
                            id="template-notes"
                            className="annotation-inline-textarea"
                            value={templateFieldValues.notes || ""}
                            onChange={(event) =>
                              updateTemplateFieldValue(
                                "notes",
                                event.target.value,
                              )
                            }
                            rows={2}
                            placeholder="Optional note for this annotation."
                          />

                          <label
                            className="config-subsection__label"
                            htmlFor="template-duration-seconds"
                          >
                            <Trans
                              ns="features"
                              i18nKey="ui.copy.09b44e3660d4"
                            />
                            {selectedQuickTemplateDefinition.requiredFields.includes(
                              "duration_seconds",
                            )
                              ? " *"
                              : ""}
                          </label>
                          <input
                            id="template-duration-seconds"
                            className="annotation-inline-input"
                            type="number"
                            min={0}
                            step={0.1}
                            value={templateFieldValues.duration_seconds || ""}
                            onChange={(event) =>
                              updateTemplateFieldValue(
                                "duration_seconds",
                                event.target.value,
                              )
                            }
                            placeholder="Leave blank for point annotation"
                          />

                          <div className="quick-open-template-channel-scope">
                            <div className="config-subsection__label">
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.8b61585413ac"
                              />
                              {selectedQuickTemplateDefinition.requiredFields.includes(
                                "channels_affected",
                              )
                                ? " *"
                                : ""}
                            </div>
                            {selectedQuickTemplateConfiguredChannels.length >
                            0 ? (
                              <div className="annotation-inline-hint">
                                <Trans
                                  ns="features"
                                  i18nKey="ui.copy.27ac2fcba265"
                                />{" "}
                                {selectedQuickTemplateConfiguredChannels.join(
                                  ", ",
                                )}
                              </div>
                            ) : (
                              <div className="quick-open-channel-mode">
                                <label>
                                  <input
                                    type="radio"
                                    name="template-channel-scope"
                                    checked={templateChannelMode === "selected"}
                                    onChange={() => {
                                      setTemplateChannelMode("selected");
                                      setTemplateFieldError(null);
                                    }}
                                  />
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.81a5f0085c7a"
                                  />
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name="template-channel-scope"
                                    checked={templateChannelMode === "all"}
                                    onChange={() => {
                                      setTemplateChannelMode("all");
                                      setTemplateFieldError(null);
                                    }}
                                  />
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.844df142c628"
                                  />
                                </label>
                              </div>
                            )}
                          </div>

                          {selectedQuickTemplateDefinition.structuredFields.map(
                            (field) => (
                              <div
                                key={field.key}
                                className="quick-open-template-field"
                              >
                                <label
                                  className="config-subsection__label"
                                  htmlFor={`template-structured-${field.key}`}
                                >
                                  {field.label}
                                  {field.required ? " *" : ""}
                                </label>
                                {field.type === "select" ? (
                                  <select
                                    id={`template-structured-${field.key}`}
                                    className="annotation-inline-select"
                                    value={templateFieldValues[field.key] || ""}
                                    onChange={(event) =>
                                      updateTemplateFieldValue(
                                        field.key,
                                        event.target.value,
                                      )
                                    }
                                  >
                                    <option value="">
                                      <Trans
                                        ns="features"
                                        i18nKey="ui.copy.24115b2099f7"
                                      />
                                    </option>
                                    {field.options.map((option) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                ) : field.type === "boolean" ? (
                                  <label className="control-inline-check">
                                    <input
                                      id={`template-structured-${field.key}`}
                                      type="checkbox"
                                      checked={
                                        templateFieldValues[field.key] ===
                                        "true"
                                      }
                                      onChange={(event) =>
                                        updateTemplateFieldValue(
                                          field.key,
                                          event.target.checked
                                            ? "true"
                                            : "false",
                                        )
                                      }
                                    />
                                    {field.label}
                                  </label>
                                ) : (
                                  <input
                                    id={`template-structured-${field.key}`}
                                    className="annotation-inline-input"
                                    type={
                                      field.type === "number"
                                        ? "number"
                                        : "text"
                                    }
                                    step={
                                      field.type === "number" ? 0.1 : undefined
                                    }
                                    value={templateFieldValues[field.key] || ""}
                                    onChange={(event) =>
                                      updateTemplateFieldValue(
                                        field.key,
                                        event.target.value,
                                      )
                                    }
                                    placeholder={field.placeholder || ""}
                                  />
                                )}
                              </div>
                            ),
                          )}

                          {selectedQuickTemplateDefinition.requiredFields
                            .length > 0 && (
                            <div className="annotation-inline-hint">
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.d57939886670"
                              />{" "}
                              {selectedQuickTemplateDefinition.requiredFields.join(
                                ", ",
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="annotation-tool-actions">
                        <button
                          type="button"
                          onClick={applyTemplateAnnotation}
                          disabled={!quickSelectedTemplateId}
                        >
                          <Trans ns="features" i18nKey="ui.copy.97b567ce2a26" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            void refetchQuickReviewTemplates();
                            void refetchQuickTemplateUsageSummary();
                          }}
                          disabled={
                            isFetchingQuickReviewTemplates ||
                            isFetchingQuickTemplateUsageSummary
                          }
                        >
                          <Trans ns="features" i18nKey="ui.copy.56e3badc4e6c" />
                        </button>
                      </div>
                      {templateFieldError && (
                        <div className="annotation-inline-error">
                          {templateFieldError}
                        </div>
                      )}
                      {quickReviewTemplatesError && (
                        <div className="annotation-inline-error">
                          <Trans ns="features" i18nKey="ui.copy.394af226fc95" />
                        </div>
                      )}
                      {quickTemplateUsageSummaryError && (
                        <div className="annotation-inline-error">
                          <Trans ns="features" i18nKey="ui.copy.a5286ef16141" />
                        </div>
                      )}
                      <div className="quick-open-usage-section">
                        <div className="quick-open-usage-section__header">
                          <Trans ns="features" i18nKey="ui.copy.2f904a39ffc5" />
                        </div>
                        {isFetchingQuickTemplateUsageSummary && (
                          <div className="annotation-inline-hint">
                            <Trans
                              ns="features"
                              i18nKey="ui.copy.9b80ab91075f"
                            />
                          </div>
                        )}
                        {quickTemplateUsageSummary && (
                          <>
                            <div className="annotation-inline-hint">
                              <Trans
                                ns="features"
                                i18nKey="ui.copy.d7625eb0d0ba"
                              />{" "}
                              {quickTemplateUsageSummary.total_template_uses}
                            </div>
                            <div className="quick-open-usage-list">
                              {quickTemplateUsageSummary.templates
                                .slice(0, 5)
                                .map((usage) => (
                                  <div
                                    key={`${usage.template_id || "unknown"}-${usage.template_name}`}
                                    className={`quick-open-usage-item${
                                      usage.template_id ===
                                      quickSelectedTemplateId
                                        ? " quick-open-usage-item--selected"
                                        : ""
                                    }`}
                                  >
                                    <span>{usage.template_name}</span>
                                    <span>
                                      {usage.usage_count}{" "}
                                      <Trans
                                        ns="features"
                                        i18nKey="ui.copy.d3040546b877"
                                      />{" "}
                                      {usage.unique_user_count}{" "}
                                      <Trans
                                        ns="features"
                                        i18nKey="ui.copy.5b7dcd14a4fa"
                                      />
                                    </span>
                                  </div>
                                ))}
                              {quickTemplateUsageSummary.templates.length ===
                                0 && (
                                <div className="annotation-inline-hint">
                                  <Trans
                                    ns="features"
                                    i18nKey="ui.copy.439f282916f1"
                                  />
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="quick-open-column">
                      <h4>
                        <Trans ns="features" i18nKey="ui.copy.7409da4a29ad" />
                      </h4>
                      <select
                        value={quickSelectedMacroId}
                        onChange={(event) =>
                          setQuickSelectedMacroId(event.target.value)
                        }
                      >
                        <option value="">
                          <Trans ns="features" i18nKey="ui.copy.4c822d8f58d7" />
                        </option>
                        {quickDictionaryMacros.map((entry) => (
                          <option key={entry.key} value={entry.key}>
                            {entry.macro.hotkey} - {entry.macro.name} (
                            {entry.dictionary.name})
                          </option>
                        ))}
                      </select>
                      <div className="annotation-tool-actions">
                        <button
                          type="button"
                          onClick={applyDictionaryMacroAnnotation}
                          disabled={!quickSelectedMacroId}
                        >
                          <Trans ns="features" i18nKey="ui.copy.f6905bb201e1" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            void refetchQuickAnnotationDictionaries()
                          }
                          disabled={isFetchingQuickAnnotationDictionaries}
                        >
                          <Trans ns="features" i18nKey="ui.copy.56e3badc4e6c" />
                        </button>
                      </div>
                      {quickAnnotationDictionariesError && (
                        <div className="annotation-inline-error">
                          <Trans ns="features" i18nKey="ui.copy.3a6c3513e5b8" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="quick-open-footer">
                    <Link to="/eeg/customization">
                      <Trans ns="features" i18nKey="ui.copy.f76c584222bb" />
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {annotationNoteComposer && (
              <div className="config-panel-modal">
                <div className="config-panel eeg-note-composer-modal">
                  <h3>{annotationNoteComposer.title}</h3>
                  <button
                    className="close-btn"
                    onClick={() => closeAnnotationNoteComposer(true)}
                  >
                    ×
                  </button>
                  <textarea
                    className="annotation-inline-textarea eeg-note-composer-modal__textarea"
                    value={annotationNoteComposerValue}
                    onChange={(event) =>
                      setAnnotationNoteComposerValue(event.target.value)
                    }
                    placeholder={annotationNoteComposer.placeholder}
                    rows={4}
                    maxLength={240}
                    autoFocus
                  />
                  <div className="eeg-note-composer-modal__meta">
                    {annotationNoteComposerValue.trim().length}/240
                  </div>
                  {annotationNoteComposerError && (
                    <div className="annotation-inline-error">
                      {annotationNoteComposerError}
                    </div>
                  )}
                  <div className="annotation-tool-actions">
                    <button
                      type="button"
                      onClick={() => void submitAnnotationNoteComposer()}
                    >
                      <Trans ns="features" i18nKey="ui.copy.8d554b2ab74d" />
                    </button>
                    <button
                      type="button"
                      className="config-inline-btn config-inline-btn--subtle"
                      onClick={() => closeAnnotationNoteComposer(true)}
                    >
                      <Trans ns="features" i18nKey="ui.copy.77dfd2135f4d" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showMontageMobileSheet && (
              <div
                className="config-panel-modal"
                role="presentation"
                onClick={(event) => {
                  if (event.target === event.currentTarget) {
                    setShowMontageMobileSheet(false);
                  }
                }}
              >
                <div
                  className="config-panel viewer-montage-mobile-sheet"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="viewer-montage-mobile-sheet-title"
                >
                  <div className="config-panel__header viewer-montage-mobile-sheet__header">
                    <h3 id="viewer-montage-mobile-sheet-title">
                      <Trans ns="features" i18nKey="ui.copy.414a02107274" />
                    </h3>
                    <button
                      type="button"
                      className="close-btn"
                      onClick={() => setShowMontageMobileSheet(false)}
                      aria-label={t("ui.copy.34d8b60fe253", {
                        defaultValue: "Hide",
                      })}
                    >
                      ×
                    </button>
                  </div>
                  <div className="viewer-montage-mobile-sheet__body">
                    <p className="config-subsection__hint">
                      <Trans ns="features" i18nKey="ui.copy.ba8faefc4a6b" />
                    </p>
                    <div className="viewer-montage-mobile-sheet-actions">
                      <button
                        type="button"
                        className="viewer-montage-mobile-sheet__action"
                        onClick={handleInsertViewerMontagePair}
                      >
                        <Trans ns="features" i18nKey="ui.copy.793efa667400" />
                      </button>
                      <button
                        type="button"
                        className="viewer-montage-mobile-sheet__action"
                        onClick={handleDuplicateViewerMontagePair}
                      >
                        <Trans ns="features" i18nKey="ui.copy.972d57379db3" />
                      </button>
                      <button
                        type="button"
                        className="viewer-montage-mobile-sheet__action"
                        onClick={handleOpenViewerMontagePairEditor}
                        disabled={selectedMontagePairIndexes.length === 0}
                      >
                        <Trans ns="features" i18nKey="ui.copy.517dda997797" />
                      </button>
                      <button
                        type="button"
                        className="viewer-montage-mobile-sheet__action viewer-montage-mobile-sheet__action--danger"
                        onClick={handleDeleteViewerMontagePairs}
                        disabled={selectedMontagePairIndexes.length === 0}
                      >
                        <Trans ns="features" i18nKey="ui.copy.76bf56ab1404" />
                      </button>
                      <button
                        type="button"
                        className="viewer-montage-mobile-sheet__action viewer-montage-mobile-sheet__action--primary"
                        onClick={handleSaveViewerMontageDraft}
                        disabled={
                          !viewerMontageDraftDirty || isSavingViewerMontage
                        }
                      >
                        {isSavingViewerMontage ? "Saving..." : "Save Draft"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {editableMontagePair && (
              <Suspense fallback={null}>
                <EditChannelPairModal
                key={editableMontagePair.id}
                show={editingMontagePairIndex !== null}
                onHide={() => setEditingMontagePairIndex(null)}
                pair={editableMontagePair}
                availableElectrodes={montageAvailableElectrodes}
                onSave={handleSaveViewerMontagePairEdit}
              />
              </Suspense>
            )}

            {activeCustomizationTool && (
              <div className="config-panel-modal">
                <div className="config-panel eeg-customization-modal eeg-customization-modal--wide">
                  <h3 className="eeg-customization-modal__heading">
                    {activeCustomizationTool === "bad-channels"
                      ? "Bad-Channel Manager"
                      : "Filter Override Editor"}
                  </h3>
                  <button
                    className="close-btn"
                    onClick={() => setActiveCustomizationTool(null)}
                  >
                    ×
                  </button>
                  <p className="config-subsection__hint eeg-customization-modal__scope">
                    <Trans ns="features" i18nKey="ui.copy.40af15b016b8" />
                    {" "}
                    {headerRecordingLabel !== "N/A"
                      ? headerRecordingLabel
                      : "No active recording"}
                  </p>
                  <Suspense fallback={null}>
                    {activeCustomizationTool === "bad-channels" ? (
                      <BadChannelManagerPanel
                        recordingId={clinicalScopeId || ""}
                        recordingLabel={headerRecordingLabel}
                      />
                    ) : (
                      <FilterOverrideEditorPanel
                        recordingId={clinicalScopeId || ""}
                      />
                    )}
                  </Suspense>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "realtime" && (
          <div className="realtime-tab-content">
            <Suspense fallback={null}>
              <WebSocketStreaming
                recordingId={studyId}
                initialResolutionLevel={resolutionLevel}
                onDataReceived={(data) =>
                  logger.debug("[EEGViewer] Streaming data received:", data)
                }
              />
            </Suspense>
          </div>
        )}

        {showVideoPanel && (
          <Suspense fallback={null}>
            <EEGVideoSyncPanel
              studyId={studyId}
              preloadedVideos={
                studyVideoRecordings.length > 0
                  ? studyVideoRecordings
                  : undefined
              }
              onClose={handleCloseVideoPanel}
              isCollapsed={!showVideoPanel}
              playbackController={videoPlaybackController}
              externalEventMarkers={videoPanelEventMarkers}
              onLayoutChange={setVideoPanelLayout}
              videoPopout={videoPopout}
              layout={videoPanelLayout}
            />
          </Suspense>
        )}
        </div>

        {/* EEG-CUX-004 AC#6: popup-blocked warning toast */}
        {popupBlockedMessage && (
          <div className="eeg-popup-blocked-toast" role="alert">
            <span className="eeg-popup-blocked-toast__icon">⚠</span>
            <span className="eeg-popup-blocked-toast__text">
              {popupBlockedMessage}
            </span>
            <button
              type="button"
              className="eeg-popup-blocked-toast__dismiss"
              aria-label="Dismiss"
              onClick={() => setPopupBlockedMessage(null)}
            >
              ✕
            </button>
          </div>
        )}
        {clinicalNotice && (
          <div
            className={`eeg-clinical-notice eeg-clinical-notice--${clinicalNotice.tone}`}
            role={clinicalNotice.tone === "error" ? "alert" : "status"}
            data-testid="eeg-clinical-notice"
          >
            <span className="eeg-clinical-notice__icon" aria-hidden="true">
              {clinicalNotice.tone === "error" ? "⚠" : "✓"}
            </span>
            <span className="eeg-clinical-notice__text">
              {clinicalNotice.message}
            </span>
            <button
              type="button"
              className="eeg-clinical-notice__dismiss"
              aria-label="Dismiss"
              onClick={() => {
                if (clinicalNoticeTimerRef.current) {
                  clearTimeout(clinicalNoticeTimerRef.current);
                  clinicalNoticeTimerRef.current = null;
                }
                setClinicalNotice(null);
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Sprint 12: Video Panel Integration */}
        {/* EEG-CUX-004: Detached panel portals */}
        {[...poppedOutPanels].map((panelKey) => {
          const win = poppedOutWindowsRef.current.get(panelKey);
          if (!win || win.closed) return null;
          return (
            <Suspense key={panelKey} fallback={null}>
              <EegDetachedPanelWindow
                win={win}
                title={DOCK_PANEL_LABELS[panelKey]}
                panelKey={panelKey}
                recordingId={currentRecordingId}
                onPopIn={() => popInPanel(panelKey)}
                onExternalClose={
                  panelKey === "event_detection"
                    ? () => {
                        // Window dismiss is mid-study / non-complete: draft only, no page.
                        void completeAiTriageCloseDraft({
                          studyId,
                          recordingId: currentRecordingId,
                          notifyAttending: false,
                          force: true,
                        })
                          .then((result) => {
                            if (!result.success) {
                              showClinicalNotice(
                                "error",
                                result.error ||
                                  "Could not save interim triage draft.",
                              );
                              return;
                            }
                            // Do not flash the long regulatory toast here — it looked like a
                            // browser pop-up over the EEG viewer when the draft report opened.
                            // Success feedback stays on the triage panel / report page itself.
                          })
                          .catch((error: unknown) => {
                            showClinicalNotice(
                              "error",
                              error instanceof Error
                                ? error.message
                                : "Interim triage draft failed.",
                            );
                          });
                      }
                    : undefined
                }
                onSyncMessage={handleDetachedSyncMessage}
              >
                {renderDockPanel(panelKey)}
              </EegDetachedPanelWindow>
            </Suspense>
          );
        })}
        {floatingToolbarPopout.isPoppedOut &&
        floatingToolbarPopout.popoutWindow &&
        !floatingToolbarPopout.popoutWindow.closed ? (
          <Suspense fallback={null}>
            <EegDetachedPanelWindow
              win={floatingToolbarPopout.popoutWindow}
              title="Annotation Set"
              panelKey="floating-annotation-toolbar"
              compactToolbar
              allowPopIn={false}
              allowBringToFront={false}
              onPopIn={handleFloatingToolbarPopIn}
            >
              {renderFloatingAnnotationToolbar("detached")}
            </EegDetachedPanelWindow>
          </Suspense>
        ) : null}
        {import.meta.env.VITE_EEG_DIAGNOSTICS === "true" &&
        viewerDeferredChromeReady ? (
          <Suspense fallback={null}>
            <EEGDiagnosticsPanel
              enabled
              recordingId={currentRecordingId || null}
              isPlaying={isPlaying}
              currentTime={currentTime}
              playbackSpeed={playbackSpeed}
            />
          </Suspense>
        ) : null}
      </div>
      </EEGViewerRuntimeProvider>
    </>
  );
};

export default EEGViewer;
